import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { parityCopy } from "./parityLocalization";

const LANGUAGE_STORAGE_KEY = "shopbook:language:v2";
const LEGACY_LANGUAGE_STORAGE_KEY = "shopflow:language:v1";

export const allLanguageOptions = [
  { code: "en", englishLabel: "English", nativeLabel: "English" },
  { code: "ar", englishLabel: "Arabic", nativeLabel: "العربية" },
  { code: "fr", englishLabel: "French", nativeLabel: "Français" },
  { code: "ha", englishLabel: "Hausa", nativeLabel: "Hausa" },
  { code: "ig", englishLabel: "Igbo", nativeLabel: "Igbo" },
  { code: "pt", englishLabel: "Portuguese", nativeLabel: "Português" },
  { code: "sw", englishLabel: "Swahili", nativeLabel: "Kiswahili" },
  { code: "yo", englishLabel: "Yoruba", nativeLabel: "Yoruba" },
  { code: "zu", englishLabel: "Zulu", nativeLabel: "isiZulu" },
];

const localeMap = {
  ar: "ar",
  en: "en-NG",
  fr: "fr",
  ha: "ha-NG",
  ig: "ig-NG",
  pt: "pt",
  sw: "sw",
  yo: "yo-NG",
  zu: "zu",
};

const baseMessages = {
  common: {
    back: "Back",
    balanceCredits: (credits) => `Balance: ${credits} credits`,
    breadcrumb: "Breadcrumb",
    close: "Close",
    datePlaceholder: "dd/mm/yyyy",
    enterPaymentAmount: "Enter a payment amount",
    enterStockQuantity: "Enter a stock quantity",
    availableBalance: "Available balance",
    aiGenerateImage: "Generate with AI",
    chooseProductImage: "Choose product image",
    cleanImageDescriptionHelp:
      "Optionally tell AI what should be preserved or corrected.",
    cleanUploadedImage: "Clean and apply",
    uploadAndClean: "Upload & clean",
    uploadImageHelp: "JPEG, PNG, or WebP up to 1 MB. AI will standardize the image before it is saved.",
    uploadImageTooLarge: "The selected image must be 1 MB or smaller.",
    credits: "Credits",
    generateImage: "Generate Image",
    generateImageAction: "Generate image",
    generateImageCost: "Image generation cost",
    generateImageDialogFallbackProduct: "this product",
    generateImageDialogIntro: "We’ll generate an image for",
    generateImageDialogOutro:
      "and apply it to the product card as soon as it is ready.",
    generateImageDescriptionHelp:
      "Optionally describe the look, angle, or packaging details you expect.",
    generateImageDescriptionLabel: "Optional description",
    generateImageDescriptionPlaceholder:
      "e.g. front-facing transparent bottle with gold cap",
    generateImageDialogTitle: "Generate product image?",
    generatingImage: "Generating...",
    processingImage: "Processing image",
    general: "General",
    goToNextPage: "Go to next page",
    goToPreviousPage: "Go to previous page",
    language: "Language",
    more: "More",
    morePages: "More pages",
    next: "Next",
    nextSlide: "Next slide",
    pagination: "Pagination",
    previous: "Previous",
    previousSlide: "Previous slide",
    productImageGenerateFailed: "Could not generate product image",
    productImageGenerated: "Product image generated",
    productImageDialogIntro: "Choose how to create the image for",
    productImageDialogTitle: "Add product image",
    qrCodeTitle: "QR code",
    step: (current, total) => `Step ${current} of ${total}`,
    unknown: "Unknown",
    walkIn: "Walk-in",
  },
  layout: {
    access: "access",
    allCaughtUp: "You’re all caught up",
    analytics: "Analytics",
    analyticsSubtitle: "Trends, forecasts & insights",
    askAssistant: "Ask the assistant",
    assistant: "Assistant",
    assistantSubtitle: "Ask anything about your shop",
    attention: "Attention",
    branches: "Branches",
    branchesSubtitle: "Multi-branch operations",
    cancel: "Cancel",
    commandPlaceholder: "Search shops, sales, products, or jump to a page…",
    dashboard: "Dashboard",
    dashboardSubtitle: "Today's operational pulse",
    debts: "Debts",
    debtsSubtitle: "Customer credit tracker",
    expenses: "Expenses",
    expensesSubtitle: "Operating costs",
    credits: "Credits",
    creditsSubtitle: "Balance & top up",
    failures: "Failures",
    history: "History",
    historySubtitle: "All sales transactions",
    inStock: "in stock",
    inventory: "Inventory",
    inventorySubtitle: "Products & stock",
    manageBranches: "Manage branches…",
    markAllRead: "Mark all read",
    navigate: "Navigate",
    noBranchSelected: "No branch selected",
    noBranchesAvailable: "No branches available",
    noCompletedSync: "No completed sync yet",
    noNotifications: "No notifications yet",
    noResults: "No results found.",
    notifications: "Notifications",
    notificationsSubtitle: "Inbox & preferences",
    notificationPreferences: "Notification preferences",
    offline: "Offline",
    offlineMode: "Offline mode",
    openNotificationCenter: "Open notification center",
    openSyncSettings: "Open sync settings",
    operate: "Operate",
    preferences: "Preferences",
    processing: "Processing",
    products: "Products",
    profileSettings: "Profile & settings",
    queue: "Queue",
    queued: "queued",
    quickActions: "Quick actions",
    refreshWorkspace: "Refresh workspace",
    referrals: "Referrals",
    reports: "Reports",
    reportsSubtitle: "Insight-rich PDF reports",
    retailOperations: "Retail Operations",
    searchInventory: "Search inventory",
    sell: "Sell",
    sellSubtitle: "Run point-of-sale",
    settings: "Settings",
    wallet: "Wallet",
    walletSubtitle: "Subscription balance & history",
    settingsSubtitle: "Profile, security & sessions",
    support: "Help & support",
    supportMeta: "Cases & conversations",
    supportSubtitle: "Talk to the Shopbook team",
    signOut: "Sign out",
    signOutDescription:
      "You’ll be signed out of this device. You can sign in again anytime with your PIN or OTP.",
    signOutTitle: "Sign out?",
    staff: "Staff",
    staffLabel: "staff",
    staffSubtitle: "Team members & permissions",
    subscription: "Subscription",
    startSale: "Start a new sale",
    switchBranch: "Switch branch",
    synced: "Synced",
    tutorials: "Tutorials",
    toggleTheme: (mode) => `Toggle ${mode} mode`,
    unreadMessages: (count) => `${count} unread messages`,
    workspace: "Workspace",
    workspaceSynced: "Workspace synced",
    yes: "Yes",
    no: "No",
    lastSync: "Last sync",
    lastSynced: "Last synced",
  },
  dashboard: {
    actionRecommended: "Action recommended",
    activeBranch: (name) => `${name} is active`,
    askAI: "Ask AI",
    branch: "Branch",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} customers`,
    goodAfternoon: "Good afternoon",
    goodEvening: "Good evening",
    goodMorning: "Good morning",
    liveAnalyticsRecommendations: "Live analytics recommendations",
    liveInsight: "Live insight",
    newSale: "New sale",
    noBranchSelected: "No branch is selected in this session yet.",
    noProductMovementYet: "No product movement yet.",
    noRecentSalesYet: "No recent sales yet.",
    noRestockRecommendationsYet: "No restock recommendations yet.",
    noTopProductData: "No top product data has loaded yet.",
    openAnalytics: "Open analytics",
    outstandingDebt: "Outstanding debt",
    recent: (count) => `${count} recent`,
    recentSales: "Recent transactions",
    recentSalesMeta: (count, branchName) =>
      `Last ${count} sales · ${branchName}`,
    restockAnalyticsEmpty:
      "Restock recommendations will appear when analytics loads.",
    restockBody: (reason, quantity) =>
      `${reason} Recommended order: +${quantity} units.`,
    restockPlan: "Restock plan",
    restockPlanReview: "Review restock plan",
    restockToday: (name) => `Restock ${name} today`,
    reviewBranches: "Review branches",
    revenue: "Revenue",
    revenueLastDays: (days) => `Revenue · last ${days} days`,
    salesCompleted: "Sales completed",
    salesSyncing: "Sales data is syncing",
    stockAlerts: "Stock alerts",
    stockedOut: (count) => `${count} stocked out`,
    syncingBody: "No top product data has loaded yet.",
    topMovers: "Top movers",
    topMoversMeta: "Today · by units",
    topProductLead: (name) => `${name} is moving fastest today`,
    topProductLeadBody: (sold) =>
      `${sold} units sold so far. Use this momentum to protect stock before the afternoon rush.`,
    units: "units",
    viewAll: "View all",
    viewProduct: "View product",
    viewWorkspaceFor: (name) =>
      `You are viewing the live workspace for ${name}.`,
    todayRevenue: "Today's revenue",
    paymentPaid: "Paid",
    paymentPartlyPaid: "Partly paid",
    paymentUnpaid: "Unpaid",
  },
  analytics: {
    actionRecommended: "Action recommended",
    analyticsTitle: "Analytics",
    analyticsSubtitle: (count) =>
      `${count} business tips · restock, buying patterns, turnover, demand, and product performance`,
    analyticsBack: "Analytics",
    refresh: "Refresh",
    tryAgain: "Try again",
    previous: "Previous",
    next: "Next",
    sessionNotReady: "Your session is not ready yet.",
    loadingItems: "Loading items",
    thisTabCouldNotLoad: "This tab could not load",
    pageSummary: (page, totalPages, totalCount) =>
      `Page ${page} of ${totalPages} · ${totalCount} item${totalCount === 1 ? "" : "s"}`,
    cards: {
      restock: {
        title: "Plan a restock",
        metric: "Stock watch",
        detail:
          "See which items are running low and what you may need to buy again soon.",
        suggestion:
          "Buy enough to cover current shelf gaps and your next supplier cycle.",
      },
      buyingPatterns: {
        title: "Customer buying patterns",
        metric: "Basket signal",
        detail: "Shows the items customers usually buy in the same sale.",
        suggestion:
          "Place paired products closer together to lift checkout value.",
      },
      turnover: {
        title: "Fast and Slow selling products",
        metric: "Turnover",
        detail:
          "Shows which products sell quickly and which ones stay longer in your shop.",
        suggestion:
          "Keep fast sellers visible and review slow stock before buying more.",
      },
      demandForecast: {
        title: "What customers may need next",
        metric: "Demand forecast",
        detail:
          "Gives a simple idea of what customers may ask for soon based on recent sales.",
        suggestion:
          "Use recent demand to plan the next supplier order more confidently.",
      },
      topPerformers: {
        title: "Top performing products",
        metric: "Revenue leaders",
        detail: "Shows the products bringing in the most money for your shop.",
        suggestion:
          "Watch your strongest products closely so they stay available.",
      },
    },
    restockPlanner: {
      title: "Restock planner",
      subtitle:
        "Current stock, recent sales speed, and supplier timing in one buying flow.",
      loading: "Building your live restock plan",
      errorTitle: "Restock plan is not ready",
      summaryNeedsAttention: "Needs attention",
      summaryMonitor: "Monitor closely",
      summaryHealthy: "Healthy today",
      productsToCheck: (count) =>
        `${count} product${count === 1 ? "" : "s"} to check`,
      stockLooksHealthy: "Stock looks healthy",
      productsToCheckDescription:
        "A few products are nearing their reorder point based on current stock, recent sales, and supplier timing.",
      stockHealthyDescription:
        "Nothing is close to running out based on the latest stock and sales records.",
      supplierLeadTime: "Supplier lead time",
      safetyStock: "Safety stock",
      estimatedBuy: "Estimated buy",
      withDataSteps: [
        "Start with products marked Urgent or Soon.",
        "Use your supplier lead time when deciding when to buy.",
        "Confirm shelf count before ordering, then update stock after delivery.",
      ],
      withoutDataSteps: [
        "Keep recording sales and stock counts.",
        "Review this again before your next supplier order.",
        "Add low-stock levels for products that do not have them yet.",
      ],
      detailedView: "Detailed view",
      openFullList: "Open the full restock list",
      reviewList: "Review the restock list",
      fullListDescription: (count) =>
        `${count} product${count === 1 ? "" : "s"} are ready in the dedicated list view with buying quantities and cost signals.`,
      reviewListDescription:
        "There is nothing urgent right now, but you can still open the dedicated list view and review the latest planner output.",
      viewRestockList: "View restock list",
      livePlan: "Live plan",
      updatedAt: (date) => `Updated ${date}`,
    },
    restockList: {
      title: "Restock list",
      subtitle: "Detailed product recommendations for the next supplier order.",
      loading: "Loading restock recommendations",
      errorTitle: "Restock list is not ready",
      reviewedNow: "Reviewed just now",
      reviewedAt: (date) => `Reviewed ${date}`,
      inventoryGoodShape: "Inventory in good shape",
      noRestockNeeded: "No restock needed right now",
      noRestockDescription:
        "Current stock is covering projected demand within your supplier lead time.",
      supplierLeadTime: "Supplier lead time",
      safetyStock: "Safety stock",
      urgencyUrgent: "Urgent",
      urgencySoon: "Soon",
      urgencyWatch: "Watch",
      buyAtLeast: "Buy at least",
      estimatedCost: "Estimated cost",
      inStock: "In stock",
      leadTimeDemand: "Lead-time demand",
      sold30Days: "Sold in 30 days",
      dailySales: "Daily sales",
    },
    buyingPatterns: {
      title: "Customer buying patterns",
      subtitle: "Live product pairings from synced sales and customer baskets.",
      loading: "Finding repeat product pairings",
      errorTitle: "Buying patterns are not ready",
      summaryHighIntent: "High intent",
      summaryEmerging: "Emerging behavior",
      summaryBuilding: "Building signal",
      usefulPairings: (count) =>
        `${count} useful pairing${count === 1 ? "" : "s"} found`,
      noStrongPair: "No strong pair yet",
      usefulDescription:
        "Some products are showing up together repeatedly in customer baskets.",
      noStrongPairDescription:
        "There are not enough repeated mixed-item sales yet to identify a reliable product pair.",
      salesChecked: "Sales checked",
      pairingsFound: "Pairings found",
      strongestLift: "Strongest lift",
      withDataSteps: [
        "Place the paired products closer together so customers notice them.",
        "Suggest the second product when a customer buys the first one.",
        "Try a small bundle or shelf sign for the strongest pair.",
      ],
      withoutDataSteps: [
        "Keep recording sales with multiple items in one receipt.",
        "Watch for products customers naturally pick together.",
        "Review this again after more mixed sales come in.",
      ],
      detailedView: "Detailed view",
      openFullList: "Open the full pairing list",
      reviewList: "Review the pairing list",
      fullListDescription: (count) =>
        `${count} pairing${count === 1 ? "" : "s"} are ready in the dedicated list view with strength and basket metrics.`,
      reviewListDescription:
        "There is no strong pattern yet, but you can still open the dedicated list view and review the latest basket analysis.",
      viewPairingList: "View pairing list",
      livePairings: "Live pairings",
      updatedAt: (date) => `Updated ${date}`,
      listTitle: "Pairing list",
      listSubtitle: "Detailed basket pairings from recent multi-item sales.",
      listLoading: "Loading product pairings",
      listErrorTitle: "Pairing list is not ready",
      basketBehavior: "Basket behavior still forming",
      noReliablePairing: "No reliable pairing yet",
      noReliablePairingDescription:
        "More mixed-item sales are needed before this list can surface dependable product combinations.",
      salesWindow: "Sales window",
      shareOfSales: "Share of sales",
      pairStrength: "Pair strength",
      extraSignal: "Extra signal",
      pairFrequency: "Pair frequency",
      firstProduct: "First product",
      secondProduct: "Second product",
      paymentSaleCount: (count) => `${count} sale${count === 1 ? "" : "s"}`,
    },
    turnover: {
      title: "Fast and slow selling products",
      subtitle: "Live selling speed from synced stock and sales.",
      loading: "Checking product movement",
      errorTitle: "Turnover view is not ready",
      summaryClear: "Clear movement",
      summaryEarly: "Early movement",
      summaryBuilding: "Building signal",
      summaryCounts: (fast, slow) => `${fast} fast, ${slow} slow`,
      withSlowDescription:
        "Some products are selling quickly, while others are taking longer to leave the shop.",
      withoutSlowDescription:
        "Your tracked stock does not show a serious slow-selling problem right now.",
      fastMovers: "Fast movers",
      slowSellers: "Slow sellers",
      turnoverRate: "Sell-through rate",
      steps: [
        "Keep fast sellers visible and easy to reach.",
        "Check slow sellers for price, placement, expiry date, or customer interest.",
        "Avoid buying more slow stock until you understand why it is not moving.",
      ],
      detailedView: "Detailed view",
      openFullList: "Open the full movement list",
      reviewList: "Review the movement list",
      fullListDescription: (count) =>
        `${count} product${count === 1 ? "" : "s"} are ready in the dedicated list view with movement bands and stock-cover context.`,
      reviewListDescription:
        "There is no strong movement signal yet, but you can still open the dedicated list view and review the latest stock-speed analysis.",
      viewMovementList: "View movement list",
      liveMovement: "Live movement",
      updatedAt: (date) => `Updated ${date}`,
      notTracked: "Not tracked",
      tooEarly: "Too early",
      daysCover90Plus: "90+ days",
      aboutDays: (days) => `About ${days} day${days === 1 ? "" : "s"}`,
      listTitle: "Movement list",
      listSubtitle:
        "Detailed product movement from recent sales and tracked stock.",
      listLoading: "Loading product movement",
      listErrorTitle: "Movement list is not ready",
      couldNotLoadProducts: "Could not load turnover products.",
      fastDescription: "Products selling fastest in this sales window.",
      fastEmpty: "No fast-moving products yet in this sales window.",
      slowDescription: "Products that may be holding cash on the shelf.",
      slowEmpty: "No slow or stale products need review right now.",
      slowReviewTitle: "Slow sellers to review",
      sold30Days: "Sold in 30 days",
      weeklyRunRate: "Weekly run rate",
      currentStock: "Current stock",
      stockMayLast: "Stock may last",
      reviewedNow: "Reviewed just now",
      reviewedAt: (date) => `Reviewed ${date}`,
    },
    demandForecast: {
      title: "What customers may need next",
      subtitle: "Live demand stability from recent sales.",
      loading: "Grouping demand patterns",
      errorTitle: "Demand forecast is not ready",
      summaryReliable: "Reliable demand",
      summaryEarly: "Early demand pattern",
      summaryBuilding: "Building history",
      summaryCounts: (steady, care) => `${steady} steady, ${care} need care`,
      withDataDescription:
        "Some products have steady demand, while others rise and fall from week to week.",
      withoutDataDescription:
        "There is not enough sales history yet to show a reliable demand pattern.",
      steady: "Steady",
      watch: "Watch",
      irregular: "Irregular",
      steps: [
        "Buy steady-demand products with more confidence.",
        "Buy smaller quantities for irregular products until the pattern becomes clearer.",
        "Use the product list below before making your next supplier order.",
      ],
      detailedView: "Detailed view",
      openFullList: "Open the full demand list",
      reviewList: "Review the demand list",
      fullListDescription: (count) =>
        `${count} product${count === 1 ? "" : "s"} are ready in the dedicated list view with demand bands and stability metrics.`,
      reviewListDescription:
        "There is not enough sales history yet, but you can still open the dedicated list view and review the latest demand analysis.",
      viewDemandList: "View demand list",
      liveDemand: "Live demand",
      updatedAt: (date) => `Updated ${date}`,
      listTitle: "Demand list",
      listSubtitle: "Detailed product demand bands from recent sales history.",
      listLoading: "Loading demand groups",
      listErrorTitle: "Demand list is not ready",
      couldNotLoadProducts: "Could not load demand products.",
      salesHistoryBuilding: "Sales history still building",
      noReliableBand: "No reliable demand band yet",
      noReliableBandDescription:
        "A few more weeks of sales will help this view sort products into steady and irregular demand more confidently.",
      bandStableDescription: "Products customers ask for regularly.",
      bandStableEmpty: "No products are in Steady demand yet.",
      bandStableLong: "Steady demand",
      bandWatchDescription:
        "Products with demand worth watching before you buy deeply.",
      bandWatchEmpty: "No products are in Watch list yet.",
      bandWatchLong: "Watch list",
      bandIrregularDescription:
        "Products that rise and fall from week to week.",
      bandIrregularEmpty: "No products are in Irregular demand yet.",
      bandIrregularLong: "Irregular demand",
      avgWeekly: "Avg weekly",
      weeksWithSales: "Weeks with sales",
      soldInWindow: "Sold in window",
      variability: "Variability",
      reviewedNow: "Reviewed just now",
      reviewedAt: (date) => `Reviewed ${date}`,
    },
    topPerformers: {
      title: "Top performing products",
      subtitle: "Live product ranking from synced sales.",
      loading: "Ranking top products by revenue",
      errorTitle: "Top performers are not ready",
      summaryClear: "Clear leaders",
      summaryEarly: "Early ranking",
      summaryBuilding: "Building sales history",
      withDataDescription:
        "A smaller group of products is contributing more to your sales than the rest.",
      withoutDataDescription:
        "There is not enough product revenue data yet to show your strongest performers.",
      revenueLeaders: "Revenue leaders",
      steadySellers: "Steady sellers",
      reviewSoon: "Review soon",
      withDataSteps: [
        "Keep your top products in stock and easy to find.",
        "Check their prices and margins before running discounts.",
        "Review low-performing products before buying more of them.",
      ],
      withoutDataSteps: [
        "Record product sales so the app can rank what is bringing money in.",
        "Make sure product prices are filled correctly.",
        "Review this page after more sales are synced.",
      ],
      detailedView: "Detailed view",
      openFullList: "Open the full performance list",
      reviewList: "Review the performance list",
      fullListDescription: (count) =>
        `${count} product${count === 1 ? "" : "s"} are ready in the dedicated list view with revenue contribution and ranking bands.`,
      reviewListDescription:
        "There is not enough product sales data yet, but you can still open the dedicated list view and review the latest performance analysis.",
      viewPerformanceList: "View performance list",
      liveRanking: "Live ranking",
      updatedAt: (date) => `Updated ${date}`,
      listTitle: "Performance list",
      listSubtitle:
        "Detailed product ranking from recent revenue contribution.",
      listLoading: "Loading product ranking",
      listErrorTitle: "Performance list is not ready",
      couldNotLoadProducts: "Could not load top performers.",
      salesRankingForming: "Sales ranking still forming",
      noLeadersYet: "No product leaders yet",
      noLeadersDescription:
        "Once products generate more sales, this view will sort them by revenue contribution and surface your strongest performers.",
      bandADescription: "Products carrying the strongest revenue contribution.",
      bandAEmpty: "No products are in Revenue leaders yet.",
      bandALong: "Revenue leaders",
      bandBDescription:
        "Reliable sellers that still deserve regular attention.",
      bandBEmpty: "No products are in Steady sellers yet.",
      bandBLong: "Steady sellers",
      bandCDescription: "Products to review before buying more stock.",
      bandCEmpty: "No products are in Review soon yet.",
      bandCLong: "Review soon",
      leaders: "Leaders",
      steady: "Steady",
      review: "Review",
      revenue: "Revenue",
      contribution: "Contribution",
      soldInWindow: "Sold in window",
      currentStock: "Current stock",
      reviewedNow: "Reviewed just now",
      reviewedAt: (date) => `Reviewed ${date}`,
    },
  },
  notificationsPage: {
    title: "Notifications",
    subtitle: "Alerts, summaries, and delivery controls for this workspace.",
    markAllRead: "Mark all read",
    messages: "Messages",
    unread: "Unread",
    channelsOn: "Channels on",
    inbox: "Inbox",
    preferences: "Preferences",
    filterAll: "All",
    filterStock: "Stock",
    filterSales: "Sales",
    filterSync: "Sync",
    filterInsights: "Insights",
    noNotificationsFound: "No notifications found",
    noNotificationsDescription:
      "Try another filter or check back after more shop activity.",
    selectNotification: "Select a notification",
    deliveryPreferences: "Delivery preferences",
    enabled: "enabled",
    disabled: "disabled",
    on: "On",
    off: "Off",
    updatePreferencesFailed: "Could not update preferences",
    today: "Today",
    earlier: "Earlier",
    reviewSync: "Review sync",
    addStock: "Add stock",
    syncNeedsAttention: "Sync needs attention",
    syncPending: "Sync pending",
    queuedRecords: (count) =>
      `${count} record${count === 1 ? "" : "s"} waiting to sync.`,
    stockOut: "Stock out",
    lowStock: "Low stock",
    outOfStockBody: (name) => `${name} is out of stock.`,
    lowStockBody: (name, count) => `${name} has ${count} left.`,
    prefDailySummary: "Daily summary",
    prefDailySummaryDesc: "End-of-day revenue, sales count, top-movers.",
    prefLowStock: "Low stock alerts",
    prefLowStockDesc: "When a product drops below its threshold.",
    prefSmsSummary: "SMS summary",
    prefSmsSummaryDesc: "Owner SMS digest at 8pm daily.",
    prefWeeklyInsights: "Weekly insights",
    prefWeeklyInsightsDesc: "Pattern detection, fast/slow movers, anomalies.",
    prefMonthlyReports: "Monthly reports",
    prefMonthlyReportsDesc: "Full P&L style monthly recap.",
    teamReportsTitle: "Team report delivery",
    teamReportsDescription:
      "Choose which active staff receive daily, weekly, and monthly reports. Your own delivery remains controlled above.",
    teamReportsLoading: "Loading team delivery settings…",
    teamReportsLockedTitle: "Team delivery is not included in this package",
    teamReportsLockedDescription:
      "Choose an eligible team package to share scheduled reports with staff.",
    teamReportsLoadFailed: "Could not load team delivery settings",
    teamReportsUpdated: "Report delivery updated",
    teamReportsUpdateFailed: "Could not update report delivery",
    noTeamRecipients: "No active staff yet",
    noTeamRecipientsDescription:
      "Staff will appear here after they accept their invitation and activate their account.",
    participant: "Participant",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    reviewPlans: "Review packages",
    tryAgain: "Try again",
    saving: "Saving report delivery",
  },
  staffPage: {
    title: "Staff",
    subtitle:
      "Invite team members, assign branches, and keep permissions tidy.",
    inviteStaff: "Invite staff",
    members: "Members",
    active: "Active",
    invited: "Invited",
    disabled: "Disabled",
    team: "Team",
    invite: "Invite",
    searchPlaceholder: "Search by name or phone...",
    prepareInvite: "Prepare invite",
    fullName: "Full name",
    fullNamePlaceholder: "e.g. Amaka Onwe",
    phoneNumber: "Phone number",
    phoneNumberPlaceholder: "+2348012345678",
    branch: "Branch",
    role: "Role",
    access: "Access",
    accessLevel: "Access level",
    fineTunePermissions: "Fine tune permissions",
    sendInvite: "Send invite",
    inviteSent: "Invite sent",
    couldNotInviteStaff: "Could not invite staff",
    noStaffFound: "No staff found",
    noStaffDescription: "Invite a teammate or adjust your search.",
    editAccess: "Edit access",
    newTeammate: "New teammate",
    selectBranch: "Select branch",
    customAccess: "Custom access",
    noAccess: "No access",
    noBranch: "No branch",
    selectTeamMember: "Select a team member",
    remove: "Remove",
    staffRemoved: "Staff removed",
    couldNotRemoveStaff: "Could not remove staff",
    saveChanges: "Save changes",
    saved: "Saved",
    couldNotSaveStaff: "Could not save staff",
    lastSeen: "Last seen",
    statusActive: "Active",
    statusInvited: "Invited",
    statusDisabled: "Disabled",
    roleStaff: "Staff",
    roleManager: "Manager",
    select: "Select",
    chooseAccessLevel: "Choose an access level",
    chooseBranch: "Choose a branch",
    enterFullName: "Enter the staff member's full name",
    enterValidPhone: "Enter a valid phone number",
    presetBasicSeller: "Basic seller",
    presetBasicSellerDesc: "Can sell and review their recent sales.",
    presetOperations: "Operations",
    presetOperationsDesc: "Good for day-to-day stock, sales, debt, and alerts.",
    presetFullAccess: "Full access",
    presetFullAccessDesc:
      "Manager-level access across staff, branches, and analytics.",
    permissionSell: "Sell / POS",
    permissionHistory: "Sales history",
    permissionInventory: "Inventory",
    permissionDebts: "Debts",
    permissionExpenses: "Expenses",
    permissionAnalytics: "Analytics",
    permissionNotifications: "Notifications",
    permissionStaffAccess: "Staff access",
    permissionBranchManagement: "Branch management",
    permissionsCount: (count) => `${count} permission${count === 1 ? "" : "s"}`,
  },
  branchesPage: {
    title: "Branches",
    locationsSummary: (count, active) =>
      `${count} location${count === 1 ? "" : "s"} · ${active} active`,
    newBranch: "New branch",
    current: "Current",
    liveBranchRecord: "Live branch record",
    active: "Active",
    inactive: "Inactive",
    staff: "Staff",
    skus: "SKUs",
    today: "Today",
    switchedToBranch: (name) => `Switched to ${name}`,
    currentBranch: "Current branch",
    switchToBranch: "Switch to branch",
    createNewBranch: "Create new branch",
    createDescription: "Add a new physical location to your shop.",
    branchName: "Branch name",
    branchNamePlaceholder: "e.g. Ikeja Central",
    createHelper:
      "You can add the exact location later from the mobile or admin side.",
    cancel: "Cancel",
    enterValidBranchName: "Enter a valid branch name",
    branchCreated: "Branch created",
    couldNotCreateBranch: "Could not create branch",
    createBranch: "Create branch",
    updateBranch: "Update branch",
    updateDescription: "Rename this branch or update its active status.",
    activeBranch: "Active branch",
    activeBranchDescription:
      "Inactive branches stay visible but cannot be used as active locations.",
    branchUpdated: "Branch updated",
    couldNotUpdateBranch: "Could not update branch",
    saveChanges: "Save changes",
    branchNameTooShort: "Branch name must be at least 2 characters",
    branchNameTooLong: "Branch name must be 80 characters or less",
    roleAdmin: "Admin",
    roleMember: "Member",
    roleOwner: "Owner",
  },
  assistantPage: {
    title: "Shop Assistant",
    live: "Live",
    languageLabel: "Assistant language",
    connecting: "Connecting",
    reconnecting: "Reconnecting",
    loadingEarlierMessages: "Loading earlier messages…",
    chatReconnecting: "Chat is reconnecting",
    chatReconnectDescription:
      "Your secure chat history is temporarily unavailable. No message has been lost or charged.",
    tryAgain: "Try again",
    subtitle: "Answers come from the authenticated workspace data.",
    welcome:
      "Ask about live sales, stock, debts, staff, or restock recommendations.",
    inputPlaceholder: "Ask about sales, stock, analytics, or sync…",
    inputCostHint: "0.5 credit per message",
    outOfCreditsBody:
      "Each assistant message costs 0.5 credit. Add credits to continue chatting.",
    outOfCreditsPlaceholder: "Assistant credits exhausted",
    outOfCreditsTitle: "Not enough credits for another message",
    footer: "Answers are generated from live workspace data.",
    suggestedPrompts: "Suggested prompts",
    actions: "Actions",
    actionNewSale: "New sale",
    actionAddProduct: "Add product",
    actionWeeklyReport: "Weekly report",
    actionForceSync: "Force sync",
    promptSoldToday: "What was sold today?",
    promptLowStock: "Show low stock items",
    promptDebtOutstanding: "How much debt is outstanding?",
    promptRestockFirst: "What should we restock first?",
    promptPerformingBest: "Who is performing best?",
    promptCompareWeeks: "Compare this week with last week",
    dontKnowYet: "I don't know that from your shop data yet.",
    requestFailed: "Assistant request failed.",
    thinking: "Thinking...",
  },
  inventory: {
    action: "Action",
    addProduct: "Add product",
    addQuery: (name) => `Add "${name}"`,
    addStock: "Add stock",
    addStockFailed: "Could not add stock",
    addStockView: "Add stock",
    all: "All",
    allocation: "Stock allocation",
    allocationDescription:
      "A quick split of the available stock by team member.",
    backToInventory: "Back to inventory",
    backToProductDetails: "Back to product details",
    barcodeAttached: "Barcode attached",
    barcodeModeReady: "Barcode mode ready",
    barcodeModeReadyDescription: "Type or paste the scanned code into search.",
    barcodeNotFound: "Barcode not found",
    barcodeNotFoundDescription: "Add product details to save it.",
    calculate: "Calculate",
    cancel: "Cancel",
    category: "Category",
    checkRequiredProductDetails: "Check the required product details",
    clearRange: "Clear range",
    countNeedAttention: (count) => `${count} need attention`,
    createProduct: "Create product",
    createProductTitle: "Create product",
    currentStock: "Current stock",
    currentStockIs: (stock) => `Current stock is ${stock}.`,
    editLabel: (name) => `Edit ${name}`,
    editProduct: "Edit product",
    estimatedProfit: "Estimated profit",
    formDescription:
      "Keep pricing, barcode, and stock controls aligned with the shop catalog.",
    from: "From",
    inStock: "In stock",
    low: "Low",
    lowStockAt: "Low stock at",
    lostPotentialSales: (amount) => `Lost ${amount} in potential sales today.`,
    margin: "Margin",
    needAttention: "need attention",
    next: "Next",
    noBarcode: "no barcode",
    noProductsMatch: "No products match your filters.",
    noTransactionsYet: "No transactions yet",
    noTransactionsYetDescription:
      "This product has not appeared in any recorded sale.",
    openingStock: "Opening stock",
    outOfStock: "Out of stock",
    owner: "Owner",
    partlyPaid: "Partly paid",
    paymentPaid: "Paid",
    paymentUnpaid: "Unpaid",
    pickDate: "Pick a date",
    previous: "Previous",
    price: "Price",
    product: "Product",
    productDetails: "Product details",
    productScanned: "Product scanned",
    productUpdated: "Product updated",
    productAdded: "Product added",
    productName: "Product name",
    productNamePlaceholder: "e.g. Sample product",
    profitCalculator: "Profit calculator",
    quantity: "Quantity",
    recordIncomingStock: "Record incoming stock",
    revenue: "Revenue",
    saveFailed: "Could not save product",
    saveProduct: "Save product",
    saving: "Saving...",
    scan: "Scan",
    scannerReady: "Scanner ready",
    scannerReadyDescription: "Scan a barcode now to attach it to this product.",
    scanToAttach: "Scan to attach",
    searchPlaceholder: "Search products or scan barcode…",
    showing: (start, end, total) => `Showing ${start} to ${end} of ${total}`,
    skuCount: (count) => `${count} SKUs`,
    soldToday: "Sold today",
    soldTodayColumn: "Sold today",
    staff: "Staff",
    status: "Status",
    stock: "Stock",
    stockOut: "Stock-out",
    stockUpdated: "Stock updated",
    subtitle: (all, attention) => `${all} SKUs · ${attention} need attention`,
    to: "To",
    today: "Today",
    transactions: "Transactions",
    transactionsDescription:
      "This product has not appeared in any recorded sale.",
    transactionsWhen: "When",
    tryAgain: "Try again.",
    unitPrice: "Unit price",
    unitsSold: "Units sold",
    untracked: "untracked",
    viewDetails: "View details",
    inventoryTitle: "Inventory",
    allRange: "All",
    optional: "optional",
    optionalBarcode: "Optional barcode",
    costPrice: "Cost price",
    sellingPrice: "Selling price",
    saveProductFailed: "Could not save product",
    saveProductCreate: "Create product",
    transactionSaleId: "Sale ID",
    qty: "Qty",
    amount: "Amount",
    cashier: "Cashier",
    manager: "Manager",
    ownerName: "Amina",
    staffName: "Yusuf",
    managerName: "Halima",
    stockAllocationBalance: "Owner, staff, and manager balance",
    transactionStatus: "Status",
    profitRevenue: "Revenue",
    profitCostTotal: "Cost total",
    profitSellingPrice: "Selling price",
    validDateRange: "Choose a valid date range.",
    validation: {
      costRequired: "Cost price is required",
      costValid: "Cost price must be a valid amount",
      costNonNegative: "Cost price cannot be negative",
      lowStockWhole: "Low stock alert must be a whole number",
      lowStockNonNegative: "Low stock alert cannot be negative",
      productNameRequired: "Product name is required",
      priceRequired: "Selling price is required",
      priceValid: "Selling price must be a valid amount",
      priceAboveZero: "Selling price must be above zero",
      stockRequired: "Opening stock is required",
      stockWhole: "Opening stock must be a whole number",
      stockNonNegative: "Opening stock cannot be negative",
    },
  },
  history: {
    action: "Action",
    all: "All",
    amount: "Amount",
    amountPaid: "Amount paid",
    backToHistory: "Back to history",
    balanceDue: "Balance due",
    cashier: "Cashier",
    countTransactions: (count, total) =>
      `${count} transactions · ${total} total`,
    export: "Export",
    exportDownloaded: "Sales export downloaded",
    exportFailed: "Could not export sales",
    historyTitle: "History",
    items: "Items",
    lineItems: "Line items",
    noTransactionsMatch: "No transactions match your filters.",
    next: "Next",
    paid: "Paid",
    partial: "Partial",
    pending: "Pending",
    previous: "Previous",
    printReceipt: "Print receipt",
    receiptTitle: "Shopbook Receipt",
    sale: "Sale",
    saleDetail: "Sale detail",
    searchPlaceholder: "Search item or cashier…",
    showing: (start, end, total) => `Showing ${start} to ${end} of ${total}`,
    status: "Status",
    sync: "Sync",
    synced: "Synced",
    today: "Today",
    total: "Total",
    transactions: "transactions",
    unpaid: "Unpaid",
    viewDetails: "View details",
    when: "When",
    tryAgain: "Try again.",
    range7d: "7D",
    range30d: "30D",
  },
  debts: {
    action: "Action",
    all: "All",
    amount: "Amount",
    amountLabel: "Amount",
    balance: "Balance",
    cancel: "Cancel",
    customer: "Customer",
    date: "Date",
    debtId: "Debt ID",
    debtsTab: "Debts",
    debtsTitle: "Debts",
    dialogDescription: (name, amount) => `${name} owes ${amount}`,
    dialogTitle: "Record payment",
    due: "Due",
    items: "Items",
    markPaid: "Mark paid",
    next: "Next",
    noDebtsMatch: "No debts match your filters.",
    noDebtPaymentsMatch: "No debt payments match your filters.",
    noPhone: "No phone",
    none: "None",
    originalCreditSale: "Included in the original credit sale",
    overdue: "Overdue",
    paid: "Paid",
    paidSuffix: "paid",
    paymentDetails: "Payment details",
    paymentDetailsDescription: (name, amount) => `${name} paid ${amount}.`,
    paymentReceived: "Payment received",
    paymentRecorded: (amount) => `Payment of ${amount} recorded`,
    paymentsTab: "Payments",
    previous: "Previous",
    recordPaymentFailed: "Could not record payment",
    recording: "Recording...",
    searchDebtsPlaceholder: "Search customer, phone, or item...",
    searchPaymentsPlaceholder: "Search customer or debt ID...",
    settlement: "Settlement",
    settle: "Settle",
    showing: (start, end, total) => `Showing ${start} to ${end} of ${total}`,
    soldItems: "Sold items",
    soldItemsDescription: "Expanded item breakdown for this debt record.",
    status: "Status",
    statusOverdue: "Overdue",
    statusPaid: "Paid",
    statusPartlyPaid: "Partly paid",
    statusUnpaid: "Unpaid",
    subtitle: (open, outstanding) =>
      `${open} open · ${outstanding} outstanding`,
    totalItems: (count) => `${count} total`,
    tryAgain: "Try again.",
    unpaid: "Unpaid",
    units: (count) => `${count} unit${count === 1 ? "" : "s"}`,
    viewDetails: "View details",
    confirmPayment: "Confirm payment",
    markPaidSuccess: (name) => `${name} marked as paid`,
    markPaidFailed: "Could not mark debt paid",
    validation: {
      amountRequired: "Amount is required",
      amountValid: "Amount must be a valid number",
      amountPositive: "Amount must be greater than zero",
      amountCannotExceed: (balance) => `Amount cannot exceed ${balance}`,
    },
  },
  sell: {
    allProducts: "All Products",
    amountPaidNow: "Amount paid now",
    optionalShort: "optional",
    openingStock: "Opening stock",
    addFirstProduct: "Add first product",
    addNewProduct: "Add a new product",
    addNewProductDescription: "It will be added to this sale immediately.",
    balanceDue: "Balance due",
    barcodeNotFound: "Barcode not found",
    barcodeNotFoundDescription: "Add product details to save it.",
    cancel: "Cancel",
    cart: "Cart",
    cartEmpty: "Cart is empty",
    cartEmptyDescription:
      "Tap a product, scan a barcode, or search to start a sale.",
    checkRequiredProductDetails: "Check the required product details",
    clear: "Clear",
    completeSale: "Complete sale",
    costPrice: "Cost price",
    customerName: "Customer name",
    customerNamePlaceholder: "Customer name",
    dueDate: "Due date",
    keepTyping: "Keep typing to create or find a product.",
    newProduct: "New product",
    noProductsAvailableYet: "No products available yet",
    onCredit: "On credit",
    optional: "Optional",
    optionalBarcode: "Optional barcode",
    paid: "Paid",
    phone: "Phone",
    productAddedToCart: "Product added to cart",
    productMenu: "Product menu",
    productName: "Product name",
    productNamePlaceholder: "e.g. Sample product",
    productScanned: "Product scanned",
    editProduct: "Edit product",
    regenerateImage: "Regenerate image",
    recordCreditSale: "Record credit sale",
    recordSaleFailed: "Could not record sale",
    recording: "Recording...",
    refreshProducts: "Refresh catalogue",
    refreshProductsDone: "Catalogue refreshed",
    refreshProductsFailed: "Could not refresh catalogue",
    refreshProductsHint: "Fetch the latest catalogue from the server.",
    refreshAndTryAgain: "Refresh and try again.",
    saleRecorded: "Sale recorded",
    saleRecordedSummary: (total, count) =>
      `${total} · ${count} item${count === 1 ? "" : "s"}`,
    saveAndAddToCart: "Save and add to cart",
    saveProductFailed: "Could not add product",
    saving: "Saving...",
    scannerReady: "Scanner ready",
    scannerReadyDescription: "Scan a barcode now to attach it to this product.",
    scanToAttach: "Scan to attach",
    searchPlaceholder: "Search product, scan barcode, or type to find…",
    sellingPrice: "Selling price",
    total: "Total",
    tryAgain: "Try again.",
    untracked: "Untracked",
    stockIn: (count) => `${count} in stock`,
    stockLeft: (count) => `${count} left`,
    stockOut: "Out",
    productOutOfStock: (name) => `${name} is out of stock`,
    productOnlyInStock: (name, count) => `${name} has only ${count} in stock`,
    validation: {
      amountPaidLessThanTotal:
        "Amount paid must be less than the invoice total for credit sales",
      amountPaidRequired: "Amount paid is required",
      amountPaidValid: "Amount paid must be a valid amount",
      amountPaidNonNegative: "Amount paid cannot be negative",
      costValid: "Cost price must be a valid amount",
      costNonNegative: "Cost price cannot be negative",
      customerNameRequired: "Customer name is required for credit sales",
      dueDateRequired: "Due date is required for credit sales",
      lowStockWhole: "Low stock alert must be a whole number",
      lowStockNonNegative: "Low stock alert cannot be negative",
      priceRequired: "Selling price is required",
      priceValid: "Selling price must be a valid amount",
      priceAboveZero: "Selling price must be above zero",
      productNameRequired: "Product name is required",
      stockRequired: "Opening stock is required",
      stockWhole: "Opening stock must be a whole number",
      stockNonNegative: "Opening stock cannot be negative",
      invalidCreditAmount: "Enter a valid credit sale amount",
    },
  },
  settings: {
    account: "Account",
    activeBranch: "Active branch",
    appearance: "Appearance",
    authMode: "Auth mode",
    authModeIndependent: "Independent",
    authModeSession: "Session",
    authModeStaffSetup: "Staff setup",
    branchLocation: "Branch location",
    branchSecurity: "Branch security",
    cancel: "Cancel",
    changePin: "Change PIN",
    confirmation: "Confirmation",
    current: "Current",
    darkMode: "Dark mode",
    darkModeDescription: "Reduce brightness in low-light environments.",
    deleting: "Deleting...",
    deleteAccount: "Delete account",
    deleteAccountBody:
      "Delete your account access, revoke active sessions, and irreversibly anonymise your personal identity. Legally required organization records may be retained for the required period.",
    deleteAccountConfirmAction: "Delete permanently",
    deleteAccountConfirmDescription:
      "Type DELETE to confirm this account deletion.",
    deleteAccountConfirmTitle: "Delete account?",
    deleteAccountFailed: "Could not delete account",
    deleteAccountSuccess: "Account deleted",
    deleteAccountTypeError: "Type DELETE to confirm",
    devicesAndSync: "Devices & sync",
    expires: "Expires",
    forceSync: "Force sync",
    language: "Language",
    languageDescription: "Choose the language used across the web workspace.",
    lastSync: "Last sync",
    noActiveBranch: "No active branch selected",
    noBranch: "No branch",
    noBranchLocation: "No branch location saved",
    noPendingSync: "No pending sync records.",
    noSyncYet: "No sync yet",
    notificationPreferences: "Notifications",
    failures: "failures",
    pending: "pending",
    pinAndSessions: "PIN & sessions",
    pinAndSessionsDescription: "Authentication controls for this browser.",
    pinTitle: "4-digit PIN",
    pinDescription: "Used for phone-and-PIN sign in.",
    permissions: "Role & permissions",
    permissionsDescription: "Your access to modules and actions.",
    permissionAssistant: "Assistant",
    permissionSettings: "Settings",
    preferences: "Preferences",
    profile: "Profile",
    retry: "Retry",
    retryStarted: "Retry started",
    role: "Role",
    removeQueuedFailed: "Could not remove queued record",
    removeQueuedSuccess: "Queued stock change removed",
    queuedRecordUnavailable: "That queued record is no longer available.",
    queuedDeleteWait:
      "Wait for the current sync attempt to finish before deleting this record.",
    queuedDeleteStandaloneOnly:
      "Only standalone stock changes can be removed from the queue.",
    queueProductChangeBlocked:
      "Product changes stay queued until they sync or are replaced by a newer edit.",
    queueProductArchivePending: "Product archive pending",
    queueProductUpdatePending: "Product update pending",
    queueStockDeleteWait:
      "Wait for the current sync attempt to finish before removing this stock change.",
    queueStockSaleLinked:
      "This stock movement belongs to a sale and cannot be removed here.",
    queueStockAdditionPending: "Stock addition pending",
    queueStockReductionPending: "Stock reduction pending",
    queueInventoryItem: "Inventory item",
    queueSaleDeleteBlocked:
      "Sales affect stock and history, so they cannot be removed from this queue screen.",
    queueSaleItems: (count) => `${count} item${count === 1 ? "" : "s"} in sale`,
    queuePendingSale: "Pending sale",
    queueDebtDeleteBlocked:
      "Debt records stay queued until they sync or are updated by a newer change.",
    queueCustomerCredit: "Customer credit",
    queuePendingDebtRecord: "Pending debt record",
    queueDebtPaymentDeleteBlocked:
      "Debt payments cannot be removed from the sync queue screen.",
    queueCustomerPayment: "Customer payment",
    queuePendingDebtPayment: "Pending debt payment",
    queueGenericDeleteBlocked:
      "This queued record cannot be removed safely here.",
    queueGenericRecord: "Queued sync record",
    requestPrepareFailed: "Unable to prepare request",
    requestFailedGeneric: "Request failed",
    networkUnavailable: "We couldn't connect. Check that Wi-Fi or mobile data is working, then try again.",
    csrfTokenUnavailable: "Security token unavailable",
    indexedDbUnavailable: "Offline storage is not available in this browser",
    indexedDbOpenFailed: "Could not open offline storage",
    indexedDbRequestFailed: "Offline storage request failed",
    indexedDbTransactionFailed: "Offline storage transaction failed",
    indexedDbTransactionAborted: "Offline storage transaction was aborted",
    syncRecoveredInterrupted: "Recovered interrupted sync batch",
    syncWaitInitialStockBeforeSale:
      "Waiting for initial stock sync before sale",
    syncWaitProductBeforeSale: "Waiting for product sync before sale",
    syncWaitProductBeforeStockMovement:
      "Waiting for product sync before stock movement",
    syncWaitSaleBeforeStockMovement:
      "Waiting for sale sync before stock movement",
    savedLocation: (source) => `${source} location saved`,
    saveProfile: "Save profile",
    saving: "Saving...",
    sectionDescription:
      "Manage profile, branch security, sessions, and account access.",
    sectionTitle: "Settings",
    securityDescription:
      "Control whether staff must be physically near their assigned branch to keep working.",
    securityTitle: "Staff sign-in security",
    selectLanguageLabel: "Workspace language",
    shopName: "Shop name",
    shopNameRequired: "Shop name must be at least 2 characters",
    shopNameTooLong: "Shop name must be 120 characters or less",
    shopProfileDescription:
      "Update the shop name your team sees across this workspace.",
    shopProfileTitle: "Shop profile",
    signOut: "Sign out of this browser",
    staffRestrictionOff: "Restriction off",
    staffRestrictionOn: "Restriction on",
    staffRestrictionSavedOff: "Staff location restriction disabled",
    staffRestrictionSavedOn: "Staff location restriction enabled",
    staffRestrictionUpdateFailed: "Could not update branch security",
    staffRestrictionDescriptionOff:
      "Staff can work after sign-in without branch-area enforcement.",
    staffRestrictionDescriptionOn:
      "Staff must be near this branch when signing in.",
    staffRestrictionHint:
      "Save this branch location before enabling restriction.",
    statusAllCaughtUp: "All caught up",
    statusOffline: "Offline",
    statusPending: "Sync pending",
    statusAttention: "Sync needs attention",
    syncFailed: "Failed",
    syncPendingShort: "Pending",
    syncQueued: "Queued",
    syncSynced: "Synced",
    syncStatus: "Sync status",
    syncDescription: "Realtime connection with the Shopbook cloud.",
    syncRefreshed: "Sync refreshed",
    session: "Session",
    active: "Active",
    typeDelete: "DELETE",
    locationSourceAdmin: "admin",
    locationSourceDevice: "device",
    locationSourceGps: "gps",
    operationDebt: "Debt",
    operationDebtPayment: "Debt payment",
    operationProduct: "Product",
    operationSale: "Sale",
    operationStock: "Stock",
    updateLocation: "Use current shop location",
    updateLocationFailed: "Could not update branch location",
    updateLocationSuccess: "Branch location updated",
    updateLocationRefresh: "Refresh branch location",
    updateProfileFailed: "Could not update shop profile",
    updateProfileSuccess: "Shop profile updated",
  },
  auth: {
    panel: {
      badge: "Shopbook access",
      titleLead: "One secure flow.",
      titleTail: "Every device.",
      subtitle:
        "Sign in, create a shop, recover your PIN, or finish staff setup with the same account flow used in the mobile app.",
      cards: {
        phone: { title: "Email", label: "Your account key" },
        pin: { title: "PIN / OTP", label: "Context-aware" },
        branches: { title: "Branches", label: "Created at signup" },
        staff: { title: "Staff", label: "Shop selection" },
      },
      footer: "Server-backed sessions · one active device flow",
    },
    modeSwitch: {
      login: "Login",
      recover: "Recover",
      register: "Register",
    },
    copy: {
      login: {
        eyebrow: "Welcome back",
        title: "Sign in to your shop",
        helper: "Use the email address connected to your Shopbook account.",
        action: "Continue",
      },
      recover: {
        eyebrow: "Recover access",
        title: "Reset your password",
        helper: "We will send a one-time code to your registered email address.",
        action: "Send reset code",
      },
      register: {
        eyebrow: "Create account",
        title: "Start your shop profile",
        helper:
          "We will verify your email address before creating your shop workspace.",
        action: "Send verification code",
      },
    },
    fields: {
      branchName: "Branch name",
      confirmPin: "Confirm password",
      industry: "Industry",
      loginPin: "Password",
      newPin: "New password",
      phone: "Email",
      shopName: "Shop name",
      supplierLeadTime: "How long delivery takes",
    },
    industryOptions: [
      "Provision shop",
      "Boutique or fashion shop",
      "Fabrics, textiles and tailoring materials",
      "Shoes, bags and leather goods",
      "Cosmetics and beauty shop",
      "Phone and accessories shop",
      "Electronics and electrical appliances",
      "Supermarket or mini-mart",
      "Pharmacy or chemist",
      "Packaged food and dry goods",
      "Home items shop",
      "Building materials and hardware",
      "Vehicle or motorcycle parts and lubricants",
      "Grains, seeds and animal feed",
      "Drinks depot",
      "Books and stationery shop",
      "Furniture, timber and plywood",
      "Other or mixed-goods shop",
    ],
    placeholders: {
      branchName: "Main Branch",
      confirmPin: "Repeat PIN",
      leadTimeDays: "For example, 3",
      pin: "4 digits",
      selectIndustry: "Select industry",
      shopName: "Bluegate Stores",
    },
    verification: {
      createAccountInstead: "Create account instead",
      enterCode: "Enter the code",
      enterCodeHint: "Use the one-time code sent to your email.",
      enterPin: "Enter your PIN",
      enterPinHint: "Use the PIN assigned to this account.",
      noCodeYet: "No code yet?",
      resend: (count) => `Resend${count ? ` (${count})` : ""}`,
      secureSignIn: "Secure sign in",
      staffSetup: "Staff setup",
      verification: "Verification",
      verifyAndContinue: "Verify and continue",
      pinCapturedReady: "Password ready. You can sign in now.",
      codeCapturedReady: "Code captured. You can continue.",
      enterPinFallback: "Enter your password.",
      enterCodeFallback: "Enter the 4-digit code.",
    },
    screen: {
      activateStaffAccess: "Activate staff access",
      addYourBranch: "Add your branch",
      branch: "Branch",
      branchSetup: "Branch setup",
      branchSetupHelper:
        "Add at least one branch so your inventory and sales have a home.",
      chooseYourShop: "Choose your shop",
      chooseYourShopHelper:
        "This staff email is linked to more than one shop. Pick the workspace to open.",
      continue: "Continue",
      createAccount: "Create account",
      defaultBranch: "Default branch",
      nameYourShop: "Name your shop",
      recoverPinSubtitle: "Choose a new 4-digit PIN for this account.",
      recoverPinTitle: "Reset your PIN",
      setStaffPinSubtitle: "Create a 4-digit PIN for future staff sign-ins.",
      setStaffPinTitle: "Set up staff PIN",
      shopSetup: "Shop setup",
      shopSetupHelper:
        "Add your shop name, business phone, login PIN, and usual supplier lead time.",
      staffAccess: "Staff access",
      signIn: "Sign in",
      supplierLeadTime: (days) => `Delivery takes ${days} day${days === 1 ? "" : "s"}`,
    },
    status: {
      selectShopToContinue: "Select a shop to continue.",
      shopSelectedContinue: "Shop selected. Continue to open it.",
    },
    validation: {
      branchDuplicate: "This branch has already been added.",
      branchRequired: "Branch name is required.",
      branchTooShort: "Branch name is too short.",
      confirmPinMismatch: "Passwords do not match.",
      enterNewPin: "Enter a new password.",
      invalidLeadTimeWhole: "Enter delivery time in whole days.",
      invalidPhone: "Enter a valid email address.",
      leadTimeOneOrMore: "Enter 1 or more days.",
      leadTimeRequired: "How many days does delivery usually take?",
      needIndustry: "Choose an industry to continue.",
      pinMustBe4Digits: "Use 8–72 characters with a letter and a number.",
      shopNameRequired: "Shop name is required.",
      shopNameTooLong: "Shop name is too long.",
      addBranchToContinue: "Add a branch name to continue.",
    },
    feedback: {
      chooseShopToContinue: "Choose your shop to continue.",
      codeSent: "Code sent",
      completeRegistration: "Could not complete registration.",
      continueFailed:
        "Could not continue. Check your connection and try again.",
      finishStaffSetup: "Could not finish staff setup.",
      noAccountFound: "No shop account was found for this email.",
      openShopFailed: "Could not open that shop.",
      phoneTaken: "This email already has a Shopbook account. Sign in instead.",
      resetPinFailed: "Could not reset your PIN.",
      resendFailed: "Could not resend code.",
      reviewPinFields: "Please fix the highlighted PIN fields.",
      verifyCodeFailed: "Could not verify this code.",
    },
  },
};

const translations = {
  en: baseMessages,
  ar: {
    common: {
      back: "رجوع",
      breadcrumb: "مسار التنقل",
      close: "إغلاق",
      datePlaceholder: "يوم/شهر/سنة",
      enterPaymentAmount: "أدخل مبلغ الدفعة",
      enterStockQuantity: "أدخل كمية المخزون",
      general: "عام",
      goToNextPage: "اذهب إلى الصفحة التالية",
      goToPreviousPage: "اذهب إلى الصفحة السابقة",
      language: "اللغة",
      more: "المزيد",
      morePages: "صفحات إضافية",
      next: "التالي",
      nextSlide: "الشريحة التالية",
      pagination: "ترقيم الصفحات",
      previous: "السابق",
      previousSlide: "الشريحة السابقة",
      qrCodeTitle: "رمز QR",
      step: (current, total) => `الخطوة ${current} من ${total}`,
      unknown: "غير معروف",
      walkIn: "زائر مباشر",
    },
    auth: {
      panel: {
        badge: "دخول Shopbook",
        titleLead: "تدفق آمن واحد.",
        titleTail: "كل جهاز.",
        subtitle:
          "سجّل الدخول، أنشئ متجرك، استعد رقم PIN، أو أكمل إعداد الموظف من نفس تدفق الحساب المستخدم في التطبيق.",
        cards: {
          phone: { title: "الهاتف", label: "مفتاح حسابك" },
          pin: { title: "PIN / OTP", label: "بحسب السياق" },
          branches: { title: "الفروع", label: "تُنشأ عند التسجيل" },
          staff: { title: "الموظفون", label: "اختيار المتجر" },
        },
        footer: "جلسات مدعومة من الخادم · تدفق جهاز نشط واحد",
      },
      modeSwitch: {
        login: "دخول",
        recover: "استعادة",
        register: "تسجيل",
      },
      copy: {
        login: {
          eyebrow: "مرحباً بعودتك",
          title: "سجّل الدخول إلى متجرك",
          helper: "استخدم رقم الهاتف المرتبط بحساب Shopbook الخاص بك.",
          action: "متابعة",
        },
        recover: {
          eyebrow: "استعادة الوصول",
          title: "أعد تعيين PIN الدخول",
          helper: "سنرسل رمزاً لمرة واحدة إلى رقم هاتفك المسجل.",
          action: "إرسال رمز الاستعادة",
        },
        register: {
          eyebrow: "إنشاء حساب",
          title: "ابدأ ملف متجرك",
          helper: "سنتحقق من رقم هاتفك قبل إنشاء مساحة عمل متجرك.",
          action: "إرسال رمز التحقق",
        },
      },
      fields: {
        branchName: "اسم الفرع",
        confirmPin: "تأكيد PIN",
        industry: "النشاط",
        loginPin: "PIN الدخول",
        newPin: "PIN جديد",
        phone: "الهاتف",
        shopName: "اسم المتجر",
        supplierLeadTime: "مدة التوريد",
      },
      industryOptions: [
        "بقالة أو مواد تموينية",
        "ملابس أو بوتيك",
        "أقمشة ومنسوجات ولوازم خياطة",
        "أحذية وحقائب ومنتجات جلدية",
        "تجميل وعناية",
        "هواتف وإكسسوارات",
        "إلكترونيات وأجهزة كهربائية",
        "سوبرماركت أو ميني ماركت",
        "صيدلية",
        "أغذية معبأة ومواد جافة",
        "أدوات منزلية",
        "مواد بناء وأدوات صيانة",
        "قطع غيار سيارات أو دراجات وزيوت",
        "حبوب وبذور وأعلاف حيوانات",
        "مشروبات",
        "كتب وأدوات مكتبية",
        "أثاث وأخشاب وخشب رقائقي",
        "متجر بضائع متنوعة",
      ],
      placeholders: {
        branchName: "الفرع الرئيسي",
        confirmPin: "أعد إدخال PIN",
        leadTimeDays: "مثلاً 3",
        pin: "4 أرقام",
        selectIndustry: "اختر النشاط",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "أنشئ حساباً بدلاً من ذلك",
        enterCode: "أدخل الرمز",
        enterCodeHint: "استخدم الرمز المرسل إلى هاتفك.",
        enterPin: "أدخل PIN",
        enterPinHint: "استخدم PIN المخصص لهذا الحساب.",
        noCodeYet: "لم يصلك الرمز؟",
        resend: (count) => `إعادة الإرسال${count ? ` (${count})` : ""}`,
        secureSignIn: "دخول آمن",
        staffSetup: "إعداد الموظف",
        verification: "التحقق",
        verifyAndContinue: "تحقق وتابع",
        pinCapturedReady: "تم إدخال PIN. يمكنك تسجيل الدخول الآن.",
        codeCapturedReady: "تم إدخال الرمز. يمكنك المتابعة.",
        enterPinFallback: "أدخل PIN المكوّن من 4 أرقام.",
        enterCodeFallback: "أدخل الرمز المكوّن من 4 أرقام.",
      },
      screen: {
        activateStaffAccess: "تفعيل وصول الموظف",
        addYourBranch: "أضف فرعك",
        branch: "فرع",
        branchSetup: "إعداد الفرع",
        branchSetupHelper:
          "أضف فرعاً واحداً على الأقل ليكون للمخزون والمبيعات مكان واضح.",
        chooseYourShop: "اختر متجرك",
        chooseYourShopHelper:
          "رقم الموظف هذا مرتبط بأكثر من متجر. اختر مساحة العمل التي تريد فتحها.",
        continue: "متابعة",
        createAccount: "إنشاء حساب",
        defaultBranch: "الفرع الافتراضي",
        nameYourShop: "سمِّ متجرك",
        recoverPinSubtitle: "اختر PIN جديداً من 4 أرقام لهذا الحساب.",
        recoverPinTitle: "إعادة تعيين PIN",
        setStaffPinSubtitle: "أنشئ PIN من 4 أرقام لعمليات دخول الموظف القادمة.",
        setStaffPinTitle: "إعداد PIN الموظف",
        shopSetup: "إعداد المتجر",
        shopSetupHelper: "اضبط اسم المتجر وPIN الدخول ومدة التوريد المعتادة.",
        staffAccess: "وصول الموظف",
        signIn: "تسجيل الدخول",
        supplierLeadTime: (days) => `مدة التوريد: ${days} يوم`,
      },
      status: {
        selectShopToContinue: "اختر متجراً للمتابعة.",
        shopSelectedContinue: "تم اختيار المتجر. تابع لفتحه.",
      },
      validation: {
        branchDuplicate: "تمت إضافة هذا الفرع بالفعل.",
        branchRequired: "اسم الفرع مطلوب.",
        branchTooShort: "اسم الفرع قصير جداً.",
        confirmPinMismatch: "تأكيد PIN غير مطابق.",
        enterNewPin: "أدخل PIN دخول جديداً من 4 أرقام.",
        invalidLeadTimeWhole: "أدخل مدة التوريد كعدد صحيح من الأيام.",
        invalidPhone: "أدخل رقم هاتف صحيحاً.",
        leadTimeOneOrMore: "أدخل يوماً واحداً أو أكثر.",
        leadTimeRequired: "كم يوماً يستغرق التوريد عادة؟",
        needIndustry: "اختر النشاط للمتابعة.",
        pinMustBe4Digits: "اضبط PIN من 4 أرقام.",
        shopNameRequired: "اسم المتجر مطلوب.",
        shopNameTooLong: "اسم المتجر طويل جداً.",
        addBranchToContinue: "أضف اسم فرع للمتابعة.",
      },
      feedback: {
        chooseShopToContinue: "اختر متجرك للمتابعة.",
        codeSent: "تم إرسال الرمز",
        completeRegistration: "تعذر إكمال التسجيل.",
        continueFailed: "تعذر المتابعة. تحقّق من الاتصال وحاول مرة أخرى.",
        finishStaffSetup: "تعذر إكمال إعداد الموظف.",
        noAccountFound: "لم يتم العثور على حساب متجر لهذا الرقم.",
        openShopFailed: "تعذر فتح هذا المتجر.",
        phoneTaken:
          "هذا الرقم مرتبط بالفعل بحساب متجر. جرّب تسجيل الدخول بدلاً من ذلك.",
        resetPinFailed: "تعذر إعادة تعيين PIN.",
        resendFailed: "تعذر إعادة إرسال الرمز.",
        reviewPinFields: "يرجى تصحيح حقول PIN المظللة.",
        verifyCodeFailed: "تعذر التحقق من هذا الرمز.",
      },
    },
  },
  fr: {
    common: {
      back: "Retour",
      breadcrumb: "Fil d’Ariane",
      close: "Fermer",
      datePlaceholder: "jj/mm/aaaa",
      enterPaymentAmount: "Saisissez un montant de paiement",
      enterStockQuantity: "Saisissez une quantité de stock",
      general: "Général",
      goToNextPage: "Aller à la page suivante",
      goToPreviousPage: "Aller à la page précédente",
      language: "Langue",
      more: "Plus",
      morePages: "Plus de pages",
      next: "Suivant",
      nextSlide: "Diapositive suivante",
      pagination: "Pagination",
      previous: "Précédent",
      previousSlide: "Diapositive précédente",
      qrCodeTitle: "Code QR",
      step: (current, total) => `Étape ${current} sur ${total}`,
      unknown: "Inconnu",
      walkIn: "Client de passage",
    },
    auth: {
      panel: {
        badge: "Accès Shopbook",
        titleLead: "Un seul flux sécurisé.",
        titleTail: "Chaque appareil.",
        subtitle:
          "Connectez-vous, créez une boutique, récupérez votre PIN ou terminez la configuration du personnel avec le même flux de compte que sur l’application mobile.",
        cards: {
          phone: { title: "Téléphone", label: "Clé de votre compte" },
          pin: { title: "PIN / OTP", label: "Selon le contexte" },
          branches: { title: "Succursales", label: "Créées à l'inscription" },
          staff: { title: "Personnel", label: "Sélection de boutique" },
        },
        footer: "Sessions gérées côté serveur · un seul appareil actif",
      },
      modeSwitch: {
        login: "Connexion",
        recover: "Récupérer",
        register: "Inscription",
      },
      copy: {
        login: {
          eyebrow: "Bon retour",
          title: "Connectez-vous à votre boutique",
          helper:
            "Utilisez le numéro de téléphone lié à votre compte Shopbook.",
          action: "Continuer",
        },
        recover: {
          eyebrow: "Récupérer l'accès",
          title: "Réinitialisez votre PIN",
          helper: "Nous enverrons un code unique à votre numéro enregistré.",
          action: "Envoyer le code",
        },
        register: {
          eyebrow: "Créer un compte",
          title: "Commencez le profil de votre boutique",
          helper:
            "Nous vérifierons votre numéro avant de créer votre espace de travail.",
          action: "Envoyer le code de vérification",
        },
      },
      fields: {
        branchName: "Nom de la succursale",
        confirmPin: "Confirmer le PIN",
        industry: "Secteur",
        loginPin: "PIN de connexion",
        newPin: "Nouveau PIN",
        phone: "Téléphone",
        shopName: "Nom de la boutique",
        supplierLeadTime: "Délai fournisseur",
      },
      industryOptions: [
        "Boutique de provisions",
        "Boutique ou mode",
        "Tissus, textiles et matériel de couture",
        "Chaussures, sacs et articles en cuir",
        "Cosmétiques et beauté",
        "Téléphones et accessoires",
        "Électronique et appareils électriques",
        "Supermarché ou supérette",
        "Pharmacie",
        "Aliments emballés et produits secs",
        "Articles de maison",
        "Matériaux de construction et quincaillerie",
        "Pièces auto ou moto et lubrifiants",
        "Céréales, semences et aliments pour animaux",
        "Dépôt de boissons",
        "Livres et papeterie",
        "Meubles, bois et contreplaqué",
        "Produits divers ou magasin mixte",
      ],
      placeholders: {
        branchName: "Succursale principale",
        confirmPin: "Répétez le PIN",
        leadTimeDays: "Par exemple, 3",
        pin: "4 chiffres",
        selectIndustry: "Choisissez un secteur",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Créer un compte à la place",
        enterCode: "Saisissez le code",
        enterCodeHint: "Utilisez le code envoyé à votre téléphone.",
        enterPin: "Saisissez votre PIN",
        enterPinHint: "Utilisez le PIN attribué à ce compte.",
        noCodeYet: "Vous n’avez pas reçu le code ?",
        resend: (count) => `Renvoyer${count ? ` (${count})` : ""}`,
        secureSignIn: "Connexion sécurisée",
        staffSetup: "Configuration du personnel",
        verification: "Vérification",
        verifyAndContinue: "Vérifier et continuer",
        pinCapturedReady: "PIN saisi. Vous pouvez vous connecter.",
        codeCapturedReady: "Code saisi. Vous pouvez continuer.",
        enterPinFallback: "Saisissez votre PIN à 4 chiffres.",
        enterCodeFallback: "Saisissez le code à 4 chiffres.",
      },
      screen: {
        activateStaffAccess: "Activer l’accès du personnel",
        addYourBranch: "Ajoutez votre succursale",
        branch: "Succursale",
        branchSetup: "Configuration des succursales",
        branchSetupHelper:
          "Ajoutez au moins une succursale pour accueillir vos ventes et votre stock.",
        chooseYourShop: "Choisissez votre boutique",
        chooseYourShopHelper:
          "Ce numéro de personnel est lié à plusieurs boutiques. Choisissez l’espace à ouvrir.",
        continue: "Continuer",
        createAccount: "Créer un compte",
        defaultBranch: "Succursale par défaut",
        nameYourShop: "Nommez votre boutique",
        recoverPinSubtitle:
          "Choisissez un nouveau PIN à 4 chiffres pour ce compte.",
        recoverPinTitle: "Réinitialiser le PIN",
        setStaffPinSubtitle:
          "Créez un PIN à 4 chiffres pour les futures connexions du personnel.",
        setStaffPinTitle: "Configurer le PIN du personnel",
        shopSetup: "Configuration de la boutique",
        shopSetupHelper:
          "Définissez le nom de la boutique, le PIN et le délai fournisseur habituel.",
        staffAccess: "Accès du personnel",
        signIn: "Se connecter",
        supplierLeadTime: (days) => `Délai fournisseur : ${days} jours`,
      },
      status: {
        selectShopToContinue: "Choisissez une boutique pour continuer.",
        shopSelectedContinue: "Boutique sélectionnée. Continuez pour l’ouvrir.",
      },
      validation: {
        branchDuplicate: "Cette succursale a déjà été ajoutée.",
        branchRequired: "Le nom de la succursale est requis.",
        branchTooShort: "Le nom de la succursale est trop court.",
        confirmPinMismatch: "La confirmation du PIN ne correspond pas.",
        enterNewPin: "Saisissez un nouveau PIN de 4 chiffres.",
        invalidLeadTimeWhole: "Entrez le délai en nombre entier de jours.",
        invalidPhone: "Entrez un numéro de téléphone valide.",
        leadTimeOneOrMore: "Entrez 1 jour ou plus.",
        leadTimeRequired:
          "Combien de jours prend habituellement la livraison ?",
        needIndustry: "Choisissez un secteur pour continuer.",
        pinMustBe4Digits: "Définissez un PIN de 4 chiffres.",
        shopNameRequired: "Le nom de la boutique est requis.",
        shopNameTooLong: "Le nom de la boutique est trop long.",
        addBranchToContinue: "Ajoutez un nom de succursale pour continuer.",
      },
      feedback: {
        chooseShopToContinue: "Choisissez votre boutique pour continuer.",
        codeSent: "Code envoyé",
        completeRegistration: "Impossible de terminer l’inscription.",
        continueFailed:
          "Impossible de continuer. Vérifiez votre connexion et réessayez.",
        finishStaffSetup:
          "Impossible de terminer la configuration du personnel.",
        noAccountFound: "Aucun compte boutique n’a été trouvé pour ce numéro.",
        openShopFailed: "Impossible d’ouvrir cette boutique.",
        phoneTaken:
          "Ce numéro est déjà lié à un compte boutique. Essayez plutôt de vous connecter.",
        resetPinFailed: "Impossible de réinitialiser le PIN.",
        resendFailed: "Impossible de renvoyer le code.",
        reviewPinFields: "Veuillez corriger les champs PIN mis en évidence.",
        verifyCodeFailed: "Impossible de vérifier ce code.",
      },
    },
  },
  ha: {
    common: {
      back: "Baya",
      breadcrumb: "Hanyar shafi",
      close: "Rufe",
      datePlaceholder: "dd/mm/yyyy",
      enterPaymentAmount: "Shigar da adadin biyan kuɗi",
      enterStockQuantity: "Shigar da yawan kaya",
      general: "Na gama gari",
      goToNextPage: "Je zuwa shafi na gaba",
      goToPreviousPage: "Je zuwa shafi na baya",
      language: "Harshe",
      more: "Kari",
      morePages: "Karin shafuka",
      next: "Na gaba",
      nextSlide: "Silidi na gaba",
      pagination: "Rarraba shafuka",
      previous: "Na baya",
      previousSlide: "Silidi na baya",
      qrCodeTitle: "Lambar QR",
      step: (current, total) => `Mataki na ${current} cikin ${total}`,
      unknown: "Ba a sani ba",
      walkIn: "Mai shigowa kai tsaye",
    },
    auth: {
      panel: {
        badge: "Shigar Shopbook",
        titleLead: "Hanya daya mai tsaro.",
        titleTail: "Kowace na'ura.",
        subtitle:
          "Shiga, kirkiro shago, dawo da PIN, ko kammala saitin ma'aikaci ta hanyar da app din wayar ke amfani da ita.",
        cards: {
          phone: { title: "Waya", label: "Mabudin asusunka" },
          pin: { title: "PIN / OTP", label: "Ya danganta da hali" },
          branches: { title: "Rassa", label: "An kirkiro su yayin rajista" },
          staff: { title: "Ma'aikata", label: "Zabin shago" },
        },
        footer: "Sessions masu goyon bayan sabar · na'ura daya kacal a kunne",
      },
      modeSwitch: {
        login: "Shiga",
        recover: "Dawo",
        register: "Rajista",
      },
      copy: {
        login: {
          eyebrow: "Shiga",
          title: "Shiga cikin shagonka",
          helper:
            "Yi amfani da lambar wayar da ke hade da asusun Shopbook dinka.",
          action: "Ci gaba",
        },
        recover: {
          eyebrow: "Dawo da shiga",
          title: "Sake saita PIN na shiga",
          helper: "Za mu aika lambar wucin gadi zuwa lambar wayar ka.",
          action: "Aika lambar dawo da shiga",
        },
        register: {
          eyebrow: "Kirkiro asusu",
          title: "Fara bayanan shaganka",
          helper:
            "Za mu tabbatar da lambar wayarka kafin mu kirkiro wurin aikinka.",
          action: "Aika lambar tabbaci",
        },
      },
      fields: {
        branchName: "Sunan reshe",
        confirmPin: "Tabbatar da PIN",
        industry: "Bangaren kasuwanci",
        loginPin: "PIN na shiga",
        newPin: "Sabon PIN",
        phone: "Waya",
        shopName: "Sunan shago",
        supplierLeadTime: "Lokacin kawo kaya",
      },
      industryOptions: [
        "Shagon kayan masarufi",
        "Shagon kaya ko sutura",
        "Shagon yadudduka da kayan dinki",
        "Shagon takalma, jakunkuna da kayan fata",
        "Shagon kwalliya da kyau",
        "Shagon waya da kayan hada waya",
        "Shagon kayan lantarki da na’urorin gida",
        "Babban shago ko karamin mart",
        "Shagon magani",
        "Shagon busassun kayan abinci da na kunshe",
        "Shagon kayan gida",
        "Shagon kayan gini da hardware",
        "Shagon kayan mota ko babur da man shafawa",
        "Shagon hatsi, iri da abincin dabbobi",
        "Wurin sayar da abin sha",
        "Shagon littattafai da kayan rubutu",
        "Shagon kayan daki, katako da plywood",
        "Shagon kayayyaki iri-iri",
      ],
      placeholders: {
        branchName: "Babban Reshe",
        confirmPin: "Maimaita PIN",
        leadTimeDays: "Misali, 3",
        pin: "Lambobi 4",
        selectIndustry: "Zabi bangare",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Kirkiro asusu maimakon haka",
        enterCode: "Shigar da lambar",
        enterCodeHint:
          "Yi amfani da lambar wucin gadi da aka aika zuwa wayarka.",
        enterPin: "Shigar da PIN dinka",
        enterPinHint: "Yi amfani da PIN da aka ba wannan asusun.",
        noCodeYet: "Ba ka samu lambar ba?",
        resend: (count) => `Sake aikawa${count ? ` (${count})` : ""}`,
        secureSignIn: "Shiga cikin tsaro",
        staffSetup: "Saitin ma'aikaci",
        verification: "Tabbatarwa",
        verifyAndContinue: "Tabbatar ka ci gaba",
        pinCapturedReady: "An karbi PIN. Za ka iya shiga yanzu.",
        codeCapturedReady: "An karbi lambar. Za ka iya ci gaba.",
        enterPinFallback: "Shigar da PIN dinka mai lambobi 4.",
        enterCodeFallback: "Shigar da lambar lambobi 4.",
      },
      screen: {
        activateStaffAccess: "Kunna shiga ma'aikaci",
        addYourBranch: "Kara reshenka",
        branch: "Reshe",
        branchSetup: "Saitin reshe",
        branchSetupHelper:
          "Kara akalla reshe daya domin kayanka da tallace-tallace su sami gida.",
        chooseYourShop: "Zabi shaganka",
        chooseYourShopHelper:
          "Wannan lambar ma'aikaci tana hade da shaguna fiye da daya. Zabi wurin aiki.",
        continue: "Ci gaba",
        createAccount: "Kirkiro asusu",
        defaultBranch: "Babban reshe",
        nameYourShop: "Sanya sunan shaganka",
        recoverPinSubtitle: "Zabi sabon PIN mai lambobi 4 don wannan asusun.",
        recoverPinTitle: "Sake saita PIN",
        setStaffPinSubtitle:
          "Kirkiro PIN mai lambobi 4 domin shiga ma'aikaci a gaba.",
        setStaffPinTitle: "Saita PIN na ma'aikaci",
        shopSetup: "Saitin shago",
        shopSetupHelper:
          "Saita sunan shago, PIN na shiga, da lokacin kawo kaya.",
        staffAccess: "Damar ma'aikata",
        signIn: "Shiga",
        supplierLeadTime: (days) => `Ana kawo kaya cikin kwanaki ${days}`,
      },
      status: {
        selectShopToContinue: "Zabi shago domin ci gaba.",
        shopSelectedContinue: "An zabi shago. Ci gaba domin budewa.",
      },
      validation: {
        branchDuplicate: "An riga an kara wannan reshe.",
        branchRequired: "Ana bukatar sunan reshe.",
        branchTooShort: "Sunan reshen ya yi gajarta.",
        confirmPinMismatch: "Tabbatarwar PIN ba ta yi daidai ba.",
        enterNewPin: "Shigar da sabon PIN mai lambobi 4.",
        invalidLeadTimeWhole: "Shigar da kwanakin kawo kaya a cikakken lamba.",
        invalidPhone: "Shigar da ingantacciyar lambar waya.",
        leadTimeOneOrMore: "Shigar da 1 ko fiye da haka.",
        leadTimeRequired: "Kwanaki nawa kai kayan kaya ke dauka?",
        needIndustry: "Zabi bangaren shaganka domin ci gaba.",
        pinMustBe4Digits: "Saita PIN mai lambobi 4.",
        shopNameRequired: "Ana bukatar sunan shago.",
        shopNameTooLong: "Sunan shagon ya yi tsawo sosai.",
        addBranchToContinue: "Kara sunan reshe domin ci gaba.",
      },
      feedback: {
        chooseShopToContinue: "Zabi shaganka domin ci gaba.",
        codeSent: "An aika lambar",
        completeRegistration: "Ba a iya kammala rajista ba.",
        continueFailed:
          "Ba a iya ci gaba ba. Duba haɗin ka sannan ka sake gwadawa.",
        finishStaffSetup: "Ba a iya kammala saitin ma'aikaci ba.",
        noAccountFound: "Ba a samu asusun shago da wannan lambar ba.",
        openShopFailed: "Ba a iya bude wannan shago ba.",
        phoneTaken:
          "Wannan lambar waya tana da asusun shago tuni. Gwada shiga maimakon haka.",
        resetPinFailed: "Ba a iya sake saita PIN ba.",
        resendFailed: "Ba a iya sake aika lambar ba.",
        reviewPinFields: "Da fatan a gyara filayen PIN da aka haskaka.",
        verifyCodeFailed: "Ba a iya tabbatar da wannan lambar ba.",
      },
    },
  },
  ig: {
    common: {
      back: "Azu",
      breadcrumb: "Ụzọ ibe",
      close: "Mechie",
      datePlaceholder: "ụbọchị/ọnwa/afọ",
      enterPaymentAmount: "Tinye ego ịkwụ ụgwọ",
      enterStockQuantity: "Tinye ọnụọgụ stock",
      general: "Izugbe",
      goToNextPage: "Gaa na ibe sochirinụ",
      goToPreviousPage: "Gaa na ibe gara aga",
      language: "Asusu",
      more: "Ọzọ",
      morePages: "Ibe ndị ọzọ",
      next: "Na-esote",
      nextSlide: "Silaịdụ na-esote",
      pagination: "Ntupu ibe",
      previous: "Nke gara aga",
      previousSlide: "Silaịdụ gara aga",
      qrCodeTitle: "Koodu QR",
      step: (current, total) => `Nzọụkwụ ${current} n'ime ${total}`,
      unknown: "Amaghị",
      walkIn: "Onye bịara ozugbo",
    },
    auth: {
      panel: {
        badge: "Nbanye Shopbook",
        titleLead: "Otu usoro nchedo.",
        titleTail: "Ngwa obula.",
        subtitle:
          "Banye, mepụta ulo ahia, weghachi PIN gi, ma obu mezue nhazi ndi oru site na otu uzo a na-eji na app ekwentị.",
        cards: {
          phone: { title: "Ekwenti", label: "Mkpisi akaụntụ gi" },
          pin: { title: "PIN / OTP", label: "Dabere na onodu" },
          branches: { title: "Alaka", label: "A na-emepụta ha mgbe ndebanye" },
          staff: { title: "Ndi oru", label: "Nhọrọ ulo ahia" },
        },
        footer: "Sessions nke sava na-akwado · otu ngwaọrụ nọ n'ọrụ",
      },
      modeSwitch: {
        login: "Banye",
        recover: "Weghachi",
        register: "Debanye",
      },
      copy: {
        login: {
          eyebrow: "Nbanye",
          title: "Banye n'ime ulo ahia gi",
          helper: "Jiri numeru ekwenti ejikoro na akaụntụ Shopbook gi.",
          action: "Gaa n'ihu",
        },
        recover: {
          eyebrow: "Weghachi ohere",
          title: "Tọgharịa PIN nbanye gi",
          helper: "Anyi ga-eziga koodu otu ugboro na numeru ekwenti gi.",
          action: "Zipu koodu iweghachi",
        },
        register: {
          eyebrow: "Mepụta akaụntụ",
          title: "Bido profaịlụ ulo ahia gi",
          helper:
            "Anyị ga-enyocha numeru ekwentị gị tupu anyị emepụta ebe ọrụ gi.",
          action: "Zipu koodu nkwenye",
        },
      },
      fields: {
        branchName: "Aha alaka",
        confirmPin: "Kwenye PIN",
        industry: "Udiri ahia",
        loginPin: "PIN nbanye",
        newPin: "PIN ohuru",
        phone: "Ekwenti",
        shopName: "Aha ulo ahia",
        supplierLeadTime: "Oge mbubata",
      },
      industryOptions: [
        "Ulo ahia ngwa nri na ihe eji kwa ubochi",
        "Ulo ahia ejiji",
        "Ahia akwa na ngwa eji akwa uwe",
        "Ahia akpụkpọ ụkwụ, akpa na ngwaahịa akpụkpọ anụ",
        "Ahia mma na nlekọta aru",
        "Ahia ekwentI na ngwa ya",
        "Ahia eletrọniki na ngwa eletrik",
        "Nnukwu ulo ahia ma obu mini-mart",
        "Ulo ogwu",
        "Ahia nri akọrọ na nke akpọchiri akpọchi",
        "Ahia ihe ulo",
        "Ahia ihe owuwu na hardware",
        "Ahia akụkụ ụgbọala ma ọ bụ ọgba tum tum na mmanụ",
        "Ahia ọka, mkpụrụ na nri anụmanụ",
        "Depot ihe ọṅụṅụ",
        "Ahia akwụkwọ na ngwa ide ihe",
        "Ahia arịa ụlọ, osisi na plywood",
        "Ahia ngwongwo agwakọtara",
      ],
      placeholders: {
        branchName: "Isi Alaka",
        confirmPin: "Tinye PIN ozo",
        leadTimeDays: "Dika, 3",
        pin: "Nọmba 4",
        selectIndustry: "Horo udiri ahia",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Mepụta akaụntụ kama",
        enterCode: "Tinye koodu",
        enterCodeHint: "Jiri koodu otu ugboro e zitere na ekwentị gi.",
        enterPin: "Tinye PIN gi",
        enterPinHint: "Jiri PIN e nyere akaụntụ a.",
        noCodeYet: "I nataghi koodu a?",
        resend: (count) => `Zipu ozo${count ? ` (${count})` : ""}`,
        secureSignIn: "Nbanye echekwara",
        staffSetup: "Nhazi ndi oru",
        verification: "Nkwenye",
        verifyAndContinue: "Kwenye gaa n'ihu",
        pinCapturedReady: "Anabatala PIN. I nwere ike ibanye ugbu a.",
        codeCapturedReady: "Anabatala koodu. I nwere ike ịga n'ihu.",
        enterPinFallback: "Tinye PIN gi nke onu ogugu 4.",
        enterCodeFallback: "Tinye koodu nke onu ogugu 4.",
      },
      screen: {
        activateStaffAccess: "Mee ohere ndi oru ka oru",
        addYourBranch: "Tinye alaka gi",
        branch: "Alaka",
        branchSetup: "Nhazi alaka",
        branchSetupHelper:
          "Tinye opekata mpe alaka otu ka ngwa ahia na ire ahia nwee ebe.",
        chooseYourShop: "Horo ulo ahia gi",
        chooseYourShopHelper:
          "Numeru ndi oru a jikoro na ulo ahia kariri otu. Horo ebe oru.",
        continue: "Gaa n'ihu",
        createAccount: "Mepụta akaụntụ",
        defaultBranch: "Alaka ndabere",
        nameYourShop: "Kpoo ulo ahia gi aha",
        recoverPinSubtitle: "Horo PIN ohuru nke onu ogugu 4 maka akaụntụ a.",
        recoverPinTitle: "Tọgharịa PIN gi",
        setStaffPinSubtitle:
          "Mepụta PIN nke onu ogugu 4 maka nbanye ndi oru n'ọdịnihu.",
        setStaffPinTitle: "Tọọ PIN ndi oru",
        shopSetup: "Nhazi ulo ahia",
        shopSetupHelper:
          "Tọọ aha ulo ahia gi, PIN nbanye, na oge mbubata ndi na-eweta ihe.",
        staffAccess: "Ohere ndi oru",
        signIn: "Banye",
        supplierLeadTime: (days) => `Mbubata n'ime ụbọchị ${days}`,
      },
      status: {
        selectShopToContinue: "Horo ulo ahia ka i gaa n'ihu.",
        shopSelectedContinue: "A họrọla ulo ahia. Gaa n'ihu imeghe ya.",
      },
      validation: {
        branchDuplicate: "A tinyela alaka a ugbua.",
        branchRequired: "A choro aha alaka.",
        branchTooShort: "Aha alaka a di mkpirikpi.",
        confirmPinMismatch: "Nkwenye PIN adabaghi.",
        enterNewPin: "Tinye PIN ohuru nke onu ogugu 4.",
        invalidLeadTimeWhole: "Tinye oge mbubata n'ujo nke ubochi.",
        invalidPhone: "Tinye numeru ekwentI ziri ezi.",
        leadTimeOneOrMore: "Tinye 1 ma obu karia.",
        leadTimeRequired: "Ubochi ole ka ibubata ihe na-ewe?",
        needIndustry: "Horo udiri ulo ahia gi ka i gaa n'ihu.",
        pinMustBe4Digits: "Tinye PIN nke onu ogugu 4.",
        shopNameRequired: "A choro aha ulo ahia.",
        shopNameTooLong: "Aha ulo ahia a toro oke.",
        addBranchToContinue: "Tinye aha alaka ka i gaa n'ihu.",
      },
      feedback: {
        chooseShopToContinue: "Horo ulo ahia gi ka i gaa n'ihu.",
        codeSent: "E zigara koodu",
        completeRegistration: "Enweghi ike mezue ndebanye.",
        continueFailed: "Enweghi ike ịga n'ihu. Lelee njikọ gị ma nwalee ọzọ.",
        finishStaffSetup: "Enweghi ike mezue nhazi ndi oru.",
        noAccountFound: "Enweghi akaụntụ ulo ahia maka numeru a.",
        openShopFailed: "Enweghi ike imeghe ulo ahia a.",
        phoneTaken:
          "Numeru ekwentI a ejikola akaụntụ ulo ahia. Gbalịa ibanye kama.",
        resetPinFailed: "Enweghi ike ịtọgharịa PIN.",
        resendFailed: "Enweghi ike izipu koodu ozo.",
        reviewPinFields: "Biko dozie mpaghara PIN a gosiri.",
        verifyCodeFailed: "Enweghi ike ikwenye koodu a.",
      },
    },
  },
  pt: {
    common: {
      back: "Voltar",
      breadcrumb: "Navegação estrutural",
      close: "Fechar",
      datePlaceholder: "dd/mm/aaaa",
      enterPaymentAmount: "Introduza um valor de pagamento",
      enterStockQuantity: "Introduza uma quantidade de stock",
      general: "Geral",
      goToNextPage: "Ir para a página seguinte",
      goToPreviousPage: "Ir para a página anterior",
      language: "Idioma",
      more: "Mais",
      morePages: "Mais páginas",
      next: "Seguinte",
      nextSlide: "Slide seguinte",
      pagination: "Paginação",
      previous: "Anterior",
      previousSlide: "Slide anterior",
      qrCodeTitle: "Código QR",
      step: (current, total) => `Passo ${current} de ${total}`,
      unknown: "Desconhecido",
      walkIn: "Cliente ocasional",
    },
    auth: {
      panel: {
        badge: "Acesso Shopbook",
        titleLead: "Um fluxo seguro.",
        titleTail: "Todos os dispositivos.",
        subtitle:
          "Entre, crie a sua loja, recupere o seu PIN ou conclua a configuração do funcionário com o mesmo fluxo usado no aplicativo móvel.",
        cards: {
          phone: { title: "Telefone", label: "Chave da sua conta" },
          pin: { title: "PIN / OTP", label: "Conforme o contexto" },
          branches: { title: "Filiais", label: "Criadas no cadastro" },
          staff: { title: "Funcionários", label: "Escolha da loja" },
        },
        footer: "Sessões no servidor · apenas um dispositivo ativo",
      },
      modeSwitch: {
        login: "Entrar",
        recover: "Recuperar",
        register: "Cadastrar",
      },
      copy: {
        login: {
          eyebrow: "Bem-vindo de volta",
          title: "Entre na sua loja",
          helper: "Use o número de telefone ligado à sua conta Shopbook.",
          action: "Continuar",
        },
        recover: {
          eyebrow: "Recuperar acesso",
          title: "Redefina o seu PIN",
          helper: "Enviaremos um código único para o seu número registado.",
          action: "Enviar código de recuperação",
        },
        register: {
          eyebrow: "Criar conta",
          title: "Comece o perfil da sua loja",
          helper:
            "Vamos verificar o seu número antes de criar o espaço de trabalho da sua loja.",
          action: "Enviar código de verificação",
        },
      },
      fields: {
        branchName: "Nome da filial",
        confirmPin: "Confirmar PIN",
        industry: "Setor",
        loginPin: "PIN de acesso",
        newPin: "Novo PIN",
        phone: "Telefone",
        shopName: "Nome da loja",
        supplierLeadTime: "Prazo do fornecedor",
      },
      industryOptions: [
        "Mercearia de conveniência",
        "Boutique ou loja de moda",
        "Tecidos, têxteis e materiais de costura",
        "Calçado, malas e artigos de couro",
        "Loja de cosméticos e beleza",
        "Loja de telemóveis e acessórios",
        "Eletrónica e aparelhos elétricos",
        "Supermercado ou minimercado",
        "Farmácia",
        "Alimentos embalados e produtos secos",
        "Loja de artigos para casa",
        "Materiais de construção e ferragens",
        "Peças para automóveis ou motas e lubrificantes",
        "Cereais, sementes e ração animal",
        "Depósito de bebidas",
        "Livraria e papelaria",
        "Móveis, madeira e contraplacado",
        "Loja de produtos diversos",
      ],
      placeholders: {
        branchName: "Filial principal",
        confirmPin: "Repita o PIN",
        leadTimeDays: "Por exemplo, 3",
        pin: "4 dígitos",
        selectIndustry: "Selecione o setor",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Criar conta em vez disso",
        enterCode: "Digite o código",
        enterCodeHint: "Use o código enviado para o seu telefone.",
        enterPin: "Digite o seu PIN",
        enterPinHint: "Use o PIN atribuído a esta conta.",
        noCodeYet: "Não recebeu o código?",
        resend: (count) => `Reenviar${count ? ` (${count})` : ""}`,
        secureSignIn: "Entrada segura",
        staffSetup: "Configuração do funcionário",
        verification: "Verificação",
        verifyAndContinue: "Verificar e continuar",
        pinCapturedReady: "PIN registado. Já pode entrar.",
        codeCapturedReady: "Código registado. Já pode continuar.",
        enterPinFallback: "Digite o seu PIN de 4 dígitos.",
        enterCodeFallback: "Digite o código de 4 dígitos.",
      },
      screen: {
        activateStaffAccess: "Ativar acesso do funcionário",
        addYourBranch: "Adicione a sua filial",
        branch: "Filial",
        branchSetup: "Configuração da filial",
        branchSetupHelper:
          "Adicione pelo menos uma filial para dar casa ao seu stock e vendas.",
        chooseYourShop: "Escolha a sua loja",
        chooseYourShopHelper:
          "Este número de funcionário está ligado a mais de uma loja. Escolha o espaço a abrir.",
        continue: "Continuar",
        createAccount: "Criar conta",
        defaultBranch: "Filial padrão",
        nameYourShop: "Dê nome à sua loja",
        recoverPinSubtitle: "Escolha um novo PIN de 4 dígitos para esta conta.",
        recoverPinTitle: "Redefinir PIN",
        setStaffPinSubtitle:
          "Crie um PIN de 4 dígitos para futuros acessos do funcionário.",
        setStaffPinTitle: "Configurar PIN do funcionário",
        shopSetup: "Configuração da loja",
        shopSetupHelper:
          "Defina o nome da loja, o PIN e o prazo habitual do fornecedor.",
        staffAccess: "Acesso do funcionário",
        signIn: "Entrar",
        supplierLeadTime: (days) => `Prazo do fornecedor: ${days} dias`,
      },
      status: {
        selectShopToContinue: "Escolha uma loja para continuar.",
        shopSelectedContinue: "Loja escolhida. Continue para a abrir.",
      },
      validation: {
        branchDuplicate: "Esta filial já foi adicionada.",
        branchRequired: "O nome da filial é obrigatório.",
        branchTooShort: "O nome da filial é muito curto.",
        confirmPinMismatch: "A confirmação do PIN não corresponde.",
        enterNewPin: "Digite um novo PIN de 4 dígitos.",
        invalidLeadTimeWhole: "Digite o prazo em dias inteiros.",
        invalidPhone: "Digite um número de telefone válido.",
        leadTimeOneOrMore: "Digite 1 dia ou mais.",
        leadTimeRequired: "Quantos dias costuma levar a entrega?",
        needIndustry: "Escolha o setor para continuar.",
        pinMustBe4Digits: "Defina um PIN de 4 dígitos.",
        shopNameRequired: "O nome da loja é obrigatório.",
        shopNameTooLong: "O nome da loja é muito longo.",
        addBranchToContinue: "Adicione o nome de uma filial para continuar.",
      },
      feedback: {
        chooseShopToContinue: "Escolha a sua loja para continuar.",
        codeSent: "Código enviado",
        completeRegistration: "Não foi possível concluir o cadastro.",
        continueFailed:
          "Não foi possível continuar. Verifique a ligação e tente novamente.",
        finishStaffSetup:
          "Não foi possível concluir a configuração do funcionário.",
        noAccountFound: "Não existe conta de loja para este número.",
        openShopFailed: "Não foi possível abrir essa loja.",
        phoneTaken:
          "Este número já está ligado a uma conta de loja. Tente entrar em vez disso.",
        resetPinFailed: "Não foi possível redefinir o PIN.",
        resendFailed: "Não foi possível reenviar o código.",
        reviewPinFields: "Corrija os campos de PIN destacados.",
        verifyCodeFailed: "Não foi possível verificar este código.",
      },
    },
  },
  sw: {
    common: {
      back: "Rudi",
      breadcrumb: "Njia ya ukurasa",
      close: "Funga",
      datePlaceholder: "dd/mm/yyyy",
      enterPaymentAmount: "Weka kiasi cha malipo",
      enterStockQuantity: "Weka kiasi cha stock",
      general: "Jumla",
      goToNextPage: "Nenda ukurasa unaofuata",
      goToPreviousPage: "Nenda ukurasa uliopita",
      language: "Lugha",
      more: "Zaidi",
      morePages: "Kurasa zaidi",
      next: "Ifuatayo",
      nextSlide: "Slaidi inayofuata",
      pagination: "Mpangilio wa kurasa",
      previous: "Iliyotangulia",
      previousSlide: "Slaidi iliyotangulia",
      qrCodeTitle: "Msimbo wa QR",
      step: (current, total) => `Hatua ya ${current} kati ya ${total}`,
      unknown: "Haijulikani",
      walkIn: "Mteja wa moja kwa moja",
    },
    auth: {
      panel: {
        badge: "Uingiaji wa Shopbook",
        titleLead: "Mtiririko mmoja salama.",
        titleTail: "Kila kifaa.",
        subtitle:
          "Ingia, tengeneza duka, rejesha PIN yako, au kamilisha usanidi wa mfanyakazi kwa mtiririko ule ule unaotumika kwenye app ya simu.",
        cards: {
          phone: { title: "Simu", label: "Funguo ya akaunti yako" },
          pin: { title: "PIN / OTP", label: "Kulingana na hali" },
          branches: { title: "Matawi", label: "Huundwa wakati wa usajili" },
          staff: { title: "Wafanyakazi", label: "Uchaguzi wa duka" },
        },
        footer: "Vipindi vinavyosaidiwa na seva · kifaa kimoja tu hai",
      },
      modeSwitch: {
        login: "Ingia",
        recover: "Rejesha",
        register: "Jisajili",
      },
      copy: {
        login: {
          eyebrow: "Karibu tena",
          title: "Ingia kwenye duka lako",
          helper:
            "Tumia nambari ya simu iliyounganishwa na akaunti yako ya Shopbook.",
          action: "Endelea",
        },
        recover: {
          eyebrow: "Rejesha ufikiaji",
          title: "Weka upya PIN ya kuingia",
          helper:
            "Tutakutumia msimbo wa mara moja kwenye nambari yako iliyosajiliwa.",
          action: "Tuma msimbo wa kurejesha",
        },
        register: {
          eyebrow: "Fungua akaunti",
          title: "Anza wasifu wa duka lako",
          helper:
            "Tutathibitisha nambari yako ya simu kabla ya kuunda nafasi ya kazi ya duka lako.",
          action: "Tuma msimbo wa uthibitisho",
        },
      },
      fields: {
        branchName: "Jina la tawi",
        confirmPin: "Thibitisha PIN",
        industry: "Sekta",
        loginPin: "PIN ya kuingia",
        newPin: "PIN mpya",
        phone: "Simu",
        shopName: "Jina la duka",
        supplierLeadTime: "Muda wa msambazaji",
      },
      industryOptions: [
        "Duka la bidhaa za matumizi ya kila siku",
        "Duka la nguo au mitindo",
        "Duka la vitambaa na vifaa vya ushonaji",
        "Duka la viatu, mifuko na bidhaa za ngozi",
        "Duka la vipodozi na urembo",
        "Duka la simu na vifaa vyake",
        "Duka la vifaa vya elektroniki na umeme",
        "Supermarket au duka dogo",
        "Duka la dawa",
        "Duka la vyakula vilivyofungwa na bidhaa kavu",
        "Duka la vifaa vya nyumbani",
        "Duka la vifaa vya ujenzi na hardware",
        "Duka la vipuri vya gari au pikipiki na vilainishi",
        "Duka la nafaka, mbegu na chakula cha mifugo",
        "Ghala la vinywaji",
        "Duka la vitabu na vifaa vya kuandikia",
        "Duka la samani, mbao na plywood",
        "Duka la bidhaa mchanganyiko",
      ],
      placeholders: {
        branchName: "Tawi Kuu",
        confirmPin: "Rudia PIN",
        leadTimeDays: "Mfano, 3",
        pin: "Nambari 4",
        selectIndustry: "Chagua sekta",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Fungua akaunti badala yake",
        enterCode: "Weka msimbo",
        enterCodeHint: "Tumia msimbo uliotumwa kwenye simu yako.",
        enterPin: "Weka PIN yako",
        enterPinHint: "Tumia PIN iliyotolewa kwa akaunti hii.",
        noCodeYet: "Hujapata msimbo?",
        resend: (count) => `Tuma tena${count ? ` (${count})` : ""}`,
        secureSignIn: "Kuingia salama",
        staffSetup: "Usanidi wa mfanyakazi",
        verification: "Uthibitisho",
        verifyAndContinue: "Thibitisha uendelee",
        pinCapturedReady: "PIN imepokelewa. Unaweza kuingia sasa.",
        codeCapturedReady: "Msimbo umepokelewa. Unaweza kuendelea.",
        enterPinFallback: "Weka PIN yako ya nambari 4.",
        enterCodeFallback: "Weka msimbo wa nambari 4.",
      },
      screen: {
        activateStaffAccess: "Washa ufikiaji wa mfanyakazi",
        addYourBranch: "Ongeza tawi lako",
        branch: "Tawi",
        branchSetup: "Usanidi wa tawi",
        branchSetupHelper:
          "Ongeza angalau tawi moja ili bidhaa na mauzo yako yawe na mahali pake.",
        chooseYourShop: "Chagua duka lako",
        chooseYourShopHelper:
          "Nambari hii ya mfanyakazi imeunganishwa na zaidi ya duka moja. Chagua eneo la kazi la kufungua.",
        continue: "Endelea",
        createAccount: "Fungua akaunti",
        defaultBranch: "Tawi la msingi",
        nameYourShop: "Taja duka lako",
        recoverPinSubtitle: "Chagua PIN mpya ya nambari 4 kwa akaunti hii.",
        recoverPinTitle: "Weka upya PIN",
        setStaffPinSubtitle:
          "Tengeneza PIN ya nambari 4 kwa kuingia kwa wafanyakazi siku zijazo.",
        setStaffPinTitle: "Weka PIN ya mfanyakazi",
        shopSetup: "Usanidi wa duka",
        shopSetupHelper:
          "Weka jina la duka, PIN ya kuingia, na muda wa kawaida wa msambazaji.",
        staffAccess: "Ufikiaji wa mfanyakazi",
        signIn: "Ingia",
        supplierLeadTime: (days) => `Muda wa msambazaji: siku ${days}`,
      },
      status: {
        selectShopToContinue: "Chagua duka ili kuendelea.",
        shopSelectedContinue: "Duka limechaguliwa. Endelea kulifungua.",
      },
      validation: {
        branchDuplicate: "Tawi hili tayari limeongezwa.",
        branchRequired: "Jina la tawi linahitajika.",
        branchTooShort: "Jina la tawi ni fupi sana.",
        confirmPinMismatch: "Uthibitisho wa PIN haulingani.",
        enterNewPin: "Weka PIN mpya ya kuingia ya nambari 4.",
        invalidLeadTimeWhole: "Weka muda wa usambazaji kwa siku kamili.",
        invalidPhone: "Weka nambari sahihi ya simu.",
        leadTimeOneOrMore: "Weka siku 1 au zaidi.",
        leadTimeRequired: "Kwa kawaida usambazaji huchukua siku ngapi?",
        needIndustry: "Chagua sekta ili kuendelea.",
        pinMustBe4Digits: "Weka PIN ya nambari 4.",
        shopNameRequired: "Jina la duka linahitajika.",
        shopNameTooLong: "Jina la duka ni refu sana.",
        addBranchToContinue: "Ongeza jina la tawi ili kuendelea.",
      },
      feedback: {
        chooseShopToContinue: "Chagua duka lako ili kuendelea.",
        codeSent: "Msimbo umetumwa",
        completeRegistration: "Imeshindikana kukamilisha usajili.",
        continueFailed:
          "Imeshindikana kuendelea. Angalia muunganisho wako ujaribu tena.",
        finishStaffSetup: "Imeshindikana kukamilisha usanidi wa mfanyakazi.",
        noAccountFound: "Hakuna akaunti ya duka kwa nambari hii.",
        openShopFailed: "Imeshindikana kufungua duka hilo.",
        phoneTaken:
          "Nambari hii tayari imeunganishwa na akaunti ya duka. Jaribu kuingia badala yake.",
        resetPinFailed: "Imeshindikana kuweka upya PIN.",
        resendFailed: "Imeshindikana kutuma msimbo tena.",
        reviewPinFields: "Tafadhali rekebisha sehemu za PIN zilizoangaziwa.",
        verifyCodeFailed: "Imeshindikana kuthibitisha msimbo huu.",
      },
    },
  },
  yo: {
    common: {
      back: "Pada",
      breadcrumb: "Ọna ìtọ́kasí",
      close: "Pa",
      datePlaceholder: "ọjọ/oṣu/ọdún",
      enterPaymentAmount: "Tẹ iye isanwo sii",
      enterStockQuantity: "Tẹ iye stock sii",
      general: "Gbogbogbo",
      goToNextPage: "Lọ si oju-iwe to tẹle",
      goToPreviousPage: "Lọ si oju-iwe to ṣaaju",
      language: "Ede",
      more: "Siwaju sii",
      morePages: "Awọn oju-iwe diẹ sii",
      next: "Tẹle",
      nextSlide: "Silaidi to tẹle",
      pagination: "Pajinesọnu",
      previous: "Ṣaaju",
      previousSlide: "Silaidi to ṣaaju",
      qrCodeTitle: "Koodu QR",
      step: (current, total) => `Igbesẹ ${current} ninu ${total}`,
      unknown: "A ko mọ",
      walkIn: "Oníbàárà tó wọlé taara",
    },
    auth: {
      panel: {
        badge: "Wiwọle Shopbook",
        titleLead: "Ona aabo kan ṣoṣo.",
        titleTail: "Gbogbo ẹrọ.",
        subtitle:
          "Wole, da ile itaja sile, gba PIN re pada, tabi pari eto osise pelu ọna kanna ti app alagbeka n lo.",
        cards: {
          phone: { title: "Foonu", label: "Bọtini akaunti re" },
          pin: { title: "PIN / OTP", label: "Da lori ipo" },
          branches: { title: "Awon eka", label: "A da sile nigba iforukọsilẹ" },
          staff: { title: "Awon osise", label: "Yiyan ile itaja" },
        },
        footer: "Awọn session to wa lori server · ẹrọ kan ṣoṣo lo n ṣiṣẹ",
      },
      modeSwitch: {
        login: "Wole",
        recover: "Gba pada",
        register: "Forukọsilẹ",
      },
      copy: {
        login: {
          eyebrow: "Wiwọle",
          title: "Wole si ile itaja re",
          helper: "Lo nomba foonu ti a so mo akaunti Shopbook re.",
          action: "Tesiwaju",
        },
        recover: {
          eyebrow: "Gba wiwọle pada",
          title: "Tun PIN wiwọle re se",
          helper: "A o fi koodu igba kan ranse si nomba foonu re.",
          action: "Fi koodu gbigba pada ranse",
        },
        register: {
          eyebrow: "Da akaunti sile",
          title: "Bẹrẹ profaili ile itaja re",
          helper: "A o jẹrisi nomba foonu re ki a to da aaye iṣẹ re sile.",
          action: "Fi koodu idaniloju ranse",
        },
      },
      fields: {
        branchName: "Oruko eka",
        confirmPin: "Jẹrisi PIN",
        industry: "Iru ise",
        loginPin: "PIN wiwọle",
        newPin: "PIN tuntun",
        phone: "Foonu",
        shopName: "Oruko ile itaja",
        supplierLeadTime: "Akoko ifijiṣẹ supplier",
      },
      industryOptions: [
        "Ile itaja ounje kekere ati nkan lilo ojoojumọ",
        "Boutique tabi ile itaja aṣọ",
        "Ile itaja aso, textile ati ohun elo iranṣọ",
        "Ile itaja bata, apo ati ohun elo alawọ",
        "Ile itaja ohun ikunra ati ẹwa",
        "Ile itaja foonu ati awọn ẹya ẹrọ",
        "Ile itaja ẹrọ itanna ati ohun elo ina",
        "Ile itaja nla tabi mini-mart",
        "Ile itaja oogun",
        "Ile itaja ounje gbigbẹ ati eyi ti a ko sinu apo",
        "Ile itaja ohun ile",
        "Ile itaja ohun elo ile ati hardware",
        "Ile itaja ẹya ọkọ tabi alupupu ati lubricant",
        "Ile itaja ọkà, irugbin ati ounje ẹranko",
        "Depot ohun mimu",
        "Ile itaja iwe ati ohun elo kikọ",
        "Ile itaja aga, igi ati plywood",
        "Ile itaja oriṣiriṣi ọja",
      ],
      placeholders: {
        branchName: "Eka Pataki",
        confirmPin: "Tun PIN te",
        leadTimeDays: "Fun apeere, 3",
        pin: "Nomba 4",
        selectIndustry: "Yan iru ise",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Da akaunti sile dipo bee",
        enterCode: "Te koodu",
        enterCodeHint: "Lo koodu igba kan ti a ran si foonu re.",
        enterPin: "Te PIN re",
        enterPinHint: "Lo PIN ti a fi si akaunti yii.",
        noCodeYet: "O ko ri koodu naa?",
        resend: (count) => `Tun ranse${count ? ` (${count})` : ""}`,
        secureSignIn: "Wiwọle to ni aabo",
        staffSetup: "Eto osise",
        verification: "Ijẹrisi",
        verifyAndContinue: "Jẹrisi ki o tesiwaju",
        pinCapturedReady: "A ti gba PIN. O le wole bayii.",
        codeCapturedReady: "A ti gba koodu. O le tesiwaju.",
        enterPinFallback: "Te PIN re oninomba merin.",
        enterCodeFallback: "Te koodu oninomba merin.",
      },
      screen: {
        activateStaffAccess: "Mu wiwọle osise ṣiṣẹ",
        addYourBranch: "Fi eka re kun",
        branch: "Eka",
        branchSetup: "Eto eka",
        branchSetupHelper:
          "Fi o kere ju eka kan kun ki ọja ati tita re le ni ile.",
        chooseYourShop: "Yan ile itaja re",
        chooseYourShopHelper:
          "Nomba osise yii so mo ile itaja ju ọkan lo. Yan aaye iṣẹ.",
        continue: "Tesiwaju",
        createAccount: "Da akaunti sile",
        defaultBranch: "Eka aiyipada",
        nameYourShop: "So ile itaja re loruko",
        recoverPinSubtitle: "Yan PIN tuntun to ni nomba merin fun akaunti yii.",
        recoverPinTitle: "Tun PIN re se",
        setStaffPinSubtitle:
          "Da PIN oninomba merin sile fun wiwọle osise ni ojo iwaju.",
        setStaffPinTitle: "Seto PIN osise",
        shopSetup: "Eto ile itaja",
        shopSetupHelper:
          "Seto oruko ile itaja, PIN wiwọle, ati akoko ifijiṣẹ supplier.",
        staffAccess: "Wiwọle osise",
        signIn: "Wole",
        supplierLeadTime: (days) => `Ifijiṣẹ laarin ọjọ ${days}`,
      },
      status: {
        selectShopToContinue: "Yan ile itaja lati tesiwaju.",
        shopSelectedContinue: "A ti yan ile itaja. Tesiwaju lati ṣi i.",
      },
      validation: {
        branchDuplicate: "A ti fi eka yii kun tẹlẹ.",
        branchRequired: "Oruko eka je dandan.",
        branchTooShort: "Oruko eka kuru ju.",
        confirmPinMismatch: "Idaniloju PIN ko ba ara won mu.",
        enterNewPin: "Te PIN tuntun to ni nomba merin.",
        invalidLeadTimeWhole: "Te iye ojo ifijiṣẹ gege bi nomba pipe.",
        invalidPhone: "Te nomba foonu to pe.",
        leadTimeOneOrMore: "Te 1 tabi ju bee lo.",
        leadTimeRequired: "Ojo melo ni ifijiṣẹ maa n gba?",
        needIndustry: "Yan iru ile itaja re lati tesiwaju.",
        pinMustBe4Digits: "Ṣeto PIN oninomba merin.",
        shopNameRequired: "Oruko ile itaja je dandan.",
        shopNameTooLong: "Oruko ile itaja gun ju.",
        addBranchToContinue: "Fi oruko eka kun lati tesiwaju.",
      },
      feedback: {
        chooseShopToContinue: "Yan ile itaja re lati tesiwaju.",
        codeSent: "A ti fi koodu ranse",
        completeRegistration: "A ko le pari iforukọsilẹ.",
        continueFailed: "A ko le tesiwaju. Ṣayẹwo asopọ re ki o tun gbiyanju.",
        finishStaffSetup: "A ko le pari eto osise.",
        noAccountFound: "Ko si akaunti ile itaja fun nomba yii.",
        openShopFailed: "A ko le ṣi ile itaja naa.",
        phoneTaken:
          "Nomba foonu yii ni akaunti ile itaja tẹlẹ. Gbiyanju wiwọle dipo bee.",
        resetPinFailed: "A ko le tun PIN se.",
        resendFailed: "A ko le tun fi koodu ranse.",
        reviewPinFields: "Jọwọ ṣatunṣe awọn aaye PIN ti a fihan.",
        verifyCodeFailed: "A ko le jẹrisi koodu yii.",
      },
    },
  },
  zu: {
    common: {
      back: "Emuva",
      breadcrumb: "Umzila wokuzulazula",
      close: "Vala",
      datePlaceholder: "usuku/inyanga/unyaka",
      enterPaymentAmount: "Faka inani lenkokhelo",
      enterStockQuantity: "Faka inani le-stock",
      general: "Okuvamile",
      goToNextPage: "Iya ekhasini elilandelayo",
      goToPreviousPage: "Iya ekhasini elidlule",
      language: "Ulimi",
      more: "Okuningi",
      morePages: "Amakhasi amaningi",
      next: "Okulandelayo",
      nextSlide: "Isilayidi esilandelayo",
      pagination: "Ukuhlela amakhasi",
      previous: "Esedlule",
      previousSlide: "Isilayidi esedlule",
      qrCodeTitle: "Ikhodi ye-QR",
      step: (current, total) => `Isinyathelo ${current} kweziyi-${total}`,
      unknown: "Akwaziwa",
      walkIn: "Ikhasimende elingene ngqo",
    },
    auth: {
      panel: {
        badge: "Ukungena kwe-Shopbook",
        titleLead: "Ukugeleza okukodwa okuphephile.",
        titleTail: "Wonke amadivayisi.",
        subtitle:
          "Ngena, sungula isitolo, buyisa i-PIN yakho, noma uqede ukusetha abasebenzi ngendlela efanayo esetshenziswa kuhlelo lweselula.",
        cards: {
          phone: { title: "Ucingo", label: "Ukhiye we-akhawunti yakho" },
          pin: { title: "PIN / OTP", label: "Ngokwesimo" },
          branches: {
            title: "Amagatsha",
            label: "Adalwa ngesikhathi sokubhalisa",
          },
          staff: { title: "Abasebenzi", label: "Ukukhetha isitolo" },
        },
        footer:
          "Ama-session asekelwe yiseva · idivayisi eyodwa kuphela esebenzayo",
      },
      modeSwitch: {
        login: "Ngena",
        recover: "Buyisa",
        register: "Bhalisa",
      },
      copy: {
        login: {
          eyebrow: "Siyakwamukela futhi",
          title: "Ngena esitolo sakho",
          helper:
            "Sebenzisa inombolo yocingo exhunywe ku-akhawunti yakho ye-Shopbook.",
          action: "Qhubeka",
        },
        recover: {
          eyebrow: "Buyisa ukufinyelela",
          title: "Setha kabusha i-PIN yokungena",
          helper:
            "Sizothumela ikhodi yesikhathi esisodwa enombolweni yakho ebhalisiwe.",
          action: "Thumela ikhodi yokubuyisa",
        },
        register: {
          eyebrow: "Dala i-akhawunti",
          title: "Qala iphrofayela yesitolo sakho",
          helper:
            "Sizokuqinisekisa inombolo yakho ngaphambi kokudala indawo yokusebenza yesitolo sakho.",
          action: "Thumela ikhodi yokuqinisekisa",
        },
      },
      fields: {
        branchName: "Igama legatsha",
        confirmPin: "Qinisekisa i-PIN",
        industry: "Umkhakha",
        loginPin: "I-PIN yokungena",
        newPin: "I-PIN entsha",
        phone: "Ucingo",
        shopName: "Igama lesitolo",
        supplierLeadTime: "Isikhathi somhlinzeki",
      },
      industryOptions: [
        "Isitolo sezimpahla zansuku zonke",
        "Isitolo sezingubo noma imfashini",
        "Isitolo sezindwangu nezinto zokuthunga",
        "Isitolo sezicathulo, izikhwama nemikhiqizo yesikhumba",
        "Isitolo sezimonyo nobuhle",
        "Isitolo samafoni nezinsiza zawo",
        "Isitolo sama-elekthronikhi nezinto zikagesi",
        "Isuphamakethe noma isitolo esincane",
        "Ikhemisi",
        "Isitolo sokudla okupakishiwe nemikhiqizo eyomile",
        "Isitolo sezinto zasekhaya",
        "Isitolo sezinto zokwakha nehardware",
        "Isitolo sezingxenye zezimoto noma izithuthuthu namafutha",
        "Isitolo sokusanhlamvu, imbewu nokudla kwezilwane",
        "Indawo yokugcina iziphuzo",
        "Isitolo sezincwadi nezinto zokubhala",
        "Isitolo sefenisha, izingodo neplywood",
        "Isitolo sezimpahla ezixubile",
      ],
      placeholders: {
        branchName: "Igatsha Elikhulu",
        confirmPin: "Phinda i-PIN",
        leadTimeDays: "Isibonelo, 3",
        pin: "Amadijithi angu-4",
        selectIndustry: "Khetha umkhakha",
        shopName: "Bluegate Stores",
      },
      verification: {
        createAccountInstead: "Dala i-akhawunti esikhundleni salokho",
        enterCode: "Faka ikhodi",
        enterCodeHint: "Sebenzisa ikhodi ethunyelwe ocingweni lwakho.",
        enterPin: "Faka i-PIN yakho",
        enterPinHint: "Sebenzisa i-PIN enikezwe le akhawunti.",
        noCodeYet: "Awuyitholanga ikhodi?",
        resend: (count) => `Thumela futhi${count ? ` (${count})` : ""}`,
        secureSignIn: "Ukungena okuphephile",
        staffSetup: "Ukusetha abasebenzi",
        verification: "Ukuqinisekisa",
        verifyAndContinue: "Qinisekisa uqhubeke",
        pinCapturedReady: "I-PIN ifakiwe. Ungangena manje.",
        codeCapturedReady: "Ikhodi ifakiwe. Ungaqhubeka.",
        enterPinFallback: "Faka i-PIN yakho enamadijithi angu-4.",
        enterCodeFallback: "Faka ikhodi enamadijithi angu-4.",
      },
      screen: {
        activateStaffAccess: "Vula ukufinyelela kwabasebenzi",
        addYourBranch: "Engeza igatsha lakho",
        branch: "Igatsha",
        branchSetup: "Ukusetha igatsha",
        branchSetupHelper:
          "Engeza okungenani igatsha elilodwa ukuze isitoko nokuthengisa kwakho kube nekhaya.",
        chooseYourShop: "Khetha isitolo sakho",
        chooseYourShopHelper:
          "Le nombolo yabasebenzi ixhunywe ezitolo ezingaphezu kwesisodwa. Khetha indawo ofuna ukuyivula.",
        continue: "Qhubeka",
        createAccount: "Dala i-akhawunti",
        defaultBranch: "Igatsha eliyinhloko",
        nameYourShop: "Qamba isitolo sakho",
        recoverPinSubtitle:
          "Khetha i-PIN entsha enamadijithi angu-4 yale akhawunti.",
        recoverPinTitle: "Setha kabusha i-PIN",
        setStaffPinSubtitle:
          "Dala i-PIN enamadijithi angu-4 yokungena kwabasebenzi esikhathini esizayo.",
        setStaffPinTitle: "Setha i-PIN yabasebenzi",
        shopSetup: "Ukusetha isitolo",
        shopSetupHelper:
          "Setha igama lesitolo, i-PIN yokungena, nesikhathi esijwayelekile somhlinzeki.",
        staffAccess: "Ukufinyelela kwabasebenzi",
        signIn: "Ngena",
        supplierLeadTime: (days) =>
          `Isikhathi somhlinzeki: izinsuku ezingu-${days}`,
      },
      status: {
        selectShopToContinue: "Khetha isitolo ukuze uqhubeke.",
        shopSelectedContinue: "Isitolo sikhethiwe. Qhubeka usivule.",
      },
      validation: {
        branchDuplicate: "Leli gatsha selivele lingeziwe.",
        branchRequired: "Igama legatsha liyadingeka.",
        branchTooShort: "Igama legatsha lifushane kakhulu.",
        confirmPinMismatch: "Ukuqinisekiswa kwe-PIN akuhambisani.",
        enterNewPin: "Faka i-PIN entsha yokungena enamadijithi angu-4.",
        invalidLeadTimeWhole:
          "Faka isikhathi sokulethwa ngezinsuku eziphelele.",
        invalidPhone: "Faka inombolo yocingo efanele.",
        leadTimeOneOrMore: "Faka usuku olu-1 noma ngaphezulu.",
        leadTimeRequired: "Kuvamise ukuthatha izinsuku ezingaki ukulethwa?",
        needIndustry: "Khetha umkhakha ukuze uqhubeke.",
        pinMustBe4Digits: "Setha i-PIN enamadijithi angu-4.",
        shopNameRequired: "Igama lesitolo liyadingeka.",
        shopNameTooLong: "Igama lesitolo lide kakhulu.",
        addBranchToContinue: "Engeza igama legatsha ukuze uqhubeke.",
      },
      feedback: {
        chooseShopToContinue: "Khetha isitolo sakho ukuze uqhubeke.",
        codeSent: "Ikhodi ithunyelwe",
        completeRegistration: "Kuhlulekile ukuqedela ukubhalisa.",
        continueFailed:
          "Kuhlulekile ukuqhubeka. Hlola uxhumano lwakho bese uzama futhi.",
        finishStaffSetup: "Kuhlulekile ukuqedela ukusetha abasebenzi.",
        noAccountFound: "Ayikho i-akhawunti yesitolo yale nombolo.",
        openShopFailed: "Kuhlulekile ukuvula leso sitolo.",
        phoneTaken:
          "Le nombolo isivele ixhunywe kwi-akhawunti yesitolo. Zama ukungena esikhundleni salokho.",
        resetPinFailed: "Kuhlulekile ukusetha kabusha i-PIN.",
        resendFailed: "Kuhlulekile ukuthumela ikhodi futhi.",
        reviewPinFields: "Sicela ulungise izinkambu ze-PIN ezikhonjisiwe.",
        verifyCodeFailed: "Kuhlulekile ukuqinisekisa le khodi.",
      },
    },
  },
};

const settingsTranslations = {
  ar: {
    account: "الحساب",
    activeBranch: "الفرع النشط",
    authModeIndependent: "مستقل",
    authModeSession: "جلسة",
    authModeStaffSetup: "إعداد الموظف",
    branchLocation: "موقع الفرع",
    branchSecurity: "أمان الفرع",
    cancel: "إلغاء",
    current: "الحالي",
    deleteAccount: "حذف الحساب",
    devicesAndSync: "الأجهزة والمزامنة",
    language: "اللغة",
    noActiveBranch: "لا يوجد فرع نشط محدد",
    platformAndroid: "أندرويد",
    platformIos: "آيفون",
    platformWeb: "الويب",
    permissionAssistant: "المساعد",
    permissionSettings: "الإعدادات",
    preferences: "التفضيلات",
    reports: "التقارير",
    profile: "الملف",
    saveProfile: "حفظ الملف",
    saving: "جارٍ الحفظ...",
    sectionDescription: "إدارة الملف، أمان الفرع، الجلسات، والوصول للحساب.",
    sectionTitle: "الإعدادات",
    securityDescription:
      "تحكم فيما إذا كان يجب أن يكون الموظفون قريبين من فرعهم المخصص لمواصلة العمل.",
    securityTitle: "أمان تسجيل دخول الموظفين",
    shopName: "اسم المتجر",
    shopProfileDescription: "حدّث اسم المتجر الذي يراه فريقك في مساحة العمل.",
    shopProfileTitle: "ملف المتجر",
    signOut: "تسجيل الخروج من هذا المتصفح",
    staffRestrictionDescriptionOff:
      "يمكن للموظفين العمل بعد تسجيل الدخول دون فرض موقع الفرع.",
    staffRestrictionDescriptionOn:
      "يجب أن يكون الموظفون قريبين من هذا الفرع لمواصلة استخدام المتجر.",
    staffRestrictionHint: "احفظ موقع الفرع قبل تفعيل التقييد.",
    staffRestrictionOff: "التقييد متوقف",
    staffRestrictionOn: "التقييد مفعل",
    syncFailed: "فشل",
    syncPendingShort: "معلق",
    syncQueued: "في الانتظار",
    syncSynced: "تمت المزامنة",
    locationSourceAdmin: "الإدارة",
    locationSourceDevice: "الجهاز",
    locationSourceGps: "نظام GPS",
    operationDebt: "دين",
    operationDebtPayment: "دفعة دين",
    operationProduct: "منتج",
    operationSale: "بيع",
    operationStock: "مخزون",
    updateLocation: "استخدام موقع المتجر الحالي",
    updateLocationRefresh: "تحديث موقع الفرع",
    queuedRecordUnavailable: "لم يعد هذا السجل المعلق متاحًا.",
    queuedDeleteWait:
      "انتظر حتى تنتهي محاولة المزامنة الحالية قبل حذف هذا السجل.",
    queuedDeleteStandaloneOnly:
      "يمكن إزالة تغييرات المخزون المستقلة فقط من قائمة الانتظار.",
    queueProductChangeBlocked:
      "تظل تغييرات المنتج في الانتظار حتى تتم مزامنتها أو يستبدلها تعديل أحدث.",
    queueProductArchivePending: "أرشفة المنتج معلقة",
    queueProductUpdatePending: "تحديث المنتج معلق",
    queueStockDeleteWait:
      "انتظر حتى تنتهي محاولة المزامنة الحالية قبل إزالة هذا التغيير في المخزون.",
    queueStockSaleLinked:
      "حركة المخزون هذه مرتبطة بعملية بيع ولا يمكن إزالتها هنا.",
    queueStockAdditionPending: "إضافة المخزون معلقة",
    queueStockReductionPending: "خفض المخزون معلق",
    queueInventoryItem: "عنصر مخزون",
    queueSaleDeleteBlocked:
      "تؤثر المبيعات على المخزون والسجل، لذلك لا يمكن إزالتها من شاشة القائمة هذه.",
    queueSaleItems: (count) => `${count} عنصر في عملية البيع`,
    queuePendingSale: "عملية بيع معلقة",
    queueDebtDeleteBlocked:
      "تبقى سجلات الدين في الانتظار حتى تتم مزامنتها أو تحديثها بتغيير أحدث.",
    queueCustomerCredit: "رصيد عميل",
    queuePendingDebtRecord: "سجل دين معلق",
    queueDebtPaymentDeleteBlocked:
      "لا يمكن إزالة دفعات الديون من شاشة قائمة المزامنة.",
    queueCustomerPayment: "دفعة عميل",
    queuePendingDebtPayment: "دفعة دين معلقة",
    queueGenericDeleteBlocked: "لا يمكن إزالة هذا السجل المعلق بأمان من هنا.",
    queueGenericRecord: "سجل مزامنة معلق",
    requestPrepareFailed: "تعذر تجهيز الطلب",
    requestFailedGeneric: "فشل الطلب",
    networkUnavailable: "تعذر الاتصال. تحقق من أن شبكة Wi-Fi أو بيانات الهاتف تعمل، ثم حاول مرة أخرى.",
    csrfTokenUnavailable: "رمز الأمان غير متاح",
    indexedDbUnavailable: "التخزين غير المتصل غير متاح في هذا المتصفح",
    indexedDbOpenFailed: "تعذر فتح التخزين غير المتصل",
    indexedDbRequestFailed: "فشل طلب التخزين غير المتصل",
    indexedDbTransactionFailed: "فشلت معاملة التخزين غير المتصل",
    indexedDbTransactionAborted: "تم إلغاء معاملة التخزين غير المتصل",
    syncRecoveredInterrupted: "تمت استعادة دفعة مزامنة متوقفة",
    syncWaitInitialStockBeforeSale: "بانتظار مزامنة المخزون الأولى قبل البيع",
    syncWaitProductBeforeSale: "بانتظار مزامنة المنتج قبل البيع",
    syncWaitProductBeforeStockMovement:
      "بانتظار مزامنة المنتج قبل حركة المخزون",
    syncWaitSaleBeforeStockMovement:
      "بانتظار مزامنة عملية البيع قبل حركة المخزون",
  },
  fr: {
    account: "Compte",
    activeBranch: "Succursale active",
    authModeIndependent: "Indépendant",
    authModeSession: "Session",
    authModeStaffSetup: "Configuration du personnel",
    branchLocation: "Emplacement de la succursale",
    branchSecurity: "Sécurité de la succursale",
    cancel: "Annuler",
    current: "Actuel",
    deleteAccount: "Supprimer le compte",
    devicesAndSync: "Appareils et synchronisation",
    language: "Langue",
    noActiveBranch: "Aucune succursale active sélectionnée",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Web",
    permissionAssistant: "Assistant",
    permissionSettings: "Paramètres",
    preferences: "Préférences",
    reports: "Rapports",
    profile: "Profil",
    saveProfile: "Enregistrer le profil",
    saving: "Enregistrement...",
    sectionDescription:
      "Gérez le profil, la sécurité de la succursale, les sessions et l’accès au compte.",
    sectionTitle: "Paramètres",
    securityDescription:
      "Contrôlez si le personnel doit être physiquement proche de sa succursale pour continuer à travailler.",
    securityTitle: "Sécurité de connexion du personnel",
    shopName: "Nom de la boutique",
    shopProfileDescription:
      "Mettez à jour le nom de la boutique visible par votre équipe.",
    shopProfileTitle: "Profil de la boutique",
    signOut: "Se déconnecter de ce navigateur",
    staffRestrictionDescriptionOff:
      "Le personnel peut travailler après connexion sans contrôle de zone.",
    staffRestrictionDescriptionOn:
      "Le personnel doit être proche de cette succursale pour continuer.",
    staffRestrictionHint:
      "Enregistrez d’abord l’emplacement avant d’activer la restriction.",
    staffRestrictionOff: "Restriction désactivée",
    staffRestrictionOn: "Restriction activée",
    syncFailed: "Échec",
    syncPendingShort: "En attente",
    syncQueued: "En file",
    syncSynced: "Synchronisé",
    locationSourceAdmin: "admin",
    locationSourceDevice: "appareil",
    locationSourceGps: "GPS",
    operationDebt: "Dette",
    operationDebtPayment: "Paiement de dette",
    operationProduct: "Produit",
    operationSale: "Vente",
    operationStock: "Stock",
    updateLocation: "Utiliser l’emplacement actuel de la boutique",
    updateLocationRefresh: "Actualiser l’emplacement",
    queuedRecordUnavailable:
      "Cet enregistrement en file n’est plus disponible.",
    queuedDeleteWait:
      "Attendez la fin de la tentative de synchronisation en cours avant de supprimer cet enregistrement.",
    queuedDeleteStandaloneOnly:
      "Seuls les changements de stock autonomes peuvent être retirés de la file.",
    queueProductChangeBlocked:
      "Les modifications produit restent en file jusqu’à leur synchronisation ou leur remplacement par une version plus récente.",
    queueProductArchivePending: "Archivage du produit en attente",
    queueProductUpdatePending: "Mise à jour du produit en attente",
    queueStockDeleteWait:
      "Attendez la fin de la tentative de synchronisation en cours avant de retirer ce changement de stock.",
    queueStockSaleLinked:
      "Ce mouvement de stock appartient à une vente et ne peut pas être retiré ici.",
    queueStockAdditionPending: "Ajout de stock en attente",
    queueStockReductionPending: "Réduction de stock en attente",
    queueInventoryItem: "Article de stock",
    queueSaleDeleteBlocked:
      "Les ventes affectent le stock et l’historique, elles ne peuvent donc pas être supprimées depuis cette file.",
    queueSaleItems: (count) =>
      `${count} article${count === 1 ? "" : "s"} dans la vente`,
    queuePendingSale: "Vente en attente",
    queueDebtDeleteBlocked:
      "Les enregistrements de dette restent en file jusqu’à leur synchronisation ou une mise à jour plus récente.",
    queueCustomerCredit: "Crédit client",
    queuePendingDebtRecord: "Enregistrement de dette en attente",
    queueDebtPaymentDeleteBlocked:
      "Les paiements de dette ne peuvent pas être supprimés depuis la file de synchronisation.",
    queueCustomerPayment: "Paiement client",
    queuePendingDebtPayment: "Paiement de dette en attente",
    queueGenericDeleteBlocked:
      "Cet enregistrement en file ne peut pas être retiré en toute sécurité ici.",
    queueGenericRecord: "Enregistrement de synchronisation en file",
    requestPrepareFailed: "Impossible de préparer la requête",
    requestFailedGeneric: "Échec de la requête",
    networkUnavailable: "Connexion impossible. Vérifiez le Wi-Fi ou les données mobiles, puis réessayez.",
    csrfTokenUnavailable: "Jeton de sécurité indisponible",
    indexedDbUnavailable:
      "Le stockage hors ligne n’est pas disponible dans ce navigateur",
    indexedDbOpenFailed: "Impossible d’ouvrir le stockage hors ligne",
    indexedDbRequestFailed: "La requête de stockage hors ligne a échoué",
    indexedDbTransactionFailed:
      "La transaction de stockage hors ligne a échoué",
    indexedDbTransactionAborted:
      "La transaction de stockage hors ligne a été interrompue",
    syncRecoveredInterrupted: "Lot de synchronisation interrompu récupéré",
    syncWaitInitialStockBeforeSale:
      "En attente de la première synchronisation du stock avant la vente",
    syncWaitProductBeforeSale:
      "En attente de la synchronisation du produit avant la vente",
    syncWaitProductBeforeStockMovement:
      "En attente de la synchronisation du produit avant le mouvement de stock",
    syncWaitSaleBeforeStockMovement:
      "En attente de la synchronisation de la vente avant le mouvement de stock",
  },
  ha: {
    account: "Asusu",
    activeBranch: "Reshen aiki",
    authModeIndependent: "Mai zaman kansa",
    authModeSession: "Zaman shiga",
    authModeStaffSetup: "Saitin ma'aikaci",
    branchLocation: "Wurin reshe",
    branchSecurity: "Tsaron reshe",
    cancel: "Soke",
    current: "Na yanzu",
    deleteAccount: "Goge asusu",
    devicesAndSync: "Na'urori da sync",
    language: "Harshe",
    noActiveBranch: "Ba a zabi reshe mai aiki ba",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Yanar gizo",
    permissionAssistant: "Mataimaki",
    permissionSettings: "Saituna",
    preferences: "Zabuka",
    reports: "Rahotanni",
    profile: "Bayanan shago",
    saveProfile: "Ajiye bayanai",
    saving: "Ana ajiyewa...",
    sectionDescription: "Sarrafa bayanai, tsaron reshe, zaman shiga, da asusu.",
    sectionTitle: "Saituna",
    securityDescription:
      "Sarrafa ko ma'aikata dole su kasance kusa da reshensu kafin su ci gaba da aiki.",
    securityTitle: "Tsaron shiga ma'aikata",
    shopName: "Sunan shago",
    shopProfileDescription: "Sabunta sunan shagon da tawagarku ke gani.",
    shopProfileTitle: "Bayanan shago",
    signOut: "Fita daga wannan browser",
    staffRestrictionDescriptionOff:
      "Ma'aikata za su iya aiki bayan shiga ba tare da tilasta wurin reshe ba.",
    staffRestrictionDescriptionOn:
      "Ma'aikata dole su kasance kusa da wannan reshen.",
    staffRestrictionHint: "Ajiye wurin reshe kafin kunna takura.",
    staffRestrictionOff: "Takura a kashe",
    staffRestrictionOn: "Takura a kunne",
    syncFailed: "Ya gaza",
    syncPendingShort: "Ana jira",
    syncQueued: "A cikin layi",
    syncSynced: "An sync",
    locationSourceAdmin: "admin",
    locationSourceDevice: "na'ura",
    locationSourceGps: "GPS",
    operationDebt: "Bashi",
    operationDebtPayment: "Biyan bashi",
    operationProduct: "Samfuri",
    operationSale: "Sayarwa",
    operationStock: "Kaya",
    updateLocation: "Yi amfani da wurin shagon yanzu",
    updateLocationRefresh: "Sabunta wurin reshe",
    queuedRecordUnavailable: "Wannan rikodin da ke layi ba ya nan kuma.",
    queuedDeleteWait:
      "Jira yunƙurin sync na yanzu ya ƙare kafin ka goge wannan rikodi.",
    queuedDeleteStandaloneOnly:
      "Canjin kaya mai zaman kansa kaɗai za a iya cirewa daga layi.",
    queueProductChangeBlocked:
      "Canjin samfuri zai ci gaba da zama a layi har sai an sync ko sabon gyara ya maye gurbinsa.",
    queueProductArchivePending: "Ajiye samfuri yana jira",
    queueProductUpdatePending: "Sabunta samfuri yana jira",
    queueStockDeleteWait:
      "Jira yunƙurin sync na yanzu ya ƙare kafin ka cire wannan canjin kaya.",
    queueStockSaleLinked:
      "Wannan motsin kaya yana da alaƙa da sayarwa kuma ba za a cire shi a nan ba.",
    queueStockAdditionPending: "Ƙarin kaya yana jira",
    queueStockReductionPending: "Rage kaya yana jira",
    queueInventoryItem: "Abun kaya",
    queueSaleDeleteBlocked:
      "Sayarwa na shafar kaya da tarihi, don haka ba za a iya cire ta daga wannan allon layi ba.",
    queueSaleItems: (count) => `abu ${count} a cikin sayarwar`,
    queuePendingSale: "Sayarwa na jira",
    queueDebtDeleteBlocked:
      "Rikodin bashi zai ci gaba da zama a layi har sai an sync ko sabon gyara ya sabunta shi.",
    queueCustomerCredit: "Bashin kwastoma",
    queuePendingDebtRecord: "Rikodin bashi na jira",
    queueDebtPaymentDeleteBlocked:
      "Ba za a iya cire biyan bashi daga allon layin sync ba.",
    queueCustomerPayment: "Biyan kwastoma",
    queuePendingDebtPayment: "Biyan bashi na jira",
    queueGenericDeleteBlocked:
      "Ba za a iya cire wannan rikodin da ke layi cikin aminci a nan ba.",
    queueGenericRecord: "Rikodin sync da ke layi",
    requestPrepareFailed: "Ba a iya shirya buƙatar ba",
    requestFailedGeneric: "Buƙatar ta gaza",
    networkUnavailable: "Ba a iya haɗawa ba. Duba Wi-Fi ko data na waya, sannan ka sake gwadawa.",
    csrfTokenUnavailable: "Alamar tsaro ba ta samuwa",
    indexedDbUnavailable: "Ajiya na offline ba ya samuwa a wannan browser",
    indexedDbOpenFailed: "Ba a iya buɗe ajiyar offline ba",
    indexedDbRequestFailed: "Buƙatar ajiyar offline ta gaza",
    indexedDbTransactionFailed: "Mu'amalar ajiyar offline ta gaza",
    indexedDbTransactionAborted: "An dakatar da mu'amalar ajiyar offline",
    syncRecoveredInterrupted: "An dawo da rukunin sync da ya tsaya",
    syncWaitInitialStockBeforeSale:
      "Ana jiran farkon sync na kaya kafin sayarwa",
    syncWaitProductBeforeSale: "Ana jiran sync na samfuri kafin sayarwa",
    syncWaitProductBeforeStockMovement:
      "Ana jiran sync na samfuri kafin motsin kaya",
    syncWaitSaleBeforeStockMovement:
      "Ana jiran sync na sayarwa kafin motsin kaya",
  },
  ig: {
    account: "Akaụntụ",
    activeBranch: "Alaka na-arụ",
    authModeIndependent: "Nnwere onwe",
    authModeSession: "Session",
    authModeStaffSetup: "Nhazi ndị ọrụ",
    branchLocation: "Ebe alaka di",
    branchSecurity: "Nchedo alaka",
    cancel: "Kagbuo",
    current: "Ugbu a",
    deleteAccount: "Hichapụ akaụntụ",
    devicesAndSync: "Ngwaọrụ na mmekọrịta",
    language: "Asụsụ",
    noActiveBranch: "Enweghị alaka ahọrọ",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Weebụ",
    permissionAssistant: "Onyemaka",
    permissionSettings: "Ntọala",
    preferences: "Nhọrọ",
    reports: "Akụkọ",
    profile: "Profaịlụ",
    saveProfile: "Chekwaa profaịlụ",
    saving: "A na-echekwa...",
    sectionDescription: "Jikwaa profaịlụ, nchedo alaka, sessions, na akaụntụ.",
    sectionTitle: "Ntọala",
    securityDescription:
      "Jikwaa ma ndị ọrụ ga-anọ nso alaka ha iji gaa n'ihu ọrụ.",
    securityTitle: "Nchedo nbanye ndị ọrụ",
    shopName: "Aha ụlọ ahịa",
    shopProfileDescription: "Melite aha ụlọ ahịa ndị otu gị na-ahụ.",
    shopProfileTitle: "Profaịlụ ụlọ ahịa",
    signOut: "Pụọ na browser a",
    staffRestrictionDescriptionOff:
      "Ndị ọrụ nwere ike ịrụ ọrụ mgbe ha banyechara n'enweghị mmanye ebe alaka.",
    staffRestrictionDescriptionOn:
      "Ndị ọrụ ga-anọ nso alaka a ka ha jiri ụlọ ahịa.",
    staffRestrictionHint: "Chekwaa ebe alaka tupu ịgbanye mmachi.",
    staffRestrictionOff: "Mmachi agbanyụọla",
    staffRestrictionOn: "Mmachi dị na ya",
    syncFailed: "Ọ dara",
    syncPendingShort: "Na-eche",
    syncQueued: "N'ahịrị",
    syncSynced: "Emezuru sync",
    locationSourceAdmin: "admin",
    locationSourceDevice: "ngwaọrụ",
    locationSourceGps: "GPS",
    operationDebt: "Ụgwọ",
    operationDebtPayment: "Ịkwụ ụgwọ",
    operationProduct: "Ngwaahịa",
    operationSale: "Ahịa",
    operationStock: "Stock",
    updateLocation: "Jiri ebe ụlọ ahịa dị ugbu a",
    updateLocationRefresh: "Melite ebe alaka",
    queuedRecordUnavailable: "Ndekọ a nọ n'ahịrị adịkwaghị.",
    queuedDeleteWait:
      "Chere ka mgbalị sync ugbu a kwụsị tupu ihichapụ ndekọ a.",
    queuedDeleteStandaloneOnly:
      "Naanị mgbanwe stock kwụụrụ onwe ya ka a pụrụ iwepụ n'ahịrị.",
    queueProductChangeBlocked:
      "Mgbanwe ngwaahịa ga-anọ n'ahịrị ruo mgbe a ga-emekọrịta ya ma ọ bụ ndozi ọhụrụ dochie ya.",
    queueProductArchivePending: "Idobe ngwaahịa n’akụkụ ka na-eche",
    queueProductUpdatePending: "Mmelite ngwaahịa ka na-eche",
    queueStockDeleteWait:
      "Chere ka mgbalị sync ugbu a kwụsị tupu iwepụ mgbanwe stock a.",
    queueStockSaleLinked:
      "Mmegharị stock a bụ nke ire ahịa ma a pụghị iwepụ ya ebe a.",
    queueStockAdditionPending: "Mgbakwunye stock ka na-eche",
    queueStockReductionPending: "Mbelata stock ka na-eche",
    queueInventoryItem: "Ihe dị na stock",
    queueSaleDeleteBlocked:
      "Ahịa na-emetụta stock na akụkọ, ya mere a pụghị iwepụ ha na ihuenyo ahịrị a.",
    queueSaleItems: (count) => `ihe ${count} n’ime ire ahịa`,
    queuePendingSale: "Ire ahịa na-eche",
    queueDebtDeleteBlocked:
      "Ndekọ ụgwọ ga-anọ n'ahịrị ruo mgbe a ga-emekọrịta ya ma ọ bụ mgbanwe ọhụrụ melite ya.",
    queueCustomerCredit: "Kredịt onye ahịa",
    queuePendingDebtRecord: "Ndekọ ụgwọ na-eche",
    queueDebtPaymentDeleteBlocked:
      "A pụghị iwepụ ịkwụ ụgwọ n'ihuenyo ahịrị sync.",
    queueCustomerPayment: "Ịkwụ ụgwọ onye ahịa",
    queuePendingDebtPayment: "Ịkwụ ụgwọ na-eche",
    queueGenericDeleteBlocked:
      "A pụghị iwepụ ndekọ a nọ n'ahịrị n'enweghị nsogbu ebe a.",
    queueGenericRecord: "Ndekọ sync nọ n'ahịrị",
    requestPrepareFailed: "Enweghị ike ịkwadebe arịrịọ",
    requestFailedGeneric: "Arịrịọ dara",
    networkUnavailable: "Enweghị ike ijikọ. Lelee Wi-Fi ma ọ bụ data ekwentị, wee nwaa ọzọ.",
    csrfTokenUnavailable: "Token nche adịghị",
    indexedDbUnavailable: "Nchekwa offline adịghị na browser a",
    indexedDbOpenFailed: "Enweghị ike imeghe nchekwa offline",
    indexedDbRequestFailed: "Arịrịọ nchekwa offline dara",
    indexedDbTransactionFailed: "Mmegharị nchekwa offline dara",
    indexedDbTransactionAborted: "Akwụsịla mmegharị nchekwa offline",
    syncRecoveredInterrupted: "E weghachila ngwugwu sync kwụsịrị",
    syncWaitInitialStockBeforeSale:
      "Na-eche ka sync mbụ nke stock mezue tupu ire ahịa",
    syncWaitProductBeforeSale: "Na-eche ka sync ngwaahịa mezue tupu ire ahịa",
    syncWaitProductBeforeStockMovement:
      "Na-eche ka sync ngwaahịa mezue tupu mmegharị stock",
    syncWaitSaleBeforeStockMovement:
      "Na-eche ka sync ire ahịa mezue tupu mmegharị stock",
  },
  pt: {
    account: "Conta",
    activeBranch: "Filial ativa",
    authModeIndependent: "Independente",
    authModeSession: "Sessão",
    authModeStaffSetup: "Configuração da equipa",
    branchLocation: "Localização da filial",
    branchSecurity: "Segurança da filial",
    cancel: "Cancelar",
    current: "Atual",
    deleteAccount: "Eliminar conta",
    devicesAndSync: "Dispositivos e sincronização",
    language: "Idioma",
    noActiveBranch: "Nenhuma filial ativa selecionada",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Web",
    permissionAssistant: "Assistente",
    permissionSettings: "Definições",
    preferences: "Preferências",
    reports: "Relatórios",
    profile: "Perfil",
    saveProfile: "Guardar perfil",
    saving: "A guardar...",
    sectionDescription:
      "Gerencie perfil, segurança da filial, sessões e acesso à conta.",
    sectionTitle: "Definições",
    securityDescription:
      "Controle se a equipa deve estar perto da filial atribuída para continuar a trabalhar.",
    securityTitle: "Segurança de entrada da equipa",
    shopName: "Nome da loja",
    shopProfileDescription: "Atualize o nome da loja que a sua equipa vê.",
    shopProfileTitle: "Perfil da loja",
    signOut: "Sair deste navegador",
    staffRestrictionDescriptionOff:
      "A equipa pode trabalhar após iniciar sessão sem controlo de área.",
    staffRestrictionDescriptionOn:
      "A equipa deve estar perto desta filial para continuar.",
    staffRestrictionHint: "Guarde a localização antes de ativar a restrição.",
    staffRestrictionOff: "Restrição desligada",
    staffRestrictionOn: "Restrição ligada",
    syncFailed: "Falhou",
    syncPendingShort: "Pendente",
    syncQueued: "Em fila",
    syncSynced: "Sincronizado",
    locationSourceAdmin: "admin",
    locationSourceDevice: "dispositivo",
    locationSourceGps: "GPS",
    operationDebt: "Dívida",
    operationDebtPayment: "Pagamento de dívida",
    operationProduct: "Produto",
    operationSale: "Venda",
    operationStock: "Stock",
    updateLocation: "Usar localização atual da loja",
    updateLocationRefresh: "Atualizar localização",
    queuedRecordUnavailable: "Esse registo em fila já não está disponível.",
    queuedDeleteWait:
      "Aguarde a tentativa de sincronização atual terminar antes de eliminar este registo.",
    queuedDeleteStandaloneOnly:
      "Apenas alterações de stock independentes podem ser removidas da fila.",
    queueProductChangeBlocked:
      "As alterações do produto ficam em fila até sincronizarem ou serem substituídas por uma edição mais recente.",
    queueProductArchivePending: "Arquivo do produto pendente",
    queueProductUpdatePending: "Atualização do produto pendente",
    queueStockDeleteWait:
      "Aguarde a tentativa de sincronização atual terminar antes de remover esta alteração de stock.",
    queueStockSaleLinked:
      "Este movimento de stock pertence a uma venda e não pode ser removido aqui.",
    queueStockAdditionPending: "Adição de stock pendente",
    queueStockReductionPending: "Redução de stock pendente",
    queueInventoryItem: "Item de stock",
    queueSaleDeleteBlocked:
      "As vendas afetam o stock e o histórico, por isso não podem ser removidas deste ecrã da fila.",
    queueSaleItems: (count) =>
      `${count} item${count === 1 ? "" : "s"} na venda`,
    queuePendingSale: "Venda pendente",
    queueDebtDeleteBlocked:
      "Os registos de dívida ficam em fila até sincronizarem ou serem atualizados por uma alteração mais recente.",
    queueCustomerCredit: "Crédito do cliente",
    queuePendingDebtRecord: "Registo de dívida pendente",
    queueDebtPaymentDeleteBlocked:
      "Pagamentos de dívida não podem ser removidos do ecrã da fila de sincronização.",
    queueCustomerPayment: "Pagamento do cliente",
    queuePendingDebtPayment: "Pagamento de dívida pendente",
    queueGenericDeleteBlocked:
      "Este registo em fila não pode ser removido com segurança aqui.",
    queueGenericRecord: "Registo de sincronização em fila",
    requestPrepareFailed: "Não foi possível preparar o pedido",
    requestFailedGeneric: "O pedido falhou",
    networkUnavailable: "Não foi possível ligar. Verifique o Wi-Fi ou os dados móveis e tente novamente.",
    csrfTokenUnavailable: "Token de segurança indisponível",
    indexedDbUnavailable:
      "O armazenamento offline não está disponível neste navegador",
    indexedDbOpenFailed: "Não foi possível abrir o armazenamento offline",
    indexedDbRequestFailed: "O pedido de armazenamento offline falhou",
    indexedDbTransactionFailed: "A transação de armazenamento offline falhou",
    indexedDbTransactionAborted:
      "A transação de armazenamento offline foi interrompida",
    syncRecoveredInterrupted: "Lote de sincronização interrompido recuperado",
    syncWaitInitialStockBeforeSale:
      "A aguardar a sincronização inicial do stock antes da venda",
    syncWaitProductBeforeSale:
      "A aguardar a sincronização do produto antes da venda",
    syncWaitProductBeforeStockMovement:
      "A aguardar a sincronização do produto antes do movimento de stock",
    syncWaitSaleBeforeStockMovement:
      "A aguardar a sincronização da venda antes do movimento de stock",
  },
  sw: {
    account: "Akaunti",
    activeBranch: "Tawi linalotumika",
    authModeIndependent: "Huru",
    authModeSession: "Kipindi",
    authModeStaffSetup: "Mpangilio wa wafanyakazi",
    branchLocation: "Mahali pa tawi",
    branchSecurity: "Usalama wa tawi",
    cancel: "Ghairi",
    current: "Sasa",
    deleteAccount: "Futa akaunti",
    devicesAndSync: "Vifaa na usawazishaji",
    language: "Lugha",
    noActiveBranch: "Hakuna tawi lililochaguliwa",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Wavuti",
    permissionAssistant: "Msaidizi",
    permissionSettings: "Mipangilio",
    preferences: "Mapendeleo",
    reports: "Ripoti",
    profile: "Wasifu",
    saveProfile: "Hifadhi wasifu",
    saving: "Inahifadhi...",
    sectionDescription: "Dhibiti wasifu, usalama wa tawi, vipindi, na akaunti.",
    sectionTitle: "Mipangilio",
    securityDescription:
      "Dhibiti kama wafanyakazi lazima wawe karibu na tawi lao ili kuendelea kufanya kazi.",
    securityTitle: "Usalama wa kuingia kwa wafanyakazi",
    shopName: "Jina la duka",
    shopProfileDescription: "Sasisha jina la duka linaloonekana kwa timu yako.",
    shopProfileTitle: "Wasifu wa duka",
    signOut: "Ondoka kwenye kivinjari hiki",
    staffRestrictionDescriptionOff:
      "Wafanyakazi wanaweza kufanya kazi baada ya kuingia bila ukaguzi wa eneo.",
    staffRestrictionDescriptionOn:
      "Wafanyakazi lazima wawe karibu na tawi hili.",
    staffRestrictionHint: "Hifadhi mahali pa tawi kabla ya kuwasha kizuizi.",
    staffRestrictionOff: "Kizuizi kimezimwa",
    staffRestrictionOn: "Kizuizi kimewashwa",
    syncFailed: "Imeshindikana",
    syncPendingShort: "Inasubiri",
    syncQueued: "Kwenye foleni",
    syncSynced: "Imesawazishwa",
    locationSourceAdmin: "admin",
    locationSourceDevice: "kifaa",
    locationSourceGps: "GPS",
    operationDebt: "Deni",
    operationDebtPayment: "Malipo ya deni",
    operationProduct: "Bidhaa",
    operationSale: "Uuzaji",
    operationStock: "Stock",
    updateLocation: "Tumia mahali pa sasa pa duka",
    updateLocationRefresh: "Sasisha mahali pa tawi",
    queuedRecordUnavailable: "Rekodi hii ya foleni haipatikani tena.",
    queuedDeleteWait:
      "Subiri jaribio la sasa la usawazishaji likamilike kabla ya kufuta rekodi hii.",
    queuedDeleteStandaloneOnly:
      "Ni mabadiliko ya stock yaliyo huru pekee yanaweza kuondolewa kwenye foleni.",
    queueProductChangeBlocked:
      "Mabadiliko ya bidhaa hubaki kwenye foleni hadi yasawazishwe au yabadilishwe na uhariri mpya.",
    queueProductArchivePending: "Kuhifadhi bidhaa pending",
    queueProductUpdatePending: "Usasishaji wa bidhaa pending",
    queueStockDeleteWait:
      "Subiri jaribio la sasa la usawazishaji likamilike kabla ya kuondoa mabadiliko haya ya stock.",
    queueStockSaleLinked:
      "Harakati hii ya stock inahusiana na uuzaji na haiwezi kuondolewa hapa.",
    queueStockAdditionPending: "Ongezeko la stock pending",
    queueStockReductionPending: "Upunguzaji wa stock pending",
    queueInventoryItem: "Item ya stock",
    queueSaleDeleteBlocked:
      "Mauzo huathiri stock na historia, kwa hiyo hayawezi kuondolewa kwenye skrini hii ya foleni.",
    queueSaleItems: (count) => `item ${count} kwenye mauzo`,
    queuePendingSale: "Uuzaji pending",
    queueDebtDeleteBlocked:
      "Rekodi za deni hubaki kwenye foleni hadi zisawazishwe au zisasishe kwa mabadiliko mapya.",
    queueCustomerCredit: "Mkopo wa mteja",
    queuePendingDebtRecord: "Rekodi ya deni pending",
    queueDebtPaymentDeleteBlocked:
      "Malipo ya deni hayawezi kuondolewa kwenye skrini ya foleni ya usawazishaji.",
    queueCustomerPayment: "Malipo ya mteja",
    queuePendingDebtPayment: "Malipo ya deni pending",
    queueGenericDeleteBlocked:
      "Rekodi hii ya foleni haiwezi kuondolewa kwa usalama hapa.",
    queueGenericRecord: "Rekodi ya usawazishaji ya foleni",
    requestPrepareFailed: "Haikuwezekana kuandaa ombi",
    requestFailedGeneric: "Ombi limeshindika",
    networkUnavailable: "Haikuwezekana kuunganisha. Angalia Wi-Fi au data ya simu, kisha ujaribu tena.",
    csrfTokenUnavailable: "Token ya usalama haipatikani",
    indexedDbUnavailable:
      "Hifadhi ya offline haipatikani kwenye kivinjari hiki",
    indexedDbOpenFailed: "Haikuwezekana kufungua hifadhi ya offline",
    indexedDbRequestFailed: "Ombi la hifadhi ya offline limeshindika",
    indexedDbTransactionFailed: "Muamala wa hifadhi ya offline umeshindikana",
    indexedDbTransactionAborted: "Muamala wa hifadhi ya offline umesitishwa",
    syncRecoveredInterrupted:
      "Kundi la usawazishaji lililokatizwa limerejeshwa",
    syncWaitInitialStockBeforeSale:
      "Inasubiri usawazishaji wa kwanza wa stock kabla ya uuzaji",
    syncWaitProductBeforeSale:
      "Inasubiri usawazishaji wa bidhaa kabla ya uuzaji",
    syncWaitProductBeforeStockMovement:
      "Inasubiri usawazishaji wa bidhaa kabla ya harakati ya stock",
    syncWaitSaleBeforeStockMovement:
      "Inasubiri usawazishaji wa uuzaji kabla ya harakati ya stock",
  },
  yo: {
    account: "Akọọlẹ",
    activeBranch: "Ẹka lọwọlọwọ",
    authModeIndependent: "Ominira",
    authModeSession: "Igbà",
    authModeStaffSetup: "Eto oṣiṣẹ",
    branchLocation: "Ipo ẹka",
    branchSecurity: "Aabo ẹka",
    cancel: "Fagilee",
    current: "Lọwọlọwọ",
    deleteAccount: "Pa akọọlẹ rẹ",
    devicesAndSync: "Ẹrọ ati sync",
    language: "Ede",
    noActiveBranch: "Ko si ẹka lọwọlọwọ",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Wẹẹbu",
    permissionAssistant: "Oluranlọwọ",
    permissionSettings: "Eto",
    preferences: "Awọn ayanfẹ",
    reports: "Àwọn ìròyìn",
    profile: "Profaili",
    saveProfile: "Fipamọ profaili",
    saving: "N fipamọ...",
    sectionDescription: "Ṣakoso profaili, aabo ẹka, sessions, ati akọọlẹ.",
    sectionTitle: "Eto",
    securityDescription:
      "Ṣakoso boya awọn oṣiṣẹ gbọdọ wa nitosi ẹka wọn lati tẹsiwaju iṣẹ.",
    securityTitle: "Aabo iwọle awọn oṣiṣẹ",
    shopName: "Orukọ ile itaja",
    shopProfileDescription: "Ṣe imudojuiwọn orukọ ile itaja ti ẹgbẹ rẹ n ri.",
    shopProfileTitle: "Profaili ile itaja",
    signOut: "Jade kuro ninu browser yii",
    staffRestrictionDescriptionOff:
      "Awọn oṣiṣẹ le ṣiṣẹ lẹhin iwọle lai si imuse agbegbe ẹka.",
    staffRestrictionDescriptionOn: "Awọn oṣiṣẹ gbọdọ wa nitosi ẹka yii.",
    staffRestrictionHint: "Fipamọ ipo ẹka ṣaaju titan ihamọ.",
    staffRestrictionOff: "Ihamọ wa ni pipa",
    staffRestrictionOn: "Ihamọ wa ni titan",
    syncFailed: "O kuna",
    syncPendingShort: "N duro",
    syncQueued: "Ninu ila",
    syncSynced: "Ti muuṣiṣẹpọ",
    locationSourceAdmin: "admin",
    locationSourceDevice: "ẹrọ",
    locationSourceGps: "GPS",
    operationDebt: "Gbese",
    operationDebtPayment: "Sisan gbese",
    operationProduct: "Ọja",
    operationSale: "Tita",
    operationStock: "Stock",
    updateLocation: "Lo ipo ile itaja lọwọlọwọ",
    updateLocationRefresh: "Tun ipo ẹka ṣe",
    queuedRecordUnavailable: "Igbasilẹ to wa ninu ila yii ko si mọ.",
    queuedDeleteWait:
      "Duro ki igbiyanju sync lọwọlọwọ pari ṣaaju ki o to pa igbasilẹ yii.",
    queuedDeleteStandaloneOnly:
      "Awọn ayipada stock ominira nikan ni a le yọ kuro ninu ila.",
    queueProductChangeBlocked:
      "Awọn ayipada ọja yoo wa ninu ila titi wọn yoo fi sync tabi atunṣe tuntun yoo rọpo wọn.",
    queueProductArchivePending: "Ibi-ipamọ ọja nduro",
    queueProductUpdatePending: "Imudojuiwọn ọja nduro",
    queueStockDeleteWait:
      "Duro ki igbiyanju sync lọwọlọwọ pari ṣaaju yiyọ ayipada stock yii.",
    queueStockSaleLinked:
      "Iṣipopada stock yii jẹ ti tita kan ati pe a ko le yọ ọ nibi.",
    queueStockAdditionPending: "Afikun stock nduro",
    queueStockReductionPending: "Idinku stock nduro",
    queueInventoryItem: "Ohun inu stock",
    queueSaleDeleteBlocked:
      "Awọn tita ni ipa lori stock ati itan, nitorina a ko le yọ wọn kuro lori iboju ila yii.",
    queueSaleItems: (count) => `ohun ${count} ninu tita`,
    queuePendingSale: "Tita nduro",
    queueDebtDeleteBlocked:
      "Awọn igbasilẹ gbese yoo wa ninu ila titi wọn yoo fi sync tabi ayipada tuntun yoo ṣe imudojuiwọn wọn.",
    queueCustomerCredit: "Kirẹditi alabara",
    queuePendingDebtRecord: "Igbasilẹ gbese nduro",
    queueDebtPaymentDeleteBlocked:
      "A ko le yọ isanwo gbese kuro lori iboju ila sync.",
    queueCustomerPayment: "Isanwo alabara",
    queuePendingDebtPayment: "Isanwo gbese nduro",
    queueGenericDeleteBlocked: "A ko le yọ igbasilẹ ila yii kuro lailewu nibi.",
    queueGenericRecord: "Igbasilẹ sync inu ila",
    requestPrepareFailed: "A ko le mura ibeere naa silẹ",
    requestFailedGeneric: "Ibeere naa kuna",
    networkUnavailable: "A ko le sopọ. Ṣayẹwo Wi-Fi tabi data fóònù, kí o tún gbìyànjú.",
    csrfTokenUnavailable: "Token aabo ko si",
    indexedDbUnavailable: "Ibi ipamọ offline ko si lori browser yii",
    indexedDbOpenFailed: "A ko le ṣii ibi ipamọ offline",
    indexedDbRequestFailed: "Ibeere ibi ipamọ offline kuna",
    indexedDbTransactionFailed: "Idunadura ibi ipamọ offline kuna",
    indexedDbTransactionAborted: "A da idunadura ibi ipamọ offline duro",
    syncRecoveredInterrupted: "A gba batch sync ti o da duro pada",
    syncWaitInitialStockBeforeSale: "N duro de sync stock akọkọ ṣaaju tita",
    syncWaitProductBeforeSale: "N duro de sync ọja ṣaaju tita",
    syncWaitProductBeforeStockMovement:
      "N duro de sync ọja ṣaaju iṣipopada stock",
    syncWaitSaleBeforeStockMovement:
      "N duro de sync tita ṣaaju iṣipopada stock",
  },
  zu: {
    account: "I-akhawunti",
    activeBranch: "Igatsha elisebenzayo",
    authModeIndependent: "Ozimele",
    authModeSession: "Iseshini",
    authModeStaffSetup: "Ukusetha abasebenzi",
    branchLocation: "Indawo yegatsha",
    branchSecurity: "Ukuphepha kwegatsha",
    cancel: "Khansela",
    current: "Okwamanje",
    deleteAccount: "Susa i-akhawunti",
    devicesAndSync: "Amadivayisi nokuvumelanisa",
    language: "Ulimi",
    noActiveBranch: "Alikho igatsha elikhethiwe",
    platformAndroid: "Android",
    platformIos: "iPhone",
    platformWeb: "Iwebhu",
    permissionAssistant: "Umsizi",
    permissionSettings: "Izilungiselelo",
    preferences: "Okuncanyelwayo",
    reports: "Imibiko",
    profile: "Iphrofayela",
    saveProfile: "Londoloza iphrofayela",
    saving: "Kuyalondolozwa...",
    sectionDescription:
      "Phatha iphrofayela, ukuphepha kwegatsha, izikhathi, ne-akhawunti.",
    sectionTitle: "Izilungiselelo",
    securityDescription:
      "Lawula ukuthi abasebenzi kufanele babe seduze negatsha labo ukuze baqhubeke nokusebenza.",
    securityTitle: "Ukuphepha kokungena kwabasebenzi",
    shopName: "Igama lesitolo",
    shopProfileDescription: "Buyekeza igama lesitolo elibonwa ithimba lakho.",
    shopProfileTitle: "Iphrofayela yesitolo",
    signOut: "Phuma kulesi siphequluli",
    staffRestrictionDescriptionOff:
      "Abasebenzi bangaqhubeka ngemva kokungena ngaphandle kokuqiniswa kwendawo.",
    staffRestrictionDescriptionOn:
      "Abasebenzi kufanele babe seduze kwaleli gatsha.",
    staffRestrictionHint:
      "Londoloza indawo yegatsha ngaphambi kokuvula ukuvinjelwa.",
    staffRestrictionOff: "Ukuvinjelwa kuvaliwe",
    staffRestrictionOn: "Ukuvinjelwa kuvuliwe",
    syncFailed: "Kuhlulekile",
    syncPendingShort: "Kulindile",
    syncQueued: "Kulayini",
    syncSynced: "Kuvumelanisiwe",
    locationSourceAdmin: "admin",
    locationSourceDevice: "idivayisi",
    locationSourceGps: "GPS",
    operationDebt: "Isikweletu",
    operationDebtPayment: "Inkokhelo yesikweletu",
    operationProduct: "Umkhiqizo",
    operationSale: "Ukuthengisa",
    operationStock: "Stock",
    updateLocation: "Sebenzisa indawo yamanje yesitolo",
    updateLocationRefresh: "Vuselela indawo yegatsha",
    queuedRecordUnavailable: "Lena irekhodi elikulayini elingasatholakali.",
    queuedDeleteWait:
      "Linda umzamo wamanje wokuvumelanisa uqede ngaphambi kokususa leli rekhodi.",
    queuedDeleteStandaloneOnly:
      "Izinguquko ze-stock ezizimele kuphela ezingasuswa kulayini.",
    queueProductChangeBlocked:
      "Izinguquko zomkhiqizo zihlala kulayini kuze kuvumelaniswe noma kushintshwe ukuhlelwa okusha.",
    queueProductArchivePending: "Ukugcinwa komkhiqizo kulayini",
    queueProductUpdatePending: "Ukuvuselelwa komkhiqizo kulayini",
    queueStockDeleteWait:
      "Linda umzamo wamanje wokuvumelanisa uqede ngaphambi kokususa lolu shintsho lwe-stock.",
    queueStockSaleLinked:
      "Lokhu kuhamba kwe-stock kuhlobene nokuthengisa futhi akukwazi ukusulwa lapha.",
    queueStockAdditionPending: "Ukwengezwa kwe-stock kulayini",
    queueStockReductionPending: "Ukwehliswa kwe-stock kulayini",
    queueInventoryItem: "Into ye-stock",
    queueSaleDeleteBlocked:
      "Ukuthengisa kuthinta i-stock nomlando, ngakho akukwazi ukusulwa kulesi sikrini somugqa.",
    queueSaleItems: (count) => `into engu-${count} ekuthengiseni`,
    queuePendingSale: "Ukuthengisa kulayini",
    queueDebtDeleteBlocked:
      "Amarekhodi esikweletu ahlala kulayini kuze kuvumelaniswe noma abuyekezwe ushintsho olusha.",
    queueCustomerCredit: "Isikweletu sekhasimende",
    queuePendingDebtRecord: "Irekhodi lesikweletu kulayini",
    queueDebtPaymentDeleteBlocked:
      "Izinkokhelo zesikweletu azinakususwa kusikrini somugqa wokuvumelanisa.",
    queueCustomerPayment: "Inkokhelo yekhasimende",
    queuePendingDebtPayment: "Inkokhelo yesikweletu kulayini",
    queueGenericDeleteBlocked:
      "Leli rekhodi elikulayini alinakususwa ngokuphepha lapha.",
    queueGenericRecord: "Irekhodi lokuvumelanisa elikulayini",
    requestPrepareFailed: "Akukwazekanga ukulungisa isicelo",
    requestFailedGeneric: "Isicelo sihlulekile",
    networkUnavailable: "Ayikwazanga ukuxhuma. Hlola i-Wi-Fi noma idatha yeselula, bese uzama futhi.",
    csrfTokenUnavailable: "Ithokheni yokuphepha ayitholakali",
    indexedDbUnavailable:
      "Isitoreji esingaxhunyiwe ku-inthanethi asitholakali kulesi siphequluli",
    indexedDbOpenFailed:
      "Akukwazekanga ukuvula isitoreji esingaxhunyiwe ku-inthanethi",
    indexedDbRequestFailed:
      "Isicelo sesitoreji esingaxhunyiwe ku-inthanethi sihlulekile",
    indexedDbTransactionFailed:
      "Ukuhwebelana kwesitoreji esingaxhunyiwe ku-inthanethi kuhlulekile",
    indexedDbTransactionAborted:
      "Ukuhwebelana kwesitoreji esingaxhunyiwe ku-inthanethi kumisiwe",
    syncRecoveredInterrupted:
      "Iqoqo lokuvumelanisa eliphazamisekile libuyisiwe",
    syncWaitInitialStockBeforeSale:
      "Kulindwe ukuvumelaniswa kokuqala kwe-stock ngaphambi kokuthengisa",
    syncWaitProductBeforeSale:
      "Kulindwe ukuvumelaniswa komkhiqizo ngaphambi kokuthengisa",
    syncWaitProductBeforeStockMovement:
      "Kulindwe ukuvumelaniswa komkhiqizo ngaphambi kokuhamba kwe-stock",
    syncWaitSaleBeforeStockMovement:
      "Kulindwe ukuvumelaniswa kokuthengisa ngaphambi kokuhamba kwe-stock",
  },
};

Object.entries(settingsTranslations).forEach(([language, settings]) => {
  translations[language] = {
    ...(translations[language] || {}),
    settings,
  };
});

const layoutTranslations = {
  ar: {
    analytics: "التحليلات",
    assistant: "المساعد",
    branches: "الفروع",
    dashboard: "لوحة التحكم",
    debts: "الديون",
    history: "السجل",
    inventory: "المخزون",
    notifications: "الإشعارات",
    operate: "التشغيل",
    preferences: "التفضيلات",
    sell: "البيع",
    settings: "الإعدادات",
    signOut: "تسجيل الخروج",
    staff: "الموظفون",
    switchBranch: "تبديل الفرع",
    synced: "تمت المزامنة",
    workspace: "مساحة العمل",
  },
  fr: {
    analytics: "Analyses",
    assistant: "Assistant",
    branches: "Succursales",
    dashboard: "Tableau de bord",
    debts: "Dettes",
    history: "Historique",
    inventory: "Stock",
    notifications: "Notifications",
    operate: "Opérations",
    preferences: "Préférences",
    sell: "Vendre",
    settings: "Paramètres",
    signOut: "Se déconnecter",
    staff: "Personnel",
    switchBranch: "Changer de succursale",
    synced: "Synchronisé",
    workspace: "Espace de travail",
  },
  ha: {
    analytics: "Nazari",
    assistant: "Mataimaki",
    branches: "Rassa",
    dashboard: "Dashboard",
    debts: "Bashi",
    history: "Tarihi",
    inventory: "Kaya",
    notifications: "Sanarwa",
    operate: "Aiki",
    preferences: "Zabuka",
    sell: "Sayarwa",
    settings: "Saituna",
    signOut: "Fita",
    staff: "Ma'aikata",
    switchBranch: "Sauya reshe",
    synced: "An daidaita",
    workspace: "Wurin aiki",
  },
  ig: {
    analytics: "Nyocha",
    assistant: "Onye enyemaka",
    branches: "Alaka",
    dashboard: "Dashboard",
    debts: "Ụgwọ",
    history: "Akụkọ",
    inventory: "Ngwaahịa",
    notifications: "Ọkwa",
    operate: "Ọrụ",
    preferences: "Nhọrọ",
    sell: "Ree",
    settings: "Ntọala",
    signOut: "Pụọ",
    staff: "Ndị ọrụ",
    switchBranch: "Gbanwee alaka",
    synced: "Emechara sync",
    workspace: "Ebe ọrụ",
  },
  pt: {
    analytics: "Análises",
    assistant: "Assistente",
    branches: "Filiais",
    dashboard: "Painel",
    debts: "Dívidas",
    history: "Histórico",
    inventory: "Inventário",
    notifications: "Notificações",
    operate: "Operar",
    preferences: "Preferências",
    sell: "Vender",
    settings: "Definições",
    signOut: "Sair",
    staff: "Equipa",
    switchBranch: "Mudar filial",
    synced: "Sincronizado",
    workspace: "Área de trabalho",
  },
  sw: {
    analytics: "Uchambuzi",
    assistant: "Msaidizi",
    branches: "Matawi",
    dashboard: "Dashibodi",
    debts: "Madeni",
    history: "Historia",
    inventory: "Bidhaa",
    notifications: "Arifa",
    operate: "Endesha",
    preferences: "Mapendeleo",
    sell: "Uza",
    settings: "Mipangilio",
    signOut: "Ondoka",
    staff: "Wafanyakazi",
    switchBranch: "Badilisha tawi",
    synced: "Imesawazishwa",
    workspace: "Eneo la kazi",
  },
  yo: {
    analytics: "Atupale",
    assistant: "Oluranlọwọ",
    branches: "Ẹka",
    dashboard: "Dashboard",
    debts: "Gbese",
    history: "Itan",
    inventory: "Ọja",
    notifications: "Iwifunni",
    operate: "Ṣiṣẹ",
    preferences: "Awọn ayanfẹ",
    sell: "Ta",
    settings: "Eto",
    signOut: "Jade",
    staff: "Oṣiṣẹ",
    switchBranch: "Yi ẹka pada",
    synced: "Ti sync",
    workspace: "Ibi iṣẹ",
  },
  zu: {
    analytics: "Ukuhlaziya",
    assistant: "Umsizi",
    branches: "Amagatsha",
    dashboard: "Ideshibhodi",
    debts: "Izikweletu",
    history: "Umlando",
    inventory: "Impahla",
    notifications: "Izaziso",
    operate: "Sebenza",
    preferences: "Okuncanyelwayo",
    sell: "Thengisa",
    settings: "Izilungiselelo",
    signOut: "Phuma",
    staff: "Abasebenzi",
    switchBranch: "Shintsha igatsha",
    synced: "Kuvumelanisiwe",
    workspace: "Indawo yokusebenza",
  },
};

Object.entries(layoutTranslations).forEach(([language, layout]) => {
  translations[language] = {
    ...(translations[language] || {}),
    layout,
  };
});

const dashboardTranslations = {
  ar: {
    actionRecommended: "إجراء موصى به",
    activeBranch: (name) => `${name} هو الفرع النشط`,
    askAI: "اسأل الذكاء",
    branch: "الفرع",
    businessPulseDate: (date, branchName) => `${branchName} · ${date}`,
    customers: (count) => `${count} عملاء`,
    goodAfternoon: "مساء الخير",
    goodEvening: "مساء الخير",
    goodMorning: "صباح الخير",
    liveAnalyticsRecommendations: "توصيات تحليلية مباشرة",
    liveInsight: "رؤية مباشرة",
    newSale: "عملية بيع جديدة",
    noBranchSelected: "لا يوجد فرع محدد في هذه الجلسة بعد.",
    noProductMovementYet: "لا توجد حركة منتجات بعد.",
    noRecentSalesYet: "لا توجد مبيعات حديثة بعد.",
    noRestockRecommendationsYet: "لا توجد توصيات إعادة تخزين بعد.",
    noTopProductData: "لم يتم تحميل بيانات أفضل المنتجات بعد.",
    openAnalytics: "افتح التحليلات",
    outstandingDebt: "الديون المستحقة",
    recent: (count) => `${count} حديثة`,
    recentSales: "المعاملات الأخيرة",
    recentSalesMeta: (count, branchName) =>
      `آخر ${count} مبيعات · ${branchName}`,
    restockAnalyticsEmpty: "ستظهر توصيات إعادة التخزين عند تحميل التحليلات.",
    restockBody: (reason, quantity) =>
      `${reason} الكمية الموصى بها: +${quantity} وحدة.`,
    restockPlan: "خطة إعادة التخزين",
    restockPlanReview: "راجع خطة التخزين",
    restockToday: (name) => `أعد تخزين ${name} اليوم`,
    reviewBranches: "راجع الفروع",
    revenue: "الإيراد",
    revenueLastDays: (days) => `الإيراد · آخر ${days} يومًا`,
    salesCompleted: "المبيعات المكتملة",
    salesSyncing: "تتم مزامنة بيانات المبيعات",
    stockAlerts: "تنبيهات المخزون",
    stockedOut: (count) => `${count} نافد`,
    syncingBody: "لم يتم تحميل بيانات أفضل المنتجات بعد.",
    topMovers: "الأسرع حركة",
    topMoversMeta: "اليوم · حسب الوحدات",
    topProductLead: (name) => `${name} الأسرع مبيعًا اليوم`,
    topProductLeadBody: (sold) =>
      `تم بيع ${sold} وحدة حتى الآن. حافظ على هذا الزخم قبل ذروة بعد الظهر.`,
    units: "وحدات",
    viewAll: "عرض الكل",
    viewProduct: "عرض المنتج",
    viewWorkspaceFor: (name) => `أنت تعرض مساحة العمل المباشرة لـ ${name}.`,
    todayRevenue: "إيراد اليوم",
    paymentPaid: "مدفوع",
    paymentPartlyPaid: "مدفوع جزئيًا",
    paymentUnpaid: "غير مدفوع",
  },
  fr: {
    actionRecommended: "Action recommandée",
    activeBranch: (name) => `${name} est la succursale active`,
    askAI: "Demander à l'IA",
    branch: "Succursale",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} clients`,
    goodAfternoon: "Bon après-midi",
    goodEvening: "Bonsoir",
    goodMorning: "Bonjour",
    liveAnalyticsRecommendations: "Recommandations analytiques en direct",
    liveInsight: "Info en direct",
    newSale: "Nouvelle vente",
    noBranchSelected:
      "Aucune succursale n'est encore sélectionnée dans cette session.",
    noProductMovementYet: "Aucun mouvement de produit pour l’instant.",
    noRecentSalesYet: "Aucune vente récente pour l’instant.",
    noRestockRecommendationsYet:
      "Aucune recommandation de réapprovisionnement pour l’instant.",
    noTopProductData:
      "Les données des meilleurs produits ne sont pas encore chargées.",
    openAnalytics: "Ouvrir les analyses",
    outstandingDebt: "Dette en attente",
    recent: (count) => `${count} récentes`,
    recentSales: "Transactions récentes",
    recentSalesMeta: (count, branchName) =>
      `${count} dernières ventes · ${branchName}`,
    restockAnalyticsEmpty:
      "Les recommandations de réapprovisionnement apparaîtront quand l’analyse sera chargée.",
    restockBody: (reason, quantity) =>
      `${reason} Commande recommandée : +${quantity} unités.`,
    restockPlan: "Plan de réapprovisionnement",
    restockPlanReview: "Revoir le plan",
    restockToday: (name) => `Réapprovisionner ${name} aujourd'hui`,
    reviewBranches: "Voir les succursales",
    revenue: "Revenu",
    revenueLastDays: (days) => `Revenu · ${days} derniers jours`,
    salesCompleted: "Ventes terminées",
    salesSyncing: "Les données de vente se synchronisent",
    stockAlerts: "Alertes de stock",
    stockedOut: (count) => `${count} en rupture`,
    syncingBody:
      "Les données des meilleurs produits ne sont pas encore chargées.",
    topMovers: "Produits moteurs",
    topMoversMeta: "Aujourd’hui · par unités",
    topProductLead: (name) => `${name} se vend le plus vite aujourd'hui`,
    topProductLeadBody: (sold) =>
      `${sold} unités vendues jusqu’à présent. Protégez ce stock avant le prochain rush.`,
    units: "unités",
    viewAll: "Voir tout",
    viewProduct: "Voir le produit",
    viewWorkspaceFor: (name) =>
      `Vous consultez l’espace de travail en direct pour ${name}.`,
    todayRevenue: "Revenu du jour",
    paymentPaid: "Payé",
    paymentPartlyPaid: "Partiellement payé",
    paymentUnpaid: "Impayé",
  },
  ha: {
    actionRecommended: "An ba da shawara",
    activeBranch: (name) => `${name} ne reshen da ke aiki`,
    askAI: "Tambayi AI",
    branch: "Reshe",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `kwastomomi ${count}`,
    goodAfternoon: "Barka da rana",
    goodEvening: "Barka da yamma",
    goodMorning: "Barka da safiya",
    liveAnalyticsRecommendations: "Shawarwarin nazari kai tsaye",
    liveInsight: "Bayanan kai tsaye",
    newSale: "Sabuwar sayarwa",
    noBranchSelected: "Ba a zabi wani reshe a wannan zaman ba tukuna.",
    noProductMovementYet: "Babu motsin kaya tukuna.",
    noRecentSalesYet: "Babu tallace-tallace na baya-bayan nan tukuna.",
    noRestockRecommendationsYet: "Babu shawarar kara kaya tukuna.",
    noTopProductData: "Ba a loda bayanan manyan kaya ba tukuna.",
    openAnalytics: "Bude nazari",
    outstandingDebt: "Bashin da ake bi",
    recent: (count) => `${count} na baya-bayan nan`,
    recentSales: "Ma’amaloli na baya-bayan nan",
    recentSalesMeta: (count, branchName) =>
      `Tallace-tallace ${count} na baya-bayan nan · ${branchName}`,
    restockAnalyticsEmpty:
      "Shawarar kara kaya za ta bayyana idan nazari ya loda.",
    restockBody: (reason, quantity) =>
      `${reason} Adadin da aka ba da shawara: +${quantity} guda.`,
    restockPlan: "Tsarin kara kaya",
    restockPlanReview: "Duba tsarin kara kaya",
    restockToday: (name) => `Kara ${name} yau`,
    reviewBranches: "Duba rassa",
    revenue: "Kudin shiga",
    revenueLastDays: (days) => `Kudin shiga · kwanaki ${days} na baya`,
    salesCompleted: "Tallace-tallace da aka kammala",
    salesSyncing: "Ana daidaita bayanan tallace-tallace",
    stockAlerts: "Faɗakarwar kaya",
    stockedOut: (count) => `${count} sun kare`,
    syncingBody: "Ba a loda bayanan manyan kaya ba tukuna.",
    topMovers: "Mafi saurin tafiya",
    topMoversMeta: "Yau · bisa adadi",
    topProductLead: (name) => `${name} ne ke tafiya mafi sauri yau`,
    topProductLeadBody: (sold) =>
      `An sayar da guda ${sold} zuwa yanzu. Kare kayan kafin cunkoso na rana.`,
    units: "guda",
    viewAll: "Duba duka",
    viewProduct: "Duba kaya",
    viewWorkspaceFor: (name) => `Kana kallon wurin aiki kai tsaye na ${name}.`,
    todayRevenue: "Kudin shiga na yau",
    paymentPaid: "An biya",
    paymentPartlyPaid: "An biya wani kaso",
    paymentUnpaid: "Ba a biya ba",
  },
  ig: {
    actionRecommended: "Akwadoro ime ihe",
    activeBranch: (name) => `${name} bu alaka na-arụ ọrụ`,
    askAI: "Juo AI",
    branch: "Alaka",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} ndị ahịa`,
    goodAfternoon: "Ehihie ọma",
    goodEvening: "Mgbede ọma",
    goodMorning: "Ụtụtụ ọma",
    liveAnalyticsRecommendations: "Nduzi nyocha dị ndụ",
    liveInsight: "Nghọta dị ndụ",
    newSale: "Ire ọhụrụ",
    noBranchSelected: "Enweghị alaka ahọrọ na session a ugbu a.",
    noProductMovementYet: "Enweghị mmegharị ngwaahịa ugbu a.",
    noRecentSalesYet: "Enweghị ire kacha nso ugbu a.",
    noRestockRecommendationsYet: "Enweghị nduzi itinye ngwa ahia ugbu a.",
    noTopProductData: "A naghị ebunye data ngwaahịa kacha elu ugbu a.",
    openAnalytics: "Mepee nyocha",
    outstandingDebt: "Ụgwọ fọdụrụ",
    recent: (count) => `${count} kacha nso`,
    recentSales: "Mmekọrịta kacha nso",
    recentSalesMeta: (count, branchName) =>
      `Ire ${count} kacha nso · ${branchName}`,
    restockAnalyticsEmpty: "Nduzi itinye ngwa ahia ga-apụta mgbe nyocha eburu.",
    restockBody: (reason, quantity) =>
      `${reason} Ọnụ ọgụgụ akwadoro: +${quantity} unit.`,
    restockPlan: "Atụmatụ itinye ngwa ahia",
    restockPlanReview: "Lelee atụmatụ ahụ",
    restockToday: (name) => `Tinye ${name} taa`,
    reviewBranches: "Lelee alaka",
    revenue: "Ego mbata",
    revenueLastDays: (days) => `Ego mbata · ụbọchị ${days} gara aga`,
    salesCompleted: "Ire emechara",
    salesSyncing: "A na-eme sync data ire",
    stockAlerts: "Ịdọ aka ná ntị ngwa ahia",
    stockedOut: (count) => `${count} agwụla`,
    syncingBody: "A naghị ebunye data ngwaahịa kacha elu ugbu a.",
    topMovers: "Nke kacha aga ngwa ngwa",
    topMoversMeta: "Taa · site na unit",
    topProductLead: (name) => `${name} kacha aga ngwa ngwa taa`,
    topProductLeadBody: (sold) =>
      `E rere unit ${sold} ugbu a. Chekwaa ngwa ahia tupu oge ọrụ sie ike.`,
    units: "unit",
    viewAll: "Lee ha niile",
    viewProduct: "Lee ngwaahịa",
    viewWorkspaceFor: (name) => `Ị na-ele ebe ọrụ dị ndụ maka ${name}.`,
    todayRevenue: "Ego mbata taa",
    paymentPaid: "A kwụrụ",
    paymentPartlyPaid: "A kwụrụ akụkụ",
    paymentUnpaid: "Akwụghị",
  },
  pt: {
    actionRecommended: "Ação recomendada",
    activeBranch: (name) => `${name} é a filial ativa`,
    askAI: "Perguntar à IA",
    branch: "Filial",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} clientes`,
    goodAfternoon: "Boa tarde",
    goodEvening: "Boa noite",
    goodMorning: "Bom dia",
    liveAnalyticsRecommendations: "Recomendações analíticas ao vivo",
    liveInsight: "Insight ao vivo",
    newSale: "Nova venda",
    noBranchSelected: "Nenhuma filial foi selecionada nesta sessão ainda.",
    noProductMovementYet: "Ainda não há movimento de produtos.",
    noRecentSalesYet: "Ainda não há vendas recentes.",
    noRestockRecommendationsYet: "Ainda não há recomendações de reposição.",
    noTopProductData:
      "Os dados dos produtos principais ainda não foram carregados.",
    openAnalytics: "Abrir análises",
    outstandingDebt: "Dívida pendente",
    recent: (count) => `${count} recentes`,
    recentSales: "Transações recentes",
    recentSalesMeta: (count, branchName) =>
      `Últimas ${count} vendas · ${branchName}`,
    restockAnalyticsEmpty:
      "As recomendações de reposição aparecerão quando a análise carregar.",
    restockBody: (reason, quantity) =>
      `${reason} Pedido recomendado: +${quantity} unidades.`,
    restockPlan: "Plano de reposição",
    restockPlanReview: "Rever plano",
    restockToday: (name) => `Repor ${name} hoje`,
    reviewBranches: "Rever filiais",
    revenue: "Receita",
    revenueLastDays: (days) => `Receita · últimos ${days} dias`,
    salesCompleted: "Vendas concluídas",
    salesSyncing: "Os dados de vendas estão a sincronizar",
    stockAlerts: "Alertas de stock",
    stockedOut: (count) => `${count} esgotados`,
    syncingBody: "Os dados dos produtos principais ainda não foram carregados.",
    topMovers: "Mais rápidos",
    topMoversMeta: "Hoje · por unidades",
    topProductLead: (name) => `${name} está a vender mais rápido hoje`,
    topProductLeadBody: (sold) =>
      `${sold} unidades vendidas até agora. Proteja este stock antes do próximo pico.`,
    units: "unidades",
    viewAll: "Ver tudo",
    viewProduct: "Ver produto",
    viewWorkspaceFor: (name) =>
      `Está a ver a área de trabalho ao vivo de ${name}.`,
    todayRevenue: "Receita de hoje",
    paymentPaid: "Pago",
    paymentPartlyPaid: "Pago parcialmente",
    paymentUnpaid: "Não pago",
  },
  sw: {
    actionRecommended: "Hatua inapendekezwa",
    activeBranch: (name) => `${name} ndio tawi linalotumika`,
    askAI: "Uliza AI",
    branch: "Tawi",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} wateja`,
    goodAfternoon: "Habari za mchana",
    goodEvening: "Habari za jioni",
    goodMorning: "Habari za asubuhi",
    liveAnalyticsRecommendations: "Mapendekezo ya uchambuzi wa moja kwa moja",
    liveInsight: "Uchambuzi wa moja kwa moja",
    newSale: "Uuzaji mpya",
    noBranchSelected: "Hakuna tawi lililochaguliwa kwenye kikao hiki bado.",
    noProductMovementYet: "Hakuna mwendo wa bidhaa bado.",
    noRecentSalesYet: "Hakuna mauzo ya hivi karibuni bado.",
    noRestockRecommendationsYet: "Hakuna mapendekezo ya kuagiza tena bado.",
    noTopProductData: "Data ya bidhaa zinazoongoza bado haijapakiwa.",
    openAnalytics: "Fungua uchambuzi",
    outstandingDebt: "Deni linalodaiwa",
    recent: (count) => `${count} za hivi karibuni`,
    recentSales: "Miamala ya hivi karibuni",
    recentSalesMeta: (count, branchName) =>
      `Mauzo ${count} ya mwisho · ${branchName}`,
    restockAnalyticsEmpty:
      "Mapendekezo ya kuagiza tena yataonekana uchambuzi ukipakia.",
    restockBody: (reason, quantity) =>
      `${reason} Agizo linalopendekezwa: +${quantity} vipande.`,
    restockPlan: "Mpango wa kuagiza tena",
    restockPlanReview: "Kagua mpango",
    restockToday: (name) => `Agiza tena ${name} leo`,
    reviewBranches: "Kagua matawi",
    revenue: "Mapato",
    revenueLastDays: (days) => `Mapato · siku ${days} zilizopita`,
    salesCompleted: "Mauzo yaliyokamilika",
    salesSyncing: "Data ya mauzo inasawazishwa",
    stockAlerts: "Tahadhari za stock",
    stockedOut: (count) => `${count} zimeisha`,
    syncingBody: "Data ya bidhaa zinazoongoza bado haijapakiwa.",
    topMovers: "Vinavyoenda haraka",
    topMoversMeta: "Leo · kwa vipande",
    topProductLead: (name) => `${name} ndio inayoenda haraka zaidi leo`,
    topProductLeadBody: (sold) =>
      `Vipande ${sold} vimeuzwa hadi sasa. Linda stock hii kabla ya msongamano.`,
    units: "vipande",
    viewAll: "Tazama zote",
    viewProduct: "Tazama bidhaa",
    viewWorkspaceFor: (name) =>
      `Unaangalia workspace ya moja kwa moja ya ${name}.`,
    todayRevenue: "Mapato ya leo",
    paymentPaid: "Imelipwa",
    paymentPartlyPaid: "Imelipwa kiasi",
    paymentUnpaid: "Haijalipwa",
  },
  yo: {
    actionRecommended: "Igbese ti a daba",
    activeBranch: (name) => `${name} ni eka to n ṣiṣẹ lọwọlọwọ`,
    askAI: "Bi AI lọwọ",
    branch: "Ẹka",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} onibara`,
    goodAfternoon: "E kaasan",
    goodEvening: "E ku irọlẹ",
    goodMorning: "E kaaro",
    liveAnalyticsRecommendations: "Awọn aba atupale laaye",
    liveInsight: "Imọran laaye",
    newSale: "Tita tuntun",
    noBranchSelected: "Ko si ẹka ti a yan ninu session yii sibẹ.",
    noProductMovementYet: "Ko si gbigbe ọja sibẹ.",
    noRecentSalesYet: "Ko si tita to ṣẹṣẹ sibẹ.",
    noRestockRecommendationsYet: "Ko si aba afikun ọja sibẹ.",
    noTopProductData: "Data ọja to ga julọ ko tii kojọ sibẹ.",
    openAnalytics: "Ṣii atupale",
    outstandingDebt: "Gbese to ku",
    recent: (count) => `${count} to ṣẹṣẹ`,
    recentSales: "Awọn iṣowo to ṣẹṣẹ",
    recentSalesMeta: (count, branchName) =>
      `Awọn tita ${count} to kẹhin · ${branchName}`,
    restockAnalyticsEmpty:
      "Awọn aba afikun ọja yoo han nigba ti atupale ba kojọ.",
    restockBody: (reason, quantity) =>
      `${reason} Iye ti a daba lati paṣẹ: +${quantity} unit.`,
    restockPlan: "Eto afikun ọja",
    restockPlanReview: "Ṣayẹwo eto naa",
    restockToday: (name) => `Tun ${name} kun loni`,
    reviewBranches: "Ṣayẹwo awọn ẹka",
    revenue: "Owo wọle",
    revenueLastDays: (days) => `Owo wọle · ọjọ ${days} to kọja`,
    salesCompleted: "Awọn tita ti pari",
    salesSyncing: "Data tita n ṣe sync",
    stockAlerts: "Awọn itaniji ọja",
    stockedOut: (count) => `${count} ti pari`,
    syncingBody: "Data ọja to ga julọ ko tii kojọ sibẹ.",
    topMovers: "Awọn to n yara lọ",
    topMoversMeta: "Loni · nipa unit",
    topProductLead: (name) => `${name} n lọ yara jù loni`,
    topProductLeadBody: (sold) =>
      `A ti ta unit ${sold} titi di isisiyi. Daabo bo stock yii ṣaaju rush ọsan.`,
    units: "unit",
    viewAll: "Wo gbogbo",
    viewProduct: "Wo ọja",
    viewWorkspaceFor: (name) => `O n wo workspace laaye fun ${name}.`,
    todayRevenue: "Owo wọle oni",
    paymentPaid: "Ti san",
    paymentPartlyPaid: "Ti san ni apakan",
    paymentUnpaid: "Ko ti san",
  },
  zu: {
    actionRecommended: "Isenzo esinconyiwe",
    activeBranch: (name) => `${name} yigatsha elisebenzayo`,
    askAI: "Buza i-AI",
    branch: "Igatsha",
    businessPulseDate: (date, branchName) => `${date} · ${branchName}`,
    customers: (count) => `${count} amakhasimende`,
    goodAfternoon: "Sanibonani ntambama",
    goodEvening: "Sanibonani kusihlwa",
    goodMorning: "Sawubona",
    liveAnalyticsRecommendations: "Izincomo zokuhlaziya bukhoma",
    liveInsight: "Ukuqonda bukhoma",
    newSale: "Ukuthengisa okusha",
    noBranchSelected: "Alikho igatsha elikhethiwe kulesi sikhathi okwamanje.",
    noProductMovementYet: "Akukho kuhamba kwemikhiqizo okwamanje.",
    noRecentSalesYet: "Akukho kuthengisa kwakamuva okwamanje.",
    noRestockRecommendationsYet:
      "Azikho izincomo zokwengeza isitoko okwamanje.",
    noTopProductData:
      "Imininingwane yemikhiqizo ehamba phambili ayikalayishwa okwamanje.",
    openAnalytics: "Vula ukuhlaziya",
    outstandingDebt: "Isikweletu esisele",
    recent: (count) => `${count} kwakamuva`,
    recentSales: "Imisebenzi yakamuva",
    recentSalesMeta: (count, branchName) =>
      `Ukuthengisa okungu-${count} kokugcina · ${branchName}`,
    restockAnalyticsEmpty:
      "Izincomo zokwengeza isitoko zizovela uma ukuhlaziya kulayisha.",
    restockBody: (reason, quantity) =>
      `${reason} I-oda elinconyiwe: +${quantity} amayunithi.`,
    restockPlan: "Uhlelo lokwengeza isitoko",
    restockPlanReview: "Bheka uhlelo",
    restockToday: (name) => `Yengeza i-${name} namuhla`,
    reviewBranches: "Bheka amagatsha",
    revenue: "Imali engenayo",
    revenueLastDays: (days) => `Imali engenayo · izinsuku ezi-${days} ezedlule`,
    salesCompleted: "Ukuthengisa okuqediwe",
    salesSyncing: "Idatha yokuthengisa iyavumelaniswa",
    stockAlerts: "Izexwayiso zesitoko",
    stockedOut: (count) => `${count} kuphelile`,
    syncingBody:
      "Imininingwane yemikhiqizo ehamba phambili ayikalayishwa okwamanje.",
    topMovers: "Ezihamba kakhulu",
    topMoversMeta: "Namuhla · ngamayunithi",
    topProductLead: (name) => `${name} ihamba ngokushesha kakhulu namuhla`,
    topProductLeadBody: (sold) =>
      `Kudayiswe amayunithi angu-${sold} kuze kube manje. Vikela isitoko ngaphambi kwesikhathi esimatasa.`,
    units: "amayunithi",
    viewAll: "Buka konke",
    viewProduct: "Buka umkhiqizo",
    viewWorkspaceFor: (name) => `Ubuka indawo yokusebenza ebukhoma ye-${name}.`,
    todayRevenue: "Imali engenayo yanamuhla",
    paymentPaid: "Kukhokhiwe",
    paymentPartlyPaid: "Kukhokhwe ingxenye",
    paymentUnpaid: "Akukhokhiwe",
  },
};

Object.entries(dashboardTranslations).forEach(([language, dashboard]) => {
  translations[language] = {
    ...(translations[language] || {}),
    dashboard,
  };
});

const inventoryTranslations = {
  ar: {
    action: "الإجراء",
    addProduct: "إضافة منتج",
    addQuery: (name) => `أضف "${name}"`,
    addStock: "إضافة مخزون",
    addStockFailed: "تعذر إضافة المخزون",
    addStockView: "إضافة مخزون",
    all: "الكل",
    allocation: "توزيع المخزون",
    allocationDescription: "تقسيم سريع للمخزون المتاح حسب عضو الفريق.",
    backToInventory: "العودة إلى المخزون",
    backToProductDetails: "العودة إلى تفاصيل المنتج",
    barcodeAttached: "تم إرفاق الباركود",
    barcodeModeReady: "وضع الباركود جاهز",
    barcodeModeReadyDescription: "اكتب أو الصق الرمز الممسوح في البحث.",
    barcodeNotFound: "لم يتم العثور على الباركود",
    barcodeNotFoundDescription: "أضف تفاصيل المنتج لحفظه.",
    calculate: "احسب",
    cancel: "إلغاء",
    category: "الفئة",
    checkRequiredProductDetails: "راجع تفاصيل المنتج المطلوبة",
    clearRange: "مسح النطاق",
    countNeedAttention: (count) => `${count} تحتاج إلى انتباه`,
    createProduct: "إنشاء منتج",
    createProductTitle: "إنشاء منتج",
    currentStock: "المخزون الحالي",
    currentStockIs: (stock) => `المخزون الحالي هو ${stock}.`,
    editProduct: "تعديل المنتج",
    estimatedProfit: "الربح التقديري",
    formDescription:
      "حافظ على اتساق الأسعار والباركود وضوابط المخزون مع كتالوج المتجر.",
    from: "من",
    inStock: "متوفر",
    low: "منخفض",
    lowStockAt: "حد المخزون المنخفض",
    lostPotentialSales: (amount) =>
      `تم فقدان ${amount} من المبيعات المحتملة اليوم.`,
    margin: "الهامش",
    next: "التالي",
    noBarcode: "بدون باركود",
    noProductsMatch: "لا توجد منتجات تطابق عوامل التصفية.",
    noTransactionsYet: "لا توجد معاملات بعد",
    noTransactionsYetDescription: "هذا المنتج لم يظهر في أي عملية بيع مسجلة.",
    openingStock: "المخزون الافتتاحي",
    outOfStock: "نفد من المخزون",
    owner: "المالك",
    partlyPaid: "مدفوع جزئيًا",
    paymentPaid: "مدفوع",
    paymentUnpaid: "غير مدفوع",
    pickDate: "اختر تاريخًا",
    previous: "السابق",
    price: "السعر",
    product: "المنتج",
    productDetails: "تفاصيل المنتج",
    productScanned: "تم مسح المنتج",
    productUpdated: "تم تحديث المنتج",
    productAdded: "تمت إضافة المنتج",
    productName: "اسم المنتج",
    productNamePlaceholder: "مثال: كيس حليب Peak",
    profitCalculator: "حاسبة الربح",
    quantity: "الكمية",
    recordIncomingStock: "سجل المخزون الوارد",
    revenue: "الإيراد",
    saveFailed: "تعذر حفظ المنتج",
    saveProduct: "حفظ المنتج",
    saving: "جارٍ الحفظ...",
    scan: "مسح",
    scannerReady: "الماسح جاهز",
    scannerReadyDescription: "امسح باركودًا الآن لإرفاقه بهذا المنتج.",
    scanToAttach: "امسح للإرفاق",
    searchPlaceholder: "ابحث عن المنتجات أو امسح باركودًا…",
    showing: (start, end, total) => `عرض ${start} إلى ${end} من ${total}`,
    skuCount: (count) => `${count} صنف`,
    soldToday: "المباع اليوم",
    soldTodayColumn: "المباع اليوم",
    staff: "الموظف",
    status: "الحالة",
    stock: "المخزون",
    stockOut: "نفاد مخزون",
    stockUpdated: "تم تحديث المخزون",
    subtitle: (all, attention) =>
      `${all} صنفًا · ${attention} تحتاج إلى انتباه`,
    to: "إلى",
    today: "اليوم",
    transactions: "المعاملات",
    transactionsWhen: "متى",
    tryAgain: "حاول مرة أخرى.",
    unitPrice: "سعر الوحدة",
    unitsSold: "الوحدات المباعة",
    untracked: "غير متتبع",
    viewDetails: "عرض التفاصيل",
    inventoryTitle: "المخزون",
    optional: "اختياري",
    optionalBarcode: "باركود اختياري",
    costPrice: "سعر التكلفة",
    sellingPrice: "سعر البيع",
    transactionSaleId: "رقم البيع",
    qty: "الكمية",
    amount: "المبلغ",
    cashier: "أمين الصندوق",
    manager: "المدير",
    stockAllocationBalance: "رصيد المالك والموظف والمدير",
    transactionStatus: "الحالة",
    profitRevenue: "الإيراد",
    profitCostTotal: "إجمالي التكلفة",
    profitSellingPrice: "سعر البيع",
    validDateRange: "اختر نطاق تاريخ صالحًا.",
    validation: {
      costRequired: "سعر التكلفة مطلوب",
      costValid: "يجب أن يكون سعر التكلفة رقمًا صحيحًا",
      costNonNegative: "لا يمكن أن يكون سعر التكلفة سالبًا",
      lowStockWhole: "تنبيه انخفاض المخزون يجب أن يكون رقمًا صحيحًا",
      lowStockNonNegative: "لا يمكن أن يكون تنبيه انخفاض المخزون سالبًا",
      productNameRequired: "اسم المنتج مطلوب",
      priceRequired: "سعر البيع مطلوب",
      priceValid: "يجب أن يكون سعر البيع رقمًا صحيحًا",
      priceAboveZero: "يجب أن يكون سعر البيع أكبر من صفر",
      stockRequired: "المخزون الافتتاحي مطلوب",
      stockWhole: "يجب أن يكون المخزون الافتتاحي رقمًا صحيحًا",
      stockNonNegative: "لا يمكن أن يكون المخزون الافتتاحي سالبًا",
    },
  },
  fr: {
    action: "Action",
    addProduct: "Ajouter un produit",
    addQuery: (name) => `Ajouter "${name}"`,
    addStock: "Ajouter du stock",
    addStockFailed: "Impossible d’ajouter du stock",
    addStockView: "Ajouter du stock",
    all: "Tout",
    allocation: "Répartition du stock",
    allocationDescription:
      "Une répartition rapide du stock disponible par membre de l’équipe.",
    backToInventory: "Retour au stock",
    backToProductDetails: "Retour aux détails du produit",
    barcodeAttached: "Code-barres attaché",
    barcodeModeReady: "Mode code-barres prêt",
    barcodeModeReadyDescription:
      "Saisissez ou collez le code scanné dans la recherche.",
    barcodeNotFound: "Code-barres introuvable",
    barcodeNotFoundDescription:
      "Ajoutez les détails du produit pour l’enregistrer.",
    calculate: "Calculer",
    cancel: "Annuler",
    category: "Catégorie",
    checkRequiredProductDetails: "Vérifiez les détails requis du produit",
    clearRange: "Effacer la période",
    countNeedAttention: (count) => `${count} nécessitent une attention`,
    createProduct: "Créer le produit",
    createProductTitle: "Créer un produit",
    currentStock: "Stock actuel",
    currentStockIs: (stock) => `Le stock actuel est ${stock}.`,
    editProduct: "Modifier le produit",
    estimatedProfit: "Profit estimé",
    formDescription:
      "Gardez les prix, le code-barres et les contrôles de stock alignés sur le catalogue de la boutique.",
    from: "Du",
    inStock: "En stock",
    low: "Faible",
    lowStockAt: "Seuil stock bas",
    lostPotentialSales: (amount) =>
      `${amount} de ventes potentielles perdues aujourd’hui.`,
    margin: "Marge",
    next: "Suivant",
    noBarcode: "pas de code-barres",
    noProductsMatch: "Aucun produit ne correspond à vos filtres.",
    noTransactionsYet: "Aucune transaction pour l’instant",
    noTransactionsYetDescription:
      "Ce produit n’est apparu dans aucune vente enregistrée.",
    openingStock: "Stock initial",
    outOfStock: "Rupture de stock",
    owner: "Propriétaire",
    partlyPaid: "Partiellement payé",
    paymentPaid: "Payé",
    paymentUnpaid: "Impayé",
    pickDate: "Choisir une date",
    previous: "Précédent",
    price: "Prix",
    product: "Produit",
    productDetails: "Détails du produit",
    productScanned: "Produit scanné",
    productUpdated: "Produit mis à jour",
    productAdded: "Produit ajouté",
    productName: "Nom du produit",
    productNamePlaceholder: "ex. sachet de lait Peak",
    profitCalculator: "Calculateur de profit",
    quantity: "Quantité",
    recordIncomingStock: "Enregistrer le stock entrant",
    revenue: "Revenu",
    saveFailed: "Impossible d’enregistrer le produit",
    saveProduct: "Enregistrer le produit",
    saving: "Enregistrement...",
    scan: "Scanner",
    scannerReady: "Scanner prêt",
    scannerReadyDescription:
      "Scannez maintenant un code-barres pour l’attacher à ce produit.",
    scanToAttach: "Scanner pour joindre",
    searchPlaceholder: "Rechercher des produits ou scanner un code-barres…",
    showing: (start, end, total) =>
      `Affichage de ${start} à ${end} sur ${total}`,
    skuCount: (count) => `${count} SKU`,
    soldToday: "Vendu aujourd’hui",
    soldTodayColumn: "Vendu aujourd’hui",
    staff: "Employé",
    status: "Statut",
    stock: "Stock",
    stockOut: "Rupture",
    stockUpdated: "Stock mis à jour",
    subtitle: (all, attention) =>
      `${all} SKU · ${attention} nécessitent une attention`,
    to: "Au",
    today: "Aujourd’hui",
    transactions: "Transactions",
    transactionsWhen: "Quand",
    tryAgain: "Réessayez.",
    unitPrice: "Prix unitaire",
    unitsSold: "Unités vendues",
    untracked: "non suivi",
    viewDetails: "Voir les détails",
    inventoryTitle: "Stock",
    optional: "optionnel",
    optionalBarcode: "Code-barres optionnel",
    costPrice: "Prix de revient",
    sellingPrice: "Prix de vente",
    transactionSaleId: "ID vente",
    qty: "Qté",
    amount: "Montant",
    cashier: "Caissier",
    manager: "Manager",
    stockAllocationBalance: "Répartition propriétaire, employé et manager",
    transactionStatus: "Statut",
    profitRevenue: "Revenu",
    profitCostTotal: "Coût total",
    profitSellingPrice: "Prix de vente",
    validDateRange: "Choisissez une période valide.",
    validation: {
      costRequired: "Le prix de revient est requis",
      costValid: "Le prix de revient doit être valide",
      costNonNegative: "Le prix de revient ne peut pas être négatif",
      lowStockWhole: "L’alerte de stock bas doit être un nombre entier",
      lowStockNonNegative: "L’alerte de stock bas ne peut pas être négative",
      productNameRequired: "Le nom du produit est requis",
      priceRequired: "Le prix de vente est requis",
      priceValid: "Le prix de vente doit être valide",
      priceAboveZero: "Le prix de vente doit être supérieur à zéro",
      stockRequired: "Le stock initial est requis",
      stockWhole: "Le stock initial doit être un nombre entier",
      stockNonNegative: "Le stock initial ne peut pas être négatif",
    },
  },
  ha: {
    action: "Aiki",
    addProduct: "Kara kaya",
    addQuery: (name) => `Kara "${name}"`,
    addStock: "Kara kaya",
    addStockFailed: "Ba a iya kara kaya ba",
    addStockView: "Kara kaya",
    all: "Duka",
    allocation: "Rarraba kaya",
    allocationDescription:
      "Rarrabawa cikin sauri na kayan da suke akwai ga ma’aikata.",
    backToInventory: "Komawa kaya",
    backToProductDetails: "Komawa bayanan kaya",
    barcodeAttached: "An daura barcode",
    barcodeModeReady: "Yanayin barcode ya shirya",
    barcodeModeReadyDescription:
      "Rubuta ko manna lambar da aka scan a bincike.",
    barcodeNotFound: "Ba a samu barcode ba",
    barcodeNotFoundDescription: "Cika bayanan kaya don a ajiye shi.",
    calculate: "Lissafa",
    cancel: "Soke",
    category: "Rukuni",
    checkRequiredProductDetails: "Duba bayanan kayan da ake bukata",
    clearRange: "Goge zangon lokaci",
    countNeedAttention: (count) => `${count} na bukatar kulawa`,
    createProduct: "Kirkiro kaya",
    createProductTitle: "Kirkiro kaya",
    currentStock: "Kayan da ke akwai yanzu",
    currentStockIs: (stock) => `Kayan da ke akwai yanzu ${stock}.`,
    editProduct: "Gyara kaya",
    estimatedProfit: "Ribar da aka kiyasta",
    formDescription:
      "Daidaita farashi, barcode, da sarrafa kaya da kundin shago.",
    from: "Daga",
    inStock: "A stock",
    low: "Kadan",
    lowStockAt: "Karancin kaya a",
    lostPotentialSales: (amount) =>
      `An rasa ${amount} na yuwuwar tallace-tallace yau.`,
    margin: "Riba",
    next: "Na gaba",
    noBarcode: "babu barcode",
    noProductsMatch: "Babu kaya da suka dace da tacewar ka.",
    noTransactionsYet: "Babu ma’amala tukuna",
    noTransactionsYetDescription:
      "Wannan kayan bai bayyana a wata sayarwa da aka rubuta ba.",
    openingStock: "Kayan farko",
    outOfStock: "Kaya ya kare",
    owner: "Mai shago",
    partlyPaid: "An biya wani bangare",
    paymentPaid: "An biya",
    paymentUnpaid: "Ba a biya ba",
    pickDate: "Zabi rana",
    previous: "Na baya",
    price: "Farashi",
    product: "Kaya",
    productDetails: "Bayanan kaya",
    productScanned: "An yi scan kaya",
    productUpdated: "An sabunta kaya",
    productAdded: "An kara kaya",
    productName: "Sunan kaya",
    productNamePlaceholder: "misali Sample product",
    profitCalculator: "Na’urar lissafin riba",
    quantity: "Yawa",
    recordIncomingStock: "Rubuta kayan da suka shigo",
    revenue: "Kudin shiga",
    saveFailed: "Ba a iya ajiye kayan ba",
    saveProduct: "Ajiye kaya",
    saving: "Ana ajiyewa...",
    scan: "Scan",
    scannerReady: "Scanner ya shirya",
    scannerReadyDescription:
      "Yi scan barcode yanzu don daura shi da wannan kaya.",
    scanToAttach: "Yi scan don daurawa",
    searchPlaceholder: "Binciki kaya ko yi scan barcode…",
    showing: (start, end, total) =>
      `Ana nuna ${start} zuwa ${end} cikin ${total}`,
    skuCount: (count) => `SKU ${count}`,
    soldToday: "An sayar yau",
    soldTodayColumn: "An sayar yau",
    staff: "Ma’aikaci",
    status: "Matsayi",
    stock: "Kaya",
    stockOut: "Kaya ya kare",
    stockUpdated: "An sabunta kaya",
    subtitle: (all, attention) => `${all} SKU · ${attention} na bukatar kulawa`,
    to: "Zuwa",
    today: "Yau",
    transactions: "Ma’amaloli",
    transactionsWhen: "Yaushe",
    tryAgain: "Sake gwadawa.",
    unitPrice: "Farashin guda",
    unitsSold: "Adadin da aka sayar",
    untracked: "ba a bibiyarsa",
    viewDetails: "Duba bayanai",
    inventoryTitle: "Kaya",
    optional: "na zabi",
    optionalBarcode: "Barcode na zabi",
    costPrice: "Farashin sayo",
    sellingPrice: "Farashin sayarwa",
    transactionSaleId: "ID sayarwa",
    qty: "Yawa",
    amount: "Kudi",
    cashier: "Mai karbar kudi",
    manager: "Manaja",
    stockAllocationBalance: "Rabon mai shago, ma’aikaci, da manaja",
    transactionStatus: "Matsayi",
    profitRevenue: "Kudin shiga",
    profitCostTotal: "Jimillar farashin sayo",
    profitSellingPrice: "Farashin sayarwa",
    validDateRange: "Zabi ingantaccen zangon lokaci.",
    validation: {
      costRequired: "Ana bukatar farashin sayo",
      costValid: "Farashin sayo dole ya zama daidai",
      costNonNegative: "Farashin sayo ba zai zama kasa da sifili ba",
      lowStockWhole: "Gargadin karancin kaya dole ya zama cikakken lamba",
      lowStockNonNegative:
        "Gargadin karancin kaya ba zai zama kasa da sifili ba",
      productNameRequired: "Ana bukatar sunan kaya",
      priceRequired: "Ana bukatar farashin sayarwa",
      priceValid: "Farashin sayarwa dole ya zama daidai",
      priceAboveZero: "Farashin sayarwa dole ya fi sifili",
      stockRequired: "Ana bukatar kayan farko",
      stockWhole: "Kayan farko dole ya zama cikakken lamba",
      stockNonNegative: "Kayan farko ba zai zama kasa da sifili ba",
    },
  },
  ig: {
    action: "Omume",
    addProduct: "Tinye ngwaahịa",
    addQuery: (name) => `Tinye "${name}"`,
    addStock: "Tinye ngwa ahia",
    addStockFailed: "Enweghị ike itinye ngwa ahia",
    addStockView: "Tinye ngwa ahia",
    all: "Niile",
    allocation: "Nkesa ngwa ahia",
    allocationDescription: "Nkesa ngwa ngwa nke ngwa ahia dị n’etiti ndị otu.",
    backToInventory: "Laghachi na ngwa ahia",
    backToProductDetails: "Laghachi na nkọwa ngwaahịa",
    barcodeAttached: "Ejikọtara barcode",
    barcodeModeReady: "Ụdị barcode adịla njikere",
    barcodeModeReadyDescription: "Pịnye ma ọ bụ tapawa koodu a scan n’ọchọ.",
    barcodeNotFound: "Ahụghị barcode",
    barcodeNotFoundDescription: "Tinye nkọwa ngwaahịa iji chekwaa ya.",
    calculate: "Gụọ",
    cancel: "Kagbuo",
    category: "Udi",
    checkRequiredProductDetails: "Lelee nkọwa ngwaahịa achọrọ",
    clearRange: "Hichapụ oge",
    countNeedAttention: (count) => `${count} chọrọ nlebara anya`,
    createProduct: "Mepụta ngwaahịa",
    createProductTitle: "Mepụta ngwaahịa",
    currentStock: "Ngwa ahia ugbu a",
    currentStockIs: (stock) => `Ngwa ahia ugbu a bụ ${stock}.`,
    editProduct: "Dezie ngwaahịa",
    estimatedProfit: "Uru a tụrụ anya",
    formDescription:
      "Debe ọnụahịa, barcode, na njikwa ngwa ahia ka ha dakọtara na katalọgụ ụlọ ahịa.",
    from: "Site na",
    inStock: "Dị na stock",
    low: "Dị ala",
    lowStockAt: "Stock dị ala na",
    lostPotentialSales: (amount) => `Furu ${amount} nke ire pụrụ ime taa.`,
    margin: "Majiin",
    next: "Na-esote",
    noBarcode: "enweghị barcode",
    noProductsMatch: "Enweghị ngwaahịa kwekọrọ na nzacha gị.",
    noTransactionsYet: "Enweghị ahịa ka",
    noTransactionsYetDescription: "Ngwaahịa a apụtaghị na ire ọ bụla e dere.",
    openingStock: "Ngwa ahia mbido",
    outOfStock: "Ngwa ahia agwụla",
    owner: "Onye nwe ya",
    partlyPaid: "Akwụgoro akụkụ",
    paymentPaid: "Akwụgoro",
    paymentUnpaid: "Akwụghị",
    pickDate: "Họrọ ụbọchị",
    previous: "Nke gara aga",
    price: "Ọnụ ahịa",
    product: "Ngwaahịa",
    productDetails: "Nkọwa ngwaahịa",
    productScanned: "E-scanala ngwaahịa",
    productUpdated: "Emegharịrị ngwaahịa",
    productAdded: "Etinyela ngwaahịa",
    productName: "Aha ngwaahịa",
    productNamePlaceholder: "dịka Sample product",
    profitCalculator: "Ngwa mgbakọ uru",
    quantity: "Ọnụọgụ",
    recordIncomingStock: "Dekọọ ngwa ahia na-abata",
    revenue: "Ego mbata",
    saveFailed: "Enweghị ike ichekwa ngwaahịa",
    saveProduct: "Chekwa ngwaahịa",
    saving: "Na-echekwa...",
    scan: "Scan",
    scannerReady: "Scanner adịla njikere",
    scannerReadyDescription: "Scan barcode ugbu a iji jikọta ya na ngwaahịa a.",
    scanToAttach: "Scan iji jikọta",
    searchPlaceholder: "Chọọ ngwaahịa ma ọ bụ scan barcode…",
    showing: (start, end, total) =>
      `Na-egosi ${start} ruo ${end} n’ime ${total}`,
    skuCount: (count) => `SKU ${count}`,
    soldToday: "E rere taa",
    soldTodayColumn: "E rere taa",
    staff: "Onye ọrụ",
    status: "Ọnọdụ",
    stock: "Ngwa ahia",
    stockOut: "Ngwa ahia agwụla",
    stockUpdated: "E megharịrị ngwa ahia",
    subtitle: (all, attention) =>
      `${all} SKU · ${attention} chọrọ nlebara anya`,
    to: "Ruo",
    today: "Taa",
    transactions: "Mmekọrịta ire",
    transactionsWhen: "Mgbe",
    tryAgain: "Nwaa ọzọ.",
    unitPrice: "Ọnụ ahịa otu",
    unitsSold: "Ngwaahịa erere",
    untracked: "a naghị eso ya",
    viewDetails: "Lee nkọwa",
    inventoryTitle: "Ngwaahịa",
    optional: "nhọrọ",
    optionalBarcode: "Barcode nhọrọ",
    costPrice: "Ọnụ ahịa ịzụta",
    sellingPrice: "Ọnụ ahịa ire",
    transactionSaleId: "ID ire",
    qty: "Ọgụgụ",
    amount: "Ego",
    cashier: "Onye na-anakọta ego",
    manager: "Onye njikwa",
    stockAllocationBalance: "Nke onye nwe ya, onye ọrụ, na onye njikwa",
    transactionStatus: "Ọnọdụ",
    profitRevenue: "Ego mbata",
    profitCostTotal: "Ngụkọta ụgwọ",
    profitSellingPrice: "Ọnụ ahịa ire",
    validDateRange: "Họrọ oge dị mma.",
    validation: {
      costRequired: "A chọrọ ọnụahịa ịzụta",
      costValid: "Ọnụahịa ịzụta ga-abụ nke ziri ezi",
      costNonNegative: "Ọnụahịa ịzụta agaghị erughị efu",
      lowStockWhole: "Ọkwa ncheta ngwaahịa dị ala ga-abụ nọmba zuru ezu",
      lowStockNonNegative: "Ọkwa ncheta ngwaahịa dị ala agaghị erughị efu",
      productNameRequired: "A chọrọ aha ngwaahịa",
      priceRequired: "A chọrọ ọnụahịa ire",
      priceValid: "Ọnụahịa ire ga-abụ nke ziri ezi",
      priceAboveZero: "Ọnụahịa ire ga-adị elu karịa efu",
      stockRequired: "A chọrọ ngwa ahia mbido",
      stockWhole: "Ngwa ahia mbido ga-abụ nọmba zuru ezu",
      stockNonNegative: "Ngwa ahia mbido agaghị erughị efu",
    },
  },
  pt: {
    action: "Ação",
    addProduct: "Adicionar produto",
    addQuery: (name) => `Adicionar "${name}"`,
    addStock: "Adicionar stock",
    addStockFailed: "Não foi possível adicionar stock",
    addStockView: "Adicionar stock",
    all: "Todos",
    allocation: "Distribuição de stock",
    allocationDescription:
      "Uma divisão rápida do stock disponível por membro da equipa.",
    backToInventory: "Voltar ao inventário",
    backToProductDetails: "Voltar aos detalhes do produto",
    barcodeAttached: "Código de barras associado",
    barcodeModeReady: "Modo código de barras pronto",
    barcodeModeReadyDescription:
      "Escreva ou cole o código digitalizado na pesquisa.",
    barcodeNotFound: "Código de barras não encontrado",
    barcodeNotFoundDescription:
      "Adicione os detalhes do produto para o guardar.",
    calculate: "Calcular",
    cancel: "Cancelar",
    category: "Categoria",
    checkRequiredProductDetails:
      "Verifique os detalhes obrigatórios do produto",
    clearRange: "Limpar período",
    countNeedAttention: (count) => `${count} precisam de atenção`,
    createProduct: "Criar produto",
    createProductTitle: "Criar produto",
    currentStock: "Stock atual",
    currentStockIs: (stock) => `O stock atual é ${stock}.`,
    editProduct: "Editar produto",
    estimatedProfit: "Lucro estimado",
    formDescription:
      "Mantenha preços, código de barras e controlo de stock alinhados com o catálogo da loja.",
    from: "De",
    inStock: "Em stock",
    low: "Baixo",
    lowStockAt: "Stock baixo em",
    lostPotentialSales: (amount) =>
      `${amount} perdidos em vendas potenciais hoje.`,
    margin: "Margem",
    next: "Seguinte",
    noBarcode: "sem código de barras",
    noProductsMatch: "Nenhum produto corresponde aos seus filtros.",
    noTransactionsYet: "Ainda sem transações",
    noTransactionsYetDescription:
      "Este produto ainda não apareceu em nenhuma venda registada.",
    openingStock: "Stock inicial",
    outOfStock: "Sem stock",
    owner: "Proprietário",
    partlyPaid: "Parcialmente pago",
    paymentPaid: "Pago",
    paymentUnpaid: "Não pago",
    pickDate: "Escolher uma data",
    previous: "Anterior",
    price: "Preço",
    product: "Produto",
    productDetails: "Detalhes do produto",
    productScanned: "Produto digitalizado",
    productUpdated: "Produto atualizado",
    productAdded: "Produto adicionado",
    productName: "Nome do produto",
    productNamePlaceholder: "ex. Sample product",
    profitCalculator: "Calculadora de lucro",
    quantity: "Quantidade",
    recordIncomingStock: "Registar stock recebido",
    revenue: "Receita",
    saveFailed: "Não foi possível guardar o produto",
    saveProduct: "Guardar produto",
    saving: "A guardar...",
    scan: "Ler",
    scannerReady: "Leitor pronto",
    scannerReadyDescription:
      "Leia agora um código de barras para o associar a este produto.",
    scanToAttach: "Ler para associar",
    searchPlaceholder: "Pesquisar produtos ou ler código de barras…",
    showing: (start, end, total) => `A mostrar ${start} a ${end} de ${total}`,
    skuCount: (count) => `${count} SKU`,
    soldToday: "Vendido hoje",
    soldTodayColumn: "Vendido hoje",
    staff: "Funcionário",
    status: "Estado",
    stock: "Stock",
    stockOut: "Rutura de stock",
    stockUpdated: "Stock atualizado",
    subtitle: (all, attention) =>
      `${all} SKU · ${attention} precisam de atenção`,
    to: "Até",
    today: "Hoje",
    transactions: "Transações",
    transactionsWhen: "Quando",
    tryAgain: "Tente novamente.",
    unitPrice: "Preço unitário",
    unitsSold: "Unidades vendidas",
    untracked: "não rastreado",
    viewDetails: "Ver detalhes",
    inventoryTitle: "Inventário",
    optional: "opcional",
    optionalBarcode: "Código de barras opcional",
    costPrice: "Preço de custo",
    sellingPrice: "Preço de venda",
    transactionSaleId: "ID da venda",
    qty: "Qtd",
    amount: "Valor",
    cashier: "Caixa",
    manager: "Gestor",
    stockAllocationBalance:
      "Equilíbrio entre proprietário, funcionário e gestor",
    transactionStatus: "Estado",
    profitRevenue: "Receita",
    profitCostTotal: "Custo total",
    profitSellingPrice: "Preço de venda",
    validDateRange: "Escolha um intervalo de datas válido.",
    validation: {
      costRequired: "O preço de custo é obrigatório",
      costValid: "O preço de custo deve ser válido",
      costNonNegative: "O preço de custo não pode ser negativo",
      lowStockWhole: "O alerta de stock baixo deve ser um número inteiro",
      lowStockNonNegative: "O alerta de stock baixo não pode ser negativo",
      productNameRequired: "O nome do produto é obrigatório",
      priceRequired: "O preço de venda é obrigatório",
      priceValid: "O preço de venda deve ser válido",
      priceAboveZero: "O preço de venda deve ser maior que zero",
      stockRequired: "O stock inicial é obrigatório",
      stockWhole: "O stock inicial deve ser um número inteiro",
      stockNonNegative: "O stock inicial não pode ser negativo",
    },
  },
  sw: {
    action: "Hatua",
    addProduct: "Ongeza bidhaa",
    addQuery: (name) => `Ongeza "${name}"`,
    addStock: "Ongeza stock",
    addStockFailed: "Haikuwezekana kuongeza stock",
    addStockView: "Ongeza stock",
    all: "Zote",
    allocation: "Mgawanyo wa stock",
    allocationDescription:
      "Mgawanyo wa haraka wa stock iliyopo kwa kila mfanyakazi.",
    backToInventory: "Rudi kwenye inventory",
    backToProductDetails: "Rudi kwenye maelezo ya bidhaa",
    barcodeAttached: "Barcode imeunganishwa",
    barcodeModeReady: "Hali ya barcode iko tayari",
    barcodeModeReadyDescription:
      "Andika au bandika code iliyoskaniwa kwenye utafutaji.",
    barcodeNotFound: "Barcode haikupatikana",
    barcodeNotFoundDescription: "Ongeza maelezo ya bidhaa ili kuihifadhi.",
    calculate: "Hesabu",
    cancel: "Ghairi",
    category: "Kategoria",
    checkRequiredProductDetails: "Angalia maelezo muhimu ya bidhaa",
    clearRange: "Futa kipindi",
    countNeedAttention: (count) => `${count} zinahitaji uangalizi`,
    createProduct: "Tengeneza bidhaa",
    createProductTitle: "Tengeneza bidhaa",
    currentStock: "Stock ya sasa",
    currentStockIs: (stock) => `Stock ya sasa ni ${stock}.`,
    editProduct: "Hariri bidhaa",
    estimatedProfit: "Faida inayokadiriwa",
    formDescription:
      "Weka bei, barcode, na udhibiti wa stock viendane na katalogi ya duka.",
    from: "Kutoka",
    inStock: "Kwenye stock",
    low: "Chini",
    lowStockAt: "Stock ndogo kwenye",
    lostPotentialSales: (amount) =>
      `Imepoteza ${amount} ya mauzo yanayowezekana leo.`,
    margin: "Faida",
    next: "Inayofuata",
    noBarcode: "hakuna barcode",
    noProductsMatch: "Hakuna bidhaa zinazolingana na vichujio vyako.",
    noTransactionsYet: "Hakuna miamala bado",
    noTransactionsYetDescription:
      "Bidhaa hii haijaonekana kwenye mauzo yoyote yaliyorekodiwa.",
    openingStock: "Stock ya kuanzia",
    outOfStock: "Imeisha stock",
    owner: "Mmiliki",
    partlyPaid: "Imelipwa sehemu",
    paymentPaid: "Imelipwa",
    paymentUnpaid: "Haijalipwa",
    pickDate: "Chagua tarehe",
    previous: "Iliyopita",
    price: "Bei",
    product: "Bidhaa",
    productDetails: "Maelezo ya bidhaa",
    productScanned: "Bidhaa imeskaniwa",
    productUpdated: "Bidhaa imesasishwa",
    productAdded: "Bidhaa imeongezwa",
    productName: "Jina la bidhaa",
    productNamePlaceholder: "mf. Sample product",
    profitCalculator: "Kikokotoo cha faida",
    quantity: "Kiasi",
    recordIncomingStock: "Rekodi stock iliyoingia",
    revenue: "Mapato",
    saveFailed: "Haikuwezekana kuhifadhi bidhaa",
    saveProduct: "Hifadhi bidhaa",
    saving: "Inahifadhiwa...",
    scan: "Skana",
    scannerReady: "Skana iko tayari",
    scannerReadyDescription:
      "Skana barcode sasa ili uiunganishe na bidhaa hii.",
    scanToAttach: "Skana kuunganisha",
    searchPlaceholder: "Tafuta bidhaa au skana barcode…",
    showing: (start, end, total) =>
      `Inaonyesha ${start} hadi ${end} kati ya ${total}`,
    skuCount: (count) => `SKU ${count}`,
    soldToday: "Zilizouzwa leo",
    soldTodayColumn: "Zilizouzwa leo",
    staff: "Mfanyakazi",
    status: "Hali",
    stock: "Stock",
    stockOut: "Imeisha stock",
    stockUpdated: "Stock imesasishwa",
    subtitle: (all, attention) =>
      `${all} SKU · ${attention} zinahitaji uangalizi`,
    to: "Hadi",
    today: "Leo",
    transactions: "Miamala",
    transactionsWhen: "Lini",
    tryAgain: "Jaribu tena.",
    unitPrice: "Bei ya moja",
    unitsSold: "Vipimo vilivyouzwa",
    untracked: "haifuatiliwi",
    viewDetails: "Tazama maelezo",
    inventoryTitle: "Bidhaa",
    optional: "hiari",
    optionalBarcode: "Barcode ya hiari",
    costPrice: "Bei ya kununua",
    sellingPrice: "Bei ya kuuza",
    transactionSaleId: "ID ya mauzo",
    qty: "Idadi",
    amount: "Kiasi",
    cashier: "Muuzaji",
    manager: "Meneja",
    stockAllocationBalance: "Uwiano wa mmiliki, mfanyakazi, na meneja",
    transactionStatus: "Hali",
    profitRevenue: "Mapato",
    profitCostTotal: "Jumla ya gharama",
    profitSellingPrice: "Bei ya kuuza",
    validDateRange: "Chagua kipindi sahihi cha tarehe.",
    validation: {
      costRequired: "Bei ya kununua inahitajika",
      costValid: "Bei ya kununua lazima iwe sahihi",
      costNonNegative: "Bei ya kununua haiwezi kuwa hasi",
      lowStockWhole: "Tahadhari ya stock ndogo lazima iwe namba kamili",
      lowStockNonNegative: "Tahadhari ya stock ndogo haiwezi kuwa hasi",
      productNameRequired: "Jina la bidhaa linahitajika",
      priceRequired: "Bei ya kuuza inahitajika",
      priceValid: "Bei ya kuuza lazima iwe sahihi",
      priceAboveZero: "Bei ya kuuza lazima iwe juu ya sifuri",
      stockRequired: "Stock ya kuanzia inahitajika",
      stockWhole: "Stock ya kuanzia lazima iwe namba kamili",
      stockNonNegative: "Stock ya kuanzia haiwezi kuwa hasi",
    },
  },
  yo: {
    action: "Iṣe",
    addProduct: "Fi ọja kun",
    addQuery: (name) => `Fi "${name}" kun`,
    addStock: "Fi ọja kun",
    addStockFailed: "Ko ṣee ṣe lati fi ọja kun",
    addStockView: "Fi ọja kun",
    all: "Gbogbo",
    allocation: "Pínpin ọja",
    allocationDescription: "Pínpin kiakia ti ọja to wa fun awọn ọmọ ẹgbẹ.",
    backToInventory: "Pada si ọja",
    backToProductDetails: "Pada si alaye ọja",
    barcodeAttached: "A ti so barcode pọ",
    barcodeModeReady: "Ipo barcode ti setan",
    barcodeModeReadyDescription: "Kọ tabi lẹ code ti a scan sinu wiwa.",
    barcodeNotFound: "A ko ri barcode",
    barcodeNotFoundDescription: "Fi alaye ọja kun lati fipamọ.",
    calculate: "Ṣiro",
    cancel: "Fagilee",
    category: "Ẹka",
    checkRequiredProductDetails: "Ṣayẹwo alaye ọja pataki",
    clearRange: "Nu akoko",
    countNeedAttention: (count) => `${count} nilo akiyesi`,
    createProduct: "Ṣẹda ọja",
    createProductTitle: "Ṣẹda ọja",
    currentStock: "Ọja lọwọlọwọ",
    currentStockIs: (stock) => `Ọja lọwọlọwọ jẹ ${stock}.`,
    editProduct: "Ṣatunkọ ọja",
    estimatedProfit: "Èrè tí a fojú dí",
    formDescription:
      "Jẹ ki iye owo, barcode, ati iṣakoso ọja ba katalogi ile itaja mu.",
    from: "Lati",
    inStock: "Wa ninu ọja",
    low: "Kere",
    lowStockAt: "Ọja kere ni",
    lostPotentialSales: (amount) =>
      `A padanu ${amount} ninu tita to ṣee ṣe loni.`,
    margin: "Èrè",
    next: "Tókàn",
    noBarcode: "ko si barcode",
    noProductsMatch: "Ko si ọja to ba awọn àlẹmọ rẹ mu.",
    noTransactionsYet: "Ko si iṣowo sibẹ",
    noTransactionsYetDescription:
      "Ọja yii ko iti han ninu tita kankan ti a ti gba silẹ.",
    openingStock: "Ọja ibẹrẹ",
    outOfStock: "Ọja ti tan",
    owner: "Oniwun",
    partlyPaid: "A ti san apakan",
    paymentPaid: "Ti san",
    paymentUnpaid: "A ko san",
    pickDate: "Yan ọjọ",
    previous: "Tẹlẹ",
    price: "Iye",
    product: "Ọja",
    productDetails: "Alaye ọja",
    productScanned: "A ti scan ọja",
    productUpdated: "A ti ṣe imudojuiwọn ọja",
    productAdded: "A fi ọja kun",
    productName: "Orukọ ọja",
    productNamePlaceholder: "e.g. Sample product",
    profitCalculator: "Ẹrọ iṣiro èrè",
    quantity: "Iye",
    recordIncomingStock: "Gba ọja to wọle silẹ",
    revenue: "Owo wọle",
    saveFailed: "Ko ṣee ṣe lati fi ọja pamọ",
    saveProduct: "Fi ọja pamọ",
    saving: "N fipamọ...",
    scan: "Ṣe scan",
    scannerReady: "Scanner ti setan",
    scannerReadyDescription: "Ṣe scan barcode bayii lati so mọ ọja yii.",
    scanToAttach: "Scan lati so pọ",
    searchPlaceholder: "Wa awọn ọja tabi ṣe scan barcode…",
    showing: (start, end, total) => `N fihan ${start} si ${end} ninu ${total}`,
    skuCount: (count) => `SKU ${count}`,
    soldToday: "Ti ta loni",
    soldTodayColumn: "Ti ta loni",
    staff: "Osise",
    status: "Ipo",
    stock: "Ọja",
    stockOut: "Ọja ti tan",
    stockUpdated: "A ti ṣe imudojuiwọn ọja",
    subtitle: (all, attention) => `${all} SKU · ${attention} nilo akiyesi`,
    to: "Si",
    today: "Loni",
    transactions: "Awọn iṣowo",
    transactionsWhen: "Nigbawo",
    tryAgain: "Gbiyanju lẹẹkansi.",
    unitPrice: "Iye ọkan",
    unitsSold: "Awọn ti a ta",
    untracked: "a ko tọpinpin",
    viewDetails: "Wo alaye",
    inventoryTitle: "Ọja",
    optional: "iyan",
    optionalBarcode: "Barcode iyan",
    costPrice: "Iye rira",
    sellingPrice: "Iye tita",
    transactionSaleId: "ID tita",
    qty: "Iye",
    amount: "Iye owo",
    cashier: "Olugba owo",
    manager: "Alakoso",
    stockAllocationBalance: "Iwọn oniwu, osise, ati alakoso",
    transactionStatus: "Ipo",
    profitRevenue: "Owo wọle",
    profitCostTotal: "Lapapọ iye rira",
    profitSellingPrice: "Iye tita",
    validDateRange: "Yan akoko ọjọ to pe.",
    validation: {
      costRequired: "A nilo iye rira",
      costValid: "Iye rira gbọdọ jẹ deede",
      costNonNegative: "Iye rira ko le jẹ odi",
      lowStockWhole: "Ikilọ iṣura kekere gbọdọ jẹ nọmba pipe",
      lowStockNonNegative: "Ikilọ iṣura kekere ko le jẹ odi",
      productNameRequired: "A nilo orukọ ọja",
      priceRequired: "A nilo iye tita",
      priceValid: "Iye tita gbọdọ jẹ deede",
      priceAboveZero: "Iye tita gbọdọ ju odo lọ",
      stockRequired: "A nilo ọja ibẹrẹ",
      stockWhole: "Ọja ibẹrẹ gbọdọ jẹ nọmba pipe",
      stockNonNegative: "Ọja ibẹrẹ ko le jẹ odi",
    },
  },
  zu: {
    action: "Isenzo",
    addProduct: "Engeza umkhiqizo",
    addQuery: (name) => `Engeza "${name}"`,
    addStock: "Engeza isitoko",
    addStockFailed: "Akukwazekanga ukwengeza isitoko",
    addStockView: "Engeza isitoko",
    all: "Konke",
    allocation: "Ukuhlukanisa isitoko",
    allocationDescription:
      "Ukuhlukanisa ngokushesha isitoko esikhona kumalungu eqembu.",
    backToInventory: "Buyela esitokweni",
    backToProductDetails: "Buyela emininingwaneni yomkhiqizo",
    barcodeAttached: "Ibhakhodi linamathiselwe",
    barcodeModeReady: "Imodi yebhakhodi ilungile",
    barcodeModeReadyDescription:
      "Thayipha noma unamathisele ikhodi eskeniwe ekusesheni.",
    barcodeNotFound: "Ibhakhodi alitholakalanga",
    barcodeNotFoundDescription:
      "Engeza imininingwane yomkhiqizo ukuze uwulondoloze.",
    calculate: "Bala",
    cancel: "Khansela",
    category: "Isigaba",
    checkRequiredProductDetails: "Hlola imininingwane yomkhiqizo edingekayo",
    clearRange: "Sula ibanga",
    countNeedAttention: (count) => `${count} kudinga ukunakwa`,
    createProduct: "Dala umkhiqizo",
    createProductTitle: "Dala umkhiqizo",
    currentStock: "Isitoko samanje",
    currentStockIs: (stock) => `Isitoko samanje singu-${stock}.`,
    editProduct: "Hlela umkhiqizo",
    estimatedProfit: "Inzuzo elinganisiwe",
    formDescription:
      "Gcina amanani, ibhakhodi, nokulawulwa kwesitoko kuhambisana nekhatalogi yesitolo.",
    from: "Kusuka",
    inStock: "Esitokweni",
    low: "Phansi",
    lowStockAt: "Isitoko siphansi ku",
    lostPotentialSales: (amount) =>
      `Kulahleke ${amount} ekuthengiseni okungenzeka namuhla.`,
    margin: "Umkhawulo",
    next: "Okulandelayo",
    noBarcode: "alikho ibhakhodi",
    noProductsMatch: "Ayikho imikhiqizo ehambisana nezihlungi zakho.",
    noTransactionsYet: "Akukho kuthengiselana okwamanje",
    noTransactionsYetDescription:
      "Lo mkhiqizo awukaveli kunoma yikuphi ukuthengisa okurekhodiwe.",
    openingStock: "Isitoko sokuqala",
    outOfStock: "Kuphelile esitokweni",
    owner: "Umnikazi",
    partlyPaid: "Kukhokhwe ingxenye",
    paymentPaid: "Kukhokhiwe",
    paymentUnpaid: "Akukhokhiwe",
    pickDate: "Khetha usuku",
    previous: "Okwedlule",
    price: "Intengo",
    product: "Umkhiqizo",
    productDetails: "Imininingwane yomkhiqizo",
    productScanned: "Umkhiqizo uskeniwe",
    productUpdated: "Umkhiqizo ubuyekeziwe",
    productAdded: "Umkhiqizo ungeziwe",
    productName: "Igama lomkhiqizo",
    productNamePlaceholder: "isb. Sample product",
    profitCalculator: "Ikhalekhuleyitha yenzuzo",
    quantity: "Ubuningi",
    recordIncomingStock: "Rekhoda isitoko esingenayo",
    revenue: "Imali engenayo",
    saveFailed: "Akukwazekanga ukulondoloza umkhiqizo",
    saveProduct: "Londoloza umkhiqizo",
    saving: "Kuyalondolozwa...",
    scan: "Skena",
    scannerReady: "Iskena silungile",
    scannerReadyDescription:
      "Skena ibhakhodi manje ukuze uyixhume kulo mkhiqizo.",
    scanToAttach: "Skena ukuze unamathisele",
    searchPlaceholder: "Sesha imikhiqizo noma skena ibhakhodi…",
    showing: (start, end, total) =>
      `Kuboniswayo ${start} kuya ku-${end} ku-${total}`,
    skuCount: (count) => `SKU ${count}`,
    soldToday: "Kudayiswe namuhla",
    soldTodayColumn: "Kudayiswe namuhla",
    staff: "Umsebenzi",
    status: "Isimo",
    stock: "Isitoko",
    stockOut: "Kuphelile",
    stockUpdated: "Isitoko sibuyekeziwe",
    subtitle: (all, attention) => `${all} SKU · ${attention} kudinga ukunakwa`,
    to: "Kuya ku",
    today: "Namuhla",
    transactions: "Ukuthengiselana",
    transactionsWhen: "Nini",
    tryAgain: "Zama futhi.",
    unitPrice: "Intengo yeyunithi",
    unitsSold: "Amayunithi adayisiwe",
    untracked: "akulandelwa",
    viewDetails: "Buka imininingwane",
    inventoryTitle: "Impahla",
    optional: "okokuzikhethela",
    optionalBarcode: "Ibhakhodi yokuzikhethela",
    costPrice: "Intengo yokuthenga",
    sellingPrice: "Intengo yokuthengisa",
    transactionSaleId: "ID yokuthengisa",
    qty: "Qty",
    amount: "Inani",
    cashier: "Umamukeli wemali",
    manager: "Umphathi",
    stockAllocationBalance: "Ibhalansi yomnikazi, umsebenzi, nomphathi",
    transactionStatus: "Isimo",
    profitRevenue: "Imali engenayo",
    profitCostTotal: "Izindleko zonke",
    profitSellingPrice: "Intengo yokuthengisa",
    validDateRange: "Khetha ibanga lezinsuku elivumelekile.",
    validation: {
      costRequired: "Intengo yokuthenga iyadingeka",
      costValid: "Intengo yokuthenga kufanele ibe semthethweni",
      costNonNegative: "Intengo yokuthenga ayinakuba ngaphansi kuka-zero",
      lowStockWhole:
        "Isixwayiso sesitoko esiphansi kufanele sibe inombolo ephelele",
      lowStockNonNegative:
        "Isixwayiso sesitoko esiphansi asikwazi ukuba ngaphansi kuka-zero",
      productNameRequired: "Igama lomkhiqizo liyadingeka",
      priceRequired: "Intengo yokuthengisa iyadingeka",
      priceValid: "Intengo yokuthengisa kufanele ibe semthethweni",
      priceAboveZero: "Intengo yokuthengisa kufanele ibe ngaphezu kuka-zero",
      stockRequired: "Isitoko sokuqala siyadingeka",
      stockWhole: "Isitoko sokuqala kufanele sibe inombolo ephelele",
      stockNonNegative: "Isitoko sokuqala asikwazi ukuba ngaphansi kuka-zero",
    },
  },
};

Object.entries(inventoryTranslations).forEach(([language, inventory]) => {
  translations[language] = {
    ...(translations[language] || {}),
    inventory,
  };
});

const historyTranslations = {
  ar: {
    action: "الإجراء",
    all: "الكل",
    amount: "المبلغ",
    amountPaid: "المبلغ المدفوع",
    backToHistory: "العودة إلى السجل",
    balanceDue: "الرصيد المستحق",
    cashier: "أمين الصندوق",
    countTransactions: (count, total) => `${count} معاملة · ${total} إجمالي`,
    export: "تصدير",
    exportDownloaded: "تم تنزيل سجل المبيعات",
    exportFailed: "تعذر تصدير المبيعات",
    historyTitle: "السجل",
    items: "العناصر",
    lineItems: "عناصر الفاتورة",
    noTransactionsMatch: "لا توجد معاملات تطابق عوامل التصفية.",
    next: "التالي",
    paid: "مدفوع",
    partial: "جزئي",
    pending: "قيد الانتظار",
    previous: "السابق",
    printReceipt: "طباعة الإيصال",
    receiptTitle: "إيصال Shopbook",
    sale: "عملية بيع",
    saleDetail: "تفاصيل البيع",
    searchPlaceholder: "ابحث عن عنصر أو أمين صندوق…",
    showing: (start, end, total) => `عرض ${start} إلى ${end} من ${total}`,
    status: "الحالة",
    sync: "المزامنة",
    synced: "تمت المزامنة",
    today: "اليوم",
    total: "الإجمالي",
    transactions: "معاملات",
    unpaid: "غير مدفوع",
    viewDetails: "عرض التفاصيل",
    when: "متى",
    tryAgain: "حاول مرة أخرى.",
  },
  fr: {
    action: "Action",
    all: "Tout",
    amount: "Montant",
    amountPaid: "Montant payé",
    backToHistory: "Retour à l’historique",
    balanceDue: "Solde dû",
    cashier: "Caissier",
    countTransactions: (count, total) =>
      `${count} transactions · ${total} au total`,
    export: "Exporter",
    exportDownloaded: "Export des ventes téléchargé",
    exportFailed: "Impossible d’exporter les ventes",
    historyTitle: "Historique",
    items: "Articles",
    lineItems: "Lignes d’articles",
    noTransactionsMatch: "Aucune transaction ne correspond à vos filtres.",
    next: "Suivant",
    paid: "Payé",
    partial: "Partiel",
    pending: "En attente",
    previous: "Précédent",
    printReceipt: "Imprimer le reçu",
    receiptTitle: "Reçu Shopbook",
    sale: "Vente",
    saleDetail: "Détail de la vente",
    searchPlaceholder: "Rechercher un article ou un caissier…",
    showing: (start, end, total) =>
      `Affichage de ${start} à ${end} sur ${total}`,
    status: "Statut",
    sync: "Synchronisation",
    synced: "Synchronisé",
    today: "Aujourd’hui",
    total: "Total",
    transactions: "transactions",
    unpaid: "Impayé",
    viewDetails: "Voir les détails",
    when: "Quand",
    tryAgain: "Réessayez.",
  },
  ha: {
    action: "Aiki",
    all: "Duka",
    amount: "Kudi",
    amountPaid: "Kudin da aka biya",
    backToHistory: "Komawa tarihi",
    balanceDue: "Ragowar bashi",
    cashier: "Mai karbar kudi",
    countTransactions: (count, total) =>
      `Ma’amaloli ${count} · jimilla ${total}`,
    export: "Fitarwa",
    exportDownloaded: "An sauke fitar tarihin tallace-tallace",
    exportFailed: "Ba a iya fitar da tallace-tallace ba",
    historyTitle: "Tarihi",
    items: "Kaya",
    lineItems: "Abubuwan da ke ciki",
    noTransactionsMatch: "Babu ma’amaloli da suka dace da tacewar ka.",
    next: "Na gaba",
    paid: "An biya",
    partial: "Wani bangare",
    pending: "Ana jira",
    previous: "Na baya",
    printReceipt: "Buga receipt",
    receiptTitle: "Receipt na Shopbook",
    sale: "Sayarwa",
    saleDetail: "Bayanan sayarwa",
    searchPlaceholder: "Binciki kaya ko mai karbar kudi…",
    showing: (start, end, total) =>
      `Ana nuna ${start} zuwa ${end} cikin ${total}`,
    status: "Matsayi",
    sync: "Daidaitawa",
    synced: "An daidaita",
    today: "Yau",
    total: "Jimilla",
    transactions: "ma’amaloli",
    unpaid: "Ba a biya ba",
    viewDetails: "Duba bayanai",
    when: "Yaushe",
    tryAgain: "Sake gwadawa.",
  },
  ig: {
    action: "Omume",
    all: "Niile",
    amount: "Ego",
    amountPaid: "Ego akwụgoro",
    backToHistory: "Laghachi na akụkọ",
    balanceDue: "Ego fọdụrụ",
    cashier: "Onye na-anakọta ego",
    countTransactions: (count, total) =>
      `Mmekọrịta ire ${count} · ngụkọta ${total}`,
    export: "Bupụ",
    exportDownloaded: "Ebupụtara akụkọ ire",
    exportFailed: "Enweghị ike ibupụ ire",
    historyTitle: "Akụkọ",
    items: "Ihe",
    lineItems: "Ihe ndị dị n’ime ya",
    noTransactionsMatch: "Enweghị mmekọrịta ire dabara na nzacha gị.",
    next: "Na-esote",
    paid: "Akwụgoro",
    partial: "Nkebi",
    pending: "Ka na-eche",
    previous: "Nke gara aga",
    printReceipt: "Bipụta risiti",
    receiptTitle: "Risiti Shopbook",
    sale: "Ire",
    saleDetail: "Nkọwa ire",
    searchPlaceholder: "Chọọ ihe ma ọ bụ onye na-anakọta ego…",
    showing: (start, end, total) =>
      `Na-egosi ${start} ruo ${end} n’ime ${total}`,
    status: "Ọnọdụ",
    sync: "Mmekọrịta data",
    synced: "Emekọrịtara",
    today: "Taa",
    total: "Ngụkọta",
    transactions: "azụmahịa",
    unpaid: "Akwụghị",
    viewDetails: "Lee nkọwa",
    when: "Mgbe",
    tryAgain: "Nwaa ọzọ.",
  },
  pt: {
    action: "Ação",
    all: "Todos",
    amount: "Montante",
    amountPaid: "Valor pago",
    backToHistory: "Voltar ao histórico",
    balanceDue: "Saldo em dívida",
    cashier: "Caixa",
    countTransactions: (count, total) =>
      `${count} transações · ${total} no total`,
    export: "Exportar",
    exportDownloaded: "Exportação de vendas descarregada",
    exportFailed: "Não foi possível exportar as vendas",
    historyTitle: "Histórico",
    items: "Itens",
    lineItems: "Linhas de itens",
    noTransactionsMatch: "Nenhuma transação corresponde aos seus filtros.",
    next: "Seguinte",
    paid: "Pago",
    partial: "Parcial",
    pending: "Pendente",
    previous: "Anterior",
    printReceipt: "Imprimir recibo",
    receiptTitle: "Recibo Shopbook",
    sale: "Venda",
    saleDetail: "Detalhe da venda",
    searchPlaceholder: "Pesquisar item ou caixa…",
    showing: (start, end, total) => `A mostrar ${start} a ${end} de ${total}`,
    status: "Estado",
    sync: "Sincronização",
    synced: "Sincronizado",
    today: "Hoje",
    total: "Total",
    transactions: "transações",
    unpaid: "Não pago",
    viewDetails: "Ver detalhes",
    when: "Quando",
    tryAgain: "Tente novamente.",
  },
  sw: {
    action: "Hatua",
    all: "Zote",
    amount: "Kiasi",
    amountPaid: "Kiasi kilicholipwa",
    backToHistory: "Rudi kwenye historia",
    balanceDue: "Salio linalodaiwa",
    cashier: "Muuzaji",
    countTransactions: (count, total) => `Miamala ${count} · jumla ${total}`,
    export: "Hamisha",
    exportDownloaded: "Uhamishaji wa mauzo umepakuliwa",
    exportFailed: "Haikuwezekana kuhamisha mauzo",
    historyTitle: "Historia",
    items: "Bidhaa",
    lineItems: "Vipengee vya mauzo",
    noTransactionsMatch: "Hakuna miamala inayolingana na vichujio vyako.",
    next: "Inayofuata",
    paid: "Imelipwa",
    partial: "Sehemu",
    pending: "Inasubiri",
    previous: "Iliyopita",
    printReceipt: "Chapisha risiti",
    receiptTitle: "Risiti ya Shopbook",
    sale: "Mauzo",
    saleDetail: "Maelezo ya mauzo",
    searchPlaceholder: "Tafuta bidhaa au muuzaji…",
    showing: (start, end, total) =>
      `Inaonyesha ${start} hadi ${end} kati ya ${total}`,
    status: "Hali",
    sync: "Usawazishaji",
    synced: "Imesawazishwa",
    today: "Leo",
    total: "Jumla",
    transactions: "miamala",
    unpaid: "Haijalipwa",
    viewDetails: "Tazama maelezo",
    when: "Lini",
    tryAgain: "Jaribu tena.",
  },
  yo: {
    action: "Iṣe",
    all: "Gbogbo",
    amount: "Iye owo",
    amountPaid: "Iye ti a san",
    backToHistory: "Pada si itan",
    balanceDue: "Iye to ku",
    cashier: "Olugba owo",
    countTransactions: (count, total) =>
      `Awọn iṣowo ${count} · lapapọ ${total}`,
    export: "Firanṣẹ jade",
    exportDownloaded: "A ti gba faili tita silẹ",
    exportFailed: "Ko ṣee ṣe lati fi awọn tita jade",
    historyTitle: "Itan",
    items: "Awọn nkan",
    lineItems: "Awọn nkan inu tita",
    noTransactionsMatch: "Ko si awọn iṣowo to ba awọn àlẹmọ rẹ mu.",
    next: "Tókàn",
    paid: "Ti san",
    partial: "Apa kan",
    pending: "N duro",
    previous: "Tẹlẹ",
    printReceipt: "Tẹ receipt jade",
    receiptTitle: "Receipt Shopbook",
    sale: "Tita",
    saleDetail: "Alaye tita",
    searchPlaceholder: "Wa nkan tabi olugba owo…",
    showing: (start, end, total) => `N fihan ${start} si ${end} ninu ${total}`,
    status: "Ipo",
    sync: "Didapọ data",
    synced: "Ti sync",
    today: "Loni",
    total: "Lapapọ",
    transactions: "awọn iṣowo",
    unpaid: "A ko san",
    viewDetails: "Wo alaye",
    when: "Nigbawo",
    tryAgain: "Gbiyanju lẹẹkansi.",
  },
  zu: {
    action: "Isenzo",
    all: "Konke",
    amount: "Inani",
    amountPaid: "Inani elikhokhwe",
    backToHistory: "Buyela emlandweni",
    balanceDue: "Ibhalansi esele",
    cashier: "Umamukeli wemali",
    countTransactions: (count, total) =>
      `Imisebenzi ${count} · isamba ${total}`,
    export: "Khipha",
    exportDownloaded: "Ukukhishwa kokuthengisa kulandiwe",
    exportFailed: "Akukwazekanga ukukhupha ukuthengisa",
    historyTitle: "Umlando",
    items: "Izinto",
    lineItems: "Izinto ezisohlwini",
    noTransactionsMatch: "Akukho misebenzi ehambisana nezihlungi zakho.",
    next: "Okulandelayo",
    paid: "Kukhokhiwe",
    partial: "Ingxenye",
    pending: "Kulindile",
    previous: "Okwedlule",
    printReceipt: "Phrinta irisidi",
    receiptTitle: "Irisidi ye-Shopbook",
    sale: "Ukuthengisa",
    saleDetail: "Imininingwane yokuthengisa",
    searchPlaceholder: "Sesha into noma umamukeli wemali…",
    showing: (start, end, total) =>
      `Kuboniswayo ${start} kuya ku-${end} ku-${total}`,
    status: "Isimo",
    sync: "Ukuvumelanisa",
    synced: "Kuvumelanisiwe",
    today: "Namuhla",
    total: "Ingqikithi",
    transactions: "imisebenzi",
    unpaid: "Akukhokhiwe",
    viewDetails: "Buka imininingwane",
    when: "Nini",
    tryAgain: "Zama futhi.",
  },
};

Object.entries(historyTranslations).forEach(([language, history]) => {
  translations[language] = {
    ...(translations[language] || {}),
    history,
  };
});

const debtsTranslations = {
  ar: {
    action: "الإجراء",
    all: "الكل",
    amount: "المبلغ",
    amountLabel: "المبلغ",
    balance: "الرصيد",
    cancel: "إلغاء",
    customer: "العميل",
    date: "التاريخ",
    debtId: "رقم الدين",
    debtsTab: "الديون",
    debtsTitle: "الديون",
    dialogDescription: (name, amount) => `${name} عليه ${amount}`,
    dialogTitle: "تسجيل دفعة",
    due: "الاستحقاق",
    items: "العناصر",
    markPaid: "تحديد كمدفوع",
    next: "التالي",
    noDebtsMatch: "لا توجد ديون تطابق عوامل التصفية.",
    noDebtPaymentsMatch: "لا توجد دفعات ديون تطابق عوامل التصفية.",
    noPhone: "لا يوجد هاتف",
    none: "لا يوجد",
    originalCreditSale: "ضمن عملية البيع الآجل الأصلية",
    overdue: "متأخر",
    paid: "مدفوع",
    paidSuffix: "مدفوع",
    paymentDetails: "تفاصيل الدفعة",
    paymentDetailsDescription: (name, amount) => `${name} دفع ${amount}.`,
    paymentReceived: "تم استلام الدفعة",
    paymentRecorded: (amount) => `تم تسجيل دفعة بقيمة ${amount}`,
    paymentsTab: "الدفعات",
    previous: "السابق",
    recordPaymentFailed: "تعذر تسجيل الدفعة",
    recording: "جارٍ التسجيل...",
    searchDebtsPlaceholder: "ابحث عن عميل أو هاتف أو عنصر...",
    searchPaymentsPlaceholder: "ابحث عن عميل أو رقم دين...",
    settlement: "التسوية",
    settle: "سداد",
    showing: (start, end, total) => `عرض ${start} إلى ${end} من ${total}`,
    soldItems: "العناصر المباعة",
    soldItemsDescription: "تفصيل موسع للعناصر في سجل هذا الدين.",
    status: "الحالة",
    statusOverdue: "متأخر",
    statusPaid: "مدفوع",
    statusPartlyPaid: "مدفوع جزئياً",
    statusUnpaid: "غير مدفوع",
    subtitle: (open, outstanding) => `${open} مفتوح · ${outstanding} مستحق`,
    totalItems: (count) => `${count} إجمالي`,
    tryAgain: "حاول مرة أخرى.",
    unpaid: "غير مدفوع",
    units: (count) => `${count} وحدة`,
    viewDetails: "عرض التفاصيل",
    confirmPayment: "تأكيد الدفعة",
    markPaidSuccess: (name) => `تم تحديد ${name} كمدفوع`,
    markPaidFailed: "تعذر تحديد الدين كمدفوع",
    validation: {
      amountRequired: "المبلغ مطلوب",
      amountValid: "يجب أن يكون المبلغ رقمًا صحيحًا",
      amountPositive: "يجب أن يكون المبلغ أكبر من صفر",
      amountCannotExceed: (balance) => `لا يمكن أن يتجاوز المبلغ ${balance}`,
    },
  },
  fr: {
    action: "Action",
    all: "Tous",
    amount: "Montant",
    amountLabel: "Montant",
    balance: "Solde",
    cancel: "Annuler",
    customer: "Client",
    date: "Date",
    debtId: "ID dette",
    debtsTab: "Dettes",
    debtsTitle: "Dettes",
    dialogDescription: (name, amount) => `${name} doit ${amount}`,
    dialogTitle: "Enregistrer un paiement",
    due: "Échéance",
    items: "Articles",
    markPaid: "Marquer payé",
    next: "Suivant",
    noDebtsMatch: "Aucune dette ne correspond à vos filtres.",
    noDebtPaymentsMatch: "Aucun paiement de dette ne correspond à vos filtres.",
    noPhone: "Aucun téléphone",
    none: "Aucun",
    originalCreditSale: "Inclus dans la vente à crédit d'origine",
    overdue: "En retard",
    paid: "Payé",
    paidSuffix: "payé",
    paymentDetails: "Détails du paiement",
    paymentDetailsDescription: (name, amount) => `${name} a payé ${amount}.`,
    paymentReceived: "Paiement reçu",
    paymentRecorded: (amount) => `Paiement de ${amount} enregistré`,
    paymentsTab: "Paiements",
    previous: "Précédent",
    recordPaymentFailed: "Impossible d’enregistrer le paiement",
    recording: "Enregistrement...",
    searchDebtsPlaceholder:
      "Rechercher un client, un téléphone ou un article...",
    searchPaymentsPlaceholder: "Rechercher un client ou un ID de dette...",
    settlement: "Règlement",
    settle: "Régler",
    showing: (start, end, total) =>
      `Affichage de ${start} à ${end} sur ${total}`,
    soldItems: "Articles vendus",
    soldItemsDescription: "Détail élargi des articles pour cette dette.",
    status: "Statut",
    statusOverdue: "En retard",
    statusPaid: "Payé",
    statusPartlyPaid: "Partiellement payé",
    statusUnpaid: "Impayé",
    subtitle: (open, outstanding) => `${open} ouvertes · ${outstanding} dus`,
    totalItems: (count) => `${count} au total`,
    tryAgain: "Réessayez.",
    unpaid: "Impayé",
    units: (count) => `${count} unité${count === 1 ? "" : "s"}`,
    viewDetails: "Voir les détails",
    confirmPayment: "Confirmer le paiement",
    markPaidSuccess: (name) => `${name} marqué comme payé`,
    markPaidFailed: "Impossible de marquer la dette comme payée",
    validation: {
      amountRequired: "Le montant est requis",
      amountValid: "Le montant doit être valide",
      amountPositive: "Le montant doit être supérieur à zéro",
      amountCannotExceed: (balance) =>
        `Le montant ne peut pas dépasser ${balance}`,
    },
  },
  ha: {
    action: "Aiki",
    all: "Duka",
    amount: "Adadi",
    amountLabel: "Adadi",
    balance: "Ragowa",
    cancel: "Soke",
    customer: "Kwastoma",
    date: "Kwanan wata",
    debtId: "Lambar bashi",
    debtsTab: "Bashi",
    debtsTitle: "Bashi",
    dialogDescription: (name, amount) => `${name} na bin ${amount}`,
    dialogTitle: "Rubuta biyan kudi",
    due: "Lokacin biya",
    items: "Kaya",
    markPaid: "Alama an biya",
    next: "Na gaba",
    noDebtsMatch: "Babu bashin da ya dace da tacewar ka.",
    noDebtPaymentsMatch: "Babu biyan bashin da ya dace da tacewar ka.",
    noPhone: "Babu waya",
    none: "Babu",
    originalCreditSale: "An hada shi cikin asalin sayarwar bashi",
    overdue: "Ya wuce lokaci",
    paid: "An biya",
    paidSuffix: "an biya",
    paymentDetails: "Bayanin biya",
    paymentDetailsDescription: (name, amount) => `${name} ya biya ${amount}.`,
    paymentReceived: "An karbi biya",
    paymentRecorded: (amount) => `An rubuta biyan ${amount}`,
    paymentsTab: "Biya",
    previous: "Na baya",
    recordPaymentFailed: "Ba a iya rubuta biyan ba",
    recording: "Ana rubutawa...",
    searchDebtsPlaceholder: "Nemi kwastoma, waya, ko kaya...",
    searchPaymentsPlaceholder: "Nemi kwastoma ko lambar bashi...",
    settlement: "Biyan karshe",
    settle: "Biya",
    showing: (start, end, total) =>
      `Ana nuna ${start} zuwa ${end} cikin ${total}`,
    soldItems: "Kayan da aka sayar",
    soldItemsDescription: "Cikakken bayanin kayan da ke cikin wannan bashin.",
    status: "Matsayi",
    statusOverdue: "Ya wuce lokaci",
    statusPaid: "An biya",
    statusPartlyPaid: "An biya wani bangare",
    statusUnpaid: "Ba a biya ba",
    subtitle: (open, outstanding) => `${open} a bude · ${outstanding} a waje`,
    totalItems: (count) => `${count} jimilla`,
    tryAgain: "Sake gwadawa.",
    unpaid: "Ba a biya ba",
    units: (count) => `${count} raka'a`,
    viewDetails: "Duba bayani",
    confirmPayment: "Tabbatar da biya",
    markPaidSuccess: (name) => `An sa ${name} a matsayin an biya`,
    markPaidFailed: "Ba a iya sanya bashin a matsayin an biya ba",
    validation: {
      amountRequired: "Ana bukatar adadi",
      amountValid: "Adadin ya zama ingantacce",
      amountPositive: "Adadin dole ya fi sifili",
      amountCannotExceed: (balance) => `Adadin ba zai wuce ${balance} ba`,
    },
  },
  ig: {
    action: "Omume",
    all: "Ha niile",
    amount: "Ọnụ ego",
    amountLabel: "Ọnụ ego",
    balance: "Fọdụrụ",
    cancel: "Kagbuo",
    customer: "Onye ahịa",
    date: "Ụbọchị",
    debtId: "ID ụgwọ",
    debtsTab: "Ụgwọ",
    debtsTitle: "Ụgwọ",
    dialogDescription: (name, amount) => `${name} ji ${amount}`,
    dialogTitle: "Dekọọ ịkwụ ụgwọ",
    due: "Ụbọchị akwụmụgwọ",
    items: "Ngwaahịa",
    markPaid: "Tinye dị ka akwụgoro",
    next: "Ọzọ",
    noDebtsMatch: "Enweghị ụgwọ dabara na nzacha gị.",
    noDebtPaymentsMatch: "Enweghị ịkwụ ụgwọ dabara na nzacha gị.",
    noPhone: "Enweghị ekwentị",
    none: "Enweghị",
    originalCreditSale: "E tinyere ya na ire n'ugbo mbụ",
    overdue: "Agafela oge",
    paid: "Akwụgoro",
    paidSuffix: "akwụgoro",
    paymentDetails: "Nkọwa ịkwụ ụgwọ",
    paymentDetailsDescription: (name, amount) => `${name} kwụrụ ${amount}.`,
    paymentReceived: "Anatala ịkwụ ụgwọ",
    paymentRecorded: (amount) => `Edere ịkwụ ụgwọ nke ${amount}`,
    paymentsTab: "Ịkwụ ụgwọ",
    previous: "Nke gara aga",
    recordPaymentFailed: "Enweghị ike idekọ ịkwụ ụgwọ",
    recording: "Na-edekọ...",
    searchDebtsPlaceholder: "Chọọ onye ahịa, ekwentị, ma ọ bụ ngwaahịa...",
    searchPaymentsPlaceholder: "Chọọ onye ahịa ma ọ bụ ID ụgwọ...",
    settlement: "Mmechi ugwo",
    settle: "Kwụọ",
    showing: (start, end, total) =>
      `Na-egosi ${start} ruo ${end} n’ime ${total}`,
    soldItems: "Ngwaahịa e rere",
    soldItemsDescription: "Nkọwa zuru ezu nke ihe dị na ndekọ ụgwọ a.",
    status: "Ọnọdụ",
    statusOverdue: "Agafela oge",
    statusPaid: "Akwụgoro",
    statusPartlyPaid: "Akwụgoro akụkụ",
    statusUnpaid: "Akwụghị",
    subtitle: (open, outstanding) => `${open} meghere · ${outstanding} ka dị`,
    totalItems: (count) => `${count} ngụkọta`,
    tryAgain: "Nwaa ọzọ.",
    unpaid: "Akwụghị",
    units: (count) => `${count} nkeji`,
    viewDetails: "Lee nkọwa",
    confirmPayment: "Kwenye ịkwụ ụgwọ",
    markPaidSuccess: (name) => `E tinyela ${name} dị ka akwụgoro`,
    markPaidFailed: "Enweghị ike ịkọwa ụgwọ dị ka akwụgoro",
    validation: {
      amountRequired: "A chọrọ ọnụ ego",
      amountValid: "Ọnụ ego ga-abụ nọmba ziri ezi",
      amountPositive: "Ọnụ ego ga-adị elu karịa efu",
      amountCannotExceed: (balance) => `Ọnụ ego agaghị agafe ${balance}`,
    },
  },
  pt: {
    action: "Ação",
    all: "Todos",
    amount: "Valor",
    amountLabel: "Valor",
    balance: "Saldo",
    cancel: "Cancelar",
    customer: "Cliente",
    date: "Data",
    debtId: "ID da dívida",
    debtsTab: "Dívidas",
    debtsTitle: "Dívidas",
    dialogDescription: (name, amount) => `${name} deve ${amount}`,
    dialogTitle: "Registar pagamento",
    due: "Vencimento",
    items: "Itens",
    markPaid: "Marcar como pago",
    next: "Seguinte",
    noDebtsMatch: "Nenhuma dívida corresponde aos seus filtros.",
    noDebtPaymentsMatch:
      "Nenhum pagamento de dívida corresponde aos seus filtros.",
    noPhone: "Sem telefone",
    none: "Nenhum",
    originalCreditSale: "Incluído na venda a crédito original",
    overdue: "Em atraso",
    paid: "Pago",
    paidSuffix: "pago",
    paymentDetails: "Detalhes do pagamento",
    paymentDetailsDescription: (name, amount) => `${name} pagou ${amount}.`,
    paymentReceived: "Pagamento recebido",
    paymentRecorded: (amount) => `Pagamento de ${amount} registado`,
    paymentsTab: "Pagamentos",
    previous: "Anterior",
    recordPaymentFailed: "Não foi possível registar o pagamento",
    recording: "A registar...",
    searchDebtsPlaceholder: "Pesquisar cliente, telefone ou item...",
    searchPaymentsPlaceholder: "Pesquisar cliente ou ID da dívida...",
    settlement: "Liquidação",
    settle: "Liquidar",
    showing: (start, end, total) => `A mostrar ${start} até ${end} de ${total}`,
    soldItems: "Itens vendidos",
    soldItemsDescription:
      "Detalhe expandido dos itens deste registo de dívida.",
    status: "Estado",
    statusOverdue: "Em atraso",
    statusPaid: "Pago",
    statusPartlyPaid: "Parcialmente pago",
    statusUnpaid: "Não pago",
    subtitle: (open, outstanding) =>
      `${open} em aberto · ${outstanding} em dívida`,
    totalItems: (count) => `${count} total`,
    tryAgain: "Tente novamente.",
    unpaid: "Não pago",
    units: (count) => `${count} unidade${count === 1 ? "" : "s"}`,
    viewDetails: "Ver detalhes",
    confirmPayment: "Confirmar pagamento",
    markPaidSuccess: (name) => `${name} marcado como pago`,
    markPaidFailed: "Não foi possível marcar a dívida como paga",
    validation: {
      amountRequired: "O valor é obrigatório",
      amountValid: "O valor deve ser válido",
      amountPositive: "O valor deve ser maior que zero",
      amountCannotExceed: (balance) => `O valor não pode exceder ${balance}`,
    },
  },
  sw: {
    action: "Hatua",
    all: "Zote",
    amount: "Kiasi",
    amountLabel: "Kiasi",
    balance: "Salio",
    cancel: "Ghairi",
    customer: "Mteja",
    date: "Tarehe",
    debtId: "Namba ya deni",
    debtsTab: "Madeni",
    debtsTitle: "Madeni",
    dialogDescription: (name, amount) => `${name} anadaiwa ${amount}`,
    dialogTitle: "Rekodi malipo",
    due: "Tarehe ya malipo",
    items: "Bidhaa",
    markPaid: "Weka kuwa imelipwa",
    next: "Ifuatayo",
    noDebtsMatch: "Hakuna deni linalolingana na vichujio vyako.",
    noDebtPaymentsMatch:
      "Hakuna malipo ya deni yanayolingana na vichujio vyako.",
    noPhone: "Hakuna simu",
    none: "Hakuna",
    originalCreditSale: "Imejumuishwa kwenye mauzo ya mkopo ya awali",
    overdue: "Imechelewa",
    paid: "Imelipwa",
    paidSuffix: "imelipwa",
    paymentDetails: "Maelezo ya malipo",
    paymentDetailsDescription: (name, amount) => `${name} amelipa ${amount}.`,
    paymentReceived: "Malipo yamepokelewa",
    paymentRecorded: (amount) => `Malipo ya ${amount} yamehifadhiwa`,
    paymentsTab: "Malipo",
    previous: "Iliyopita",
    recordPaymentFailed: "Imeshindikana kurekodi malipo",
    recording: "Inarekodi...",
    searchDebtsPlaceholder: "Tafuta mteja, simu, au bidhaa...",
    searchPaymentsPlaceholder: "Tafuta mteja au namba ya deni...",
    settlement: "Malipo",
    settle: "Lipa",
    showing: (start, end, total) =>
      `Inaonyesha ${start} hadi ${end} ya ${total}`,
    soldItems: "Bidhaa zilizouzwa",
    soldItemsDescription: "Maelezo zaidi ya bidhaa kwenye rekodi hii ya deni.",
    status: "Hali",
    statusOverdue: "Imechelewa",
    statusPaid: "Imelipwa",
    statusPartlyPaid: "Imelipwa kiasi",
    statusUnpaid: "Haijalipwa",
    subtitle: (open, outstanding) => `${open} wazi · ${outstanding} bado`,
    totalItems: (count) => `${count} jumla`,
    tryAgain: "Jaribu tena.",
    unpaid: "Haijalipwa",
    units: (count) => `${count} kipande`,
    viewDetails: "Tazama maelezo",
    confirmPayment: "Thibitisha malipo",
    markPaidSuccess: (name) => `${name} amewekwa kuwa amelipa`,
    markPaidFailed: "Imeshindikana kuweka deni kuwa limelipwa",
    validation: {
      amountRequired: "Kiasi kinahitajika",
      amountValid: "Kiasi lazima kiwe sahihi",
      amountPositive: "Kiasi lazima kiwe zaidi ya sifuri",
      amountCannotExceed: (balance) => `Kiasi hakiwezi kuzidi ${balance}`,
    },
  },
  yo: {
    action: "Iṣe",
    all: "Gbogbo",
    amount: "Iye",
    amountLabel: "Iye",
    balance: "Iyoku",
    cancel: "Fagilee",
    customer: "Onibara",
    date: "Ọjọ",
    debtId: "ID gbese",
    debtsTab: "Gbese",
    debtsTitle: "Gbese",
    dialogDescription: (name, amount) => `${name} jẹ ${amount}`,
    dialogTitle: "Ṣe igbasilẹ isanwo",
    due: "Ọjọ sisan",
    items: "Awọn nkan",
    markPaid: "Samisi pe o san",
    next: "T'okan",
    noDebtsMatch: "Ko si gbese to ba awọn asẹ rẹ mu.",
    noDebtPaymentsMatch: "Ko si isanwo gbese to ba awọn asẹ rẹ mu.",
    noPhone: "Ko si foonu",
    none: "Ko si",
    originalCreditSale: "O wa ninu tita kirẹditi atilẹba",
    overdue: "Ti pẹ",
    paid: "Ti san",
    paidSuffix: "ti san",
    paymentDetails: "Alaye isanwo",
    paymentDetailsDescription: (name, amount) => `${name} san ${amount}.`,
    paymentReceived: "A ti gba isanwo",
    paymentRecorded: (amount) => `A ti ṣe igbasilẹ isanwo ${amount}`,
    paymentsTab: "Isanwo",
    previous: "Tẹlẹ",
    recordPaymentFailed: "A ko le ṣe igbasilẹ isanwo",
    recording: "N gba silẹ...",
    searchDebtsPlaceholder: "Wa onibara, foonu, tabi nkan...",
    searchPaymentsPlaceholder: "Wa onibara tabi ID gbese...",
    settlement: "Sisan",
    settle: "San",
    showing: (start, end, total) => `Nfi ${start} si ${end} han ninu ${total}`,
    soldItems: "Awọn nkan ti a ta",
    soldItemsDescription: "Alaye kikun ti awọn nkan inu igbasilẹ gbese yii.",
    status: "Ipo",
    statusOverdue: "Ti pẹ",
    statusPaid: "Ti san",
    statusPartlyPaid: "Ti san ni apakan",
    statusUnpaid: "Ko tii san",
    subtitle: (open, outstanding) => `${open} ṣiṣi · ${outstanding} ku`,
    totalItems: (count) => `${count} lapapọ`,
    tryAgain: "Gbiyanju lẹẹkansi.",
    unpaid: "Ko tii san",
    units: (count) => `${count} uniti`,
    viewDetails: "Wo alaye",
    confirmPayment: "Jẹrisi isanwo",
    markPaidSuccess: (name) => `A ti samisi ${name} pe o san`,
    markPaidFailed: "A ko le samisi gbese naa pe o san",
    validation: {
      amountRequired: "Iye jẹ dandan",
      amountValid: "Iye gbọdọ jẹ nọmba to tọ",
      amountPositive: "Iye gbọdọ ju odo lọ",
      amountCannotExceed: (balance) => `Iye ko le ju ${balance} lọ`,
    },
  },
  zu: {
    action: "Isenzo",
    all: "Konke",
    amount: "Inani",
    amountLabel: "Inani",
    balance: "Ibhalansi",
    cancel: "Khansela",
    customer: "Ikhasimende",
    date: "Usuku",
    debtId: "I-ID yesikweletu",
    debtsTab: "Izikweletu",
    debtsTitle: "Izikweletu",
    dialogDescription: (name, amount) => `${name} ukweleta ${amount}`,
    dialogTitle: "Qopha inkokhelo",
    due: "Usuku lokukhokha",
    items: "Izinto",
    markPaid: "Maka njengokhokhiwe",
    next: "Okulandelayo",
    noDebtsMatch: "Azikho izikweletu ezihambisana nezihlungi zakho.",
    noDebtPaymentsMatch:
      "Azikho izinkokhelo zesikweletu ezihambisana nezihlungi zakho.",
    noPhone: "Alukho ucingo",
    none: "Akukho",
    originalCreditSale: "Kufakwe ekuthengisweni kwesikweletu sokuqala",
    overdue: "Sekudlule isikhathi",
    paid: "Kukhokhiwe",
    paidSuffix: "kukhokhiwe",
    paymentDetails: "Imininingwane yokukhokha",
    paymentDetailsDescription: (name, amount) => `${name} ukhokhe ${amount}.`,
    paymentReceived: "Inkokhelo yamukelwe",
    paymentRecorded: (amount) => `Inkokhelo ka-${amount} iqoshiwe`,
    paymentsTab: "Izinkokhelo",
    previous: "Okwedlule",
    recordPaymentFailed: "Ayikwazanga ukuqopha inkokhelo",
    recording: "Kuyaqoshwa...",
    searchDebtsPlaceholder: "Sesha ikhasimende, ucingo, noma into...",
    searchPaymentsPlaceholder: "Sesha ikhasimende noma i-ID yesikweletu...",
    settlement: "Ukukhokha",
    settle: "Khokha",
    showing: (start, end, total) =>
      `Kuboniswayo ${start} kuya ku-${end} ku-${total}`,
    soldItems: "Izinto ezithengisiwe",
    soldItemsDescription:
      "Ukuhlukaniswa okunemininingwane kwezinto kule rekodi yesikweletu.",
    status: "Isimo",
    statusOverdue: "Sekudlule isikhathi",
    statusPaid: "Kukhokhiwe",
    statusPartlyPaid: "Kukhokhwe ingxenye",
    statusUnpaid: "Akukhokhiwe",
    subtitle: (open, outstanding) =>
      `${open} zivuliwe · ${outstanding} kuseleyo`,
    totalItems: (count) => `${count} konke`,
    tryAgain: "Zama futhi.",
    unpaid: "Akukhokhiwe",
    units: (count) => `${count} iyunithi`,
    viewDetails: "Buka imininingwane",
    confirmPayment: "Qinisekisa inkokhelo",
    markPaidSuccess: (name) => `${name} umakwe njengokhokhiwe`,
    markPaidFailed: "Ayikwazanga ukumaka isikweletu njengokhokhiwe",
    validation: {
      amountRequired: "Inani liyadingeka",
      amountValid: "Inani kufanele libe linembile",
      amountPositive: "Inani kufanele lidlule kuqanda",
      amountCannotExceed: (balance) => `Inani alikwazi ukudlula ${balance}`,
    },
  },
};

Object.entries(debtsTranslations).forEach(([language, debts]) => {
  translations[language] = {
    ...(translations[language] || {}),
    debts,
  };
});

const analyticsTranslations = {
  ar: {
    analyticsTitle: "التحليلات",
    analyticsSubtitle: (count) =>
      `${count} نصائح للأعمال · إعادة التخزين وأنماط الشراء وسرعة الدوران والطلب وأداء المنتجات`,
    analyticsBack: "التحليلات",
    refresh: "تحديث",
    tryAgain: "حاول مرة أخرى",
    previous: "السابق",
    next: "التالي",
    sessionNotReady: "جلستك غير جاهزة بعد.",
    pageSummary: (page, totalPages, totalCount) =>
      `الصفحة ${page} من ${totalPages} · ${totalCount} عنصر`,
    cards: {
      restock: {
        title: "خطط لإعادة التخزين",
        metric: "مراقبة المخزون",
        detail: "اعرف العناصر التي تنخفض كميتها وما قد تحتاج إلى شرائه قريبًا.",
        suggestion: "اشترِ ما يكفي لتغطية النقص الحالي ودورة المورد التالية.",
      },
      buyingPatterns: {
        title: "أنماط شراء العملاء",
        metric: "إشارة السلة",
        detail: "يوضح العناصر التي يشتريها العملاء عادةً في نفس العملية.",
        suggestion: "ضع المنتجات المتلازمة قرب بعضها لزيادة قيمة السلة.",
      },
      turnover: {
        title: "المنتجات السريعة والبطيئة في البيع",
        metric: "الدوران",
        detail:
          "يوضح المنتجات التي تُباع سريعًا وتلك التي تبقى أطول في المتجر.",
        suggestion:
          "أبقِ السريعة ظاهرة وراجع المخزون البطيء قبل الشراء من جديد.",
      },
      demandForecast: {
        title: "ما الذي قد يحتاجه العملاء لاحقًا",
        metric: "توقع الطلب",
        detail:
          "يعطي فكرة بسيطة عما قد يطلبه العملاء قريبًا بناءً على المبيعات الأخيرة.",
        suggestion: "استخدم الطلب الأخير للتخطيط للطلبية القادمة بثقة أكبر.",
      },
      topPerformers: {
        title: "أفضل المنتجات أداءً",
        metric: "قادة الإيرادات",
        detail: "يعرض المنتجات التي تجلب أكبر قدر من المال لمتجرك.",
        suggestion: "راقب أقوى منتجاتك جيدًا حتى تبقى متاحة.",
      },
    },
    restockPlanner: {
      title: "مخطط إعادة التخزين",
      subtitle:
        "المخزون الحالي وسرعة المبيعات وتوقيت المورد في تدفق شراء واحد.",
      loading: "جارٍ إعداد خطة إعادة التخزين المباشرة",
      errorTitle: "خطة إعادة التخزين غير جاهزة",
      summaryNeedsAttention: "تحتاج إلى انتباه",
      summaryMonitor: "راقب عن قرب",
      summaryHealthy: "الوضع جيد اليوم",
      productsToCheck: (count) => `${count} منتجًا للمراجعة`,
      stockLooksHealthy: "المخزون يبدو جيدًا",
      productsToCheckDescription:
        "بعض المنتجات تقترب من نقطة إعادة الطلب بناءً على المخزون الحالي والمبيعات الأخيرة وتوقيت المورد.",
      stockHealthyDescription:
        "لا يوجد شيء يقترب من النفاد بناءً على أحدث سجلات المخزون والمبيعات.",
      supplierLeadTime: "مدة المورد",
      safetyStock: "مخزون الأمان",
      estimatedBuy: "تكلفة الشراء التقديرية",
      withDataSteps: [
        "ابدأ بالمنتجات المعلّمة بعاجل أو قريبًا.",
        "استخدم مدة المورد عند تحديد وقت الشراء.",
        "أكد عدد الرفوف قبل الطلب ثم حدّث المخزون بعد التسليم.",
      ],
      withoutDataSteps: [
        "استمر في تسجيل المبيعات وعدّ المخزون.",
        "راجع هذا مرة أخرى قبل طلبك القادم من المورد.",
        "أضف حدود انخفاض المخزون للمنتجات التي لا تحتوي عليها بعد.",
      ],
      detailedView: "عرض تفصيلي",
      openFullList: "افتح قائمة إعادة التخزين الكاملة",
      reviewList: "راجع قائمة إعادة التخزين",
      fullListDescription: (count) =>
        `${count} منتجًا جاهزًا في العرض المخصص مع كميات الشراء وإشارات التكلفة.`,
      reviewListDescription:
        "لا يوجد شيء عاجل الآن، لكن يمكنك فتح العرض المخصص ومراجعة أحدث مخرجات المخطط.",
      viewRestockList: "عرض قائمة إعادة التخزين",
      livePlan: "خطة مباشرة",
      updatedAt: (date) => `تم التحديث ${date}`,
    },
    restockList: {
      title: "قائمة إعادة التخزين",
      subtitle: "توصيات تفصيلية للمنتجات لطلب المورد القادم.",
      loading: "جارٍ تحميل توصيات إعادة التخزين",
      errorTitle: "قائمة إعادة التخزين غير جاهزة",
      reviewedNow: "تمت المراجعة الآن",
      reviewedAt: (date) => `تمت المراجعة ${date}`,
      inventoryGoodShape: "المخزون بحالة جيدة",
      noRestockNeeded: "لا حاجة لإعادة التخزين الآن",
      noRestockDescription: "المخزون الحالي يغطي الطلب المتوقع ضمن مدة المورد.",
      supplierLeadTime: "مدة المورد",
      safetyStock: "مخزون الأمان",
      urgencyUrgent: "عاجل",
      urgencySoon: "قريبًا",
      urgencyWatch: "مراقبة",
      buyAtLeast: "اشترِ على الأقل",
      estimatedCost: "التكلفة التقديرية",
      inStock: "في المخزون",
      leadTimeDemand: "طلب مدة المورد",
      sold30Days: "المباع خلال 30 يومًا",
      dailySales: "المبيعات اليومية",
    },
    buyingPatterns: {
      title: "أنماط شراء العملاء",
      subtitle:
        "ترابطات المنتجات المباشرة من المبيعات المتزامنة وسلال العملاء.",
      loading: "جارٍ البحث عن ترابطات متكررة",
      errorTitle: "أنماط الشراء غير جاهزة",
      summaryHighIntent: "نية مرتفعة",
      summaryEmerging: "سلوك ناشئ",
      summaryBuilding: "الإشارة تتكوّن",
      usefulPairings: (count) => `تم العثور على ${count} ترابطات مفيدة`,
      noStrongPair: "لا يوجد ترابط قوي بعد",
      usefulDescription: "تظهر بعض المنتجات معًا بشكل متكرر في سلال العملاء.",
      noStrongPairDescription:
        "لا توجد مبيعات مختلطة متكررة كافية بعد لتحديد زوج منتجات موثوق.",
      salesChecked: "المبيعات المفحوصة",
      pairingsFound: "الترابطات المكتشفة",
      strongestLift: "أقوى رفع",
      withDataSteps: [
        "ضع المنتجات المتلازمة بالقرب من بعضها ليلاحظها العملاء.",
        "اقترح المنتج الثاني عندما يشتري العميل الأول.",
        "جرّب حزمة صغيرة أو لافتة رف لأقوى زوج.",
      ],
      withoutDataSteps: [
        "استمر في تسجيل المبيعات متعددة العناصر في إيصال واحد.",
        "راقب المنتجات التي يختارها العملاء طبيعيًا معًا.",
        "راجع هذا مرة أخرى بعد ورود مبيعات مختلطة أكثر.",
      ],
      detailedView: "عرض تفصيلي",
      openFullList: "افتح قائمة الترابطات الكاملة",
      reviewList: "راجع قائمة الترابطات",
      fullListDescription: (count) =>
        `${count} ترابطات جاهزة في العرض المخصص مع القوة ومقاييس السلة.`,
      reviewListDescription:
        "لا يوجد نمط قوي بعد، لكن يمكنك فتح العرض المخصص ومراجعة أحدث تحليل للسلة.",
      viewPairingList: "عرض قائمة الترابطات",
      livePairings: "ترابطات مباشرة",
      updatedAt: (date) => `تم التحديث ${date}`,
      listTitle: "قائمة الترابطات",
      listSubtitle: "ترابطات تفصيلية للسلة من المبيعات الحديثة متعددة العناصر.",
      listLoading: "جارٍ تحميل ترابطات المنتجات",
      listErrorTitle: "قائمة الترابطات غير جاهزة",
      basketBehavior: "سلوك السلة ما زال يتكوّن",
      noReliablePairing: "لا يوجد ترابط موثوق بعد",
      noReliablePairingDescription:
        "نحتاج إلى مبيعات مختلطة أكثر قبل أن تعرض هذه القائمة تركيبات منتجات يعتمد عليها.",
      salesWindow: "نافذة المبيعات",
      shareOfSales: "حصة المبيعات",
      pairStrength: "قوة الترابط",
      extraSignal: "إشارة إضافية",
      pairFrequency: "تكرار الترابط",
      firstProduct: "المنتج الأول",
      secondProduct: "المنتج الثاني",
      paymentSaleCount: (count) => `${count} عملية بيع`,
      reviewedNow: "تمت المراجعة الآن",
      reviewedAt: (date) => `تمت المراجعة ${date}`,
    },
    turnover: {
      title: "المنتجات السريعة والبطيئة في البيع",
      subtitle: "سرعة بيع مباشرة من المخزون المتزامن والمبيعات.",
      loading: "جارٍ فحص حركة المنتجات",
      errorTitle: "عرض الدوران غير جاهز",
      summaryClear: "حركة واضحة",
      summaryEarly: "حركة مبكرة",
      summaryBuilding: "الإشارة تتكوّن",
      summaryCounts: (fast, slow) => `${fast} سريع، ${slow} بطيء`,
      withSlowDescription:
        "بعض المنتجات تُباع بسرعة بينما يستغرق البعض الآخر وقتًا أطول لمغادرة المتجر.",
      withoutSlowDescription:
        "المخزون المتتبع لديك لا يُظهر مشكلة بطء بيع خطيرة الآن.",
      fastMovers: "سريعة الحركة",
      slowSellers: "بطيئة البيع",
      turnoverRate: "معدل البيع من المخزون",
      steps: [
        "أبقِ السريعة ظاهرة وسهلة الوصول.",
        "تحقق من السعر والموقع وتاريخ الصلاحية أو اهتمام العملاء للمنتجات البطيئة.",
        "تجنب شراء المزيد من المخزون البطيء حتى تفهم سبب عدم تحركه.",
      ],
      detailedView: "عرض تفصيلي",
      openFullList: "افتح قائمة الحركة الكاملة",
      reviewList: "راجع قائمة الحركة",
      fullListDescription: (count) =>
        `${count} منتجًا جاهزًا في العرض المخصص مع نطاقات الحركة وسياق تغطية المخزون.`,
      reviewListDescription:
        "لا توجد إشارة حركة قوية بعد، لكن يمكنك فتح العرض المخصص ومراجعة أحدث تحليل لسرعة المخزون.",
      viewMovementList: "عرض قائمة الحركة",
      liveMovement: "حركة مباشرة",
      updatedAt: (date) => `تم التحديث ${date}`,
      notTracked: "غير متتبع",
      tooEarly: "مبكر جدًا",
      daysCover90Plus: "+90 يومًا",
      aboutDays: (days) => `حوالي ${days} يومًا`,
      listTitle: "قائمة الحركة",
      listSubtitle:
        "حركة تفصيلية للمنتجات من المبيعات الحديثة والمخزون المتتبع.",
      listLoading: "جارٍ تحميل حركة المنتجات",
      listErrorTitle: "قائمة الحركة غير جاهزة",
      couldNotLoadProducts: "تعذر تحميل منتجات الدوران.",
      fastDescription: "المنتجات الأسرع بيعًا في نافذة المبيعات هذه.",
      fastEmpty: "لا توجد منتجات سريعة الحركة بعد في نافذة المبيعات هذه.",
      slowDescription: "المنتجات التي قد تحتجز النقد على الرف.",
      slowEmpty: "لا توجد منتجات بطيئة أو راكدة تحتاج إلى مراجعة الآن.",
      slowReviewTitle: "منتجات بطيئة للمراجعة",
      sold30Days: "المباع خلال 30 يومًا",
      weeklyRunRate: "معدل البيع الأسبوعي",
      currentStock: "المخزون الحالي",
      stockMayLast: "قد يدوم المخزون",
      reviewedNow: "تمت المراجعة الآن",
      reviewedAt: (date) => `تمت المراجعة ${date}`,
    },
    demandForecast: {
      title: "ما الذي قد يحتاجه العملاء لاحقًا",
      subtitle: "استقرار الطلب المباشر من المبيعات الأخيرة.",
      loading: "جارٍ تجميع أنماط الطلب",
      errorTitle: "توقع الطلب غير جاهز",
      summaryReliable: "طلب موثوق",
      summaryEarly: "نمط طلب مبكر",
      summaryBuilding: "السجل يتكوّن",
      summaryCounts: (steady, care) => `${steady} ثابت، ${care} يحتاج متابعة`,
      withDataDescription:
        "بعض المنتجات عليها طلب ثابت بينما يرتفع وينخفض غيرها من أسبوع لآخر.",
      withoutDataDescription:
        "لا يوجد سجل مبيعات كافٍ بعد لإظهار نمط طلب موثوق.",
      steady: "ثابت",
      watch: "مراقبة",
      irregular: "غير منتظم",
      steps: [
        "اشترِ المنتجات ذات الطلب الثابت بثقة أكبر.",
        "اشترِ كميات أصغر للمنتجات غير المنتظمة حتى يتضح النمط أكثر.",
        "استخدم قائمة المنتجات أدناه قبل طلب المورد القادم.",
      ],
      detailedView: "عرض تفصيلي",
      openFullList: "افتح قائمة الطلب الكاملة",
      reviewList: "راجع قائمة الطلب",
      fullListDescription: (count) =>
        `${count} منتجًا جاهزًا في العرض المخصص مع نطاقات الطلب ومقاييس الاستقرار.`,
      reviewListDescription:
        "لا يوجد سجل مبيعات كافٍ بعد، لكن يمكنك فتح العرض المخصص ومراجعة أحدث تحليل للطلب.",
      viewDemandList: "عرض قائمة الطلب",
      liveDemand: "طلب مباشر",
      updatedAt: (date) => `تم التحديث ${date}`,
      listTitle: "قائمة الطلب",
      listSubtitle: "نطاقات تفصيلية لطلب المنتجات من سجل المبيعات الحديث.",
      listLoading: "جارٍ تحميل مجموعات الطلب",
      listErrorTitle: "قائمة الطلب غير جاهزة",
      couldNotLoadProducts: "تعذر تحميل منتجات الطلب.",
      salesHistoryBuilding: "سجل المبيعات ما زال يتكوّن",
      noReliableBand: "لا يوجد نطاق طلب موثوق بعد",
      noReliableBandDescription:
        "بضعة أسابيع إضافية من المبيعات ستساعد هذا العرض على فرز المنتجات بثقة أكبر.",
      bandStableDescription: "منتجات يطلبها العملاء بانتظام.",
      bandStableEmpty: "لا توجد منتجات في الطلب الثابت بعد.",
      bandStableLong: "طلب ثابت",
      bandWatchDescription: "منتجات تستحق المتابعة قبل شراء كميات كبيرة.",
      bandWatchEmpty: "لا توجد منتجات في قائمة المراقبة بعد.",
      bandWatchLong: "قائمة المراقبة",
      bandIrregularDescription: "منتجات يرتفع وينخفض طلبها من أسبوع لآخر.",
      bandIrregularEmpty: "لا توجد منتجات في الطلب غير المنتظم بعد.",
      bandIrregularLong: "طلب غير منتظم",
      avgWeekly: "متوسط أسبوعي",
      weeksWithSales: "أسابيع بها مبيعات",
      soldInWindow: "المباع في النافذة",
      variability: "التقلب",
    },
    topPerformers: {
      title: "أفضل المنتجات أداءً",
      subtitle: "ترتيب مباشر للمنتجات من المبيعات المتزامنة.",
      loading: "جارٍ ترتيب أفضل المنتجات حسب الإيراد",
      errorTitle: "أفضل المنتجات غير جاهزة",
      summaryClear: "قادة واضحون",
      summaryEarly: "ترتيب مبكر",
      summaryBuilding: "سجل المبيعات يتكوّن",
      withDataDescription:
        "مجموعة أصغر من المنتجات تساهم في مبيعاتك أكثر من غيرها.",
      withoutDataDescription:
        "لا توجد بيانات إيرادات منتجات كافية بعد لإظهار أقوى المنتجات.",
      revenueLeaders: "قادة الإيرادات",
      steadySellers: "بائعون ثابتون",
      reviewSoon: "راجع قريبًا",
      withDataSteps: [
        "أبقِ أفضل منتجاتك متوفرة وسهلة العثور.",
        "تحقق من أسعارها وهوامشها قبل تشغيل الخصومات.",
        "راجع المنتجات الضعيفة قبل شراء المزيد منها.",
      ],
      withoutDataSteps: [
        "سجل مبيعات المنتجات حتى يستطيع التطبيق ترتيب ما يجلب المال.",
        "تأكد من تعبئة أسعار المنتجات بشكل صحيح.",
        "راجع هذه الصفحة بعد مزامنة مبيعات أكثر.",
      ],
      detailedView: "عرض تفصيلي",
      openFullList: "افتح قائمة الأداء الكاملة",
      reviewList: "راجع قائمة الأداء",
      fullListDescription: (count) =>
        `${count} منتجًا جاهزًا في العرض المخصص مع مساهمة الإيراد ونطاقات الترتيب.`,
      reviewListDescription:
        "لا توجد بيانات مبيعات كافية بعد، لكن يمكنك فتح العرض المخصص ومراجعة أحدث تحليل للأداء.",
      viewPerformanceList: "عرض قائمة الأداء",
      liveRanking: "ترتيب مباشر",
      updatedAt: (date) => `تم التحديث ${date}`,
      listTitle: "قائمة الأداء",
      listSubtitle: "ترتيب تفصيلي للمنتجات من مساهمة الإيرادات الحديثة.",
      listLoading: "جارٍ تحميل ترتيب المنتجات",
      listErrorTitle: "قائمة الأداء غير جاهزة",
      couldNotLoadProducts: "تعذر تحميل أفضل المنتجات.",
      salesRankingForming: "ترتيب المبيعات ما زال يتكوّن",
      noLeadersYet: "لا يوجد قادة منتجات بعد",
      noLeadersDescription:
        "عندما تحقق المنتجات مبيعات أكثر، سيقوم هذا العرض بترتيبها حسب مساهمة الإيراد وإظهار أقواها.",
      bandADescription: "منتجات تحمل أقوى مساهمة في الإيراد.",
      bandAEmpty: "لا توجد منتجات في قادة الإيرادات بعد.",
      bandALong: "قادة الإيرادات",
      bandBDescription: "بائعون موثوقون يستحقون متابعة منتظمة.",
      bandBEmpty: "لا توجد منتجات في البائعين الثابتين بعد.",
      bandBLong: "بائعون ثابتون",
      bandCDescription: "منتجات للمراجعة قبل شراء المزيد من المخزون.",
      bandCEmpty: "لا توجد منتجات في راجع قريبًا بعد.",
      bandCLong: "راجع قريبًا",
      leaders: "قادة",
      steady: "ثابت",
      review: "مراجعة",
      revenue: "الإيراد",
      contribution: "المساهمة",
      soldInWindow: "المباع في النافذة",
      currentStock: "المخزون الحالي",
    },
  },
  fr: {
    analyticsTitle: "Analyses",
    analyticsSubtitle: (count) =>
      `${count} conseils métier · réassort, paniers, rotation, demande et performance produit`,
    analyticsBack: "Analyses",
    refresh: "Actualiser",
    tryAgain: "Réessayer",
    previous: "Précédent",
    next: "Suivant",
    sessionNotReady: "Votre session n’est pas encore prête.",
    pageSummary: (page, totalPages, totalCount) =>
      `Page ${page} sur ${totalPages} · ${totalCount} éléments`,
    cards: {
      restock: {
        title: "Planifier un réassort",
        metric: "Surveillance stock",
        detail:
          "Voyez quels articles baissent et ce qu’il faudra racheter bientôt.",
        suggestion:
          "Achetez assez pour couvrir le manque actuel et le prochain cycle fournisseur.",
      },
      buyingPatterns: {
        title: "Habitudes d’achat des clients",
        metric: "Signal panier",
        detail:
          "Montre les articles souvent achetés ensemble dans la même vente.",
        suggestion:
          "Placez les produits associés plus près pour augmenter la valeur du panier.",
      },
      turnover: {
        title: "Produits rapides et lents",
        metric: "Rotation",
        detail:
          "Montre les produits qui partent vite et ceux qui restent plus longtemps.",
        suggestion:
          "Gardez les rapides visibles et revoyez le stock lent avant de racheter.",
      },
      demandForecast: {
        title: "Ce que les clients demanderont ensuite",
        metric: "Prévision de demande",
        detail:
          "Donne une idée simple de ce que les clients pourraient demander bientôt.",
        suggestion:
          "Utilisez la demande récente pour préparer la prochaine commande fournisseur.",
      },
      topPerformers: {
        title: "Produits les plus performants",
        metric: "Leaders revenus",
        detail: "Montre les produits qui rapportent le plus à votre boutique.",
        suggestion:
          "Surveillez vos meilleurs produits pour qu’ils restent disponibles.",
      },
    },
    restockPlanner: {
      title: "Plan de réassort",
      subtitle:
        "Stock actuel, vitesse de vente récente et délai fournisseur dans un seul flux d’achat.",
      loading: "Création de votre plan de réassort en direct",
      errorTitle: "Le plan de réassort n’est pas prêt",
      detailedView: "Vue détaillée",
      openFullList: "Ouvrir la liste complète de réassort",
      reviewList: "Revoir la liste de réassort",
      viewRestockList: "Voir la liste de réassort",
      livePlan: "Plan en direct",
      updatedAt: (date) => `Mis à jour ${date}`,
    },
    restockList: {
      title: "Liste de réassort",
      subtitle:
        "Recommandations détaillées pour la prochaine commande fournisseur.",
      loading: "Chargement des recommandations de réassort",
      errorTitle: "La liste de réassort n’est pas prête",
      reviewedNow: "Révisé à l’instant",
      reviewedAt: (date) => `Révisé ${date}`,
      inventoryGoodShape: "Le stock est en bon état",
      noRestockNeeded: "Aucun réassort nécessaire pour le moment",
      noRestockDescription:
        "Le stock actuel couvre la demande prévue pendant votre délai fournisseur.",
      supplierLeadTime: "Délai fournisseur",
      safetyStock: "Stock de sécurité",
      urgencyUrgent: "Urgent",
      urgencySoon: "Bientôt",
      urgencyWatch: "Surveiller",
      buyAtLeast: "Acheter au moins",
      estimatedCost: "Coût estimé",
      inStock: "En stock",
      leadTimeDemand: "Demande pendant le délai",
      sold30Days: "Vendu en 30 jours",
      dailySales: "Ventes quotidiennes",
    },
    buyingPatterns: {
      title: "Habitudes d’achat des clients",
      subtitle:
        "Associations de produits en direct issues des ventes synchronisées et des paniers clients.",
      loading: "Recherche des associations répétées de produits",
      errorTitle: "Les habitudes d’achat ne sont pas prêtes",
      detailedView: "Vue détaillée",
      openFullList: "Ouvrir la liste complète des associations",
      reviewList: "Revoir la liste des associations",
      viewPairingList: "Voir la liste des associations",
      livePairings: "Associations en direct",
      updatedAt: (date) => `Mis à jour ${date}`,
      listTitle: "Liste des associations",
      listSubtitle:
        "Associations détaillées de panier à partir des ventes multi-articles récentes.",
      listLoading: "Chargement des associations de produits",
      listErrorTitle: "La liste des associations n’est pas prête",
      reviewedNow: "Révisé à l’instant",
      reviewedAt: (date) => `Révisé ${date}`,
      noReliablePairing: "Aucune association fiable pour l’instant",
      salesChecked: "Ventes analysées",
      salesWindow: "Fenêtre de ventes",
      pairingsFound: "Associations trouvées",
      strongestLift: "Plus forte affinité",
      shareOfSales: "Part des ventes",
      pairStrength: "Force du duo",
      extraSignal: "Signal supplémentaire",
      pairFrequency: "Fréquence du duo",
      firstProduct: "Premier produit",
      secondProduct: "Deuxième produit",
      paymentSaleCount: (count) => `${count} vente${count === 1 ? "" : "s"}`,
    },
    turnover: {
      title: "Produits rapides et lents",
      subtitle:
        "Vitesse de vente en direct à partir du stock et des ventes synchronisés.",
      loading: "Vérification du mouvement des produits",
      errorTitle: "La vue rotation n’est pas prête",
      fastMovers: "Produits rapides",
      slowSellers: "Produits lents",
      turnoverRate: "Taux d’écoulement",
      detailedView: "Vue détaillée",
      openFullList: "Ouvrir la liste complète des mouvements",
      reviewList: "Revoir la liste des mouvements",
      viewMovementList: "Voir la liste des mouvements",
      liveMovement: "Mouvement en direct",
      updatedAt: (date) => `Mis à jour ${date}`,
      notTracked: "Non suivi",
      tooEarly: "Trop tôt",
      daysCover90Plus: "90+ jours",
      aboutDays: (days) => `Environ ${days} jour${days === 1 ? "" : "s"}`,
      listTitle: "Liste des mouvements",
      listSubtitle:
        "Mouvement détaillé des produits à partir des ventes récentes et du stock suivi.",
      listLoading: "Chargement du mouvement des produits",
      listErrorTitle: "La liste des mouvements n’est pas prête",
      fastDescription:
        "Produits qui se vendent le plus vite pendant cette période.",
      fastEmpty: "Aucun produit rapide pour le moment dans cette période.",
      slowDescription: "Produits qui peuvent immobiliser du cash en rayon.",
      slowEmpty: "Aucun produit lent ou stagnant à revoir pour l’instant.",
      slowReviewTitle: "Produits lents à revoir",
      sold30Days: "Vendu en 30 jours",
      weeklyRunRate: "Rythme hebdomadaire",
      currentStock: "Stock actuel",
      stockMayLast: "Le stock peut durer",
    },
    demandForecast: {
      title: "Ce que les clients demanderont ensuite",
      subtitle:
        "Stabilité de la demande en direct à partir des ventes récentes.",
      loading: "Regroupement des tendances de demande",
      errorTitle: "La prévision de demande n’est pas prête",
      steady: "Stable",
      watch: "Surveiller",
      irregular: "Irrégulier",
      detailedView: "Vue détaillée",
      openFullList: "Ouvrir la liste complète de demande",
      reviewList: "Revoir la liste de demande",
      viewDemandList: "Voir la liste de demande",
      liveDemand: "Demande en direct",
      updatedAt: (date) => `Mis à jour ${date}`,
      listTitle: "Liste de demande",
      listSubtitle:
        "Bandes de demande détaillées à partir de l’historique récent des ventes.",
      listLoading: "Chargement des groupes de demande",
      listErrorTitle: "La liste de demande n’est pas prête",
      salesHistoryBuilding: "L’historique de ventes se construit encore",
      noReliableBand: "Aucune bande de demande fiable pour l’instant",
      avgWeekly: "Moy. hebdo",
      weeksWithSales: "Semaines avec ventes",
      soldInWindow: "Vendu sur la période",
      variability: "Variabilité",
    },
    topPerformers: {
      title: "Produits les plus performants",
      subtitle:
        "Classement produit en direct à partir des ventes synchronisées.",
      loading: "Classement des meilleurs produits par revenu",
      errorTitle: "Les meilleurs produits ne sont pas prêts",
      revenueLeaders: "Leaders revenus",
      steadySellers: "Vendeurs stables",
      reviewSoon: "À revoir bientôt",
      detailedView: "Vue détaillée",
      openFullList: "Ouvrir la liste complète de performance",
      reviewList: "Revoir la liste de performance",
      viewPerformanceList: "Voir la liste de performance",
      liveRanking: "Classement en direct",
      updatedAt: (date) => `Mis à jour ${date}`,
      listTitle: "Liste de performance",
      listSubtitle:
        "Classement détaillé des produits selon leur contribution récente au revenu.",
      listLoading: "Chargement du classement produit",
      listErrorTitle: "La liste de performance n’est pas prête",
      salesRankingForming: "Le classement des ventes se construit encore",
      noLeadersYet: "Aucun leader produit pour l’instant",
      leaders: "Leaders",
      steady: "Stable",
      review: "Révision",
      revenue: "Revenu",
      contribution: "Contribution",
      soldInWindow: "Vendu sur la période",
      currentStock: "Stock actuel",
    },
  },
  ha: {
    analyticsTitle: "Nazari",
    analyticsSubtitle: (count) =>
      `${count} shawarwarin kasuwanci · sake saye, halin saye, motsi, bukata da aikin kaya`,
    analyticsBack: "Nazari",
    refresh: "Sake sabuntawa",
    tryAgain: "Sake gwadawa",
    previous: "Na baya",
    next: "Na gaba",
    sessionNotReady: "Session dinka bai shirya ba tukuna.",
    pageSummary: (page, totalPages, totalCount) =>
      `Shafi ${page} na ${totalPages} · abubuwa ${totalCount}`,
    restockPlanner: {
      title: "Tsarin sake saye",
      loading: "Ana gina tsarin sake sayenka kai tsaye",
      errorTitle: "Tsarin sake saye bai shirya ba",
      viewRestockList: "Duba jerin sake saye",
    },
    buyingPatterns: {
      title: "Halin sayen kwastomomi",
      loading: "Ana gano kayayyakin da ake yawan saya tare",
      errorTitle: "Halin saye bai shirya ba",
      listTitle: "Jerin haɗuwar kaya",
    },
    turnover: {
      title: "Kayayyakin da ke saurin fita da masu jinkirin fita",
      fastMovers: "Masu saurin fita",
      slowSellers: "Masu jinkirin fita",
      listTitle: "Jerin motsin kaya",
    },
    demandForecast: {
      title: "Abin da kwastomomi za su iya bukata na gaba",
      steady: "Tsayayye",
      watch: "Sa ido",
      irregular: "Ba daidai ba",
      listTitle: "Jerin bukata",
    },
    topPerformers: {
      title: "Kayayyakin da suka fi kyau",
      revenueLeaders: "Shugabannin kudaden shiga",
      steadySellers: "Masu sayarwa a kai a kai",
      reviewSoon: "A duba nan kusa",
      listTitle: "Jerin aiki",
    },
  },
  ig: {
    analyticsTitle: "Nyocha",
    analyticsSubtitle: (count) =>
      `${count} ndụmọdụ azụmahịa · nkwakọba ọzọ, njikọta ịzụ ahịa, ngagharị, mkpa na arụmọrụ ngwaahịa`,
    analyticsBack: "Nyocha",
    refresh: "Mee ọhụrụ",
    tryAgain: "Nwaa ọzọ",
    previous: "Nke gara aga",
    next: "Ọzọ",
    sessionNotReady: "Session gị adịghị njikere ugbu a.",
    pageSummary: (page, totalPages, totalCount) =>
      `Ibe ${page} n’ime ${totalPages} · ihe ${totalCount}`,
    restockPlanner: {
      title: "Atụmatụ nkwakọba ọzọ",
      loading: "A na-ewu atụmatụ nkwakọba ọzọ gị",
      errorTitle: "Atụmatụ nkwakọba ọzọ adịghị njikere",
      viewRestockList: "Lee ndepụta nkwakọba ọzọ",
    },
    buyingPatterns: {
      title: "Àgwà ịzụ ahịa ndị ahịa",
      loading: "A na-achọta njikọta ngwaahịa na-eme ugboro ugboro",
      errorTitle: "Àgwà ịzụ ahịa adịghị njikere",
      listTitle: "Ndepụta njikọta",
    },
    turnover: {
      title: "Ngwaahịa na-apụ ngwa ngwa na ndị na-apụ nwayọ",
      fastMovers: "Ndị na-apụ ngwa ngwa",
      slowSellers: "Ndị na-apụ nwayọ",
      listTitle: "Ndepụta mmegharị",
    },
    demandForecast: {
      title: "Ihe ndị ahịa nwere ike ịchọ n’ọdịnihu",
      steady: "Kwụsie ike",
      watch: "Lelee",
      irregular: "Na-adịghị agbanwe agbanwe",
      listTitle: "Ndepụta mkpa",
    },
    topPerformers: {
      title: "Ngwaahịa kacha arụ ọrụ",
      revenueLeaders: "Ndị isi ego ntinye",
      steadySellers: "Ndị na-ere mgbe niile",
      reviewSoon: "Lelee n’oge na-adịghị anya",
      listTitle: "Ndepụta arụmọrụ",
    },
  },
  pt: {
    analyticsTitle: "Análises",
    analyticsSubtitle: (count) =>
      `${count} dicas de negócio · reposição, padrões de compra, rotação, procura e desempenho de produtos`,
    analyticsBack: "Análises",
    refresh: "Atualizar",
    tryAgain: "Tente novamente",
    previous: "Anterior",
    next: "Seguinte",
    sessionNotReady: "A sua sessão ainda não está pronta.",
    pageSummary: (page, totalPages, totalCount) =>
      `Página ${page} de ${totalPages} · ${totalCount} itens`,
    restockPlanner: {
      title: "Plano de reposição",
      subtitle:
        "Stock atual, velocidade recente de vendas e prazo do fornecedor num só fluxo de compra.",
      loading: "A criar o seu plano de reposição em direto",
      errorTitle: "O plano de reposição não está pronto",
      detailedView: "Vista detalhada",
      openFullList: "Abrir a lista completa de reposição",
      reviewList: "Rever a lista de reposição",
      viewRestockList: "Ver lista de reposição",
      livePlan: "Plano em direto",
      updatedAt: (date) => `Atualizado ${date}`,
    },
    restockList: {
      title: "Lista de reposição",
      subtitle:
        "Recomendações detalhadas de produtos para a próxima encomenda ao fornecedor.",
      loading: "A carregar recomendações de reposição",
      errorTitle: "A lista de reposição não está pronta",
      reviewedNow: "Revisto agora mesmo",
      reviewedAt: (date) => `Revisto ${date}`,
      inventoryGoodShape: "O stock está em boa forma",
      noRestockNeeded: "Não é preciso repor agora",
      supplierLeadTime: "Prazo do fornecedor",
      safetyStock: "Stock de segurança",
      buyAtLeast: "Comprar pelo menos",
      estimatedCost: "Custo estimado",
      inStock: "Em stock",
      sold30Days: "Vendido em 30 dias",
      dailySales: "Vendas diárias",
    },
    buyingPatterns: {
      title: "Padrões de compra dos clientes",
      subtitle:
        "Combinações de produtos em direto a partir de vendas sincronizadas e cestos dos clientes.",
      loading: "A procurar combinações repetidas de produtos",
      errorTitle: "Os padrões de compra não estão prontos",
      detailedView: "Vista detalhada",
      openFullList: "Abrir a lista completa de combinações",
      reviewList: "Rever a lista de combinações",
      viewPairingList: "Ver lista de combinações",
      listTitle: "Lista de combinações",
      listSubtitle:
        "Combinações detalhadas de cestos a partir de vendas recentes com vários itens.",
    },
    turnover: {
      title: "Produtos rápidos e lentos",
      subtitle:
        "Velocidade de venda em direto a partir do stock e das vendas sincronizadas.",
      loading: "A verificar o movimento dos produtos",
      errorTitle: "A vista de rotação não está pronta",
      fastMovers: "Rápidos",
      slowSellers: "Lentos",
      turnoverRate: "Taxa de venda do stock",
      listTitle: "Lista de movimento",
      listSubtitle:
        "Movimento detalhado dos produtos a partir de vendas recentes e stock acompanhado.",
      listLoading: "A carregar o movimento dos produtos",
      listErrorTitle: "A lista de movimento não está pronta",
    },
    demandForecast: {
      title: "O que os clientes podem precisar a seguir",
      subtitle:
        "Estabilidade da procura em direto a partir das vendas recentes.",
      loading: "A agrupar padrões de procura",
      errorTitle: "A previsão de procura não está pronta",
      steady: "Estável",
      watch: "Observar",
      irregular: "Irregular",
      listTitle: "Lista de procura",
      listSubtitle:
        "Faixas detalhadas de procura com base no histórico recente de vendas.",
      listLoading: "A carregar grupos de procura",
      listErrorTitle: "A lista de procura não está pronta",
    },
    topPerformers: {
      title: "Produtos com melhor desempenho",
      subtitle:
        "Classificação de produtos em direto a partir das vendas sincronizadas.",
      loading: "A classificar os melhores produtos por receita",
      errorTitle: "Os melhores produtos não estão prontos",
      revenueLeaders: "Líderes de receita",
      steadySellers: "Vendedores estáveis",
      reviewSoon: "Rever em breve",
      listTitle: "Lista de desempenho",
      listSubtitle:
        "Classificação detalhada de produtos pela contribuição recente para a receita.",
      listLoading: "A carregar a classificação dos produtos",
      listErrorTitle: "A lista de desempenho não está pronta",
    },
  },
  sw: {
    analyticsTitle: "Uchanganuzi",
    analyticsSubtitle: (count) =>
      `${count} vidokezo vya biashara · uongezaji wa stok, mifumo ya ununuzi, mzunguko, mahitaji na utendaji wa bidhaa`,
    analyticsBack: "Uchanganuzi",
    refresh: "Onyesha upya",
    tryAgain: "Jaribu tena",
    previous: "Iliyopita",
    next: "Ifuatayo",
    sessionNotReady: "Kikao chako bado hakijawa tayari.",
    pageSummary: (page, totalPages, totalCount) =>
      `Ukurasa wa ${page} kati ya ${totalPages} · vipengee ${totalCount}`,
    restockPlanner: {
      title: "Mpango wa kuongeza stok",
      viewRestockList: "Tazama orodha ya kuongeza stok",
      loading: "Inatengeneza mpango wako wa kuongeza stok",
      errorTitle: "Mpango wa kuongeza stok haujawa tayari",
    },
    buyingPatterns: {
      title: "Mifumo ya ununuzi ya wateja",
      viewPairingList: "Tazama orodha ya mchanganyiko",
      loading: "Inatafuta mchanganyiko wa bidhaa unaojirudia",
      errorTitle: "Mifumo ya ununuzi haijawa tayari",
      listTitle: "Orodha ya mchanganyiko",
    },
    turnover: {
      title: "Bidhaa zinazouzwa haraka na polepole",
      fastMovers: "Wauzaji wa haraka",
      slowSellers: "Wauzaji wa polepole",
      listTitle: "Orodha ya mzunguko",
      listLoading: "Inapakia mwendo wa bidhaa",
    },
    demandForecast: {
      title: "Wateja wanaweza kuhitaji nini baadaye",
      steady: "Thabiti",
      watch: "Angalia",
      irregular: "Isiyo thabiti",
      listTitle: "Orodha ya mahitaji",
    },
    topPerformers: {
      title: "Bidhaa zenye utendaji bora",
      revenueLeaders: "Viongozi wa mapato",
      steadySellers: "Wauzaji thabiti",
      reviewSoon: "Kagua karibuni",
      listTitle: "Orodha ya utendaji",
    },
  },
  yo: {
    analyticsTitle: "Ìtúpalẹ̀",
    analyticsSubtitle: (count) =>
      `${count} ìmọ̀ràn owóòṣùwò · fífi stok kún, àṣà rira, ìyípo, ìbéèrè àti iṣẹ́ ọja`,
    analyticsBack: "Ìtúpalẹ̀",
    refresh: "Tun ṣe",
    tryAgain: "Gbiyanju lẹẹkansi",
    previous: "Tẹ́lẹ̀",
    next: "Tó kàn",
    sessionNotReady: "Session rẹ kò tíì ṣetan.",
    pageSummary: (page, totalPages, totalCount) =>
      `Ojú-ìwé ${page} nínú ${totalPages} · ohun ${totalCount}`,
    restockPlanner: {
      title: "Eto fífi stok kún",
      loading: "N ṣẹda eto fífi stok kún rẹ",
      errorTitle: "Eto fífi stok kún kò tíì ṣetan",
      viewRestockList: "Wo àkójọ fífi stok kún",
    },
    buyingPatterns: {
      title: "Àṣà rira àwọn oníbàárà",
      loading: "N wa àwọn àjọṣe ọja tó ń tún hàn",
      errorTitle: "Àṣà rira kò tíì ṣetan",
      listTitle: "Àkójọ àjọṣe",
    },
    turnover: {
      title: "Ọja tó ń tà yarayara àti lọra",
      fastMovers: "Tita yarayara",
      slowSellers: "Tita lọra",
      listTitle: "Àkójọ ìrìnàjò ọja",
    },
    demandForecast: {
      title: "Ohun tí àwọn oníbàárà lè nílò lẹ́yìn",
      steady: "Dídúróṣinṣin",
      watch: "Ṣọ́ra",
      irregular: "Kò déédé",
      listTitle: "Àkójọ ìbéèrè",
    },
    topPerformers: {
      title: "Àwọn ọja tó ṣiṣẹ́ dáadáa jù",
      revenueLeaders: "Olórí owó-wiwọlé",
      steadySellers: "Tita tó dúróṣinṣin",
      reviewSoon: "Ṣàyẹ̀wò láìpẹ́",
      listTitle: "Àkójọ iṣẹ́",
    },
  },
  zu: {
    analyticsTitle: "Ukuhlaziya",
    analyticsSubtitle: (count) =>
      `${count} amathiphu ebhizinisi · ukugcwalisa isitoko, izindlela zokuthenga, ukujikeleza, isidingo nokusebenza kwemikhiqizo`,
    analyticsBack: "Ukuhlaziya",
    refresh: "Vuselela",
    tryAgain: "Zama futhi",
    previous: "Okwedlule",
    next: "Okulandelayo",
    sessionNotReady: "Iseshini yakho ayikalungi okwamanje.",
    pageSummary: (page, totalPages, totalCount) =>
      `Ikhasi ${page} ku-${totalPages} · izinto ${totalCount}`,
    restockPlanner: {
      title: "Uhlelo lokugcwalisa isitoko",
      loading: "Kwakhiwa uhlelo lwakho lokugcwalisa isitoko",
      errorTitle: "Uhlelo lokugcwalisa isitoko alusakalungi",
      viewRestockList: "Buka uhlu lokugcwalisa isitoko",
    },
    buyingPatterns: {
      title: "Izindlela zokuthenga zamakhasimende",
      loading: "Kuseshwa ukuhambisana kwemikhiqizo okuvame ukuphindwa",
      errorTitle: "Izindlela zokuthenga azikalungi",
      listTitle: "Uhlu lokuhambisana",
    },
    turnover: {
      title: "Imikhiqizo eshesha futhi ebambezela ukuthengisa",
      fastMovers: "Esheshayo",
      slowSellers: "Ephuzayo",
      listTitle: "Uhlu lokunyakaza",
    },
    demandForecast: {
      title: "Okungase kudingwe amakhasimende ngokulandelayo",
      steady: "Kuzinzile",
      watch: "Bheka",
      irregular: "Akuzinzile",
      listTitle: "Uhlu lwesidingo",
    },
    topPerformers: {
      title: "Imikhiqizo esebenza kakhulu",
      revenueLeaders: "Abaholi bemali engenayo",
      steadySellers: "Abathengisi abazinze",
      reviewSoon: "Buyekeza maduze",
      listTitle: "Uhlu lokusebenza",
    },
  },
};

Object.entries(analyticsTranslations).forEach(([language, analytics]) => {
  translations[language] = {
    ...(translations[language] || {}),
    analytics: {
      ...(translations[language]?.analytics || {}),
      ...analytics,
    },
  };
});

const notificationsPageTranslations = {
  ar: {
    title: "الإشعارات",
    subtitle: "تنبيهات وملخصات وإعدادات التسليم لمساحة العمل هذه.",
    markAllRead: "تحديد الكل كمقروء",
    messages: "الرسائل",
    unread: "غير المقروءة",
    channelsOn: "القنوات المفعلة",
    inbox: "الوارد",
    preferences: "التفضيلات",
    filterAll: "الكل",
    filterStock: "المخزون",
    filterSales: "المبيعات",
    filterSync: "المزامنة",
    filterInsights: "الرؤى",
    noNotificationsFound: "لم يتم العثور على إشعارات",
    noNotificationsDescription:
      "جرّب عامل تصفية آخر أو عد لاحقًا بعد نشاط متجر أكثر.",
    selectNotification: "اختر إشعارًا",
    deliveryPreferences: "تفضيلات التسليم",
    enabled: "مفعل",
    disabled: "معطل",
    on: "تشغيل",
    off: "إيقاف",
    updatePreferencesFailed: "تعذر تحديث التفضيلات",
    today: "اليوم",
    earlier: "سابقًا",
    reviewSync: "راجع المزامنة",
    addStock: "أضف مخزونًا",
    syncNeedsAttention: "المزامنة تحتاج انتباهًا",
    syncPending: "المزامنة معلقة",
    queuedRecords: (count) => `${count} سجلًا في انتظار المزامنة.`,
    stockOut: "نفاد المخزون",
    lowStock: "انخفاض المخزون",
    outOfStockBody: (name) => `${name} نفد من المخزون.`,
    lowStockBody: (name, count) => `بقي من ${name} عدد ${count}.`,
    prefDailySummary: "ملخص يومي",
    prefDailySummaryDesc: "إيراد نهاية اليوم، عدد المبيعات، وأفضل المتحركات.",
    prefLowStock: "تنبيهات انخفاض المخزون",
    prefLowStockDesc: "عندما ينخفض المنتج تحت حدّه.",
    prefSmsSummary: "ملخص الرسائل النصية",
    prefSmsSummaryDesc: "ملخص SMS للمالك يوميًا الساعة 8 مساءً.",
    prefWeeklyInsights: "رؤى أسبوعية",
    prefWeeklyInsightsDesc: "اكتشاف الأنماط، السريع/البطيء، والشذوذ.",
    prefMonthlyReports: "تقارير شهرية",
    prefMonthlyReportsDesc: "ملخص شهري كامل بأسلوب الأرباح والخسائر.",
  },
  fr: {
    title: "Notifications",
    subtitle: "Alertes, résumés et préférences d’envoi pour cet espace.",
    markAllRead: "Tout marquer comme lu",
    messages: "Messages",
    unread: "Non lus",
    channelsOn: "Canaux actifs",
    inbox: "Boîte de réception",
    preferences: "Préférences",
    filterAll: "Tout",
    filterStock: "Stock",
    filterSales: "Ventes",
    filterSync: "Sync",
    filterInsights: "Insights",
    noNotificationsFound: "Aucune notification trouvée",
    noNotificationsDescription:
      "Essayez un autre filtre ou revenez après plus d’activité.",
    selectNotification: "Sélectionnez une notification",
    deliveryPreferences: "Préférences d’envoi",
    enabled: "activé",
    disabled: "désactivé",
    on: "Oui",
    off: "Non",
    updatePreferencesFailed: "Impossible de mettre à jour les préférences",
    today: "Aujourd’hui",
    earlier: "Plus tôt",
    reviewSync: "Vérifier la sync",
    addStock: "Ajouter du stock",
  },
  pt: {
    title: "Notificações",
    subtitle:
      "Alertas, resumos e controlos de entrega para este espaço de trabalho.",
    markAllRead: "Marcar tudo como lido",
    messages: "Mensagens",
    unread: "Não lidas",
    channelsOn: "Canais ativos",
    inbox: "Caixa de entrada",
    preferences: "Preferências",
    filterAll: "Todos",
    filterStock: "Stock",
    filterSales: "Vendas",
    filterSync: "Sync",
    filterInsights: "Insights",
    noNotificationsFound: "Nenhuma notificação encontrada",
    noNotificationsDescription:
      "Tente outro filtro ou volte depois de mais atividade.",
    selectNotification: "Selecione uma notificação",
    deliveryPreferences: "Preferências de entrega",
    enabled: "ativado",
    disabled: "desativado",
    on: "Ligado",
    off: "Desligado",
    updatePreferencesFailed: "Não foi possível atualizar as preferências",
    today: "Hoje",
    earlier: "Antes",
    reviewSync: "Rever sync",
    addStock: "Adicionar stock",
  },
};

Object.entries(notificationsPageTranslations).forEach(
  ([language, notificationsPage]) => {
    translations[language] = {
      ...(translations[language] || {}),
      notificationsPage: {
        ...(translations[language]?.notificationsPage || {}),
        ...notificationsPage,
      },
    };
  },
);

const staffPageTranslations = {
  ar: {
    title: "الموظفون",
    subtitle: "ادعُ أعضاء الفريق ووزّع الفروع ونظّم الصلاحيات.",
    inviteStaff: "دعوة موظف",
    members: "الأعضاء",
    active: "النشطون",
    invited: "المدعوون",
    disabled: "المعطلون",
    team: "الفريق",
    invite: "دعوة",
    searchPlaceholder: "ابحث بالاسم أو الهاتف...",
    prepareInvite: "إعداد الدعوة",
    fullName: "الاسم الكامل",
    fullNamePlaceholder: "مثال: أمالا أونوي",
    phoneNumber: "رقم الهاتف",
    branch: "الفرع",
    role: "الدور",
    access: "الصلاحية",
    accessLevel: "مستوى الصلاحية",
    fineTunePermissions: "تخصيص الصلاحيات",
    sendInvite: "إرسال الدعوة",
    inviteSent: "تم إرسال الدعوة",
    couldNotInviteStaff: "تعذر دعوة الموظف",
    noStaffFound: "لم يتم العثور على موظفين",
    noStaffDescription: "ادعُ زميلًا أو عدّل البحث.",
    editAccess: "تعديل الصلاحيات",
    newTeammate: "عضو جديد",
    selectBranch: "اختر فرعًا",
    customAccess: "صلاحيات مخصصة",
    noAccess: "بدون صلاحيات",
    noBranch: "بدون فرع",
    selectTeamMember: "اختر عضوًا من الفريق",
    remove: "إزالة",
    staffRemoved: "تمت إزالة الموظف",
    couldNotRemoveStaff: "تعذر إزالة الموظف",
    saveChanges: "حفظ التغييرات",
    saved: "تم الحفظ",
    couldNotSaveStaff: "تعذر حفظ بيانات الموظف",
    lastSeen: "آخر ظهور",
    statusActive: "نشط",
    statusInvited: "مدعو",
    statusDisabled: "معطل",
    roleStaff: "موظف",
    roleManager: "مدير",
    select: "اختر",
    chooseAccessLevel: "اختر مستوى الصلاحية",
    chooseBranch: "اختر فرعًا",
    enterFullName: "أدخل الاسم الكامل للموظف",
    enterValidPhone: "أدخل رقم هاتف صحيحًا",
    presetBasicSeller: "بائع أساسي",
    presetBasicSellerDesc: "يمكنه البيع ومراجعة مبيعاته الأخيرة.",
    presetOperations: "العمليات",
    presetOperationsDesc: "مناسب للمخزون والمبيعات والديون والتنبيهات اليومية.",
    presetFullAccess: "صلاحية كاملة",
    presetFullAccessDesc: "صلاحيات إدارية عبر الموظفين والفروع والتحليلات.",
    permissionSell: "البيع / نقطة البيع",
    permissionHistory: "سجل المبيعات",
    permissionInventory: "المخزون",
    permissionDebts: "الديون",
    permissionAnalytics: "التحليلات",
    permissionNotifications: "الإشعارات",
    permissionStaffAccess: "إدارة الموظفين",
    permissionBranchManagement: "إدارة الفروع",
    permissionsCount: (count) => `${count} صلاحية`,
  },
  fr: {
    title: "Personnel",
    subtitle:
      "Invitez l’équipe, assignez des boutiques et gardez les accès clairs.",
    inviteStaff: "Inviter un membre",
    members: "Membres",
    active: "Actifs",
    invited: "Invités",
    disabled: "Désactivés",
    team: "Équipe",
    invite: "Inviter",
    searchPlaceholder: "Rechercher par nom ou téléphone...",
    prepareInvite: "Préparer l’invitation",
    fullName: "Nom complet",
    fullNamePlaceholder: "ex. Amaka Onwe",
    phoneNumber: "Téléphone",
    branch: "Boutique",
    role: "Rôle",
    access: "Accès",
    accessLevel: "Niveau d’accès",
    fineTunePermissions: "Ajuster les permissions",
    sendInvite: "Envoyer l’invitation",
    inviteSent: "Invitation envoyée",
    couldNotInviteStaff: "Impossible d’inviter ce membre",
    noStaffFound: "Aucun membre trouvé",
    noStaffDescription: "Invitez un coéquipier ou ajustez votre recherche.",
    editAccess: "Modifier l’accès",
    newTeammate: "Nouveau coéquipier",
    selectBranch: "Sélectionner une boutique",
    customAccess: "Accès personnalisé",
    noAccess: "Aucun accès",
    noBranch: "Aucune boutique",
    selectTeamMember: "Sélectionnez un membre",
    remove: "Supprimer",
    staffRemoved: "Membre supprimé",
    couldNotRemoveStaff: "Impossible de supprimer ce membre",
    saveChanges: "Enregistrer",
    saved: "Enregistré",
    couldNotSaveStaff: "Impossible d’enregistrer ce membre",
    lastSeen: "Vu pour la dernière fois",
    statusActive: "Actif",
    statusInvited: "Invité",
    statusDisabled: "Désactivé",
    roleStaff: "Employé",
    roleManager: "Responsable",
    select: "Sélectionner",
    chooseAccessLevel: "Choisissez un niveau d’accès",
    chooseBranch: "Choisissez une boutique",
    enterFullName: "Entrez le nom complet du membre",
    enterValidPhone: "Entrez un numéro valide",
    presetBasicSeller: "Vendeur basique",
    presetBasicSellerDesc: "Peut vendre et consulter ses ventes récentes.",
    presetOperations: "Opérations",
    presetOperationsDesc:
      "Utile pour le stock, les ventes, les dettes et les alertes.",
    presetFullAccess: "Accès complet",
    presetFullAccessDesc:
      "Accès manager sur le personnel, les boutiques et les analyses.",
    permissionSell: "Vente / POS",
    permissionHistory: "Historique des ventes",
    permissionInventory: "Stock",
    permissionDebts: "Dettes",
    permissionAnalytics: "Analyses",
    permissionNotifications: "Notifications",
    permissionStaffAccess: "Accès au personnel",
    permissionBranchManagement: "Gestion des boutiques",
    permissionsCount: (count) =>
      `${count} autorisation${count === 1 ? "" : "s"}`,
  },
  ha: {
    title: "Ma'aikata",
    subtitle: "Gayyaci 'yan kungiya, rarraba rassa, sannan ka tsara izini.",
    inviteStaff: "Gayyaci ma'aikaci",
    members: "Mambobi",
    active: "Masu aiki",
    invited: "Wadanda aka gayyata",
    disabled: "An kashe",
    team: "Tawaga",
    invite: "Gayyata",
    searchPlaceholder: "Nema da suna ko waya...",
    prepareInvite: "Shirya gayyata",
    fullName: "Cikakken suna",
    fullNamePlaceholder: "misali Amaka Onwe",
    phoneNumber: "Lambar waya",
    branch: "Reshe",
    role: "Matsayi",
    access: "Izini",
    accessLevel: "Matsayin izini",
    fineTunePermissions: "Gyara izini",
    sendInvite: "Aika gayyata",
    inviteSent: "An aika gayyata",
    couldNotInviteStaff: "Ba a iya gayyatar ma'aikaci ba",
    noStaffFound: "Ba a samu ma'aikata ba",
    noStaffDescription: "Gayyaci abokin aiki ko gyara bincikenka.",
    editAccess: "Gyara izini",
    newTeammate: "Sabon abokin aiki",
    selectBranch: "Zaɓi reshe",
    customAccess: "Izini na musamman",
    noAccess: "Babu izini",
    noBranch: "Babu reshe",
    selectTeamMember: "Zaɓi dan tawaga",
    remove: "Cire",
    staffRemoved: "An cire ma'aikaci",
    couldNotRemoveStaff: "Ba a iya cire ma'aikaci ba",
    saveChanges: "Ajiye canje-canje",
    saved: "An ajiye",
    couldNotSaveStaff: "Ba a iya ajiye ma'aikaci ba",
    lastSeen: "An gan shi na ƙarshe",
    statusActive: "Mai aiki",
    statusInvited: "An gayyata",
    statusDisabled: "An kashe",
    roleStaff: "Ma'aikaci",
    roleManager: "Manaja",
    select: "Zaɓi",
    chooseAccessLevel: "Zaɓi matakin izini",
    chooseBranch: "Zaɓi reshe",
    enterFullName: "Shigar da cikakken sunan ma'aikacin",
    enterValidPhone: "Shigar da ingantacciyar lambar waya",
    presetBasicSeller: "Mai sayarwa na asali",
    presetBasicSellerDesc:
      "Zai iya sayarwa kuma ya duba tallace-tallacensa na baya-bayan nan.",
    presetOperations: "Ayyuka",
    presetOperationsDesc:
      "Yana da kyau ga kaya, tallace-tallace, bashi, da faɗakarwa na yau da kullum.",
    presetFullAccess: "Cikakken izini",
    presetFullAccessDesc: "Izinin manaja a kan ma'aikata, rassa, da nazari.",
    permissionSell: "Sayarwa / POS",
    permissionHistory: "Tarihin tallace-tallace",
    permissionInventory: "Kaya",
    permissionDebts: "Bashi",
    permissionAnalytics: "Nazari",
    permissionNotifications: "Sanarwa",
    permissionStaffAccess: "Samun ma'aikata",
    permissionBranchManagement: "Gudanar da rassa",
    permissionsCount: (count) => `${count} izini`,
  },
  ig: {
    title: "Ndị ọrụ",
    subtitle: "Kpọọ ndị otu, kee ha n’ụlọ ahịa, ma debe ikike ha nke ọma.",
    inviteStaff: "Kpọọ onye ọrụ",
    members: "Ndị otu",
    active: "Na-arụ ọrụ",
    invited: "Akpọrọ",
    disabled: "Agbanyụrụ",
    team: "Otu",
    invite: "Kpọọ",
    searchPlaceholder: "Chọọ aha ma ọ bụ ekwentị...",
    prepareInvite: "Kwadebe oku",
    fullName: "Aha zuru oke",
    fullNamePlaceholder: "dịka Amaka Onwe",
    phoneNumber: "Nọmba ekwentị",
    branch: "Alaka",
    role: "Ọkwa",
    access: "Ikike",
    accessLevel: "Ọkwa ikike",
    fineTunePermissions: "Hazie ikike",
    sendInvite: "Zipu oku",
    inviteSent: "Ezila oku",
    couldNotInviteStaff: "Enweghị ike ịkpọ onye ọrụ",
    noStaffFound: "Achọtaghị ndị ọrụ",
    noStaffDescription: "Kpọọ onye otu ma ọ bụ gbanwee ọchụchọ gị.",
    editAccess: "Dezie ikike",
    newTeammate: "Onye otu ọhụrụ",
    selectBranch: "Họrọ alaka",
    customAccess: "Ikike ahaziri",
    noAccess: "Enweghị ikike",
    noBranch: "Enweghị alaka",
    selectTeamMember: "Họrọ onye otu",
    remove: "Wepu",
    staffRemoved: "Ewepụla onye ọrụ",
    couldNotRemoveStaff: "Enweghị ike iwepu onye ọrụ",
    saveChanges: "Chekwa mgbanwe",
    saved: "Echekwara",
    couldNotSaveStaff: "Enweghị ike ichekwa onye ọrụ",
    lastSeen: "Hụrụ ya ikpeazụ",
    statusActive: "Na-arụ ọrụ",
    statusInvited: "Akpọrọ",
    statusDisabled: "Agbanyụrụ",
    roleStaff: "Onye ọrụ",
    roleManager: "Onye njikwa",
    select: "Họrọ",
    chooseAccessLevel: "Họrọ ọkwa ikike",
    chooseBranch: "Họrọ alaka",
    enterFullName: "Tinye aha zuru oke nke onye ọrụ",
    enterValidPhone: "Tinye nọmba ekwentị ziri ezi",
    presetBasicSeller: "Onye na-ere ahịa nkịtị",
    presetBasicSellerDesc:
      "Ọ nwere ike ire ahịa ma lelee ahịa ya ndị gara nso.",
    presetOperations: "Ọrụ kwa ụbọchị",
    presetOperationsDesc:
      "Ọ bara uru maka ngwongwo, ahịa, ụgwọ, na mgbasa ozi.",
    presetFullAccess: "Ikike zuru oke",
    presetFullAccessDesc:
      "Ikike onye njikwa n’ebe ndị ọrụ, alaka na nyocha nọ.",
    permissionSell: "Ire ahịa / POS",
    permissionHistory: "Akụkọ ahịa",
    permissionInventory: "Ngwongwo",
    permissionDebts: "Ụgwọ",
    permissionAnalytics: "Nyocha",
    permissionNotifications: "Ọkwa",
    permissionStaffAccess: "Nweta ndị ọrụ",
    permissionBranchManagement: "Njikwa alaka",
    permissionsCount: (count) => `${count} ikike`,
  },
  pt: {
    title: "Equipa",
    subtitle:
      "Convide colegas, atribua filiais e mantenha os acessos organizados.",
    inviteStaff: "Convidar equipa",
    members: "Membros",
    active: "Ativos",
    invited: "Convidados",
    disabled: "Desativados",
    team: "Equipa",
    invite: "Convidar",
    searchPlaceholder: "Pesquisar por nome ou telefone...",
    prepareInvite: "Preparar convite",
    fullName: "Nome completo",
    fullNamePlaceholder: "ex. Amaka Onwe",
    phoneNumber: "Número de telefone",
    branch: "Filial",
    role: "Função",
    access: "Acesso",
    accessLevel: "Nível de acesso",
    fineTunePermissions: "Ajustar permissões",
    sendInvite: "Enviar convite",
    inviteSent: "Convite enviado",
    couldNotInviteStaff: "Não foi possível convidar o colaborador",
    noStaffFound: "Nenhum colaborador encontrado",
    noStaffDescription: "Convide um colega ou ajuste a sua pesquisa.",
    editAccess: "Editar acesso",
    newTeammate: "Novo colega",
    selectBranch: "Selecionar filial",
    customAccess: "Acesso personalizado",
    noAccess: "Sem acesso",
    noBranch: "Sem filial",
    selectTeamMember: "Selecione um membro",
    remove: "Remover",
    staffRemoved: "Colaborador removido",
    couldNotRemoveStaff: "Não foi possível remover o colaborador",
    saveChanges: "Guardar alterações",
    saved: "Guardado",
    couldNotSaveStaff: "Não foi possível guardar o colaborador",
    lastSeen: "Visto pela última vez",
    statusActive: "Ativo",
    statusInvited: "Convidado",
    statusDisabled: "Desativado",
    roleStaff: "Funcionário",
    roleManager: "Gerente",
    select: "Selecionar",
    chooseAccessLevel: "Escolha um nível de acesso",
    chooseBranch: "Escolha uma filial",
    enterFullName: "Introduza o nome completo do colaborador",
    enterValidPhone: "Introduza um número de telefone válido",
    presetBasicSeller: "Vendedor básico",
    presetBasicSellerDesc: "Pode vender e rever as vendas recentes.",
    presetOperations: "Operações",
    presetOperationsDesc: "Bom para stock, vendas, dívidas e alertas diários.",
    presetFullAccess: "Acesso total",
    presetFullAccessDesc: "Acesso de gerente a equipa, filiais e análises.",
    permissionSell: "Venda / POS",
    permissionHistory: "Histórico de vendas",
    permissionInventory: "Inventário",
    permissionDebts: "Dívidas",
    permissionAnalytics: "Análises",
    permissionNotifications: "Notificações",
    permissionStaffAccess: "Acesso da equipa",
    permissionBranchManagement: "Gestão de filiais",
    permissionsCount: (count) =>
      `${count} permissão${count === 1 ? "" : "ões"}`,
  },
  sw: {
    title: "Wafanyakazi",
    subtitle: "Alika timu, gawa matawi, na weka ruhusa kwa mpangilio.",
    inviteStaff: "Alika mfanyakazi",
    members: "Wanachama",
    active: "Hai",
    invited: "Waalikwa",
    disabled: "Wamezimwa",
    team: "Timu",
    invite: "Alika",
    searchPlaceholder: "Tafuta kwa jina au simu...",
    prepareInvite: "Andaa mwaliko",
    fullName: "Jina kamili",
    fullNamePlaceholder: "mf. Amaka Onwe",
    phoneNumber: "Namba ya simu",
    branch: "Tawi",
    role: "Wajibu",
    access: "Ufikiaji",
    accessLevel: "Kiwango cha ufikiaji",
    fineTunePermissions: "Rekebisha ruhusa",
    sendInvite: "Tuma mwaliko",
    inviteSent: "Mwaliko umetumwa",
    couldNotInviteStaff: "Haikuwezekana kumualika mfanyakazi",
    noStaffFound: "Hakuna wafanyakazi waliopatikana",
    noStaffDescription: "Alika mwenzako au rekebisha utafutaji wako.",
    editAccess: "Hariri ufikiaji",
    newTeammate: "Mshiriki mpya",
    selectBranch: "Chagua tawi",
    customAccess: "Ufikiaji maalum",
    noAccess: "Hakuna ufikiaji",
    noBranch: "Hakuna tawi",
    selectTeamMember: "Chagua mshiriki wa timu",
    remove: "Ondoa",
    staffRemoved: "Mfanyakazi ameondolewa",
    couldNotRemoveStaff: "Haikuwezekana kumuondoa mfanyakazi",
    saveChanges: "Hifadhi mabadiliko",
    saved: "Imehifadhiwa",
    couldNotSaveStaff: "Haikuwezekana kuhifadhi mfanyakazi",
    lastSeen: "Mara ya mwisho kuonekana",
    statusActive: "Hai",
    statusInvited: "Amealikwa",
    statusDisabled: "Amezimwa",
    roleStaff: "Mfanyakazi",
    roleManager: "Meneja",
    select: "Chagua",
    chooseAccessLevel: "Chagua kiwango cha ufikiaji",
    chooseBranch: "Chagua tawi",
    enterFullName: "Weka jina kamili la mfanyakazi",
    enterValidPhone: "Weka namba sahihi ya simu",
    presetBasicSeller: "Muuzaji wa kawaida",
    presetBasicSellerDesc: "Anaweza kuuza na kuona mauzo yake ya karibuni.",
    presetOperations: "Uendeshaji",
    presetOperationsDesc:
      "Nzuri kwa stock, mauzo, madeni, na tahadhari za kila siku.",
    presetFullAccess: "Ufikiaji kamili",
    presetFullAccessDesc:
      "Ufikiaji wa meneja kwa wafanyakazi, matawi, na uchambuzi.",
    permissionSell: "Uuzaji / POS",
    permissionHistory: "Historia ya mauzo",
    permissionInventory: "Hesabu",
    permissionDebts: "Madeni",
    permissionAnalytics: "Uchambuzi",
    permissionNotifications: "Arifa",
    permissionStaffAccess: "Ufikiaji wa wafanyakazi",
    permissionBranchManagement: "Usimamizi wa matawi",
    permissionsCount: (count) => `ruhusa ${count}`,
  },
  yo: {
    title: "Oṣiṣẹ",
    subtitle: "Pe awon ọmọ ẹgbẹ, pin ẹka, ki o si jẹ ki awọn aṣẹ wa ni ṣoki.",
    inviteStaff: "Pe oṣiṣẹ",
    members: "Awọn ọmọ ẹgbẹ",
    active: "Nṣiṣẹ",
    invited: "Ti a pe",
    disabled: "Ti wa ni pipa",
    team: "Ẹgbẹ",
    invite: "Pe",
    searchPlaceholder: "Wa nipa orukọ tabi foonu...",
    prepareInvite: "Mura ìpè",
    fullName: "Orukọ kikun",
    fullNamePlaceholder: "àpẹẹrẹ Amaka Onwe",
    phoneNumber: "Nọmba foonu",
    branch: "Ẹka",
    role: "Ipo",
    access: "Aṣẹ",
    accessLevel: "Ipele aṣẹ",
    fineTunePermissions: "Ṣatunṣe awọn aṣẹ",
    sendInvite: "Fi ìpè ranṣẹ",
    inviteSent: "A ti fi ìpè ranṣẹ",
    couldNotInviteStaff: "A ko le pe oṣiṣẹ",
    noStaffFound: "Ko si oṣiṣẹ ti a ri",
    noStaffDescription: "Pe ọmọ ẹgbẹ kan tabi tun wiwa rẹ ṣe.",
    editAccess: "Ṣatunkọ aṣẹ",
    newTeammate: "Ọmọ ẹgbẹ tuntun",
    selectBranch: "Yan ẹka",
    customAccess: "Aṣẹ aṣa",
    noAccess: "Ko si aṣẹ",
    noBranch: "Ko si ẹka",
    selectTeamMember: "Yan ọmọ ẹgbẹ kan",
    remove: "Yọ kuro",
    staffRemoved: "A ti yọ oṣiṣẹ kuro",
    couldNotRemoveStaff: "A ko le yọ oṣiṣẹ kuro",
    saveChanges: "Fipamọ awọn ayipada",
    saved: "Ti fipamọ",
    couldNotSaveStaff: "A ko le fipamọ oṣiṣẹ",
    lastSeen: "A rii ni igba to kẹhin",
    statusActive: "Nṣiṣẹ",
    statusInvited: "Ti a pe",
    statusDisabled: "Ti wa ni pipa",
    roleStaff: "Osise",
    roleManager: "Alakoso",
    select: "Yan",
    chooseAccessLevel: "Yan ipele aṣẹ",
    chooseBranch: "Yan ẹka",
    enterFullName: "Tẹ orukọ kikun oṣiṣẹ sii",
    enterValidPhone: "Tẹ nọmba foonu to pe sii",
    presetBasicSeller: "Oluta ipilẹ",
    presetBasicSellerDesc: "Ó lè tà ati wo awọn tita to ṣẹṣẹ.",
    presetOperations: "Ìṣiṣẹ",
    presetOperationsDesc:
      "Dara fun ọja, tita, gbese, ati awọn itaniji ojoojumọ.",
    presetFullAccess: "Aṣẹ kikun",
    presetFullAccessDesc: "Aṣẹ alakoso lori oṣiṣẹ, awọn ẹka, ati atupale.",
    permissionSell: "Tita / POS",
    permissionHistory: "Itan tita",
    permissionInventory: "Ọja",
    permissionDebts: "Gbese",
    permissionAnalytics: "Atupale",
    permissionNotifications: "Awọn iwifunni",
    permissionStaffAccess: "Aṣẹ oṣiṣẹ",
    permissionBranchManagement: "Ìṣàkóso ẹka",
    permissionsCount: (count) => `${count} aṣẹ`,
  },
  zu: {
    title: "Abasebenzi",
    subtitle: "Mema ithimba, hlela amagatsha, bese ugcina izimvume zicacile.",
    inviteStaff: "Mema umsebenzi",
    members: "Amalungu",
    active: "Asebenzayo",
    invited: "Amenyiwe",
    disabled: "Kukhutshaziwe",
    team: "Ithimba",
    invite: "Mema",
    searchPlaceholder: "Sesha ngegama noma ngenombolo...",
    prepareInvite: "Lungisa isimemo",
    fullName: "Igama eligcwele",
    fullNamePlaceholder: "isb. Amaka Onwe",
    phoneNumber: "Inombolo yocingo",
    branch: "Igatsha",
    role: "Indima",
    access: "Ukufinyelela",
    accessLevel: "Izinga lokufinyelela",
    fineTunePermissions: "Lungisa izimvume",
    sendInvite: "Thumela isimemo",
    inviteSent: "Isimemo sithunyelwe",
    couldNotInviteStaff: "Akukwazekanga ukumema umsebenzi",
    noStaffFound: "Akukho basebenzi abatholakele",
    noStaffDescription: "Mema osebenza nawe noma ulungise usesho lwakho.",
    editAccess: "Hlela ukufinyelela",
    newTeammate: "Umlingani omusha",
    selectBranch: "Khetha igatsha",
    customAccess: "Ukufinyelela okukhethekile",
    noAccess: "Akukho ukufinyelela",
    noBranch: "Alikho igatsha",
    selectTeamMember: "Khetha ilungu lethimba",
    remove: "Susa",
    staffRemoved: "Umsebenzi ususiwe",
    couldNotRemoveStaff: "Akukwazekanga ukususa umsebenzi",
    saveChanges: "Londoloza izinguquko",
    saved: "Kulondoloziwe",
    couldNotSaveStaff: "Akukwazekanga ukulondoloza umsebenzi",
    lastSeen: "Ugcinwe ukubonakala",
    statusActive: "Usebenzayo",
    statusInvited: "Umenyiwe",
    statusDisabled: "Ukhutshaziwe",
    roleStaff: "Umsebenzi",
    roleManager: "Umphathi",
    select: "Khetha",
    chooseAccessLevel: "Khetha izinga lokufinyelela",
    chooseBranch: "Khetha igatsha",
    enterFullName: "Faka igama eligcwele lomsebenzi",
    enterValidPhone: "Faka inombolo yocingo efanele",
    presetBasicSeller: "Umthengisi ojwayelekile",
    presetBasicSellerDesc:
      "Angathengisa futhi abuke ukuthengisa kwakhe kwakamuva.",
    presetOperations: "Imisebenzi",
    presetOperationsDesc:
      "Kuhle esitokweni, ekuthengiseni, ezikweletini nasezazisweni zansuku zonke.",
    presetFullAccess: "Ukufinyelela okuphelele",
    presetFullAccessDesc:
      "Ukufinyelela komphathi kubasebenzi, emagatsheni nasekuhlaziyeni.",
    permissionSell: "Ukuthengisa / POS",
    permissionHistory: "Umlando wokuthengisa",
    permissionInventory: "Isitoko",
    permissionDebts: "Izikweletu",
    permissionAnalytics: "Ukuhlaziya",
    permissionNotifications: "Izaziso",
    permissionStaffAccess: "Ukufinyelela kwabasebenzi",
    permissionBranchManagement: "Ukuphatha amagatsha",
    permissionsCount: (count) => `${count} imvume`,
  },
};

Object.entries(staffPageTranslations).forEach(([language, staffPage]) => {
  translations[language] = {
    ...(translations[language] || {}),
    staffPage: {
      ...(translations[language]?.staffPage || {}),
      ...staffPage,
    },
  };
});

const branchesPageTranslations = {
  ar: {
    title: "الفروع",
    locationsSummary: (count, active) => `${count} موقع · ${active} نشط`,
    newBranch: "فرع جديد",
    current: "الحالي",
    liveBranchRecord: "سجل فرع مباشر",
    active: "نشط",
    inactive: "غير نشط",
    staff: "الموظفون",
    skus: "الوحدات",
    today: "اليوم",
    switchedToBranch: (name) => `تم التبديل إلى ${name}`,
    currentBranch: "الفرع الحالي",
    switchToBranch: "التبديل إلى الفرع",
    createNewBranch: "إنشاء فرع جديد",
    createDescription: "أضف موقعًا فعليًا جديدًا لمتجرك.",
    branchName: "اسم الفرع",
    branchNamePlaceholder: "مثال: إيكيجا سنترال",
    createHelper: "يمكنك إضافة الموقع الدقيق لاحقًا من الهاتف أو لوحة الإدارة.",
    cancel: "إلغاء",
    enterValidBranchName: "أدخل اسم فرع صالحًا",
    branchCreated: "تم إنشاء الفرع",
    couldNotCreateBranch: "تعذر إنشاء الفرع",
    createBranch: "إنشاء الفرع",
    updateBranch: "تحديث الفرع",
    updateDescription: "أعد تسمية هذا الفرع أو حدّث حالته النشطة.",
    activeBranch: "فرع نشط",
    activeBranchDescription:
      "تظل الفروع غير النشطة ظاهرة ولكن لا يمكن استخدامها كموقع نشط.",
    branchUpdated: "تم تحديث الفرع",
    couldNotUpdateBranch: "تعذر تحديث الفرع",
    saveChanges: "حفظ التغييرات",
    branchNameTooShort: "يجب أن يحتوي اسم الفرع على حرفين على الأقل",
    branchNameTooLong: "يجب ألا يزيد اسم الفرع عن 80 حرفًا",
    roleAdmin: "مشرف",
    roleMember: "عضو",
    roleOwner: "المالك",
  },
  fr: {
    title: "Succursales",
    locationsSummary: (count, active) =>
      `${count} site${count === 1 ? "" : "s"} · ${active} actif${active === 1 ? "" : "s"}`,
    newBranch: "Nouvelle succursale",
    current: "Actuelle",
    liveBranchRecord: "Fiche de succursale active",
    active: "Active",
    inactive: "Inactive",
    staff: "Personnel",
    skus: "SKU",
    today: "Aujourd’hui",
    switchedToBranch: (name) => `Basculé vers ${name}`,
    currentBranch: "Succursale actuelle",
    switchToBranch: "Basculer vers cette succursale",
    createNewBranch: "Créer une succursale",
    createDescription: "Ajoutez un nouveau site physique à votre boutique.",
    branchName: "Nom de la succursale",
    branchNamePlaceholder: "ex. Ikeja Central",
    createHelper:
      "Vous pourrez ajouter l’emplacement exact plus tard depuis le mobile ou l’admin.",
    cancel: "Annuler",
    enterValidBranchName: "Entrez un nom de succursale valide",
    branchCreated: "Succursale créée",
    couldNotCreateBranch: "Impossible de créer la succursale",
    createBranch: "Créer la succursale",
    updateBranch: "Mettre à jour la succursale",
    updateDescription:
      "Renommez cette succursale ou modifiez son statut actif.",
    activeBranch: "Succursale active",
    activeBranchDescription:
      "Les succursales inactives restent visibles mais ne peuvent pas être utilisées comme site actif.",
    branchUpdated: "Succursale mise à jour",
    couldNotUpdateBranch: "Impossible de mettre à jour la succursale",
    saveChanges: "Enregistrer",
    branchNameTooShort: "Le nom doit contenir au moins 2 caractères",
    branchNameTooLong: "Le nom doit contenir au plus 80 caractères",
    roleAdmin: "Admin",
    roleMember: "Membre",
    roleOwner: "Propriétaire",
  },
  ha: {
    title: "Rassa",
    locationsSummary: (count, active) => `${count} wuri · ${active} masu aiki`,
    newBranch: "Sabon reshe",
    current: "Na yanzu",
    liveBranchRecord: "Bayanan reshe kai tsaye",
    active: "Mai aiki",
    inactive: "Ba ya aiki",
    staff: "Ma'aikata",
    skus: "SKU",
    today: "Yau",
    switchedToBranch: (name) => `An koma zuwa ${name}`,
    currentBranch: "Reshen yanzu",
    switchToBranch: "Koma zuwa reshe",
    createNewBranch: "Kirkiri sabon reshe",
    createDescription: "Kara sabon wuri na zahiri a shaginka.",
    branchName: "Sunan reshe",
    branchNamePlaceholder: "misali Ikeja Central",
    createHelper:
      "Za ka iya kara cikakken wurin daga wayar hannu ko bangaren admin daga baya.",
    cancel: "Soke",
    enterValidBranchName: "Shigar da ingantaccen sunan reshe",
    branchCreated: "An kirkiri reshe",
    couldNotCreateBranch: "Ba a iya kirkirar reshe ba",
    createBranch: "Kirkiri reshe",
    updateBranch: "Sabunta reshe",
    updateDescription: "Canja sunan reshen ko sabunta matsayin aikinsa.",
    activeBranch: "Reshe mai aiki",
    activeBranchDescription:
      "Rassan da ba sa aiki za su ci gaba da bayyana amma ba za a iya amfani da su a matsayin wurare masu aiki ba.",
    branchUpdated: "An sabunta reshe",
    couldNotUpdateBranch: "Ba a iya sabunta reshe ba",
    saveChanges: "Ajiye canje-canje",
    branchNameTooShort: "Sunan reshe dole ne ya zama aƙalla haruffa 2",
    branchNameTooLong: "Sunan reshe ba zai wuce haruffa 80 ba",
    roleAdmin: "Admin",
    roleMember: "Mamba",
    roleOwner: "Mai shago",
  },
  ig: {
    title: "Alaka",
    locationsSummary: (count, active) => `${count} ebe · ${active} na-arụ ọrụ`,
    newBranch: "Alaka ọhụrụ",
    current: "Nke ugbu a",
    liveBranchRecord: "Nkọwa alaka na-arụ ọrụ",
    active: "Na-arụ ọrụ",
    inactive: "Anaghị arụ ọrụ",
    staff: "Ndị ọrụ",
    skus: "SKU",
    today: "Taa",
    switchedToBranch: (name) => `Agbanweela gaa ${name}`,
    currentBranch: "Alaka ugbu a",
    switchToBranch: "Gaa na alaka",
    createNewBranch: "Mepụta alaka ọhụrụ",
    createDescription: "Tinye ebe ọhụrụ nke ụlọ ahịa gị.",
    branchName: "Aha alaka",
    branchNamePlaceholder: "dịka Ikeja Central",
    createHelper:
      "Ị nwere ike itinye ebe ziri ezi e mesịrị site na mobile ma ọ bụ admin.",
    cancel: "Kagbuo",
    enterValidBranchName: "Tinye aha alaka ziri ezi",
    branchCreated: "E mepụtara alaka",
    couldNotCreateBranch: "Enweghị ike imepụta alaka",
    createBranch: "Mepụta alaka",
    updateBranch: "Melite alaka",
    updateDescription: "Gbanwee aha alaka a ma ọ bụ melite ọnọdụ ọrụ ya.",
    activeBranch: "Alaka na-arụ ọrụ",
    activeBranchDescription:
      "Alaka ndị na-adịghị arụ ọrụ ga-anọgide na-apụta mana a gaghị eji ha dị ka ebe ọrụ.",
    branchUpdated: "Emelitere alaka",
    couldNotUpdateBranch: "Enweghị ike imelite alaka",
    saveChanges: "Chekwa mgbanwe",
    branchNameTooShort: "Aha alaka ga-adịkarịa ala mkpụrụedemede 2",
    branchNameTooLong: "Aha alaka agaghị agafe mkpụrụedemede 80",
    roleAdmin: "Admin",
    roleMember: "Onye otu",
    roleOwner: "Onye nwe ya",
  },
  pt: {
    title: "Filiais",
    locationsSummary: (count, active) =>
      `${count} local${count === 1 ? "" : "ais"} · ${active} ativo${active === 1 ? "" : "s"}`,
    newBranch: "Nova filial",
    current: "Atual",
    liveBranchRecord: "Registo ativo da filial",
    active: "Ativa",
    inactive: "Inativa",
    staff: "Equipa",
    skus: "SKU",
    today: "Hoje",
    switchedToBranch: (name) => `Mudou para ${name}`,
    currentBranch: "Filial atual",
    switchToBranch: "Mudar para filial",
    createNewBranch: "Criar nova filial",
    createDescription: "Adicione uma nova localização física à sua loja.",
    branchName: "Nome da filial",
    branchNamePlaceholder: "ex. Ikeja Central",
    createHelper:
      "Pode adicionar a localização exata depois a partir do telemóvel ou do admin.",
    cancel: "Cancelar",
    enterValidBranchName: "Introduza um nome de filial válido",
    branchCreated: "Filial criada",
    couldNotCreateBranch: "Não foi possível criar a filial",
    createBranch: "Criar filial",
    updateBranch: "Atualizar filial",
    updateDescription: "Renomeie esta filial ou atualize o seu estado ativo.",
    activeBranch: "Filial ativa",
    activeBranchDescription:
      "As filiais inativas continuam visíveis, mas não podem ser usadas como localizações ativas.",
    branchUpdated: "Filial atualizada",
    couldNotUpdateBranch: "Não foi possível atualizar a filial",
    saveChanges: "Guardar alterações",
    branchNameTooShort: "O nome da filial deve ter pelo menos 2 caracteres",
    branchNameTooLong: "O nome da filial deve ter no máximo 80 caracteres",
    roleAdmin: "Admin",
    roleMember: "Membro",
    roleOwner: "Proprietário",
  },
  sw: {
    title: "Matawi",
    locationsSummary: (count, active) => `${count} eneo · ${active} hai`,
    newBranch: "Tawi jipya",
    current: "La sasa",
    liveBranchRecord: "Rekodi hai ya tawi",
    active: "Hai",
    inactive: "Haliwezi kutumika",
    staff: "Wafanyakazi",
    skus: "SKU",
    today: "Leo",
    switchedToBranch: (name) => `Umehamia ${name}`,
    currentBranch: "Tawi la sasa",
    switchToBranch: "Hamia kwenye tawi",
    createNewBranch: "Unda tawi jipya",
    createDescription: "Ongeza eneo jipya la duka lako.",
    branchName: "Jina la tawi",
    branchNamePlaceholder: "mf. Ikeja Central",
    createHelper:
      "Unaweza kuongeza mahali halisi baadaye kupitia simu au admin.",
    cancel: "Ghairi",
    enterValidBranchName: "Weka jina sahihi la tawi",
    branchCreated: "Tawi limeundwa",
    couldNotCreateBranch: "Haikuwezekana kuunda tawi",
    createBranch: "Unda tawi",
    updateBranch: "Sasisha tawi",
    updateDescription: "Badilisha jina la tawi hili au hali yake ya matumizi.",
    activeBranch: "Tawi hai",
    activeBranchDescription:
      "Matawi yasiyo hai yanaendelea kuonekana lakini hayawezi kutumika kama maeneo hai.",
    branchUpdated: "Tawi limesasishwa",
    couldNotUpdateBranch: "Haikuwezekana kusasisha tawi",
    saveChanges: "Hifadhi mabadiliko",
    branchNameTooShort: "Jina la tawi lazima liwe angalau herufi 2",
    branchNameTooLong: "Jina la tawi lisizidi herufi 80",
    roleAdmin: "Msimamizi",
    roleMember: "Mwanachama",
    roleOwner: "Mmiliki",
  },
  yo: {
    title: "Ẹka",
    locationsSummary: (count, active) => `${count} ibi · ${active} nṣiṣẹ`,
    newBranch: "Ẹka tuntun",
    current: "Ti lọwọlọwọ",
    liveBranchRecord: "Igbasilẹ ẹka laaye",
    active: "Nṣiṣẹ",
    inactive: "Ko ṣiṣẹ",
    staff: "Oṣiṣẹ",
    skus: "SKU",
    today: "Loni",
    switchedToBranch: (name) => `Ti yipada si ${name}`,
    currentBranch: "Ẹka lọwọlọwọ",
    switchToBranch: "Yipada si ẹka",
    createNewBranch: "Ṣẹda ẹka tuntun",
    createDescription: "Fi ipo tuntun kun fun ile itaja rẹ.",
    branchName: "Orukọ ẹka",
    branchNamePlaceholder: "àpẹẹrẹ Ikeja Central",
    createHelper: "O le fi ipo gangan kun nigbamii lati mobile tabi admin.",
    cancel: "Fagilee",
    enterValidBranchName: "Tẹ orukọ ẹka to pe sii",
    branchCreated: "A ti ṣẹda ẹka",
    couldNotCreateBranch: "A ko le ṣẹda ẹka",
    createBranch: "Ṣẹda ẹka",
    updateBranch: "Ṣe imudojuiwọn ẹka",
    updateDescription: "Tun orukọ ẹka yii ṣe tabi yi ipo iṣẹ rẹ pada.",
    activeBranch: "Ẹka to nṣiṣẹ",
    activeBranchDescription:
      "Awọn ẹka ti ko nṣiṣẹ yoo ṣi han ṣugbọn a ko le lo wọn gẹgẹ bi ibi iṣẹ lọwọlọwọ.",
    branchUpdated: "A ti ṣe imudojuiwọn ẹka",
    couldNotUpdateBranch: "A ko le ṣe imudojuiwọn ẹka",
    saveChanges: "Fipamọ awọn ayipada",
    branchNameTooShort: "Orukọ ẹka gbọdọ jẹ o kere ju awọn lẹta 2",
    branchNameTooLong: "Orukọ ẹka ko gbọdọ ju awọn lẹta 80 lọ",
    roleAdmin: "Admin",
    roleMember: "Ọmọ ẹgbẹ",
    roleOwner: "Oniwun",
  },
  zu: {
    title: "Amagatsha",
    locationsSummary: (count, active) =>
      `${count} indawo · ${active} asebenzayo`,
    newBranch: "Igatsha elisha",
    current: "Lamanje",
    liveBranchRecord: "Irekhodi legatsha elibukhoma",
    active: "Liyasebenza",
    inactive: "Alisebenzi",
    staff: "Abasebenzi",
    skus: "SKU",
    today: "Namuhla",
    switchedToBranch: (name) => `Kushintshelwe ku-${name}`,
    currentBranch: "Igatsha lamanje",
    switchToBranch: "Shintshela egatsheni",
    createNewBranch: "Dala igatsha elisha",
    createDescription: "Engeza indawo entsha yesitolo sakho.",
    branchName: "Igama legatsha",
    branchNamePlaceholder: "isb. Ikeja Central",
    createHelper:
      "Ungangeza indawo eqondile kamuva kusuka kuselula noma ku-admin.",
    cancel: "Khansela",
    enterValidBranchName: "Faka igama legatsha elivumelekile",
    branchCreated: "Igatsha lidaliwe",
    couldNotCreateBranch: "Akukwazekanga ukudala igatsha",
    createBranch: "Dala igatsha",
    updateBranch: "Buyekeza igatsha",
    updateDescription: "Qamba kabusha leli gatsha noma ubuyekeze isimo salo.",
    activeBranch: "Igatsha elisebenzayo",
    activeBranchDescription:
      "Amagatsha angasebenzi azohlala ebonakala kodwa angeke asetshenziswe njengendawo esebenzayo.",
    branchUpdated: "Igatsha libuyekeziwe",
    couldNotUpdateBranch: "Akukwazekanga ukubuyekeza igatsha",
    saveChanges: "Londoloza izinguquko",
    branchNameTooShort:
      "Igama legatsha kumele libe nezinhlamvu ezi-2 okungenani",
    branchNameTooLong: "Igama legatsha kumele lingedluli izinhlamvu ezingu-80",
    roleAdmin: "Admin",
    roleMember: "Ilungu",
    roleOwner: "Umnikazi",
  },
};

Object.entries(branchesPageTranslations).forEach(([language, branchesPage]) => {
  translations[language] = {
    ...(translations[language] || {}),
    branchesPage: {
      ...(translations[language]?.branchesPage || {}),
      ...branchesPage,
    },
  };
});

const assistantPageTranslations = {
  ar: {
    title: "مساعد المتجر",
    live: "مباشر",
    subtitle: "تأتي الإجابات من بيانات مساحة العمل الموثقة.",
    welcome:
      "اسأل عن المبيعات المباشرة، المخزون، الديون، الموظفين، أو توصيات إعادة التخزين.",
    inputPlaceholder: "اسأل عن المبيعات أو المخزون أو التحليلات أو المزامنة…",
    footer: "يتم إنشاء الإجابات من بيانات مساحة العمل المباشرة.",
    suggestedPrompts: "اقتراحات",
    actions: "إجراءات",
    actionNewSale: "عملية بيع جديدة",
    actionAddProduct: "أضف منتجًا",
    actionWeeklyReport: "تقرير أسبوعي",
    actionForceSync: "فرض المزامنة",
    promptSoldToday: "ما الذي تم بيعه اليوم؟",
    promptLowStock: "اعرض الأصناف منخفضة المخزون",
    promptDebtOutstanding: "كم حجم الديون المستحقة؟",
    promptRestockFirst: "ما الذي يجب إعادة تخزينه أولًا؟",
    promptPerformingBest: "من الأفضل أداءً؟",
    promptCompareWeeks: "قارن هذا الأسبوع بالأسبوع الماضي",
    dontKnowYet: "لا أعرف ذلك من بيانات متجرك حتى الآن.",
    requestFailed: "فشل طلب المساعد.",
    thinking: "جارٍ التفكير...",
  },
  fr: {
    title: "Assistant boutique",
    live: "Live",
    subtitle:
      "Les réponses proviennent des données authentifiées de cet espace.",
    welcome:
      "Posez des questions sur les ventes en direct, le stock, les dettes, l’équipe ou le réassort.",
    inputPlaceholder:
      "Posez une question sur les ventes, le stock, les analyses ou la sync…",
    footer:
      "Les réponses sont générées à partir des données en direct de l’espace.",
    suggestedPrompts: "Prompts suggérés",
    actions: "Actions",
    actionNewSale: "Nouvelle vente",
    actionAddProduct: "Ajouter un produit",
    actionWeeklyReport: "Rapport hebdomadaire",
    actionForceSync: "Forcer la sync",
    promptSoldToday: "Qu’a-t-on vendu aujourd’hui ?",
    promptLowStock: "Afficher les articles en stock faible",
    promptDebtOutstanding: "Quel est le montant des dettes en attente ?",
    promptRestockFirst: "Que faut-il réapprovisionner en premier ?",
    promptPerformingBest: "Qui performe le mieux ?",
    promptCompareWeeks: "Comparer cette semaine à la semaine dernière",
    dontKnowYet:
      "Je ne le sais pas encore à partir des données de votre boutique.",
    requestFailed: "La requête de l’assistant a échoué.",
    thinking: "Réflexion en cours...",
  },
  ha: {
    title: "Mataimakin shago",
    live: "Kai tsaye",
    subtitle: "Amsoshi suna fitowa daga bayanan shagon da aka tantance.",
    welcome:
      "Tambayi tallace-tallace kai tsaye, kaya, bashi, ma'aikata, ko shawarwarin sake zuba kaya.",
    inputPlaceholder: "Tambaya game da tallace-tallace, kaya, nazari, ko sync…",
    footer: "Ana samar da amsoshi daga bayanan shago kai tsaye.",
    suggestedPrompts: "Tambayoyi da aka ba da shawara",
    actions: "Ayyuka",
    actionNewSale: "Sabuwar sayarwa",
    actionAddProduct: "Kara samfuri",
    actionWeeklyReport: "Rahoton mako",
    actionForceSync: "Tilasta sync",
    promptSoldToday: "Me aka sayar yau?",
    promptLowStock: "Nuna kayan da suka yi kasa",
    promptDebtOutstanding: "Nawa ne bashin da ake bi?",
    promptRestockFirst: "Me ya kamata mu fara sake zubawa?",
    promptPerformingBest: "Wane ne ke aiki mafi kyau?",
    promptCompareWeeks: "Kwatanta wannan makon da na baya",
    dontKnowYet: "Ban san hakan daga bayanan shaginka ba tukuna.",
    requestFailed: "Bukatar mataimaki ta gaza.",
    thinking: "Ana tunani...",
  },
  ig: {
    title: "Onye enyemaka ụlọ ahịa",
    live: "Na-ebi ndụ",
    subtitle: "Azịza sitere na data ụlọ ahịa a kwadoro.",
    welcome:
      "Jụọ gbasara ahịa ndụ, ngwongwo, ụgwọ, ndị ọrụ, ma ọ bụ ndụmọdụ ịtinye ngwaahịa ọzọ.",
    inputPlaceholder: "Jụọ gbasara ahịa, ngwongwo, nyocha, ma ọ bụ sync…",
    footer: "A na-emepụta azịza site na data ụlọ ahịa dị ndụ.",
    suggestedPrompts: "Ajụjụ atụ aro",
    actions: "Omume",
    actionNewSale: "Ahịa ọhụrụ",
    actionAddProduct: "Tinye ngwaahịa",
    actionWeeklyReport: "Akụkọ izu",
    actionForceSync: "Mepụta sync ike",
    promptSoldToday: "Kedu ihe e rere taa?",
    promptLowStock: "Gosi ihe nwere obere stock",
    promptDebtOutstanding: "Ego ole ka a ka ji?",
    promptRestockFirst: "Kedu ihe anyị kwesịrị ịtinye ọzọ mbụ?",
    promptPerformingBest: "Onye na-arụ ọrụ kacha mma?",
    promptCompareWeeks: "Tụnyere izu a na nke gara aga",
    dontKnowYet: "Amabeghị m nke ahụ na data ụlọ ahịa gị ugbu a.",
    requestFailed: "Arịrịọ onye enyemaka dara.",
    thinking: "Na-eche echiche...",
  },
  pt: {
    title: "Assistente da loja",
    live: "Ao vivo",
    subtitle:
      "As respostas vêm dos dados autenticados deste espaço de trabalho.",
    welcome:
      "Pergunte sobre vendas ao vivo, stock, dívidas, equipa ou recomendações de reposição.",
    inputPlaceholder:
      "Pergunte sobre vendas, stock, análises ou sincronização…",
    footer:
      "As respostas são geradas a partir dos dados em direto do espaço de trabalho.",
    suggestedPrompts: "Sugestões",
    actions: "Ações",
    actionNewSale: "Nova venda",
    actionAddProduct: "Adicionar produto",
    actionWeeklyReport: "Relatório semanal",
    actionForceSync: "Forçar sincronização",
    promptSoldToday: "O que foi vendido hoje?",
    promptLowStock: "Mostrar itens com stock baixo",
    promptDebtOutstanding: "Quanto está em dívida?",
    promptRestockFirst: "O que devemos repor primeiro?",
    promptPerformingBest: "Quem está com melhor desempenho?",
    promptCompareWeeks: "Comparar esta semana com a semana passada",
    dontKnowYet: "Ainda não sei isso a partir dos dados da sua loja.",
    requestFailed: "O pedido do assistente falhou.",
    thinking: "A pensar...",
  },
  sw: {
    title: "Msaidizi wa duka",
    live: "Moja kwa moja",
    subtitle: "Majibu yanatoka kwenye data ya nafasi ya kazi iliyothibitishwa.",
    welcome:
      "Uliza kuhusu mauzo ya moja kwa moja, stock, madeni, wafanyakazi, au mapendekezo ya kujaza tena.",
    inputPlaceholder: "Uliza kuhusu mauzo, stock, uchambuzi, au sync…",
    footer: "Majibu yanazalishwa kutoka kwenye data hai ya nafasi ya kazi.",
    suggestedPrompts: "Mapendekezo",
    actions: "Vitendo",
    actionNewSale: "Uuzaji mpya",
    actionAddProduct: "Ongeza bidhaa",
    actionWeeklyReport: "Ripoti ya wiki",
    actionForceSync: "Lazimisha sync",
    promptSoldToday: "Nini kimeuzwa leo?",
    promptLowStock: "Onyesha bidhaa zilizo na stock ndogo",
    promptDebtOutstanding: "Kiasi gani cha deni bado kipo?",
    promptRestockFirst: "Tujaze nini kwanza?",
    promptPerformingBest: "Nani anafanya vizuri zaidi?",
    promptCompareWeeks: "Linganisha wiki hii na wiki iliyopita",
    dontKnowYet: "Bado sijui hilo kutoka kwenye data ya duka lako.",
    requestFailed: "Ombi la msaidizi limeshindwa.",
    thinking: "Nafikiria...",
  },
  yo: {
    title: "Oluranlọwọ ile itaja",
    live: "Láàyè",
    subtitle: "Awọn idahun wa lati data ile itaja ti a fọwọsi.",
    welcome:
      "Beere nipa tita láàyè, ọja, gbese, oṣiṣẹ, tabi awọn iṣeduro atunfi ọja kun.",
    inputPlaceholder: "Beere nipa tita, ọja, atupale, tabi sync…",
    footer: "Awọn idahun ni a n ṣe lati data ile itaja láàyè.",
    suggestedPrompts: "Awọn aba",
    actions: "Awọn iṣe",
    actionNewSale: "Tita tuntun",
    actionAddProduct: "Fi ọja kun",
    actionWeeklyReport: "Iroyin ọsẹ",
    actionForceSync: "Fi sync ṣiṣẹ ni agbara",
    promptSoldToday: "Kini a ta loni?",
    promptLowStock: "Fi awọn ọja to ku diẹ han",
    promptDebtOutstanding: "Elo ni gbese to ku?",
    promptRestockFirst: "Kini o yẹ ki a tun fi kun kọkọ?",
    promptPerformingBest: "Ta ni n ṣiṣẹ dara julọ?",
    promptCompareWeeks: "Fi ọsẹ yii we ọsẹ to kọja",
    dontKnowYet: "Mi ò tíì mọ iyẹn lati data ile itaja rẹ.",
    requestFailed: "Ìbéèrè oluranlọwọ kuna.",
    thinking: "N ronú...",
  },
  zu: {
    title: "Umsizi wesitolo",
    live: "Bukhoma",
    subtitle: "Izimpendulo zivela kudatha eqinisekisiwe yendawo yokusebenza.",
    welcome:
      "Buza ngokuthengisa bukhoma, isitoko, izikweletu, abasebenzi, noma izincomo zokugcwalisa kabusha.",
    inputPlaceholder:
      "Buza ngokuthengisa, isitoko, ukuhlaziya, noma ukuvumelanisa…",
    footer:
      "Izimpendulo zenziwa kusukela kudatha ebukhoma yendawo yokusebenza.",
    suggestedPrompts: "Izimpendulo eziphakanyisiwe",
    actions: "Izenzo",
    actionNewSale: "Ukuthengisa okusha",
    actionAddProduct: "Engeza umkhiqizo",
    actionWeeklyReport: "Umbiko weviki",
    actionForceSync: "Phoqa ukuvumelanisa",
    promptSoldToday: "Yini ethengisiwe namuhla?",
    promptLowStock: "Bonisa izinto ezinestoko esiphansi",
    promptDebtOutstanding: "Yimalini esasele ezikweletini?",
    promptRestockFirst: "Yini okufanele siyigcwalise kuqala?",
    promptPerformingBest: "Ubani owenza kahle kakhulu?",
    promptCompareWeeks: "Qhathanisa leli sonto nelidlule",
    dontKnowYet: "Angikakwazi lokho kusuka kudatha yesitolo sakho okwamanje.",
    requestFailed: "Isicelo somsizi sihlulekile.",
    thinking: "Kuyacatshangwa...",
  },
};

Object.entries(assistantPageTranslations).forEach(
  ([language, assistantPage]) => {
    translations[language] = {
      ...(translations[language] || {}),
      assistantPage: {
        ...(translations[language]?.assistantPage || {}),
        ...assistantPage,
      },
    };
  },
);

const assistantConnectionTranslations = {
  ar: {
    languageLabel: "لغة المساعد",
    connecting: "جارٍ الاتصال",
    reconnecting: "جارٍ إعادة الاتصال",
    loadingEarlierMessages: "جارٍ تحميل الرسائل السابقة…",
    chatReconnecting: "تجري إعادة اتصال المحادثة",
    chatReconnectDescription: "سجل محادثتك الآمن غير متاح مؤقتًا. لم تُفقد أي رسالة ولم تُحتسب أي رسوم.",
    tryAgain: "حاول مرة أخرى",
  },
  fr: {
    languageLabel: "Langue de l’assistant",
    connecting: "Connexion",
    reconnecting: "Reconnexion",
    loadingEarlierMessages: "Chargement des messages précédents…",
    chatReconnecting: "La discussion se reconnecte",
    chatReconnectDescription: "Votre historique sécurisé est temporairement indisponible. Aucun message n’a été perdu ou facturé.",
    tryAgain: "Réessayer",
  },
  ha: {
    languageLabel: "Harshen mataimaki",
    connecting: "Ana haɗawa",
    reconnecting: "Ana sake haɗawa",
    loadingEarlierMessages: "Ana loda saƙonnin baya…",
    chatReconnecting: "Ana sake haɗa tattaunawa",
    chatReconnectDescription: "Tarihin tattaunawarka mai tsaro baya samuwa na ɗan lokaci. Babu saƙon da ya ɓace ko aka caje.",
    tryAgain: "Sake gwadawa",
  },
  ig: {
    languageLabel: "Asụsụ onye enyemaka",
    connecting: "Na-ejikọ",
    reconnecting: "Na-ejikọ ọzọ",
    loadingEarlierMessages: "Na-ebunye ozi ndị gara aga…",
    chatReconnecting: "Mkparịta ụka na-ejikọ ọzọ",
    chatReconnectDescription: "Akụkọ mkparịta ụka gị echedoro adịghị nwa oge. Ọ dịghị ozi furu efu ma ọ bụ ego akwụrọ.",
    tryAgain: "Nwaa ọzọ",
  },
  pt: {
    languageLabel: "Idioma do assistente",
    connecting: "A ligar",
    reconnecting: "A restabelecer ligação",
    loadingEarlierMessages: "A carregar mensagens anteriores…",
    chatReconnecting: "A conversa está a restabelecer a ligação",
    chatReconnectDescription: "O histórico seguro está temporariamente indisponível. Nenhuma mensagem foi perdida ou cobrada.",
    tryAgain: "Tentar novamente",
  },
  sw: {
    languageLabel: "Lugha ya msaidizi",
    connecting: "Inaunganisha",
    reconnecting: "Inaunganisha tena",
    loadingEarlierMessages: "Inapakia ujumbe wa awali…",
    chatReconnecting: "Mazungumzo yanaunganisha tena",
    chatReconnectDescription: "Historia yako salama ya mazungumzo haipatikani kwa muda. Hakuna ujumbe uliopotea au kutozwa.",
    tryAgain: "Jaribu tena",
  },
  yo: {
    languageLabel: "Èdè olùrànlọ́wọ́",
    connecting: "Ń sopọ̀",
    reconnecting: "Ń tún sopọ̀",
    loadingEarlierMessages: "Ń gba àwọn ìfiranṣẹ́ àtijọ́ wọlé…",
    chatReconnecting: "Ìjíròrò ń tún sopọ̀",
    chatReconnectDescription: "Ìtàn ìjíròrò ààbò rẹ kò sí fún ìgbà díẹ̀. Kò sí ìfiranṣẹ́ tó sọnù tàbí owó tí a yọ.",
    tryAgain: "Gbìyànjú lẹ́ẹ̀kan síi",
  },
  zu: {
    languageLabel: "Ulimi lomsizi",
    connecting: "Iyaxhuma",
    reconnecting: "Ixhuma kabusha",
    loadingEarlierMessages: "Ilayisha imilayezo yangaphambilini…",
    chatReconnecting: "Ingxoxo ixhuma kabusha",
    chatReconnectDescription: "Umlando wakho wengxoxo ovikelekile awutholakali okwesikhashana. Awukho umlayezo olahlekile noma okhokhisiwe.",
    tryAgain: "Zama futhi",
  },
};

Object.entries(assistantConnectionTranslations).forEach(
  ([language, assistantPage]) => {
    translations[language] = {
      ...(translations[language] || {}),
      assistantPage: {
        ...(translations[language]?.assistantPage || {}),
        ...assistantPage,
      },
    };
  },
);

const sellTranslations = {
  ar: {
    allProducts: "كل المنتجات",
    amountPaidNow: "المبلغ المدفوع الآن",
    optionalShort: "اختياري",
    openingStock: "المخزون الافتتاحي",
    addFirstProduct: "أضف أول منتج",
    addNewProduct: "أضف منتجًا جديدًا",
    addNewProductDescription: "سيُضاف إلى هذه العملية فورًا.",
    balanceDue: "الرصيد المستحق",
    barcodeNotFound: "لم يتم العثور على الباركود",
    barcodeNotFoundDescription: "أضف تفاصيل المنتج لحفظه.",
    cancel: "إلغاء",
    cart: "السلة",
    cartEmpty: "السلة فارغة",
    cartEmptyDescription: "اضغط منتجًا أو امسح باركودًا أو ابحث لبدء البيع.",
    checkRequiredProductDetails: "راجع تفاصيل المنتج المطلوبة",
    clear: "مسح",
    completeSale: "إتمام البيع",
    costPrice: "سعر التكلفة",
    customerName: "اسم العميل",
    customerNamePlaceholder: "اسم العميل",
    dueDate: "تاريخ الاستحقاق",
    keepTyping: "واصل الكتابة لإنشاء منتج أو العثور عليه.",
    newProduct: "منتج جديد",
    noProductsAvailableYet: "لا توجد منتجات بعد",
    onCredit: "بالآجل",
    optional: "اختياري",
    optionalBarcode: "باركود اختياري",
    paid: "مدفوع",
    phone: "الهاتف",
    productAddedToCart: "تمت إضافة المنتج إلى السلة",
    productName: "اسم المنتج",
    productNamePlaceholder: "مثال: كيس حليب Peak",
    productScanned: "تم مسح المنتج",
    recordCreditSale: "تسجيل بيع آجل",
    recordSaleFailed: "تعذر تسجيل البيع",
    recording: "جارٍ التسجيل...",
    refreshProducts: "تحديث الكتالوج",
    refreshProductsDone: "تم تحديث الكتالوج",
    refreshProductsFailed: "تعذر تحديث الكتالوج",
    refreshProductsHint: "اجلب أحدث الكتالوج من الخادم.",
    refreshAndTryAgain: "حدّث الصفحة وحاول مرة أخرى.",
    saleRecorded: "تم تسجيل البيع",
    saleRecordedSummary: (total, count) => `${total} · ${count} عنصر`,
    saveAndAddToCart: "احفظ وأضف إلى السلة",
    saveProductFailed: "تعذر إضافة المنتج",
    saving: "جارٍ الحفظ...",
    scannerReady: "الماسح جاهز",
    scannerReadyDescription: "امسح باركودًا الآن لإرفاقه بهذا المنتج.",
    scanToAttach: "امسح للإرفاق",
    searchPlaceholder: "ابحث عن منتج أو امسح باركودًا أو اكتب للعثور...",
    sellingPrice: "سعر البيع",
    total: "الإجمالي",
    tryAgain: "حاول مرة أخرى.",
    untracked: "غير متتبع",
    stockIn: (count) => `${count} في المخزون`,
    stockLeft: (count) => `${count} متبقٍ`,
    stockOut: "نفد",
    productOutOfStock: (name) => `${name} غير متوفر في المخزون`,
    productOnlyInStock: (name, count) => `${name} متوفر منه ${count} فقط`,
    validation: {
      amountPaidLessThanTotal:
        "يجب أن يكون المبلغ المدفوع أقل من إجمالي الفاتورة للبيع الآجل",
      amountPaidRequired: "المبلغ المدفوع مطلوب",
      amountPaidValid: "يجب أن يكون المبلغ المدفوع رقمًا صحيحًا",
      amountPaidNonNegative: "لا يمكن أن يكون المبلغ المدفوع سالبًا",
      costValid: "يجب أن يكون سعر التكلفة رقمًا صحيحًا",
      costNonNegative: "لا يمكن أن يكون سعر التكلفة سالبًا",
      customerNameRequired: "اسم العميل مطلوب للبيع الآجل",
      dueDateRequired: "تاريخ الاستحقاق مطلوب للبيع الآجل",
      lowStockWhole: "تنبيه انخفاض المخزون يجب أن يكون رقمًا صحيحًا",
      lowStockNonNegative: "لا يمكن أن يكون تنبيه انخفاض المخزون سالبًا",
      priceRequired: "سعر البيع مطلوب",
      priceValid: "يجب أن يكون سعر البيع رقمًا صحيحًا",
      priceAboveZero: "يجب أن يكون سعر البيع أكبر من صفر",
      productNameRequired: "اسم المنتج مطلوب",
      stockRequired: "المخزون الافتتاحي مطلوب",
      stockWhole: "يجب أن يكون المخزون الافتتاحي رقمًا صحيحًا",
      stockNonNegative: "لا يمكن أن يكون المخزون الافتتاحي سالبًا",
      invalidCreditAmount: "أدخل مبلغ بيع آجل صالحًا",
    },
  },
  fr: {
    allProducts: "tous les produits",
    amountPaidNow: "Montant payé maintenant",
    optionalShort: "optionnel",
    openingStock: "Stock d'ouverture",
    addFirstProduct: "Ajouter le premier produit",
    addNewProduct: "Ajouter un nouveau produit",
    addNewProductDescription: "Il sera ajouté immédiatement à cette vente.",
    balanceDue: "Solde dû",
    barcodeNotFound: "Code-barres introuvable",
    barcodeNotFoundDescription:
      "Ajoutez les détails du produit pour l'enregistrer.",
    cancel: "Annuler",
    cart: "Panier",
    cartEmpty: "Le panier est vide",
    cartEmptyDescription:
      "Touchez un produit, scannez un code-barres ou recherchez pour commencer une vente.",
    checkRequiredProductDetails: "Vérifiez les détails requis du produit",
    clear: "Effacer",
    completeSale: "Finaliser la vente",
    costPrice: "Prix de revient",
    customerName: "Nom du client",
    customerNamePlaceholder: "Nom du client",
    dueDate: "Date d'échéance",
    keepTyping: "Continuez à taper pour créer ou trouver un produit.",
    newProduct: "Nouveau produit",
    noProductsAvailableYet: "Aucun produit disponible pour l'instant",
    onCredit: "À crédit",
    optional: "Optionnel",
    optionalBarcode: "Code-barres optionnel",
    paid: "Payé",
    phone: "Téléphone",
    productAddedToCart: "Produit ajouté au panier",
    productName: "Nom du produit",
    productNamePlaceholder: "ex. sachet de lait Peak",
    productScanned: "Produit scanné",
    recordCreditSale: "Enregistrer la vente à crédit",
    recordSaleFailed: "Impossible d'enregistrer la vente",
    recording: "Enregistrement...",
    refreshProducts: "Actualiser le catalogue",
    refreshProductsDone: "Catalogue actualise",
    refreshProductsFailed: "Impossible d'actualiser le catalogue",
    refreshProductsHint:
      "Recuperez le catalogue le plus recent depuis le serveur.",
    refreshAndTryAgain: "Actualisez puis réessayez.",
    saleRecorded: "Vente enregistrée",
    saleRecordedSummary: (total, count) =>
      `${total} · ${count} article${count === 1 ? "" : "s"}`,
    saveAndAddToCart: "Enregistrer et ajouter au panier",
    saveProductFailed: "Impossible d'ajouter le produit",
    saving: "Enregistrement...",
    scannerReady: "Scanner prêt",
    scannerReadyDescription:
      "Scannez maintenant un code-barres pour l'attacher à ce produit.",
    scanToAttach: "Scanner pour joindre",
    searchPlaceholder:
      "Rechercher un produit, scanner un code-barres ou taper pour trouver…",
    sellingPrice: "Prix de vente",
    total: "Total",
    tryAgain: "Réessayez.",
    untracked: "Non suivi",
    stockIn: (count) => `${count} en stock`,
    stockLeft: (count) => `${count} restants`,
    stockOut: "Rupture",
    productOutOfStock: (name) => `${name} est en rupture de stock`,
    productOnlyInStock: (name, count) =>
      `${name} n'a plus que ${count} en stock`,
    validation: {
      amountPaidLessThanTotal:
        "Le montant payé doit être inférieur au total de la facture pour les ventes à crédit",
      amountPaidRequired: "Le montant payé est requis",
      amountPaidValid: "Le montant payé doit être valide",
      amountPaidNonNegative: "Le montant payé ne peut pas être négatif",
      costValid: "Le prix de revient doit être valide",
      costNonNegative: "Le prix de revient ne peut pas être négatif",
      customerNameRequired:
        "Le nom du client est requis pour la vente à crédit",
      dueDateRequired: "La date d'échéance est requise pour la vente à crédit",
      lowStockWhole: "L'alerte de stock bas doit être un nombre entier",
      lowStockNonNegative: "L'alerte de stock bas ne peut pas être négative",
      priceRequired: "Le prix de vente est requis",
      priceValid: "Le prix de vente doit être valide",
      priceAboveZero: "Le prix de vente doit être supérieur à zéro",
      productNameRequired: "Le nom du produit est requis",
      stockRequired: "Le stock d'ouverture est requis",
      stockWhole: "Le stock d'ouverture doit être un nombre entier",
      stockNonNegative: "Le stock d'ouverture ne peut pas être négatif",
      invalidCreditAmount: "Entrez un montant de vente à crédit valide",
    },
  },
  ha: {
    allProducts: "duk kayayyaki",
    amountPaidNow: "Kudin da aka biya yanzu",
    optionalShort: "na zabi",
    openingStock: "Adadin budewa",
    addFirstProduct: "Kara kaya na farko",
    addNewProduct: "Kara sabon kaya",
    addNewProductDescription: "Za a kara shi cikin wannan sayarwar nan take.",
    balanceDue: "Ragowar bashi",
    barcodeNotFound: "Ba a samu barcode ba",
    barcodeNotFoundDescription: "Cika bayanan kaya don a ajiye shi.",
    cancel: "Soke",
    cart: "Kwando",
    cartEmpty: "Kwando babu komai",
    cartEmptyDescription:
      "Danna kaya, yi scan barcode, ko bincika don fara sayarwa.",
    checkRequiredProductDetails: "Duba bayanan kayan da ake bukata",
    clear: "Goge",
    completeSale: "Kammala sayarwa",
    costPrice: "Farashin sayo",
    customerName: "Sunan abokin ciniki",
    customerNamePlaceholder: "Sunan abokin ciniki",
    dueDate: "Ranar biya",
    keepTyping: "Ci gaba da rubutu don kirkira ko nemo kaya.",
    newProduct: "Sabon kaya",
    noProductsAvailableYet: "Babu kaya tukuna",
    onCredit: "A bashi",
    optional: "Na zabi",
    optionalBarcode: "Barcode na zabi",
    paid: "An biya",
    phone: "Waya",
    productAddedToCart: "An kara kaya zuwa kwando",
    productName: "Sunan kaya",
    productNamePlaceholder: "misali Sample product",
    productScanned: "An yi scan kaya",
    recordCreditSale: "Rubuta sayarwar bashi",
    recordSaleFailed: "Ba a iya rubuta sayarwar ba",
    recording: "Ana rubutawa...",
    refreshProducts: "Sabunta kasida",
    refreshProductsDone: "An sabunta kasida",
    refreshProductsFailed: "An kasa sabunta kasida",
    refreshProductsHint: "Dauko sabuwar kasida daga uwar garke.",
    refreshAndTryAgain: "Sabunta ka sake gwadawa.",
    saleRecorded: "An rubuta sayarwa",
    saleRecordedSummary: (total, count) => `${total} · kaya ${count}`,
    saveAndAddToCart: "Ajiye ka kara zuwa kwando",
    saveProductFailed: "Ba a iya kara kayan ba",
    saving: "Ana ajiyewa...",
    scannerReady: "Scanner ya shirya",
    scannerReadyDescription:
      "Yi scan barcode yanzu don daura shi da wannan kaya.",
    scanToAttach: "Yi scan don daurawa",
    searchPlaceholder: "Binciki kaya, yi scan barcode, ko rubuta don nema...",
    sellingPrice: "Farashin sayarwa",
    total: "Jimilla",
    tryAgain: "Sake gwadawa.",
    untracked: "Ba a bibiyarsa",
    stockIn: (count) => `${count} a stock`,
    stockLeft: (count) => `${count} suka rage`,
    stockOut: "Ya kare",
    productOutOfStock: (name) => `${name} ya kare a stock`,
    productOnlyInStock: (name, count) =>
      `${name} yana da ${count} kacal a stock`,
    validation: {
      amountPaidLessThanTotal:
        "Kudin da aka biya dole ya kasance kasa da jimillar invoice don sayarwar bashi",
      amountPaidRequired: "Ana bukatar kudin da aka biya",
      amountPaidValid: "Kudin da aka biya dole ya zama daidai",
      amountPaidNonNegative: "Kudin da aka biya ba zai zama kasa da sifili ba",
      costValid: "Farashin sayo dole ya zama daidai",
      costNonNegative: "Farashin sayo ba zai zama kasa da sifili ba",
      customerNameRequired:
        "Ana bukatar sunan abokin ciniki don sayarwar bashi",
      dueDateRequired: "Ana bukatar ranar biya don sayarwar bashi",
      lowStockWhole: "Gargadin karancin kaya dole ya zama cikakken lamba",
      lowStockNonNegative:
        "Gargadin karancin kaya ba zai zama kasa da sifili ba",
      priceRequired: "Ana bukatar farashin sayarwa",
      priceValid: "Farashin sayarwa dole ya zama daidai",
      priceAboveZero: "Farashin sayarwa dole ya fi sifili",
      productNameRequired: "Ana bukatar sunan kaya",
      stockRequired: "Ana bukatar adadin budewa",
      stockWhole: "Adadin budewa dole ya zama cikakken lamba",
      stockNonNegative: "Adadin budewa ba zai zama kasa da sifili ba",
      invalidCreditAmount: "Shigar da ingantaccen adadin sayarwar bashi",
    },
  },
  ig: {
    allProducts: "ngwaahịa niile",
    amountPaidNow: "Ego akwụgoro ugbu a",
    optionalShort: "nhọrọ",
    openingStock: "Ngwaahịa mbido",
    addFirstProduct: "Tinye ngwaahịa mbụ",
    addNewProduct: "Tinye ngwaahịa ọhụrụ",
    addNewProductDescription: "A ga-etinye ya ozugbo na ire a.",
    balanceDue: "Ego fọdụrụ",
    barcodeNotFound: "Ahụghị barcode",
    barcodeNotFoundDescription: "Tinye nkọwa ngwaahịa iji chekwaa ya.",
    cancel: "Kagbuo",
    cart: "Kata",
    cartEmpty: "Kata efu",
    cartEmptyDescription:
      "Pịa ngwaahịa, scan barcode, ma ọ bụ chọọ iji malite ire.",
    checkRequiredProductDetails: "Lelee nkọwa ngwaahịa achọrọ",
    clear: "Kpochapụ",
    completeSale: "Mechaa ire",
    costPrice: "Ọnụahịa zụtara",
    customerName: "Aha onye ahịa",
    customerNamePlaceholder: "Aha onye ahịa",
    dueDate: "Ụbọchị akwụmụgwọ",
    keepTyping: "Gaa n’ihu na ide iji mepụta ma ọ bụ chọta ngwaahịa.",
    newProduct: "Ngwaahịa ọhụrụ",
    noProductsAvailableYet: "Enweghị ngwaahịa ugbu a",
    onCredit: "Na mgbazinye",
    optional: "Nhọrọ",
    optionalBarcode: "Barcode nhọrọ",
    paid: "Akwụgoro",
    phone: "Ekwentị",
    productAddedToCart: "Etinyela ngwaahịa na kata",
    productName: "Aha ngwaahịa",
    productNamePlaceholder: "dịka Sample product",
    productScanned: "E-scanala ngwaahịa",
    recordCreditSale: "Chekwaa ire mgbazinye",
    recordSaleFailed: "Enweghị ike ichekwa ire",
    recording: "Na-echekwa...",
    refreshProducts: "Melite katalọgụ",
    refreshProductsDone: "Emelitela katalọgụ",
    refreshProductsFailed: "Enweghị ike imelite katalọgụ",
    refreshProductsHint: "Nweta katalọgụ kacha ọhụrụ site na sava.",
    refreshAndTryAgain: "Melite peeji ma nwalee ọzọ.",
    saleRecorded: "Echekwara ire",
    saleRecordedSummary: (total, count) => `${total} · ihe ${count}`,
    saveAndAddToCart: "Chekwaa tinye na kata",
    saveProductFailed: "Enweghị ike itinye ngwaahịa",
    saving: "Na-echekwa...",
    scannerReady: "Scanner adịla njikere",
    scannerReadyDescription: "Scan barcode ugbu a iji jikọta ya na ngwaahịa a.",
    scanToAttach: "Scan iji jikọta",
    searchPlaceholder: "Chọọ ngwaahịa, scan barcode, ma ọ bụ dee ka ịchọta...",
    sellingPrice: "Ọnụahịa ire",
    total: "Ngụkọta",
    tryAgain: "Nwaa ọzọ.",
    untracked: "A naghị eso ya",
    stockIn: (count) => `${count} dị na ngwaahịa`,
    stockLeft: (count) => `${count} fọdụrụ`,
    stockOut: "Agwụla",
    productOutOfStock: (name) => `${name} agwụla na ngwaahịa`,
    productOnlyInStock: (name, count) => `${name} nwere naanị ${count} fọdụrụ`,
    validation: {
      amountPaidLessThanTotal:
        "Ego akwụgoro ga-adị obere karịa ngụkọta invoice maka ire mgbazinye",
      amountPaidRequired: "A chọrọ ego akwụgoro",
      amountPaidValid: "Ego akwụgoro ga-abụ nke ziri ezi",
      amountPaidNonNegative: "Ego akwụgoro agaghị abụ nke na-erughị efu",
      costValid: "Ọnụahịa zụtara ga-abụ nke ziri ezi",
      costNonNegative: "Ọnụahịa zụtara agaghị abụ nke na-erughị efu",
      customerNameRequired: "A chọrọ aha onye ahịa maka ire mgbazinye",
      dueDateRequired: "A chọrọ ụbọchị akwụmụgwọ maka ire mgbazinye",
      lowStockWhole: "Ọkwa ncheta ngwaahịa dị ala ga-abụ nọmba zuru ezu",
      lowStockNonNegative: "Ọkwa ncheta ngwaahịa dị ala agaghị erughị efu",
      priceRequired: "A chọrọ ọnụahịa ire",
      priceValid: "Ọnụahịa ire ga-abụ nke ziri ezi",
      priceAboveZero: "Ọnụahịa ire ga-adị elu karịa efu",
      productNameRequired: "A chọrọ aha ngwaahịa",
      stockRequired: "A chọrọ ngwaahịa mbido",
      stockWhole: "Ngwaahịa mbido ga-abụ nọmba zuru ezu",
      stockNonNegative: "Ngwaahịa mbido agaghị erughị efu",
      invalidCreditAmount: "Tinye ego ire mgbazinye ziri ezi",
    },
  },
  pt: {
    allProducts: "todos os produtos",
    amountPaidNow: "Valor pago agora",
    optionalShort: "opcional",
    openingStock: "Stock inicial",
    addFirstProduct: "Adicionar primeiro produto",
    addNewProduct: "Adicionar novo produto",
    addNewProductDescription: "Será adicionado imediatamente a esta venda.",
    balanceDue: "Saldo em dívida",
    barcodeNotFound: "Código de barras não encontrado",
    barcodeNotFoundDescription:
      "Adicione os detalhes do produto para o guardar.",
    cancel: "Cancelar",
    cart: "Carrinho",
    cartEmpty: "O carrinho está vazio",
    cartEmptyDescription:
      "Toque num produto, leia um código de barras ou pesquise para iniciar uma venda.",
    checkRequiredProductDetails:
      "Verifique os detalhes obrigatórios do produto",
    clear: "Limpar",
    completeSale: "Concluir venda",
    costPrice: "Preço de custo",
    customerName: "Nome do cliente",
    customerNamePlaceholder: "Nome do cliente",
    dueDate: "Data de vencimento",
    keepTyping: "Continue a escrever para criar ou encontrar um produto.",
    newProduct: "Novo produto",
    noProductsAvailableYet: "Ainda não há produtos disponíveis",
    onCredit: "A crédito",
    optional: "Opcional",
    optionalBarcode: "Código de barras opcional",
    paid: "Pago",
    phone: "Telefone",
    productAddedToCart: "Produto adicionado ao carrinho",
    productName: "Nome do produto",
    productNamePlaceholder: "ex. Sample product",
    productScanned: "Produto digitalizado",
    recordCreditSale: "Registar venda a crédito",
    recordSaleFailed: "Não foi possível registar a venda",
    recording: "A registar...",
    refreshProducts: "Atualizar catalogo",
    refreshProductsDone: "Catalogo atualizado",
    refreshProductsFailed: "Nao foi possivel atualizar o catalogo",
    refreshProductsHint: "Obtenha o catalogo mais recente do servidor.",
    refreshAndTryAgain: "Atualize e tente novamente.",
    saleRecorded: "Venda registada",
    saleRecordedSummary: (total, count) =>
      `${total} · ${count} item${count === 1 ? "" : "s"}`,
    saveAndAddToCart: "Guardar e adicionar ao carrinho",
    saveProductFailed: "Não foi possível adicionar o produto",
    saving: "A guardar...",
    scannerReady: "Leitor pronto",
    scannerReadyDescription:
      "Leia um código de barras agora para o associar a este produto.",
    scanToAttach: "Ler para associar",
    searchPlaceholder:
      "Pesquisar produto, ler código de barras ou escrever para encontrar…",
    sellingPrice: "Preço de venda",
    total: "Total",
    tryAgain: "Tente novamente.",
    untracked: "Sem rastreio",
    stockIn: (count) => `${count} em stock`,
    stockLeft: (count) => `${count} restantes`,
    stockOut: "Esgotado",
    productOutOfStock: (name) => `${name} está esgotado`,
    productOnlyInStock: (name, count) => `${name} tem apenas ${count} em stock`,
    validation: {
      amountPaidLessThanTotal:
        "O valor pago deve ser inferior ao total da fatura para vendas a crédito",
      amountPaidRequired: "O valor pago é obrigatório",
      amountPaidValid: "O valor pago deve ser válido",
      amountPaidNonNegative: "O valor pago não pode ser negativo",
      costValid: "O preço de custo deve ser válido",
      costNonNegative: "O preço de custo não pode ser negativo",
      customerNameRequired:
        "O nome do cliente é obrigatório para vendas a crédito",
      dueDateRequired:
        "A data de vencimento é obrigatória para vendas a crédito",
      lowStockWhole: "O alerta de stock baixo deve ser um número inteiro",
      lowStockNonNegative: "O alerta de stock baixo não pode ser negativo",
      priceRequired: "O preço de venda é obrigatório",
      priceValid: "O preço de venda deve ser válido",
      priceAboveZero: "O preço de venda deve ser maior que zero",
      productNameRequired: "O nome do produto é obrigatório",
      stockRequired: "O stock inicial é obrigatório",
      stockWhole: "O stock inicial deve ser um número inteiro",
      stockNonNegative: "O stock inicial não pode ser negativo",
      invalidCreditAmount: "Introduza um valor válido para venda a crédito",
    },
  },
  sw: {
    allProducts: "bidhaa zote",
    amountPaidNow: "Kiasi kilicholipwa sasa",
    optionalShort: "hiari",
    openingStock: "Stock ya kuanzia",
    addFirstProduct: "Ongeza bidhaa ya kwanza",
    addNewProduct: "Ongeza bidhaa mpya",
    addNewProductDescription: "Itaongezwa kwenye mauzo haya mara moja.",
    balanceDue: "Salio linalodaiwa",
    barcodeNotFound: "Barcode haikupatikana",
    barcodeNotFoundDescription: "Ongeza maelezo ya bidhaa ili kuihifadhi.",
    cancel: "Ghairi",
    cart: "Kikapu",
    cartEmpty: "Kikapu hakina kitu",
    cartEmptyDescription: "Gusa bidhaa, skana barcode, au tafuta kuanza kuuza.",
    checkRequiredProductDetails: "Angalia maelezo muhimu ya bidhaa",
    clear: "Futa",
    completeSale: "Kamilisha mauzo",
    costPrice: "Bei ya kununua",
    customerName: "Jina la mteja",
    customerNamePlaceholder: "Jina la mteja",
    dueDate: "Tarehe ya malipo",
    keepTyping: "Endelea kuandika ili kuunda au kupata bidhaa.",
    newProduct: "Bidhaa mpya",
    noProductsAvailableYet: "Hakuna bidhaa bado",
    onCredit: "Kwa mkopo",
    optional: "Hiari",
    optionalBarcode: "Barcode ya hiari",
    paid: "Imelipwa",
    phone: "Simu",
    productAddedToCart: "Bidhaa imeongezwa kwenye kikapu",
    productName: "Jina la bidhaa",
    productNamePlaceholder: "mf. Sample product",
    productScanned: "Bidhaa imeskaniwa",
    recordCreditSale: "Hifadhi mauzo ya mkopo",
    recordSaleFailed: "Haikuwezekana kuhifadhi mauzo",
    recording: "Inahifadhiwa...",
    refreshProducts: "Onyesha upya katalogi",
    refreshProductsDone: "Katalogi imeonyeshwa upya",
    refreshProductsFailed: "Imeshindikana kuonyesha upya katalogi",
    refreshProductsHint: "Pata katalogi ya hivi karibuni kutoka kwenye seva.",
    refreshAndTryAgain: "Sasisha kisha ujaribu tena.",
    saleRecorded: "Mauzo yamehifadhiwa",
    saleRecordedSummary: (total, count) => `${total} · bidhaa ${count}`,
    saveAndAddToCart: "Hifadhi na ongeza kwenye kikapu",
    saveProductFailed: "Haikuwezekana kuongeza bidhaa",
    saving: "Inahifadhiwa...",
    scannerReady: "Skana iko tayari",
    scannerReadyDescription:
      "Skana barcode sasa ili uiunganishe na bidhaa hii.",
    scanToAttach: "Skana kuunganisha",
    searchPlaceholder: "Tafuta bidhaa, skana barcode, au andika upate...",
    sellingPrice: "Bei ya kuuza",
    total: "Jumla",
    tryAgain: "Jaribu tena.",
    untracked: "Haifuatiliwi",
    stockIn: (count) => `${count} kwenye stock`,
    stockLeft: (count) => `${count} zimebaki`,
    stockOut: "Imeisha",
    productOutOfStock: (name) => `${name} imeisha stock`,
    productOnlyInStock: (name, count) => `${name} ina ${count} tu kwenye stock`,
    validation: {
      amountPaidLessThanTotal:
        "Kiasi kilicholipwa lazima kiwe chini ya jumla ya ankara kwa mauzo ya mkopo",
      amountPaidRequired: "Kiasi kilicholipwa kinahitajika",
      amountPaidValid: "Kiasi kilicholipwa lazima kiwe sahihi",
      amountPaidNonNegative: "Kiasi kilicholipwa hakiwezi kuwa hasi",
      costValid: "Bei ya kununua lazima iwe sahihi",
      costNonNegative: "Bei ya kununua haiwezi kuwa hasi",
      customerNameRequired: "Jina la mteja linahitajika kwa mauzo ya mkopo",
      dueDateRequired: "Tarehe ya malipo inahitajika kwa mauzo ya mkopo",
      lowStockWhole: "Tahadhari ya stock ndogo lazima iwe namba kamili",
      lowStockNonNegative: "Tahadhari ya stock ndogo haiwezi kuwa hasi",
      priceRequired: "Bei ya kuuza inahitajika",
      priceValid: "Bei ya kuuza lazima iwe sahihi",
      priceAboveZero: "Bei ya kuuza lazima iwe juu ya sifuri",
      productNameRequired: "Jina la bidhaa linahitajika",
      stockRequired: "Stock ya kuanzia inahitajika",
      stockWhole: "Stock ya kuanzia lazima iwe namba kamili",
      stockNonNegative: "Stock ya kuanzia haiwezi kuwa hasi",
      invalidCreditAmount: "Weka kiasi sahihi cha mauzo ya mkopo",
    },
  },
  yo: {
    allProducts: "gbogbo ọja",
    amountPaidNow: "Iye ti a san bayii",
    optionalShort: "iyan",
    openingStock: "Iṣura ibẹrẹ",
    addFirstProduct: "Fi ọja akọkọ kun",
    addNewProduct: "Fi ọja tuntun kun",
    addNewProductDescription: "A o fi kun tita yii lẹsẹkẹsẹ.",
    balanceDue: "Iye to ku",
    barcodeNotFound: "A ko ri barcode",
    barcodeNotFoundDescription: "Fi alaye ọja kun lati fipamọ.",
    cancel: "Fagilee",
    cart: "Kẹ̀kẹ́",
    cartEmpty: "Kẹ̀kẹ́ ṣofo",
    cartEmptyDescription:
      "Tẹ ọja kan, ṣe scan barcode, tabi wa lati bẹrẹ tita.",
    checkRequiredProductDetails: "Ṣayẹwo alaye ọja pataki",
    clear: "Nu",
    completeSale: "Pari tita",
    costPrice: "Iye rira",
    customerName: "Orukọ onibara",
    customerNamePlaceholder: "Orukọ onibara",
    dueDate: "Ọjọ isanwo",
    keepTyping: "Maa tẹsiwaju lati kọ lati ṣẹda tabi wa ọja.",
    newProduct: "Ọja tuntun",
    noProductsAvailableYet: "Ko si ọja sibẹ",
    onCredit: "Lori gbese",
    optional: "Iyan",
    optionalBarcode: "Barcode iyan",
    paid: "Ti san",
    phone: "Foonu",
    productAddedToCart: "A fi ọja kun kẹ̀kẹ́",
    productName: "Orukọ ọja",
    productNamePlaceholder: "e.g. Sample product",
    productScanned: "A ti scan ọja",
    recordCreditSale: "Fipamọ tita gbese",
    recordSaleFailed: "Ko ṣee ṣe lati fipamọ tita",
    recording: "Ń fipamọ...",
    refreshProducts: "Tun katalọọgu se",
    refreshProductsDone: "A ti tun katalọọgu se",
    refreshProductsFailed: "Ko seese lati tun katalọọgu se",
    refreshProductsHint: "Gba katalọọgu tuntun lati inu olupin.",
    refreshAndTryAgain: "Tun oju-iwe ṣe ki o gbiyanju lẹẹkansi.",
    saleRecorded: "A ti fipamọ tita",
    saleRecordedSummary: (total, count) => `${total} · ọja ${count}`,
    saveAndAddToCart: "Fipamọ ki o fi kun kẹ̀kẹ́",
    saveProductFailed: "Ko ṣee ṣe lati fi ọja kun",
    saving: "Ń fipamọ...",
    scannerReady: "Scanner ti setan",
    scannerReadyDescription: "Ṣe scan barcode bayii lati so mọ ọja yii.",
    scanToAttach: "Scan lati so pọ",
    searchPlaceholder: "Wa ọja, ṣe scan barcode, tabi kọ lati wa...",
    sellingPrice: "Iye tita",
    total: "Lapapọ",
    tryAgain: "Gbiyanju lẹẹkansi.",
    untracked: "A ko tọpinpin",
    stockIn: (count) => `${count} wa ninu iṣura`,
    stockLeft: (count) => `${count} ku`,
    stockOut: "Tan",
    productOutOfStock: (name) => `${name} ti tan ninu iṣura`,
    productOnlyInStock: (name, count) => `${name} ni ${count} nikan ninu iṣura`,
    validation: {
      amountPaidLessThanTotal:
        "Iye ti a san gbọdọ kere ju lapapọ invoice lọ fun tita gbese",
      amountPaidRequired: "A nilo iye ti a san",
      amountPaidValid: "Iye ti a san gbọdọ jẹ deede",
      amountPaidNonNegative: "Iye ti a san ko le jẹ odi",
      costValid: "Iye rira gbọdọ jẹ deede",
      costNonNegative: "Iye rira ko le jẹ odi",
      customerNameRequired: "A nilo orukọ onibara fun tita gbese",
      dueDateRequired: "A nilo ọjọ isanwo fun tita gbese",
      lowStockWhole: "Ikilọ iṣura kekere gbọdọ jẹ nọmba pipe",
      lowStockNonNegative: "Ikilọ iṣura kekere ko le jẹ odi",
      priceRequired: "A nilo iye tita",
      priceValid: "Iye tita gbọdọ jẹ deede",
      priceAboveZero: "Iye tita gbọdọ ju odo lọ",
      productNameRequired: "A nilo orukọ ọja",
      stockRequired: "A nilo iṣura ibẹrẹ",
      stockWhole: "Iṣura ibẹrẹ gbọdọ jẹ nọmba pipe",
      stockNonNegative: "Iṣura ibẹrẹ ko le jẹ odi",
      invalidCreditAmount: "Tẹ iye to pe fun tita gbese",
    },
  },
  zu: {
    allProducts: "yonke imikhiqizo",
    amountPaidNow: "Inani elikhokhwe manje",
    optionalShort: "okokuzikhethela",
    openingStock: "Isitoko sokuqala",
    addFirstProduct: "Engeza umkhiqizo wokuqala",
    addNewProduct: "Engeza umkhiqizo omusha",
    addNewProductDescription: "Uzongezwa kulokhu kuthengisa ngokushesha.",
    balanceDue: "Ibhalansi esele",
    barcodeNotFound: "Ibhakhodi alitholakalanga",
    barcodeNotFoundDescription:
      "Engeza imininingwane yomkhiqizo ukuze uwulondoloze.",
    cancel: "Khansela",
    cart: "Inqola",
    cartEmpty: "Inqola ayinalutho",
    cartEmptyDescription:
      "Thinta umkhiqizo, skena ibhakhodi, noma useshe ukuze uqale ukuthengisa.",
    checkRequiredProductDetails: "Hlola imininingwane yomkhiqizo edingekayo",
    clear: "Sula",
    completeSale: "Qedela ukuthengisa",
    costPrice: "Intengo yokuthenga",
    customerName: "Igama lekhasimende",
    customerNamePlaceholder: "Igama lekhasimende",
    dueDate: "Usuku lokukhokha",
    keepTyping: "Qhubeka nokubhala ukuze udale noma uthole umkhiqizo.",
    newProduct: "Umkhiqizo omusha",
    noProductsAvailableYet: "Ayikho imikhiqizo okwamanje",
    onCredit: "Ngesikweletu",
    optional: "Okokuzikhethela",
    optionalBarcode: "Ibhakhodi yokuzikhethela",
    paid: "Kukhokhiwe",
    phone: "Ucingo",
    productAddedToCart: "Umkhiqizo ungeziwe enqoleni",
    productName: "Igama lomkhiqizo",
    productNamePlaceholder: "isb. Sample product",
    productScanned: "Umkhiqizo uskeniwe",
    recordCreditSale: "Londoloza ukuthengisa kwesikweletu",
    recordSaleFailed: "Akukwazekanga ukulondoloza ukuthengisa",
    recording: "Kuyalondolozwa...",
    refreshProducts: "Vuselela ikhathalogi",
    refreshProductsDone: "Ikhathalogi ivuselelwe",
    refreshProductsFailed: "Ayikwazanga ukuvuselela ikhathalogi",
    refreshProductsHint: "Thola ikhathalogi yakamuva kuseva.",
    refreshAndTryAgain: "Vuselela bese uzama futhi.",
    saleRecorded: "Ukuthengisa kulondoloziwe",
    saleRecordedSummary: (total, count) => `${total} · izinto ${count}`,
    saveAndAddToCart: "Londoloza ungeze enqoleni",
    saveProductFailed: "Akukwazekanga ukwengeza umkhiqizo",
    saving: "Kuyalondolozwa...",
    scannerReady: "Iskena silungile",
    scannerReadyDescription:
      "Skena ibhakhodi manje ukuze uyixhume kulo mkhiqizo.",
    scanToAttach: "Skena ukuze unamathisele",
    searchPlaceholder:
      "Sesha umkhiqizo, skena ibhakhodi, noma bhala ukuze uthole...",
    sellingPrice: "Intengo yokuthengisa",
    total: "Ingqikithi",
    tryAgain: "Zama futhi.",
    untracked: "Akulandelwa",
    stockIn: (count) => `${count} esitokweni`,
    stockLeft: (count) => `${count} esele`,
    stockOut: "Kuphelile",
    productOutOfStock: (name) => `${name} uphelile esitokweni`,
    productOnlyInStock: (name, count) =>
      `${name} unokungu-${count} kuphela esitokweni`,
    validation: {
      amountPaidLessThanTotal:
        "Inani elikhokhwe kufanele libe ngaphansi kwengqikithi ye-invoyisi ekuthengiseni kwesikweletu",
      amountPaidRequired: "Inani elikhokhwe liyadingeka",
      amountPaidValid: "Inani elikhokhwe kufanele libe semthethweni",
      amountPaidNonNegative: "Inani elikhokhwe alinakuba ngaphansi kuka-zero",
      costValid: "Intengo yokuthenga kufanele ibe semthethweni",
      costNonNegative: "Intengo yokuthenga ayinakuba ngaphansi kuka-zero",
      customerNameRequired:
        "Igama lekhasimende liyadingeka ekuthengiseni kwesikweletu",
      dueDateRequired:
        "Usuku lokukhokha luyadingeka ekuthengiseni kwesikweletu",
      lowStockWhole:
        "Isixwayiso sesitoko esiphansi kufanele sibe inombolo ephelele",
      lowStockNonNegative:
        "Isixwayiso sesitoko esiphansi asikwazi ukuba ngaphansi kuka-zero",
      priceRequired: "Intengo yokuthengisa iyadingeka",
      priceValid: "Intengo yokuthengisa kufanele ibe semthethweni",
      priceAboveZero: "Intengo yokuthengisa kufanele ibe ngaphezu kuka-zero",
      productNameRequired: "Igama lomkhiqizo liyadingeka",
      stockRequired: "Isitoko sokuqala siyadingeka",
      stockWhole: "Isitoko sokuqala kufanele sibe inombolo ephelele",
      stockNonNegative: "Isitoko sokuqala asikwazi ukuba ngaphansi kuka-zero",
      invalidCreditAmount:
        "Faka inani elisemthethweni lokuthengisa ngesikweletu",
    },
  },
};

Object.entries(sellTranslations).forEach(([language, sell]) => {
  translations[language] = {
    ...(translations[language] || {}),
    sell,
  };
});

function localizationLeafPaths(value, prefix = "") {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    typeof value === "function"
  ) {
    return [prefix];
  }
  return Object.entries(value).flatMap(([key, entry]) =>
    localizationLeafPaths(entry, prefix ? `${prefix}.${key}` : key),
  );
}

function hasLocalizationPath(value, path) {
  return path.split(".").every((segment) => {
    if (value === null || value === undefined || !Object.prototype.hasOwnProperty.call(value, segment)) {
      return false;
    }
    value = value[segment];
    return true;
  });
}

export function getTranslationCoverage(language) {
  const basePaths = localizationLeafPaths(baseMessages);
  const translated = translations[language];
  if (!translated || basePaths.length === 0) return 0;
  const covered = basePaths.filter((path) => hasLocalizationPath(translated, path)).length;
  return covered / basePaths.length;
}

export function getMissingTranslationPaths(language) {
  const translated = translations[language];
  return localizationLeafPaths(baseMessages).filter(
    (path) => !hasLocalizationPath(translated, path),
  );
}

// Every supported language remains selectable while newer copy safely falls
// back to English until its catalog entry is added.
export const languageOptions = allLanguageOptions;

function mergeMessages(base, override) {
  if (!override) return base;
  const merged = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      typeof value !== "function"
    ) {
      merged[key] = mergeMessages(base?.[key] || {}, value);
      return;
    }
    merged[key] = value;
  });
  return merged;
}

function normalizeLanguage(language) {
  if (!language) return null;
  const normalized = String(language).trim().toLowerCase();
  let candidate = null;
  if (normalized === "ar" || normalized.startsWith("ar-")) candidate = "ar";
  if (normalized === "en" || normalized.startsWith("en-")) candidate = "en";
  if (normalized === "fr" || normalized.startsWith("fr-")) candidate = "fr";
  if (normalized === "ha" || normalized.startsWith("ha-")) candidate = "ha";
  if (normalized === "ig" || normalized.startsWith("ig-")) candidate = "ig";
  if (normalized === "pt" || normalized.startsWith("pt-")) candidate = "pt";
  if (normalized === "sw" || normalized.startsWith("sw-")) candidate = "sw";
  if (normalized === "yo" || normalized.startsWith("yo-")) candidate = "yo";
  if (normalized === "zu" || normalized.startsWith("zu-")) candidate = "zu";
  return allLanguageOptions.some(({ code }) => code === candidate) ? candidate : null;
}

function detectInitialLanguage() {
  if (typeof window === "undefined") return "en";
  const storedLanguage = normalizeLanguage(
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY),
  );
  const legacyLanguage = normalizeLanguage(
    window.localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY),
  );
  const browserLanguage = normalizeLanguage(
    window.navigator?.language || window.navigator?.languages?.[0],
  );
  return storedLanguage || legacyLanguage || browserLanguage || "en";
}

export function getMessagesForLanguage(language) {
  const messages = mergeMessages(baseMessages, translations[language]);
  const parity = parityCopy[language] || parityCopy.en;
  return mergeMessages(messages, {
    layout: {
      credits: parity.nav.credits,
      creditsSubtitle: parity.nav.creditsSubtitle,
      expenses: parity.nav.expenses,
      expensesSubtitle: parity.nav.expensesSubtitle,
    },
    settings: { notificationPreferences: parity.nav.notificationPreferences },
    staffPage: { permissionExpenses: parity.nav.permissionExpenses },
  });
}

export function getLocalizationSnapshot(language = detectInitialLanguage()) {
  const normalized = normalizeLanguage(language) || "en";
  return {
    isRTL: normalized === "ar",
    language: normalized,
    locale: localeMap[normalized] || localeMap.en,
    messages: getMessagesForLanguage(normalized),
  };
}

const LocalizationContext = createContext(null);

export function LocalizationProvider({ children }) {
  const [language, setLanguageState] = useState(detectInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    window.localStorage.setItem(LEGACY_LANGUAGE_STORAGE_KEY, language);
    window.document.documentElement.lang = localeMap[language] || localeMap.en;
    window.document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      isRTL: language === "ar",
      locale: localeMap[language] || localeMap.en,
      messages: getMessagesForLanguage(language),
      setLanguage: async (nextLanguage) => {
        const normalized = normalizeLanguage(nextLanguage) || "en";
        setLanguageState(normalized);
      },
    }),
    [language],
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within LocalizationProvider.",
    );
  }
  return context;
}
